// Nomad-mon Go! — P1 five-card holo showcase
// Skills applied: threejs-procedural-materials (authored foil identity per rarity, one
// cause stack: view geometry -> thin-film phase + bands + glints + aurora ribbons, all
// fwidth-filtered), threejs-procedural-vfx (event-envelope reveal sweep, pooled rising
// ember/shimmer particles with normalized lifetime, scene-relative HDR emission
// hierarchy), threejs-bloom (HDR ordering, emissive hierarchy, no-post baseline),
// threejs-exposure-color-grading (single tone-map owner: OutputPass with renderer
// ACESFilmic; bloom composited pre tone map).

import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { KawaseBloomPass } from './kawase-bloom.js';
import { createLutController } from './lut-pass.js';
import { TYPES, KEYWORDS, typeGlyph, sigilSVG, artSrc, clipSrcUrl } from './types.js';
import * as audio from './audio.js';
import { difficulty as selectedDifficulty, initShell, setMpDeckMode, greet, levelRingHtml, raiseVeil } from './shell.js';
import { battleReward, referenceRanks } from './scaling.js';
import { starCost } from './deckrules.js';
import { plevelProgress as plevelProgressOf, budgetFor as budgetOf, matchXp as matchXpOf, missionMet } from './plevel.js';
import {
  effectivePwr, levelCard, levelProgress,
  teamMedianEffectivePwr, victoryXp, isHolo,
} from './levelling.js';
import { ENGINE_VERSION } from './engine.js';
import {
  passiveFor, hasPassive, GAUNTLET_UNLOCK_CARDS, GAUNTLET_LEGS, GAUNTLET_LABELS,
  GAUNTLET_BIAS, BOSS_DECK, BOSS_MECHANIC, gauntletReward,
} from './depth.js';
import * as identity from './identity.js';
import * as haptics from './haptics.js';
import { forgeHolo } from './forgeholo.js';
import * as guide from './guide.js';
import * as academy from './academy.js';
import * as nav from './nav.js';   // SC46: back-button navigation + exit confirmation
import * as trading from './trading.js';
import { RENDER_ACTION_TIME_SCALE } from './render-contract.js';
import { heatFor, resolveDraw } from './draw.js';
import { createDepthArt } from './depth-art.js';
import { createEntryFX, EXIT_REVEAL, ENTRY_BEATS } from './entry.js';
import { initPrismTilt } from './prism.js';
import { displayName } from './names.js';
import { initTitleCarousel } from './title-carousel.js';
import { initAccount, openNameForge, openSignIn, openLinkPage, confirmLoginSwitch } from './account.js';
import { loadForged, forgedCardDefs } from './forged.js';
import { makeFusionRim } from './fusion-rim.js';
import { cleanChampionData, makeLiveChampion, countsTowardTop, grandChampion, championCardId, isChampionCard } from './champion.js';
import { championMediaHtml, championFrameHtml, championMetaHtml, ensureChampionCss } from './champion-frame.js';
import * as forgeUI from './forgeui.js';
import * as codexLib from './codex-lib.js';   // the Codex chunk's view of the game modules (shares main's copies)
import { attachForgedFx, detachForgedFx, setLensGyro, duoLensFollow, TILT_SVG, DRAG_SVG, wireForgedVideos } from './forged-card.js';
import { LENS_DEG, makeLensTracker, makeYawAxis, yawRate, screenRollDeg as lensRollDeg } from './lens-tilt.js';   // dupduo
import { pwr as pwrOf } from './types.js';
import { entryGateWanted, openEntryGate } from './gate.js';
import * as onboard from './onboard.js';
import { veteranSeen } from './progress-gate.js';
import { plevelFromXp } from './plevel.js';
import { campaignReward as campaignLedger } from './campaign-xp.js';   // v239 B2's campaign ledger
import * as crashlog from './crashlog.js';
import * as perfmon from './perfmon.js';   // v259 frame-timing telemetry (perf beacon)
import BOSS_CARDS from '../data/campaign/boss-cards.json' with { type: 'json' };   // v243 boss-only campaign cards
import { initEternalWish, setMeter as setEternalMeter, wishStamp, isWishGranted, openWishPicker } from './eternal-wish.js';   // ETERNAL pity + wish

// The front door starts this decode when it becomes visible. The status is
// observable because fetch completion is not proof that decodeAudioData ran.
let entryAudioReady = null;
let entryAudioSettled = false;
const entryAudioStatus = { startedPerf: null, readyPerf: null, ready: false, failed: false };
function preloadEntryAudio() {
  if (entryAudioReady) return entryAudioReady;
  entryAudioStatus.startedPerf = +performance.now().toFixed(2);
  entryAudioReady = audio.loadSfx(['entryhit', 'entrycharge', 'entryplunge', 'whoosh']).then((buffers) => {
    entryAudioStatus.ready = true;
    entryAudioStatus.readyPerf = +performance.now().toFixed(2);
    entryAudioSettled = true;
    publishEntryReady();
    return buffers;
  }).catch((error) => {
    entryAudioStatus.failed = true;
    entryAudioSettled = true;
    publishEntryReady();
    throw error;
  });
  return entryAudioReady;
}

// SC2-B: load the stored player + run the guest deck-ownership migration
// before anything reads ownership (binder build, shell deck filters).
identity.init();

// ---------------------------------------------------------------- params / debug
const q = new URLSearchParams(location.search);
const SEED = Number(q.get('seed') || 7);           // deterministic inputs
const NOPOST = q.get('nopost') === '1';            // no-post baseline (must still read)
const DEBUG_MASK = q.get('debug') === 'mask';      // foil controlling-field view
const OFFLINE_RENDER = q.get('offlineRender') === '1';
const HAS_DEBUG_PIXEL_CAP = q.has('pixelcap');
const DEBUG_PIXEL_CAP = Number(q.get('pixelcap'));
const MAX_NATIVE_DPR = 3;
const NATIVE_PIXEL_CAP = OFFLINE_RENDER ? 2.0 : Math.min(devicePixelRatio, MAX_NATIVE_DPR);
const FULL_PIXEL_CAP = Number.isFinite(DEBUG_PIXEL_CAP) && DEBUG_PIXEL_CAP > 0
  ? DEBUG_PIXEL_CAP : NATIVE_PIXEL_CAP;
const QUALITY_STORAGE_KEY = 'nomadmon-render-quality-v1';
const QUALITY_SCALES = [1, 1, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3];
const readStoredQualityStep = () => {
  if (OFFLINE_RENDER || HAS_DEBUG_PIXEL_CAP) return 0;
  // Never resume a demotion: one slow moment must not make every later visit
  // start in perf mode (title went hero-only on a capable tablet). Each session
  // starts at full quality and sheds live if it must.
  try { localStorage.removeItem(QUALITY_STORAGE_KEY); } catch {}
  return 0;
};
let qualityStep = readStoredQualityStep();
const qualityPixelCap = (step = qualityStep) => Math.min(FULL_PIXEL_CAP,
  Math.max(1, NATIVE_PIXEL_CAP * QUALITY_SCALES[THREE.MathUtils.clamp(step, 0, QUALITY_SCALES.length - 1)]));
let qualityGraceUntil = performance.now() + 2000;
function noteQualityActivity(now = performance.now()) {
  qualityGraceUntil = Math.max(qualityGraceUntil, now + 2000);
}

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const rng = mulberry32(SEED * 1013 + 5);
// P8 Move 5: honor prefers-reduced-motion (idle parallax/drift become stillness)
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
const depthArt = createDepthArt({ offline: OFFLINE_RENDER });
await depthArt.ready;
// P8 Move 4: thumbnails honor an optional per-card thumbFocal {x,y}
function thumbPos(d) {
  const tf = d.thumbFocal;
  return `${(((tf && tf.x) ?? d.focalX ?? 0.5) * 100).toFixed(1)}% ` +
    `${(((tf && tf.y) ?? 0.5) * 100).toFixed(1)}%`;
}

// ---------------------------------------------------------------- renderer / scene
const app = document.getElementById('app');
const renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });
crashlog.install(renderer.domElement);
const MAX_TEXTURE_ANISOTROPY = Math.min(16, renderer.capabilities.getMaxAnisotropy());
let pixelCap = qualityPixelCap();
renderer.setPixelRatio(Math.min(devicePixelRatio, pixelCap));
renderer.setSize(innerWidth, innerHeight);
// Single tone-map owner: renderer setting is applied ONLY by OutputPass at chain end.
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.0;
app.appendChild(renderer.domElement);

const scene = new THREE.Scene();
scene.background = new THREE.Color(0x05060c);

const camera = new THREE.PerspectiveCamera(40, innerWidth / innerHeight, 0.1, 40);

// Authored HDR studio shared by the fan, deck picker, pack and battle. Long,
// edged sources give metal and foil a readable streak instead of a soft blob.
function makeStudioEnv() {
  const W = 256, H = 128;
  const data = new Float32Array(W * H * 4);
  const wrapped = (a, b) => Math.min(Math.abs(a - b), 1 - Math.abs(a - b));
  const panel = (u, v, cu, cv, hu, hv, feather) => {
    const outside = Math.max(wrapped(u, cu) - hu, Math.abs(v - cv) - hv, 0);
    return outside === 0 ? 1 : Math.exp(-4 * (outside / feather) ** 2);
  };
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const u = (x + 0.5) / W, v = (y + 0.5) / H;
      const ground = Math.max(0, (v - 0.52) / 0.48);
      let r = 0.025 + ground * 0.14, g = 0.03 + ground * 0.105;
      let b = 0.07 + (1 - v) * 0.045 + ground * 0.08;

      // Top-front warm key and top-back cool strip: flat cores create the
      // hard highlight, while the exponential skirt keeps their edge soft.
      const warm = panel(u, v, 0.48, 0.205, 0.29, 0.022, 0.045);
      const cool = panel(u, v, 0.98, 0.285, 0.24, 0.018, 0.04);
      r += warm * 5.75 + cool * 1.75;
      g += warm * 4.35 + cool * 2.65;
      b += warm * 2.35 + cool * 5.55;

      // A narrow horizon and two vertical colour cards produce secondary
      // streaks as a card tilts instead of washing the whole metal surface.
      const horizon = Math.exp(-Math.pow((v - 0.505) / 0.009, 2));
      const violet = panel(u, v, 0.18, 0.49, 0.025, 0.18, 0.045);
      const teal = panel(u, v, 0.82, 0.48, 0.025, 0.18, 0.045);
      r += horizon * 2.6 + violet * 2.25 + teal * 0.25;
      g += horizon * 2.35 + violet * 0.6 + teal * 2.55;
      b += horizon * 2.8 + violet * 4.25 + teal * 3.45;
      const i = (y * W + x) * 4;
      data[i] = Math.min(r, 6); data[i + 1] = Math.min(g, 6);
      data[i + 2] = Math.min(b, 6); data[i + 3] = 1;
    }
  }
  // HALF float, not FloatType: linear filtering of 32-bit float textures needs
  // OES_texture_float_linear, which Android GPUs (Mali/Immortalis, Adreno) do not
  // expose. Without it the texture is incomplete, samples black, and PMREM bakes
  // a BLACK studio -- every env-lit frame/metal lost its reflections on Jack's
  // tablet while every Mac/headless probe looked right. Half floats filter everywhere.
  const half = new Uint16Array(data.length);
  for (let i = 0; i < data.length; i++) half[i] = THREE.DataUtils.toHalfFloat(data[i]);
  const tex = new THREE.DataTexture(half, W, H, THREE.RGBAFormat, THREE.HalfFloatType);
  tex.mapping = THREE.EquirectangularReflectionMapping;
  tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}
const pmrem = new THREE.PMREMGenerator(renderer);
const studioEnvSource = makeStudioEnv();
const studioPmremStart = performance.now();
scene.environment = pmrem.fromEquirectangular(studioEnvSource).texture;
const studioPmremMs = performance.now() - studioPmremStart;
studioEnvSource.dispose();
pmrem.dispose();
console.info(`[nomadmon] studio PMREM ${studioPmremMs.toFixed(2)}ms`);

scene.add(new THREE.AmbientLight(0x8a86b0, 0.55));
const key = new THREE.DirectionalLight(0xffe2b0, 1.35);
key.position.set(2.2, 3.0, 3.5);
scene.add(key);
const rim = new THREE.PointLight(0x7a5df0, 1.8, 12);
rim.position.set(-2.6, -1.2, 2.2);
scene.add(rim);

// ---------------------------------------------------------------- card dimensions
const CW = 1.26, CH = 1.76, CR = 0.085;   // 63:88 portrait
const DEPTH = 0.075;                       // total edge depth (parallax budget)

function roundedRectShape(w, h, r) {
  const s = new THREE.Shape();
  const x = -w / 2, y = -h / 2;
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);  s.absarc(x + w - r, y + r, r, -Math.PI / 2, 0);
  s.lineTo(x + w, y + h - r); s.absarc(x + w - r, y + h - r, r, 0, Math.PI / 2);
  s.lineTo(x + r, y + h);  s.absarc(x + r, y + h - r, r, Math.PI / 2, Math.PI);
  s.lineTo(x, y + r);      s.absarc(x + r, y + r, r, Math.PI, Math.PI * 1.5);
  return s;
}
function roundedRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// ---------------------------------------------------------------- rarity language
// One escalating identity per tier. Grouped by perceptual role:
// trim (edge emitter duo), foilA/foilB (thin-film tint duo), prismatic (0 tinted foil
// -> 1 full spectral + aurora ribbons), foil (pattern energy), glass (art glass tint),
// frame (border metal gradient stops), gem duo, particles, DOM label color.
const RARITY = {
  common: {
    label: 'Common', color: '#b8c4cc',
    trimA: [0.55, 0.62, 0.70], trimB: [0.78, 0.84, 0.92],
    foilA: [0.60, 0.66, 0.75], foilB: [0.82, 0.88, 0.95],
    prismatic: 0.04, foil: 0.32, glass: [1.0, 1.0, 1.0],
    frame: ['#3c454e', '#9fb0bc', '#4e5a66', '#c8d4dc', '#2c343c'],
    gemA: '#dce8f0', gemB: '#4e5a66', edge: 1.7, particles: null,
  },
  rare: {
    label: 'Rare', color: '#4fd8ff',
    trimA: [0.10, 0.75, 1.00], trimB: [0.45, 0.95, 1.00],
    foilA: [0.35, 0.85, 1.00], foilB: [0.70, 1.00, 0.95],
    prismatic: 0.10, foil: 0.55, glass: [0.88, 1.02, 1.08],
    frame: ['#31536b', '#8fd8ec', '#3f6f88', '#c2f0fa', '#22394c'],
    gemA: '#aef2ff', gemB: '#1f6f96', edge: 2.4, particles: null,
  },
  epic: {
    label: 'Epic', color: '#b98aff',
    trimA: [0.55, 0.30, 1.00], trimB: [0.85, 0.60, 1.00],
    foilA: [0.65, 0.40, 1.00], foilB: [0.90, 0.70, 1.00],
    prismatic: 0.24, foil: 0.75, glass: [0.99, 0.92, 1.07],
    frame: ['#4a2e72', '#b48ae8', '#61399a', '#dcc0fa', '#341f58'],
    gemA: '#e6d2ff', gemB: '#5c2ea8', edge: 2.7, particles: null,
  },
  legendary: {
    label: 'Legendary', color: '#f0c463',
    trimA: [1.00, 0.55, 0.12], trimB: [0.50, 0.30, 1.00],
    foilA: [1.00, 0.80, 0.35], foilB: [1.00, 0.95, 0.70],
    prismatic: 0.50, foil: 1.0, glass: [1.05, 1.00, 0.93],
    frame: ['#8a6a24', '#e8c46a', '#a37c2e', '#f2d98f', '#7c5e1e'],
    gemA: '#f4e6ff', gemB: '#3c1f78', edge: 3.4, particles: 'embers',
  },
  mythic: {
    label: 'Mythic', color: '#ff9ae8',
    trimA: [1.00, 0.40, 0.90], trimB: [0.30, 0.90, 1.00],
    foilA: [1.00, 0.70, 0.95], foilB: [0.60, 0.95, 1.00],
    prismatic: 1.0, foil: 1.15, glass: [1.0, 1.0, 1.0],
    frame: ['#6e5a86', '#e8d4f0', '#8a6aa0', '#f8ecff', '#4c3a66'],
    gemA: '#ffffff', gemB: '#7a3ea0', edge: 3.4, particles: 'shimmer',
  },
  // SC23 THE ETERNAL TIER — the one tier ABOVE prismatic. Mythic's identity is
  // "full spectrum"; there is nowhere further to go on hue, so eternal changes
  // MATERIAL instead: the foil stops being thin-film and becomes liquid gold
  // flowing over a starfield (uEternal in the foil shader), the frame drops to
  // obsidian so the gold is the only bright thing on the card, and the motes
  // are slow drifting stars that NEVER switch off — every other tier's
  // particles are focus-only. Read order at a glance: darker card, hotter trim.
  eternal: {
    label: 'Eternal', color: '#ffe9a8',
    trimA: [1.00, 0.78, 0.26], trimB: [1.00, 0.98, 0.88],
    foilA: [1.00, 0.84, 0.38], foilB: [1.00, 1.00, 0.94],
    prismatic: 1.0, foil: 1.35, glass: [1.07, 1.02, 0.94],
    frame: ['#120c02', '#ffe9a8', '#5c4410', '#fffaf0', '#0a0700'],
    gemA: '#fffdf4', gemB: '#8a6a18', edge: 4.4, particles: 'starfield',
    eternal: true,
  },
};

// ---------------------------------------------------------------- frame texture
// Procedural per-rarity frame: metal border, gems, starburst, crest medallion mount.
// No reading-size text — DOM owns text.
function makeFrameTexture(rar, crestImg) {
  const W = 768, H = 1072, SCALE = 1.5;
  const c = document.createElement('canvas'); c.width = W * SCALE; c.height = H * SCALE;
  const g = c.getContext('2d');
  g.scale(SCALE, SCALE);
  const M = 26, B = 62;

  const metal = g.createLinearGradient(0, 0, W, H);
  metal.addColorStop(0, rar.frame[0]); metal.addColorStop(0.25, rar.frame[1]);
  metal.addColorStop(0.5, rar.frame[2]); metal.addColorStop(0.75, rar.frame[3]);
  metal.addColorStop(1, rar.frame[4]);
  roundedRectPath(g, M, M, W - 2 * M, H - 2 * M, 46);
  g.fillStyle = metal; g.fill();

  // speckle wear (shared cause with roughness feel)
  for (let i = 0; i < 2600; i++) {
    const x = rng() * W, y = rng() * H;
    g.fillStyle = rng() > 0.5 ? 'rgba(255,240,200,0.05)' : 'rgba(40,30,20,0.07)';
    g.fillRect(x, y, 1.6, 1.6);
  }

  // inner plate
  roundedRectPath(g, M + 14, M + 14, W - 2 * (M + 14), H - 2 * (M + 14), 36);
  g.strokeStyle = 'rgba(25,18,10,0.75)'; g.lineWidth = 4; g.stroke();
  roundedRectPath(g, M + 20, M + 20, W - 2 * (M + 20), H - 2 * (M + 20), 32);
  g.strokeStyle = 'rgba(255,244,220,0.5)'; g.lineWidth = 2; g.stroke();

  // corner gems
  const gems = [[M + 34, M + 34], [W - M - 34, M + 34], [M + 34, H - M - 34], [W - M - 34, H - M - 34]];
  for (const [x, y] of gems) {
    const rg = g.createRadialGradient(x - 3, y - 3, 1, x, y, 15);
    rg.addColorStop(0, rar.gemA); rg.addColorStop(0.45, rar.gemB); rg.addColorStop(1, '#170c30');
    g.save(); g.translate(x, y); g.rotate(Math.PI / 4);
    g.fillStyle = rg; g.fillRect(-11, -11, 22, 22);
    g.strokeStyle = 'rgba(20,10,40,.8)'; g.lineWidth = 2.5; g.strokeRect(-11, -11, 22, 22);
    g.restore();
  }

  // top emblem: starburst (glyph, not text)
  g.save(); g.translate(W / 2, M + B * 0.62);
  g.fillStyle = 'rgba(20,14,6,0.85)';
  for (let i = 0; i < 8; i++) {
    g.rotate(Math.PI / 4);
    g.beginPath(); g.moveTo(0, -20); g.lineTo(5, -6); g.lineTo(-5, -6); g.closePath(); g.fill();
  }
  g.fillStyle = rar.frame[3]; g.beginPath(); g.arc(0, 0, 7, 0, 7); g.fill();
  g.restore();

  // bottom band: two side diamonds flanking the crest
  g.save(); g.translate(W / 2, H - M - B * 0.6);
  for (const i of [-1, 1]) {
    g.save(); g.translate(i * 150, 0); g.rotate(Math.PI / 4);
    g.fillStyle = 'rgba(25,15,5,0.8)'; g.fillRect(-11, -11, 22, 22);
    g.strokeStyle = 'rgba(255,244,220,0.5)'; g.lineWidth = 2; g.strokeRect(-11, -11, 22, 22);
    g.restore();
  }
  g.restore();

  // punch the art window
  g.fillStyle = '#000';
  g.globalCompositeOperation = 'destination-out';
  roundedRectPath(g, M + B, M + B + 26, W - 2 * (M + B), H - 2 * (M + B) - 30, 26);
  g.fill();
  g.globalCompositeOperation = 'source-over';

  // window inner lip
  roundedRectPath(g, M + B, M + B + 26, W - 2 * (M + B), H - 2 * (M + B) - 30, 26);
  g.strokeStyle = 'rgba(20,13,4,0.9)'; g.lineWidth = 5; g.stroke();

  // crest medallion mounted bottom-center (after punch so it survives)
  // v210: the old centre (H - M - B*0.52) put the medallion's lower rim ~20px
  // past the texture's bottom edge, so the card showed it sliced flat. The coin
  // still straddles the frame's outer edge, but now ends inside the texture:
  // centre H - 83 -> outer ring bottom at H - 2.
  const cx = W / 2, cy = H - 83, cr = 74;
  g.save();
  g.beginPath(); g.arc(cx, cy, cr, 0, 7); g.clip();
  g.drawImage(crestImg, cx - cr, cy - cr, cr * 2, cr * 2);
  g.restore();
  g.beginPath(); g.arc(cx, cy, cr + 1.5, 0, 7);
  g.strokeStyle = rar.frame[1]; g.lineWidth = 4; g.stroke();
  g.beginPath(); g.arc(cx, cy, cr + 4.5, 0, 7);
  g.strokeStyle = 'rgba(20,13,4,0.85)'; g.lineWidth = 2.5; g.stroke();

  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = MAX_TEXTURE_ANISOTROPY;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = true;
  return t;
}

// cover-crop a loaded texture onto a plane of given aspect (w/h).
// focalX (0..1, default 0.5) picks the horizontal window when cropping landscape
// sources into the portrait art window (P2 real-photo cards).
function coverTexture(tex, planeAspect, focalX = 0.5) {
  const img = tex.image;
  const texAspect = img.width / img.height;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = MAX_TEXTURE_ANISOTROPY;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  if (texAspect < planeAspect) {          // crop vertically
    const r = texAspect / planeAspect;
    tex.repeat.set(1, r); tex.offset.set(0, (1 - r) / 2);
  } else {                                // crop horizontally
    const r = planeAspect / texAspect;
    tex.repeat.set(r, 1); tex.offset.set((1 - r) * focalX, 0);
  }
  return tex;
}

// per-rarity frame textures are identical between cards — cache them
const frameTexCache = new Map();
function getFrameTexture(rar, crestImg) {
  if (!frameTexCache.has(rar)) frameTexCache.set(rar, makeFrameTexture(rar, crestImg));
  return frameTexCache.get(rar);
}

// ---------------------------------------------------------------- foil shader
// Authored identity: one cause stack — view geometry -> thin-film phase + diagonal
// holo bands + glint grid + (mythic) aurora ribbons, masked by frame/window fields.
// Specular AA: band and glint contrast attenuated by fwidth of their phase.
// uSweep drives the reveal light sweep (event envelope, normalized 0..1).
const foilVert = /* glsl */`
  varying vec2 vUv;
  varying vec3 vWorldPos;
  varying vec3 vWorldNormal;
  void main() {
    vUv = uv;
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    vWorldNormal = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;
const foilFrag = /* glsl */`
  precision highp float;
  varying vec2 vUv;
  varying vec3 vWorldPos;
  varying vec3 vWorldNormal;
  uniform float uTime, uSeed, uIntensity, uGlintDensity, uWindowGain;
  uniform float uPrismatic, uFoilStrength, uSweep, uSweepGain, uEternal;
  uniform vec3 uTintA, uTintB;
  uniform int uDebugMask, uBack;

  float hash21(vec2 p) {
    p = fract(p * vec2(234.34, 435.345) + uSeed);
    p += dot(p, p + 34.23);
    return fract(p.x * p.y);
  }
  float noise2(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash21(i), hash21(i + vec2(1,0)), f.x),
               mix(hash21(i + vec2(0,1)), hash21(i + vec2(1,1)), f.x), f.y);
  }
  float fbm(vec2 p) {
    float v = 0.0, a = 0.5;
    for (int i = 0; i < 3; i++) { v += a * noise2(p); p *= 2.13; a *= 0.5; }
    return v;
  }
  vec3 spectral(float t) {
    return 0.5 + 0.5 * cos(6.28318 * (t + vec3(0.0, 0.33, 0.67)));
  }
  float rrect(vec2 uv, vec2 half_, float r) {
    vec2 p = (uv - 0.5) * vec2(1.26, 1.76);
    vec2 d = abs(p) - half_ + r;
    return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0) - r;
  }

  void main() {
    vec3 V = normalize(cameraPosition - vWorldPos);
    vec3 N = normalize(vWorldNormal);
    float cosT = clamp(abs(dot(V, N)), 0.0, 1.0);
    float fresnel = pow(1.0 - cosT, 2.2);

    vec2 par = V.xy * 0.35;

    // -- controlling fields --------------------------------------------------
    float dWin = rrect(vUv, vec2(0.455, 0.63), 0.05);
    float dCard = rrect(vUv, vec2(0.61, 0.86), 0.08);
    float frameMask, windowMask;
    if (uBack == 1) {                       // back face: one full-card foil field
      frameMask = 0.0;
      windowMask = smoothstep(0.005, -0.03, dCard);
    } else {
      frameMask = smoothstep(0.012, 0.05, dWin) * smoothstep(-0.005, -0.045, dCard);
      windowMask = smoothstep(0.05, -0.01, dWin);
    }

    // -- thin-film iridescence ----------------------------------------------
    float thickness = fbm(vUv * 3.0 + uSeed) * 2.0 + fbm(vUv * 9.0 - par * 2.0);
    float phase = thickness * 1.4 + cosT * 2.6 + uTime * 0.05;
    // tinted foil (rarity duo) vs full spectral, blended by prismatic amount
    vec3 tinted = mix(uTintA, uTintB, 0.5 + 0.5 * sin(phase * 3.1));
    vec3 film = mix(tinted, spectral(phase), uPrismatic);

    // -- ETERNAL: liquid gold over a starfield (the tier above prismatic) ----
    // Same par/fbm causes as every other field on this card, so it parallaxes
    // with them instead of floating. Gold REPLACES most of the spectral film
    // (a card that is every colour reads as no colour); the stars are a sparse
    // hash grid that twinkles on its own clock.
    float starGlow = 0.0;
    if (uEternal > 0.5) {
      // PERF (SC23): the molten field is derived from the thickness field, the fbm sum
      // this shader ALREADY computed for the thin-film term, plus one cheap
      // travelling sine. Two extra octaved fbm() calls here cost ~2 fps on the
      // binder at M4 for no visible gain — the gold reads identically.
      float flow = thickness * 0.30 + sin(vUv.y * 3.1 - uTime * 0.11 + par.x * 2.0) * 0.16;
      float molten = pow(clamp(flow, 0.0, 1.0), 1.6);
      vec3 gold = mix(vec3(0.20, 0.11, 0.02), vec3(1.00, 0.86, 0.42), molten);
      gold = mix(gold, vec3(1.00, 0.99, 0.90), pow(molten, 4.0));  // hot cores
      film = mix(film, gold, 0.62);

      vec2 sc = vUv * 34.0 + par * 3.0;
      vec2 sid = floor(sc);
      float sh = hash21(sid);
      vec2 sp = fract(sc) - vec2(0.25 + 0.5 * hash21(sid + 3.1),
                                 0.25 + 0.5 * hash21(sid + 7.7));
      float star = smoothstep(0.30, 0.0, length(sp)) * step(0.86, sh);
      star *= 0.35 + 0.65 * pow(0.5 + 0.5 * sin(uTime * (1.1 + sh * 2.4) + sh * 51.0), 3.0);
      starGlow = star * uFoilStrength;
    }

    // -- diagonal holo bands (fwidth-filtered) ------------------------------
    float band = dot(vUv + par, vec2(0.8, 0.6)) * 22.0 + uTime * 0.35;
    float bw = fwidth(band);
    float bands = sin(band) * 0.5 + 0.5;
    bands = pow(bands, 3.0);
    bands *= clamp(1.0 - bw * 0.9, 0.0, 1.0);
    bands *= uFoilStrength;

    // -- glint grid ----------------------------------------------------------
    vec2 cell = floor(vUv * uGlintDensity);
    float gr = hash21(cell);
    float glintPhase = fract(gr + dot(V.xy, vec2(2.7, 3.9)) * 0.8 + uTime * 0.02);
    float glint = smoothstep(0.985, 1.0, 1.0 - abs(glintPhase - 0.5) * 2.0);
    vec2 cuv = fract(vUv * uGlintDensity) - 0.5;
    glint *= smoothstep(0.45, 0.05, length(cuv));
    float gw = fwidth(vUv.x * uGlintDensity);
    glint *= clamp(1.5 - gw, 0.0, 1.0) * uFoilStrength;

    // -- mythic aurora ribbons (same par/fbm causes, lower frequency) -------
    float rib = fbm(vUv * 2.4 + par * 1.5 + uTime * 0.05);
    float ribPhase = vUv.x * 2.2 + vUv.y * 5.2 + rib * 4.5 + uTime * 0.22;
    float ribAmp = pow(0.5 + 0.5 * sin(ribPhase), 2.0);
    vec3 aurora = spectral(ribPhase * 0.16 + cosT * 0.8) * ribAmp;

    // -- reveal light sweep (event envelope) --------------------------------
    float sweepD = dot(vUv, vec2(0.62, 0.78));
    float sweep = exp(-pow((sweepD - uSweep * 1.9 + 0.25) * 8.0, 2.0));
    float sweepOn = step(0.015, uSweep) * step(uSweep, 0.985);
    vec3 sweepCol = mix(uTintB, vec3(1.0), 0.55) * sweep * sweepOn;

    // -- composition ---------------------------------------------------------
    vec3 frameFoil = film * (0.25 + bands * 0.9) + vec3(1.0, 0.9, 0.6) * glint * 2.2;
    vec3 windowFoil = film * bands * 0.28 + vec3(0.9, 0.8, 1.0) * glint * 1.1;
    vec3 col = frameFoil * frameMask + windowFoil * windowMask * uWindowGain;
    col += aurora * uPrismatic * uPrismatic * (frameMask * 0.9 + windowMask * 0.55 * uWindowGain);
    // stars sit ABOVE the sweep's base range so they still read during reveal
    col += vec3(1.0, 0.97, 0.88) * starGlow * 2.6 * (frameMask + windowMask * 0.8 * uWindowGain);

    float energy = (0.16 + fresnel * 0.9) * uIntensity;
    col *= energy * 1.9;   // HDR: peaks pass bloom threshold, base stays below
    // sweep sits near the top of the card-local HDR range (blooms hard, briefly)
    col += sweepCol * (frameMask + windowMask * uWindowGain) * 4.5 * uIntensity * uSweepGain;

    float alpha = clamp((bands * 0.55 + glint + fresnel * 0.5 + sweep * sweepOn * 0.8
                         + starGlow * 0.9)
                        * (frameMask + windowMask * 0.7 * uWindowGain), 0.0, 1.0);

    if (uDebugMask == 1) {
      gl_FragColor = vec4(frameMask, windowMask, bands, 1.0);
      return;
    }
    gl_FragColor = vec4(col, alpha * 0.9);
  }
`;
function makeFoilMaterial(rar, { back = false } = {}) {
  return new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uSeed: { value: SEED },
      uIntensity: { value: 1.0 },
      uWindowGain: { value: 0.0 },   // v118: art window foil only during the picker sweep — resting veil washed every card
      uGlintDensity: { value: 26.0 },
      uPrismatic: { value: back ? Math.min(rar.prismatic, 0.5) : rar.prismatic },
      uFoilStrength: { value: back ? rar.foil * 0.45 : rar.foil },
      uSweep: { value: 0 },
      uSweepGain: { value: 1.0 },
      uTintA: { value: new THREE.Vector3(...rar.foilA) },
      uTintB: { value: new THREE.Vector3(...rar.foilB) },
      uEternal: { value: rar.eternal ? 1 : 0 },
      uDebugMask: { value: DEBUG_MASK ? 1 : 0 },
      uBack: { value: back ? 1 : 0 },
    },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: back ? THREE.BackSide : THREE.FrontSide,
    vertexShader: foilVert,
    fragmentShader: foilFrag,
  });
}

// ---------------------------------------------------------------- edge glow
// Emissive hierarchy: edge glow owns the top of card-local HDR range (blooms).
// Prismatic mode = liquid-light rainbow; accent = trap pulse (crimson-amber).
const edgeVert = /* glsl */`
  varying vec3 vLocal;
  void main() {
    vLocal = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const edgeFrag = /* glsl */`
  precision highp float;
  varying vec3 vLocal;
  uniform float uTime, uDim, uPrismatic, uAccentAmt, uLevel;
  uniform vec3 uColA, uColB, uAccent;
  vec3 spectral(float t) {
    return 0.5 + 0.5 * cos(6.28318 * (t + vec3(0.0, 0.33, 0.67)));
  }
  void main() {
    float a = atan(vLocal.y, vLocal.x);
    float pulse = 0.86 + 0.14 * sin(uTime * 1.4);
    vec3 duo = mix(uColA, uColB, 0.5 + 0.5 * sin(a * 2.0 + uTime * 0.8));
    // liquid rainbow: hue flows around the perimeter with a slow wobble
    vec3 liquid = spectral(a / 6.28318 + uTime * 0.13 + 0.06 * sin(a * 3.0 + uTime * 0.9));
    vec3 col = mix(duo, liquid, uPrismatic);
    float accentPulse = pow(0.5 + 0.5 * sin(uTime * 2.6), 2.0);
    col = mix(col, uAccent, uAccentAmt * accentPulse * 0.8);
    gl_FragColor = vec4(col * uLevel * pulse * uDim, 1.0);
  }
`;
function makeEdgeMaterial(rar, accent) {
  return new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uDim: { value: 1 },
      uPrismatic: { value: rar.prismatic >= 1.0 ? 1.0 : 0.0 },
      uAccentAmt: { value: accent === 'trap' ? 1.0 : 0.0 },
      uLevel: { value: rar.edge },
      uColA: { value: new THREE.Vector3(...rar.trimA) },
      uColB: { value: new THREE.Vector3(...rar.trimB) },
      uAccent: { value: new THREE.Vector3(1.0, 0.32, 0.10) },
    },
    vertexShader: edgeVert,
    fragmentShader: edgeFrag,
  });
}

// ---------------------------------------------------------------- interior motes
// SC25: DELETED. The art-window mote plane painted twinkling white specks over
// every photo ("snowfall") and cheapened the portraits. The rarity particles
// (embers/shimmer/ETERNAL starfield), the scene dust and the foil are separate
// systems and are untouched.
// ---------------------------------------------------------------- rarity particles
// Pooled per-card rising particles; normalized lifetime, one flow direction (up),
// mode 0 = legendary embers (gold-orange flicker), mode 1 = mythic shimmer
// (spectral sparkles rising off the card face). Fades with uFade (focus envelope).
const SPARK_N = 120;
// The eternal starfield is PERMANENT (every other tier's particles are focus-
// only), so it pays its cost on every frame the card is on screen. A sparse
// field of larger motes reads better as stars anyway and costs ~60% less.
const SPARK_N_ETERNAL = 44;
function makeSparks(mode, rar) {
  const SPARK_N = mode === 2 ? SPARK_N_ETERNAL : 120;
  const geo = new THREE.BufferGeometry();
  const pos = new Float32Array(SPARK_N * 3);
  const attr = new Float32Array(SPARK_N * 3);   // seed, speed, xSpread
  for (let i = 0; i < SPARK_N; i++) {
    attr[i * 3] = rng();
    attr[i * 3 + 1] = 0.35 + rng() * 0.65;
    attr[i * 3 + 2] = (rng() - 0.5);
    pos[i * 3] = 0; pos[i * 3 + 1] = 0;
    pos[i * 3 + 2] = mode === 1 ? 0.10 + rng() * 0.25 : 0.02 + rng() * 0.10;
  }
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('aAttr', new THREE.BufferAttribute(attr, 3));
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uPix: { value: Math.min(devicePixelRatio, pixelCap) },
      uFade: { value: 0 },
      uMode: { value: mode },
    },
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    vertexShader: /* glsl */`
      attribute vec3 aAttr;
      varying float vLife;
      varying float vSeed;
      uniform float uTime, uPix, uMode;
      void main() {
        float seed = aAttr.x, speed = aAttr.y, xs = aAttr.z;
        // normalized lifetime: 0 birth (card body) .. 1 death (above card)
        // mode 2 (eternal starfield) drifts at less than half speed — it is an
        // ambient field the card sits inside, not an emission coming off it.
        float rate = uMode > 1.5 ? 0.068 : 0.16;
        float life = fract(seed + uTime * speed * rate);
        vLife = life;
        vSeed = seed;
        vec3 p = position;
        float riseH = uMode > 1.5 ? 2.9 : (uMode > 0.5 ? 2.4 : 1.9);
        p.x = xs * (uMode > 1.5 ? 1.45 : 1.15)
            + sin(uTime * (0.8 + speed) + seed * 40.0) * 0.08 * life;
        p.y = -0.95 + life * riseH;
        p.z += sin(uTime * speed + seed * 17.0) * 0.05;
        float grow = sin(life * 3.14159);           // in/out along one envelope
        vec4 mv = modelViewMatrix * vec4(p, 1.0);
        float size = (uMode > 1.5 ? 6.4 : (uMode > 0.5 ? 5.5 : 4.0)) * grow * (0.4 + speed);
        gl_PointSize = min(size * uPix * (2.2 / -mv.z), 13.0 * uPix);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */`
      precision highp float;
      varying float vLife;
      varying float vSeed;
      uniform float uTime, uFade, uMode;
      vec3 spectral(float t) {
        return 0.5 + 0.5 * cos(6.28318 * (t + vec3(0.0, 0.33, 0.67)));
      }
      void main() {
        vec2 c = gl_PointCoord - 0.5;
        float d = smoothstep(0.5, 0.0, length(c));
        float env = sin(vLife * 3.14159);
        vec3 col;
        float lum;
        if (uMode > 1.5) {
          // ETERNAL starfield: liquid-gold motes with a few white stars among
          // them. Slow twinkle (a long sine, not the mythic hard pulse) so the
          // field reads as ambient depth rather than sparkle.
          float tw = 0.55 + 0.45 * sin(uTime * (0.7 + vSeed * 1.3) + vSeed * 44.0);
          float white = step(0.78, fract(vSeed * 7.31));
          col = mix(vec3(1.0, 0.84, 0.40), vec3(1.0, 0.99, 0.94), white);
          lum = tw * (1.5 + white * 1.1);
        } else if (uMode > 0.5) {
          // mythic shimmer: spectral sparkle, hard twinkle
          float tw = pow(0.5 + 0.5 * sin(uTime * (2.0 + vSeed * 4.0) + vSeed * 60.0), 3.0);
          col = spectral(vSeed + vLife * 0.35);
          lum = (0.5 + tw * 2.3);
        } else {
          // legendary embers: gold -> deep orange as they die, soft flicker
          float fl = 0.7 + 0.3 * sin(uTime * (3.0 + vSeed * 5.0) + vSeed * 30.0);
          col = mix(vec3(1.0, 0.82, 0.4), vec3(1.0, 0.35, 0.08), vLife);
          lum = fl * 1.6;
        }
        // Emissive hierarchy: particles sit between aura and edge glow.
        gl_FragColor = vec4(col * d * env * lum * uFade, d * env * uFade);
      }
    `,
  });
  return new THREE.Points(geo, mat);
}

// ---------------------------------------------------------------- asset loading
const bitmapLoader = new THREE.ImageBitmapLoader();
// ImageBitmaps are uploaded bottom-up; 'from-image' left every art texture upside down
// (Jack: 'most of the cards are upside down now except for video cards'). Pre-flip here.
bitmapLoader.setOptions({ imageOrientation: 'flipY', premultiplyAlpha: 'none' });
// A deploy replaces the hashed dist chunks this page was built against. A tab
// opened BEFORE the release then fails its first lazy import (the v110 deploy
// broke the deck picker on Jack's tablet exactly this way). One reload picks up
// the new build; a second failure surfaces normally instead of looping.
// Mixed-release guard: every lazy entry stamps the release it was built for
// (scripts/build.mjs). A stamp that is not this shell's V means the server
// already holds a newer deploy; reload once (same guard as a failed import).
let mixedBuild = null;
globalThis.__nmEntryBuild = (name, build) => { if (build !== V && !mixedBuild) mixedBuild = { name, build }; };
async function lazyImport(spec) {
  let mod;
  try { mod = await import(spec); }
  catch (e) { return staleReload(e); }
  if (mixedBuild) {
    console.warn(`[nomadmon] ${mixedBuild.name}.js is ${mixedBuild.build}, this page is ${V}: reloading`);
    try { return await staleReload(new Error('mixed build')); } catch { /* already reloaded once this release: carry on */ }
  }
  return mod;
}
// (also for the bundled lazy chunks, e.g. the Codex: a stale tab's first failed import reloads once)
async function staleReload(e) {
  const k = 'nomadmon-stale-reload';
  let done = null;
  try { done = sessionStorage.getItem(k); } catch {}
  if (done !== V) {
    try { sessionStorage.setItem(k, V); } catch {}
    location.reload();
    await new Promise(() => {});
  }
  throw e;
}
const texLoader = new THREE.TextureLoader();
function configureLoadedTex(tex) {
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = MAX_TEXTURE_ANISOTROPY;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}
function ensureTextureWidth(tex, minWidth = 0) {
  const image = tex.image;
  const width = Number(image?.width) || 0;
  const height = Number(image?.height) || 0;
  if (!minWidth || width >= minWidth || !width || !height) return tex;
  const canvas = document.createElement('canvas');
  canvas.width = minWidth;
  canvas.height = Math.max(1, Math.round(height * minWidth / width));
  const g = canvas.getContext('2d');
  g.imageSmoothingEnabled = true;
  g.imageSmoothingQuality = 'high';
  // v109: an ImageBitmap from bitmapLoader was decoded with imageOrientation
  // 'flipY' (rows already bottom-up, because WebGL ignores UNPACK_FLIP_Y for
  // bitmaps — the v96 lesson). A canvas is uploaded WITH tex.flipY, so redraw
  // the bitmap flipped back to upright or the card lands upside down.
  const fromBitmap = typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap;
  if (fromBitmap) { g.translate(0, canvas.height); g.scale(1, -1); }
  g.drawImage(image, 0, 0, canvas.width, canvas.height);
  image.close?.();
  tex.image = canvas;
  tex.needsUpdate = true;
  return tex;
}
// QA: prove the GL-upload orientation of a loaded art texture against a plain
// <img> of the same URL. Returns mean luma of the row that ends up at the
// BOTTOM of the GL texture, plus the plain image's top and bottom rows.
async function qaTexOrientation(url, minWidth = 0) {
  const tex = await loadTex(url, minWidth);
  const rowLuma = (src, w, h, row) => {
    const c = document.createElement('canvas'); c.width = w; c.height = h;
    const g = c.getContext('2d'); g.drawImage(src, 0, 0, w, h);
    const d = g.getImageData(0, Math.max(0, Math.min(h - 1, row)), w, 1).data;
    let sum = 0; for (let i = 0; i < d.length; i += 4) sum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    return sum / (d.length / 4);
  };
  const im = await loadImg(url);
  const w = 64, h = 96;
  const imgTop = rowLuma(im, w, h, 0), imgBottom = rowLuma(im, w, h, h - 1);
  const image = tex.image;
  const isCanvas = image instanceof HTMLCanvasElement;
  // canvas + flipY: GL bottom row = last canvas row. ImageBitmap (flipY ignored,
  // rows pre-flipped by the decoder): GL bottom row = its first row.
  const glBottom = isCanvas && tex.flipY ? rowLuma(image, w, h, h - 1) : rowLuma(image, w, h, 0);
  const kind = isCanvas ? 'canvas' : (typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap) ? 'bitmap' : 'img';
  tex.dispose();
  return { kind, flipY: tex.flipY, width: image.width, glBottom, imgTop, imgBottom };
}
async function loadTex(url, minWidth = 0) {
  let bitmap = null;
  try {
    bitmap = await bitmapLoader.loadAsync(url);
    const tex = ensureTextureWidth(configureLoadedTex(new THREE.Texture(bitmap)), minWidth);
    tex.needsUpdate = true;
    noteQualityActivity();
    return tex;
  } catch {
    bitmap?.close?.();
    // Safari versions without ImageBitmapLoader support, decode failures, and
    // unusual image responses retain the established HTMLImageElement path.
    const tex = ensureTextureWidth(configureLoadedTex(await texLoader.loadAsync(url)), minWidth);
    noteQualityActivity();
    return tex;
  }
}
function loadImg(url) {
  return new Promise((res, rej) => {
    const im = new Image();
    im.onload = () => res(im); im.onerror = rej; im.src = url;
  });
}

const V = 'v350';
// v239 THE CAMPAIGN is the menu's lead item ("Campaign" opens the world map; Quick Match sits in
// the map header). KILL SWITCH: ?campaign=0 turns it off on this device (persisted) -> the menu says
// Battle and plays Quick Match exactly as before; ?campaign=1 turns it back on.
const CAMPAIGN_OFF = 'nomadmon-campaign-off';
const campaignOn = (() => {
  try {
    const v = new URLSearchParams(location.search).get('campaign');
    if (v === '0') localStorage.setItem(CAMPAIGN_OFF, '1'); else if (v === '1') localStorage.removeItem(CAMPAIGN_OFF);
    localStorage.removeItem('nomadmon-campaign-dev');   // (the B3 dev flag, retired)
    return localStorage.getItem(CAMPAIGN_OFF) !== '1';
  } catch { return !/[?&]campaign=0\b/.test(location.search); }
})();
let campaignUI = null;
// the menu item's label follows the switch (index.html ships "Campaign")
const MODE_NAME = campaignOn ? 'Campaign' : 'Battle';   // v239 the pack gate's copy follows the flip
{ const tl = document.querySelector('#menuSolo .tl'); if (tl) tl.textContent = MODE_NAME; }
{ const mb = document.getElementById('montBattle'); if (mb) mb.textContent = 'To ' + MODE_NAME; }
// "World 1 · Level 4": the next level, read from the campaign's local progress (no module load)
function campaignNextLine() {
  try {
    const k = identity.playerKey(), key = k === 'guest' ? 'nomadmon-campaign-map-v1' : `nomadmon-campaign-map-v1:${k}`;   // (campaign.js progKey)
    const p = JSON.parse(localStorage.getItem(key) || '{}'), L = (p && p.levels) || {};
    // v243 the whole road: the first level not cleared, world by world (15 levels each)
    for (let w = 1; w <= 15; w++) for (let n = 1; n <= 15; n++) {
      const e = L[`w${w}-${String(n).padStart(2, '0')}`];
      if (!e || !e.cleared) return `World ${w} \u00b7 Level ${n}`;
    }
    return 'World 15 \u00b7 Level 15';
  } catch { return 'World 1'; }
}
// SC3 Phase 2: cards.json is merged nightly on the server — its cache-bust
// param comes from /api/nomadmon/meta so daily merges land without a redeploy.
const serverMeta = await fetch(`/api/nomadmon/meta`, { signal: AbortSignal.timeout(2500) })
  .then(r => r.json())
  .catch(() => ({}));
identity.setCapabilities(serverMeta.capabilities);
const cardsBust = serverMeta.cardsVersion ? `${V}-cv${serverMeta.cardsVersion}` : V;
// SC22 EDGE STATE: the service worker deliberately never caches cards.json
// (nightly merges must land instantly — see sw.js), so a cold offline boot
// cannot get the card set. That is a considered trade, but the failure used
// to be silent: this module simply threw at its top-level await and the
// branded loader sat on "Summoning" forever. Give it a designed ending.
function bootFailure(msg, sub) {
  const ld = document.getElementById('loader');
  if (!ld) return;
  ld.classList.remove('done');
  const w = ld.querySelector('.ld-word');
  const tip = ld.querySelector('.ld-tip');
  if (w) w.textContent = msg;
  if (tip) tip.textContent = sub;
  ld.classList.add('failed');
}
let cardsData, crestImg, backTexRaw;
// INK DUEL CHAMPIONS (data/champions.json, written by the duel): rides the boot fetches; missing = none
const championsFetch = fetch(`./data/champions.json?${V}`).then(r => (r.ok ? r.json() : null)).catch(() => null);
try {
  [, cardsData, crestImg, backTexRaw] = await Promise.all([
    // v11 FORGED (Duos + Fusions): data/fusions.json rides the boot fetch; a
    // failure only means no forged cards this session, never a dead boot.
    loadForged(V).then(() => null),
    fetch(`./cards.json?${cardsBust}`).then(r => {
      if (!r.ok) throw new Error(`cards.json ${r.status}`);
      return r.json();
    }),
    loadImg(`./assets/web/crest.webp?${V}`),
    loadTex(`./assets/web/card-back.webp?${V}`),
  ]);
} catch (err) {
  console.error('[nomadmon] boot failed:', err);
  bootFailure('No Signal', 'The archive needs a connection. Reconnect and reload.');
  throw err;                          // nothing downstream can run without the set
}
// SC41 hard rule, defense in depth: people who must never appear are dropped
// from the set before anything downstream can see them. Jack, 2026-08-24:
// "Christin, Ziggy should remain hidden in the game. the rest are okay. nli and
// Jonah should also be hidden." Kousha is retained from the standing exclusion.
// This is a FILTER, not the sanitiser below -- a sanitiser cleans a card that
// still ships; these people ship no card at all. It is deliberately here as
// well as in cards.json, because a guard that only exists in the data is not a
// guard: SC41 proved that the stills-only DECK assertion passed on a dirty
// payload precisely because a sanitiser had already rescued it.
const EXCLUDED_PEOPLE = new Set(['Jonah', 'Kousha', 'Nli', 'Christin', 'Ziggy']);
const rawDeck = cardsData.cards;
const deck = rawDeck.filter((d) => !EXCLUDED_PEOPLE.has(d.person));
// v11 FORGED: the 24 Duos + 24 Fusions join the set as ordinary card entities
// (same index-keyed art/texture/clip machinery), flagged `forged`. Every
// "roll / list the whole set" site filters them out (packs, binder grid, title,
// AI drafts); ownership-driven surfaces (squad carousel, battle) see them.
{
  const known = new Set(deck.map(d => d.id));
  for (const f of forgedCardDefs()) if (!known.has(f.id)) deck.push({ ...f, moves: f.moves.map(m => ({ ...m })) });
  // v243 BOSS-ONLY campaign cards (data/campaign/boss-cards.json): flagged forged:'boss' so every "roll / list the
  // whole set" filter (packs, binder, title, set count) already skips them; only a campaign boss record fields one
  for (const b of BOSS_CARDS.cards) if (!known.has(b.id)) deck.push({ ...b, forged: 'boss', moves: b.moves.map(m => ({ ...m })) });
}
// INK DUEL CHAMPIONS: never in packs (flagged forged:'champion', so every set/pack filter skips them);
// owned ones are real cards whose stats are recomputed live from the player's top three (src/champion.js)
const CHAMPIONS = cleanChampionData(await championsFetch, [...EXCLUDED_PEOPLE]);
const champCtx = {
  fighters: () => deck.filter(d => countsTowardTop(d) && identity.ownedCount(d.id) > 0),
  levelFor: (id) => identity.cardLevel(id),
  state: (id) => identity.championState(id),
};
for (const c of CHAMPIONS) deck.push(makeLiveChampion(c, champCtx));
initEternalWish({ deck, V, toast });   // ETERNAL pity meter + wish picker (the 24 Eternals of this deck)
const championGrand = () => grandChampion(identity.championsOwned().map(e => e.id), CHAMPIONS.map(c => c.id));
const SET_SIZE = deck.filter(d => !d.forged).length;   // "the set" excludes forged cards
if (deck.length !== rawDeck.length) {
  console.warn(`[nomadmon] dropped ${rawDeck.length - deck.length} card(s) for excluded people`);
}
// P5 hard rule, defense in depth: stills-only people NEVER get video treatment.
// Sheren/Zach are deliberately absent: their only rule is no YouTube.
const STILLS_ONLY = new Set(['Jonah', 'Kousha', 'Nli']);
for (const d of deck) {
  // Diarrhea cards wear their LAB TAPE as the card art: a whisper-volume house
  // loop re-encoded from the tape rides the exact living-clip pipeline, and
  // ACTIVATE still plays the FULL tape at full volume (labVideo.url).
  //
  // SC31 removed a hard override here that rewrote every diarrhea clip to
  // `assets/clips/lab-<id>.mp4`. That was fine at 8 cards and is not at 59:
  // those loops would have to ship IN the bundle, and 8 of them already cost
  // 16 MB. The derived-loop ROUTE (/api/nomadmon/clip/<id>.mp4) exists exactly
  // for this and is what cards.json already carries, so the override now only
  // did harm — it pointed 51 of the 59 cards at files that do not exist.
  // cards.json is the single owner of the clip path; nothing rewrites it.
  if (STILLS_ONLY.has(d.person)) { d.clip = null; d.labVideo = null; delete d.variant; }
  // SC15: GRAND ARCHIVE living cards whose clips are still remote-only render
  // as stills — the card video system needs same-origin encodes, and the
  // LIVING badge stays honest: no badge until a LOCAL clip exists.
  if (d.clipPending && !d.clip) { d.clip = null; delete d.variant; }
}

// SC21 CLIENT PREFETCH — with ~236 living cards, a reveal should never wait on
// the network. The moment we know which cards are coming (pack contents, both
// battle decks) we pull their loops into the HTTP cache at low priority; the
// route's immutable headers mean the <video> request then hits disk.
// Bounded on purpose: a pack is ~5 clips, a battle ~10, and the session cap
// keeps a long binder crawl from quietly draining a phone plan.
const clipPrefetched = new Set();
const CLIP_PREFETCH_SESSION_CAP = 48;
function prefetchClips(list, max = 8) {
  const conn = navigator.connection;
  if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return 0;
  let n = 0;
  for (const d of list || []) {
    if (n >= max || clipPrefetched.size >= CLIP_PREFETCH_SESSION_CAP) break;
    const url = d && d.clip ? clipSrcUrl(d.clip, V) : null;
    if (!url || clipPrefetched.has(url)) continue;
    clipPrefetched.add(url); n++;
    try {
      fetch(url, { priority: 'low', credentials: 'omit' })
        .then(r => (r.ok ? r.arrayBuffer() : null))   // consume so it lands in cache
        .catch(() => { /* best effort: the still is always a valid outcome */ });
    } catch { /* older browsers without priority hints */ }
  }
  return n;
}
const featuredIdx = deck.map((d, i) => d.featured ? i : -1).filter(i => i >= 0);
const artTexCache = new Map();                      // `${deckIndex}:${size}` -> texture
const ART_TEX_CAP = 64;
const artTexKey = (i, size = 'full') => `${i}:${size}`;
const artTexPinned = new Set(featuredIdx.map(i => artTexKey(i))); // title cards live for the page
const artTexLive = new Set();
const artTexLiveScopes = new Map();

function rebuildArtTexLive() {
  artTexLive.clear();
  for (const keys of artTexLiveScopes.values()) for (const key of keys) artTexLive.add(key);
}
function setArtTexLive(scope, keys) {
  if (keys && keys.size) artTexLiveScopes.set(scope, new Set(keys));
  else artTexLiveScopes.delete(scope);
  rebuildArtTexLive();
  trimArtTexCache();
}
function setArtTexKeyLive(scope, key, live) {
  const keys = new Set(artTexLiveScopes.get(scope) || []);
  if (live) keys.add(key); else keys.delete(key);
  setArtTexLive(scope, keys);
}
function touchArtTex(key) {
  const tex = artTexCache.get(key);
  if (!tex) return null;
  artTexCache.delete(key);
  artTexCache.set(key, tex);
  return tex;
}
function trimArtTexCache() {
  while (artTexCache.size > ART_TEX_CAP) {
    let evicted = false;
    for (const [key, tex] of artTexCache) {
      if (artTexPinned.has(key) || artTexLive.has(key)) continue;
      tex.dispose();
      tex.image?.close?.();
      artTexCache.delete(key);
      evicted = true;
      break;
    }
    // All over-cap entries are protected by a currently rendered context.
    // Teardown clears that scope and immediately calls this function again.
    if (!evicted) break;
  }
}
// SC22 EDGE STATE: card art is mostly served from /api/nomadmon/art/:key. A
// 500, a timeout or a flaky mobile connection used to make loadTex REJECT,
// and ensureArtTex awaited it unguarded — so a single failed art fetch threw
// straight out of buildPack(), leaving body.packing set with no pack behind
// it: a dead screen the player could only escape via the home medallion.
// Art is decoration; it must degrade to a placeholder, never abort a flow.
let artFallbackTex = null;
function artFallback() {
  if (!artFallbackTex) {
    const cv = document.createElement('canvas');
    cv.width = cv.height = 8;
    const g = cv.getContext('2d');
    g.fillStyle = '#141220';                 // the card's own dark plate tone
    g.fillRect(0, 0, 8, 8);
    artFallbackTex = new THREE.CanvasTexture(cv);
    artFallbackTex.colorSpace = THREE.SRGBColorSpace;
  }
  return artFallbackTex;
}
async function ensureArtTex(i, size = 'full') {
  const key = artTexKey(i, size);
  const cached = touchArtTex(key);
  if (cached) return cached;
  try {
    const tex = await loadTex(artSrc(deck[i], V, size === 'thumb' ? 'thumb' : undefined),
      size === 'thumb' ? 0 : 1000);
    artTexCache.set(key, tex);
    trimArtTexCache();
    return tex;
  } catch {
    // deliberately NOT cached: a transient failure must not permanently
    // condemn this card to the placeholder — the next visit retries.
    return artFallback();
  }
}
setArtTexLive('fan', new Set(featuredIdx.map(i => artTexKey(i))));
// PERF (v326 review): boot used to AWAIT all twelve featured full-size images (~2.6 MB) before the entry UI could
// appear, though the title hides this fan and only the collection shows it. They now load in the background;
// each fan card starts on the shared placeholder and takes its art the moment it lands (setCardArtTexture),
// the same swap the deck picker uses after an eviction.
// (i) only: map would pass the array index as `size` and key every texture as `${i}:0`
const featuredArt = new Map(featuredIdx.map(i => [i, ensureArtTex(i)]));
const backTex = coverTexture(backTexRaw, CW / CH);

// ---------------------------------------------------------------- card assembly
const cards = [];   // { data, rar, group, mats..., flipTarget, flipNow, sparks }
const fan = new THREE.Group();
scene.add(fan);

// Card edge. The front frame texture (makeFrameTexture: 768x1072, margin
// M=26, corner 46) leaves a transparent margin around its metal, so a ring at
// the full plane read as a separate white plate with a gap. The card is now
// one clean slab with a single outline, the metal frame's: a flat FRONT rim
// a hair under the frame plane (the metal overlaps its inset, so no seam can
// open, and the crest medallion stays on top), the BACK image cut to the same
// rounded outline with an identical BACK rim facing away (culled from the
// front), and a SIDE wall flush with both rims: dark rarity-tinted metal with
// a lit gradient, specular and catch-light lips, so a card turned edge-on reads
// as a thin premium edge (v194's flat black wall read as a glitch; the old
// full-plane back also overhung the wall as a coloured band). The slab spans
// back plane to front rim, ~0.06 deep (the old ring was 0.075). Geometries are
// shared by every card and never disposed (userData.sharedCardRim is skipped
// by every scene teardown).
const EDGE_FX = CW * (1 - 2 * 26 / 768), EDGE_FY = CH * (1 - 2 * 26 / 1072), EDGE_FR = CW * 46 / 768;
const EDGE_OUT = 0.016;                        // rim + wall reach past the metal outline
const BACK_Z = -DEPTH * 0.40;                  // back plane, just behind the art (-DEPTH*0.36)
const FRONT_RIM_Z = DEPTH * 0.42 - 0.003;      // just under the frame plane
function edgeRing(w, h, r, inset, outset) {
  const outer = roundedRectShape(w + 2 * outset, h + 2 * outset, r + outset);
  outer.holes.push(roundedRectShape(w - 2 * inset, h - 2 * inset, Math.max(0.001, r - inset)));
  const geo = new THREE.ShapeGeometry(outer, 24);
  geo.userData.sharedCardRim = true;
  return geo;
}
const edgeRim = edgeRing(EDGE_FX, EDGE_FY, EDGE_FR, 0.01, EDGE_OUT);   // front AND back
// the back image on the same rounded outline (UVs span it 0..1), tucked under the rim
const backFace = (() => {
  const w = EDGE_FX + 0.012, h = EDGE_FY + 0.012;
  const geo = new THREE.ShapeGeometry(roundedRectShape(w, h, EDGE_FR + 0.006), 24);
  const pos = geo.attributes.position, uv = geo.attributes.uv;
  for (let i = 0; i < pos.count; i++) uv.setXY(i, pos.getX(i) / w + 0.5, pos.getY(i) / h + 0.5);
  uv.needsUpdate = true;
  geo.userData.sharedCardRim = true;
  return geo;
})();
const EDGE_Z0 = BACK_Z + 0.002, EDGE_Z1 = FRONT_RIM_Z - 0.001;
const edgeWall = (() => {
  const outer = roundedRectShape(EDGE_FX + 2 * EDGE_OUT, EDGE_FY + 2 * EDGE_OUT, EDGE_FR + EDGE_OUT);
  outer.holes.push(roundedRectShape(EDGE_FX - 0.02, EDGE_FY - 0.02, EDGE_FR - 0.01));
  const geo = new THREE.ExtrudeGeometry(outer, { depth: EDGE_Z1 - EDGE_Z0, bevelEnabled: false, curveSegments: 24 });
  geo.translate(0, 0, EDGE_Z0);
  geo.userData.sharedCardRim = true;
  return geo;
})();
// side-wall metal: shares the card's edge colour + dim uniforms, so every
// recolour of the rim (rarity, diarrhea, battle attack rims, the title's
// graphite) tints the edge with it
function makeEdgeWallMaterial(edgeMat) {
  const u = edgeMat.uniforms;
  return new THREE.ShaderMaterial({
    uniforms: { uColA: u.uColA, uColB: u.uColB, uDim: u.uDim },
    vertexShader: /* glsl */`
      varying vec3 vN, vV;
      varying float vT, vY;
      void main() {
        vN = normalize(normalMatrix * normal);
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vV = -mv.xyz;
        vT = (position.z - ${EDGE_Z0.toFixed(5)}) / ${(EDGE_Z1 - EDGE_Z0).toFixed(5)};
        vY = position.y / ${(EDGE_FY / 2 + EDGE_OUT).toFixed(5)};
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: /* glsl */`
      precision highp float;
      uniform vec3 uColA, uColB;
      uniform float uDim;
      varying vec3 vN, vV;
      varying float vT, vY;
      void main() {
        vec3 n = normalize(vN), v = normalize(vV);
        vec3 L = normalize(vec3(-0.35, 0.75, 0.55));
        vec3 tint = clamp(mix(uColA, uColB, 0.5), 0.0, 1.0);
        vec3 base = mix(vec3(0.085, 0.08, 0.10), tint * 0.34, 0.35);
        float diff = 0.45 + 0.55 * max(dot(n, L), 0.0);
        float grad = 0.72 + 0.36 * (vY * 0.5 + 0.5);              // lit from above
        float spec = pow(max(dot(n, normalize(L + v)), 0.0), 40.0) * 0.8;
        float fres = pow(1.0 - abs(dot(n, v)), 3.0) * 0.18;
        // bevel catch-light along both faces' lips: reads as a thin machined edge
        float lip = smoothstep(0.80, 1.0, vT) * 0.55 + smoothstep(0.20, 0.0, vT) * 0.4;
        vec3 col = base * diff * grad
          + (spec + fres) * mix(vec3(1.0), tint, 0.45) * 0.55
          + lip * mix(vec3(0.85), tint, 0.6) * 0.32;
        col *= mix(0.55, 1.0, clamp(uDim, 0.0, 1.0));
        gl_FragColor = vec4(col, 1.0);
      }`,
  });
}

// SC4 ALT-ART "living painting": one shared clock uniform object — every alt
// material references it, ONE write per frame ticks them all (fan, pack,
// battle, binder solo). Cheap UV warp, not real depth: the art sways on a
// slow 2.5D ellipse, strongest mid-frame, pinned at the edges.
const ALT_CLOCK = { value: 0 };
function makeAltArtMaterial(tex) {
  const mat = new THREE.ShaderMaterial({
    // coverTexture sets repeat/offset — ShaderMaterial gets them explicitly
    uniforms: {
      uTime: ALT_CLOCK, map: { value: tex },
      uTint: { value: new THREE.Color(1, 1, 1) },
      uRep: { value: tex.repeat }, uOff: { value: tex.offset },
    },
    vertexShader: `varying vec2 vUv; void main(){ vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */`
      precision highp float;
      varying vec2 vUv;
      uniform float uTime;
      uniform sampler2D map;
      uniform vec3 uTint;
      uniform vec2 uRep, uOff;
      void main() {
        vec2 c = vUv - 0.5;
        // depth mask: full sway mid-art, zero at the frame edge (no border crawl)
        float m = smoothstep(0.5, 0.18, max(abs(c.x), abs(c.y)));
        float a = uTime * 0.31;
        vec2 sway = vec2(sin(a), cos(a * 0.79)) * 0.006;
        // faint counter-phase layer deeper in: two depths sell the parallax
        vec2 uv = vUv + sway * m - sway * 0.35 * (1.0 - m);
        // slow breathing zoom, same mask
        uv = 0.5 + (uv - 0.5) * (1.0 - 0.004 * m * (0.5 + 0.5 * sin(uTime * 0.23)));
        gl_FragColor = vec4(texture2D(map, uv * uRep + uOff).rgb * uTint, 1.0);
      }
    `,
  });
  // MeshBasicMaterial API parity — every mode tints/dims art via .color, and
  // battle's bakeCardFront reads .map.image/.repeat/.offset. Alias both onto
  // the shader uniforms so the alt material is a drop-in.
  mat.map = tex;
  Object.defineProperty(mat, 'color', { get() { return mat.uniforms.uTint.value; } });
  return mat;
}

// buildCard: full multi-layer card object for a deck entry. Used by the fan,
// the solo (binder) view, and battle mode.
function buildCard(data, artTex) {
  const rar = RARITY[data.rarity];
  const group = new THREE.Group();

  // deep art layer (real geometric parallax vs frame) — glass-tinted per rarity
  // Indexed cards use their base printing's depth map. Clip cards keep this on
  // the still plane only; their video plane remains an ordinary flat texture.
  const coveredArt = coverTexture(artTex, (CW * 0.94) / (CH * 0.94), data.focalX ?? 0.5);
  const artMat = depthArt.has(data)
    ? depthArt.makeMaterial(coveredArt, data)
    : data.altOf
      ? makeAltArtMaterial(coveredArt)
    : new THREE.MeshBasicMaterial({
      map: coveredArt,
    });
  if (!artMat.uniforms?.uDepth) depthArt.trackPlain(artMat, data);
  const artPlane = new THREE.Mesh(new THREE.PlaneGeometry(CW * 0.94, CH * 0.94), artMat);
  artPlane.position.z = -DEPTH * 0.36;
  group.add(artPlane);

  // P5 LIVING: lazy video window — a second plane just above the still that the
  // shared video manager cross-fades in for the ONE focused living card.
  let videoPlane = null;
  if (data.clip) {
    videoPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(CW * 0.94, CH * 0.94),
      new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
    );
    videoPlane.position.z = -DEPTH * 0.36 + 0.004;
    videoPlane.visible = false;
    group.add(videoPlane);
  }

  // metal frame (front surface)
  const frameMat = new THREE.MeshStandardMaterial({
    map: getFrameTexture(rar, crestImg), transparent: true,
    metalness: 0.82, roughness: 0.3, envMapIntensity: 0.78, alphaTest: 0.02,
  });
  frameMat.color.setScalar(1.45);   // lift metal albedo (map is dark-authored)
  const framePlane = new THREE.Mesh(new THREE.PlaneGeometry(CW, CH), frameMat);
  framePlane.position.z = DEPTH * 0.42;
  group.add(framePlane);

  // foil overlay (whole front)
  const foilMat = makeFoilMaterial(rar);
  // P5 variant printings layered on the rarity identity:
  // diarrhea holo = restrained-but-committed brown-gold foil + frame tint;
  // living = a touch more shimmer energy on the frame.
  let frameTint = [1, 1, 1];
  if (data.variant === 'diarrhea') {
    foilMat.uniforms.uTintA.value.set(0.78, 0.55, 0.16);
    foilMat.uniforms.uTintB.value.set(1.0, 0.85, 0.45);
    foilMat.uniforms.uPrismatic.value = Math.min(rar.prismatic, 0.16);
    foilMat.uniforms.uFoilStrength.value = rar.foil * 1.18;   // SC2-A sheen pass
    frameTint = [1.08, 0.96, 0.74];
  } else if (data.variant === 'living') {
    foilMat.uniforms.uFoilStrength.value = rar.foil * 1.22;
    foilMat.uniforms.uGlintDensity.value = 32.0;
  }
  const foilPlane = new THREE.Mesh(new THREE.PlaneGeometry(CW, CH), foilMat);
  foilPlane.position.z = DEPTH * 0.46;
  group.add(foilPlane);

  // card back: Damon crest back art + its own subtle foil shimmer
  const backPlane = new THREE.Mesh(
    backFace,
    new THREE.MeshStandardMaterial({ map: backTex, metalness: 0.38, roughness: 0.42,
      envMapIntensity: 0.72 })
  );
  backPlane.rotation.y = Math.PI;
  backPlane.position.z = BACK_Z;
  group.add(backPlane);
  const backFoilMat = makeFoilMaterial(rar, { back: true });
  const backFoil = new THREE.Mesh(backFace, backFoilMat);
  backFoil.position.z = BACK_Z - 0.003;
  group.add(backFoil);

  // glowing edge ring
  const edgeMat = makeEdgeMaterial(rar, data.accent);
  if (data.variant === 'diarrhea') {
    edgeMat.uniforms.uColA.value.set(0.85, 0.55, 0.12);
    edgeMat.uniforms.uColB.value.set(1.0, 0.85, 0.40);
    edgeMat.uniforms.uPrismatic.value = 0.0;
  }
  const edgeFront = new THREE.Mesh(edgeRim, edgeMat);
  edgeFront.position.z = FRONT_RIM_Z;
  group.add(edgeFront);
  const edgeBack = new THREE.Mesh(edgeRim, edgeMat);
  edgeBack.rotation.y = Math.PI;
  edgeBack.position.z = BACK_Z - 0.004;                 // just behind the back plane + foil, facing out
  group.add(edgeBack);
  const wallMat = makeEdgeWallMaterial(edgeMat);
  group.add(new THREE.Mesh(edgeWall, wallMat));

  // rarity particles (legendary embers / mythic shimmer) — focused card only
  let sparks = null;
  if (rar.particles) {
    sparks = makeSparks(
      rar.particles === 'starfield' ? 2 : rar.particles === 'shimmer' ? 1 : 0, rar);
    sparks.visible = false;
    group.add(sparks);
  }

  if (data.variant === 'diarrhea')
    frameMat.color.setRGB(1.45 * frameTint[0], 1.45 * frameTint[1], 1.45 * frameTint[2]);

  // v11 FUSION RIM: the animated border (band, filaments, motes, sigil
  // corners) every Fusion wears. Shares the foil's uTime so it moves wherever
  // the card's foil already does.
  let rim = null;
  if (data.forged === 'fusion') {
    rim = makeFusionRim({ CW, CH, CR, tier: data.tier || 1, uTime: foilMat.uniforms.uTime, z: -DEPTH * 0.7 });
    group.add(rim);
  }

  const card = {
    data, rar, group, artMat, frameMat, foilMat, backFoilMat, edgeMat, wallMat,
    artPlane, framePlane, foilPlane, backPlane, sparks, videoPlane, frameTint, rim,
    depthMat: artMat.uniforms?.uDepth ? artMat : null,
    flipTarget: 0, flipNow: 0, focusF: 0,
  };
  card.depthUpdate = (motion) => depthArt.update(card, group, motion);
  return card;
}

function setCardArtTexture(c, data, tex) {
  coverTexture(tex, (CW * 0.94) / (CH * 0.94), data.focalX ?? 0.5);
  c.artMat.map = tex;
  if (c.artMat.uniforms?.map) c.artMat.uniforms.map.value = tex;
  c.artMat.needsUpdate = true;
}

// The picker retains off-window card objects for fast reverse scrolling. Its
// Object3D visibility is therefore the exact ownership boundary: hidden cards
// may be evicted, while visible cards are protected. If a hidden object's map
// was evicted, keep it invisible until the art has been rehydrated so it can
// never flash a disposed (black) plate on a reverse scroll.
function buildDeckPickerCard(data, tex) {
  const c = buildCard(data, tex);
  // Picker art is compared directly with the same plain printing. Its private
  // material bypasses scene tone mapping so the photograph is not altered to
  // compensate for a foil veil; frame/edge layers still own HDR and bloom.
  c.artMat.toneMapped = false;
  c.artMat.needsUpdate = true;
  const i = deck.indexOf(data);
  const key = artTexKey(i, 'thumb');
  let visible = c.group.visible;
  let wanted = visible;
  let currentTex = tex;
  let reload = null;
  setArtTexKeyLive('deck-picker', key, visible);
  Object.defineProperty(c.group, 'visible', {
    configurable: true, enumerable: true,
    get() { return visible; },
    set(next) {
      next = !!next;
      if (wanted === next && (visible === next || reload)) return;
      wanted = next;
      if (!wanted) {
        visible = false;
        setArtTexKeyLive('deck-picker', key, false);
        return;
      }
      const cached = touchArtTex(key);
      if (cached) {
        if (cached !== currentTex) {
          currentTex = cached;
          setCardArtTexture(c, data, cached);
        }
        visible = true;
        setArtTexKeyLive('deck-picker', key, true);
        return;
      }
      // A transient fetch failure uses the shared fallback, which is never in
      // the LRU. It is safe to show and will retry after the next visibility
      // transition; only an evicted real texture needs an asynchronous reload.
      if (currentTex === artFallbackTex) {
        visible = true;
        setArtTexKeyLive('deck-picker', key, true);
        if (!reload) {
          reload = ensureArtTex(i, 'thumb').then((nextTex) => {
            currentTex = nextTex;
            setCardArtTexture(c, data, nextTex);
          }).finally(() => { reload = null; });
        }
        return;
      }
      visible = false;
      setArtTexKeyLive('deck-picker', key, false);
      if (!reload) {
        reload = ensureArtTex(i, 'thumb').then((nextTex) => {
          currentTex = nextTex;
          setCardArtTexture(c, data, nextTex);
          if (wanted) {
            visible = true;
            setArtTexKeyLive('deck-picker', key, true);
          }
        }).finally(() => { reload = null; });
      }
    },
  });
  return c;
}

for (const di of featuredIdx) {
  const ready = artTexCache.get(artTexKey(di));
  const c = buildCard(deck[di], ready || artFallback());
  if (!ready) featuredArt.get(di).then((tex) => { if (tex !== artFallbackTex) setCardArtTexture(c, deck[di], tex); });
  c.deckIndex = di;
  fan.add(c.group);
  cards.push(c);
}

// solo (binder-opened) card objects, cached per deck index
const soloCache = new Map();
async function ensureSoloCard(di) {
  const tex = await ensureArtTex(di);
  if (!soloCache.has(di)) {
    const c = buildCard(deck[di], tex);
    c.deckIndex = di;
    soloCache.set(di, c);
  } else {
    setCardArtTexture(soloCache.get(di), deck[di], tex);
  }
  return soloCache.get(di);
}

// ---------------------------------------------------------------- living video
// P5: ONE shared <video> element + ONE VideoTexture for the whole app. The
// focused living card acquires it (lazy-created, muted loop, playsinline);
// unfocus releases it. Autoplay rejection falls back silently to the still and
// retries on the next user gesture. Clips are local re-encoded loops — the NF
// CDN sends no CORS headers, so remote video would taint the WebGL canvas.
// SC14: the singleton became a CHANNEL factory — channel A is the app-wide
// element (fan / binder solo / pack / deck / battle player card); channel B
// exists ONLY for the battle enemy card, so two living fighters can both
// animate. Budget: 2 card videos in battle, 1 elsewhere. Perf-shed drops the
// enemy channel first (battle.js never acquires B in perf mode).
function makeVideoChannel(label) {
  const ch = {
    el: null, tex: null, card: null, ready: false, fade: 0,
    framePresented: false, gain: 0.05,
    wired: false,               // P6: element routed through the whisper bus
    blocked: new Set(),         // card ids whose autoplay was rejected
    label,
  };
  ch.acquire = (c, { gain = 0.05 } = {}) => {
    if (!c || !c.data.clip || !c.videoPlane) return;
    if (ch.card === c) {
      ch.gain = gain;
      if (ch.wired && ch.el) audio.attachWhisper(ch.el, ch.gain);
      return;
    }
    if (ch.blocked.has(c.data.id)) return;
    ch.release();
    let el = ch.el;
    if (!el) {
      el = document.createElement('video');
      el.muted = true; el.loop = true; el.playsInline = true;
      el.setAttribute('playsinline', ''); el.setAttribute('muted', '');
      el.preload = 'auto';
      ch.el = el;
    }
    // SC2-B P6 (c): whisper audio — route the element through the WebAudio
    // graph at ~5% (audio.attachWhisper). Only possible after the unlock
    // gesture; until then the element stays muted and ch.update retries.
    ch.gain = gain;
    if (!ch.wired && audio.attachWhisper(el, ch.gain)) {
      ch.wired = true;
      el.muted = false;
      el.removeAttribute('muted');
    }
    ch.card = c; ch.ready = false; ch.framePresented = false; ch.fade = 0;
    el.src = clipSrcUrl(c.data.clip, V);
    const revealFirstFrame = () => {
      if (ch.card !== c) return;
      if (!ch.tex) {
        ch.tex = new THREE.VideoTexture(el);
        ch.tex.colorSpace = THREE.SRGBColorSpace;
        // Match the still plate's renderer-capped anisotropy at the same tilt.
        // The plate that fades over it used to run at the default 1, so a
        // living card got measurably softer at exactly the tilt the carousel
        // holds cards at. Same texture, same plane, same viewing angle — there
        // was never a reason for the two to differ.
        ch.tex.anisotropy = MAX_TEXTURE_ANISOTROPY;
      }
      // cover-crop the video into the art window (same rule as coverTexture)
      const t = ch.tex;
      const planeAspect = (CW * 0.94) / (CH * 0.94);
      const va = el.videoWidth / el.videoHeight;
      t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
      if (va < planeAspect) { const r = va / planeAspect; t.repeat.set(1, r); t.offset.set(0, (1 - r) / 2); }
      else { const r = planeAspect / va; t.repeat.set(r, 1); t.offset.set((1 - r) * (c.data.focalX ?? 0.5), 0); }
      c.videoPlane.material.map = t;
      c.videoPlane.material.needsUpdate = true;
      ch.framePresented = true;
      ch.ready = true;
    };
    const onPlaying = () => {
      if (ch.card !== c) return;
      // `playing` can precede the first composited video frame. On browsers
      // that expose requestVideoFrameCallback, wait for its proof that a frame
      // was actually presented. The fallback still requires decoded data and
      // one animation frame. Until then the transparent plane stays at zero
      // and the card's still art remains fully visible underneath.
      if (typeof el.requestVideoFrameCallback === 'function')
        el.requestVideoFrameCallback(() => revealFirstFrame());
      else if (el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA)
        requestAnimationFrame(revealFirstFrame);
      else el.addEventListener('loadeddata', () => requestAnimationFrame(revealFirstFrame), { once: true });
    };
    el.addEventListener('playing', onPlaying, { once: true });
    el.play().catch(() => {
      if (ch.card === c) {
        ch.blocked.add(c.data.id);   // silent fallback to the still
        ch.release();
      }
    });
  };
  ch.release = () => {
    const c = ch.card;
    if (!c) return;
    if (ch.el) {
      ch.el.pause();
      ch.el.removeAttribute('src');
      ch.el.load();
    }
    if (c.videoPlane) {
      c.videoPlane.visible = false;
      c.videoPlane.material.opacity = 0;
      c.videoPlane.material.map = null;
      c.videoPlane.material.needsUpdate = true;
    }
    ch.card = null; ch.ready = false; ch.framePresented = false; ch.fade = 0;
  };
  // PERF: a screen that fully covers the card (the opaque Collection) pauses its decoder; the plane keeps the
  // last frame, so uncovering resumes in place with no still-to-video crossfade. (Its whisper pauses with it.)
  ch.suspend = (on) => {
    if (on === !!ch.suspended) return;
    ch.suspended = on;
    if (!ch.el || !ch.card) return;
    if (on) { if (!ch.el.paused) ch.el.pause(); }
    else if (ch.el.paused) ch.el.play().catch(() => {});
  };
  // fade the acquired card's video window in once frames are flowing
  ch.update = (dt) => {
    const c = ch.card;
    if (!c || !c.videoPlane) return;
    // whisper retry: the audio context may unlock after the first acquire
    if (!ch.wired && ch.el && audio.attachWhisper(ch.el, ch.gain)) {
      ch.wired = true;
      ch.el.muted = false;
      ch.el.removeAttribute('muted');
    }
    const target = ch.ready ? 1 : 0;
    ch.fade += (target - ch.fade) * (1 - Math.exp(-5 * dt));
    c.videoPlane.material.opacity = ch.fade;
    c.videoPlane.material.color.copy(c.artMat.color);   // inherit glass/dim tint
    c.videoPlane.visible = ch.fade > 0.01;
  };
  return ch;
}
const livingVideo = makeVideoChannel('A');
const livingVideo2 = makeVideoChannel('B');   // SC14: battle enemy card only
const videoAcquire = (c, opts) => livingVideo.acquire(c, opts);
const videoRelease = () => livingVideo.release();
const videoUpdate = (dt) => livingVideo.update(dt);
// any user gesture clears autoplay blocks (the gesture unlocks playback)
addEventListener('pointerdown', () => {
  livingVideo.blocked.clear(); livingVideo2.blocked.clear();
}, { capture: true });

// ---------------------------------------------------------------- shared aura
const AURA_N = 240;
const auraGeo = new THREE.BufferGeometry();
{
  const pos = new Float32Array(AURA_N * 3);
  const attr = new Float32Array(AURA_N * 3); // angle, radius, speed
  for (let i = 0; i < AURA_N; i++) {
    attr[i * 3] = rng() * Math.PI * 2;
    attr[i * 3 + 1] = 0.75 + rng() * 0.75;
    attr[i * 3 + 2] = 0.15 + rng() * 0.5;
    pos[i * 3 + 1] = (rng() - 0.5) * CH * 1.5;
    pos[i * 3 + 2] = (rng() - 0.5) * 0.8;
  }
  auraGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  auraGeo.setAttribute('aAttr', new THREE.BufferAttribute(attr, 3));
}
const auraUniforms = {
  uTime: { value: 0 },
  uPix: { value: Math.min(devicePixelRatio, pixelCap) },
  uFade: { value: 1 },
  uFlare: { value: 0 },
  uColA: { value: new THREE.Vector3(1.0, 0.8, 0.4) },
  uColB: { value: new THREE.Vector3(0.75, 0.55, 1.0) },
  uPrism: { value: 0 },
};
const auraMat = new THREE.ShaderMaterial({
  uniforms: auraUniforms,
  transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
  vertexShader: /* glsl */`
    attribute vec3 aAttr;
    varying float vTw;
    varying float vHue;
    uniform float uTime, uPix, uFlare;
    void main() {
      float ang = aAttr.x + uTime * aAttr.z * 0.35;
      float rad = aAttr.y * (1.0 + uFlare * 0.5);
      vec3 p = position;
      p.x = cos(ang) * rad;
      p.z += sin(ang) * rad * 0.22;
      p.y += sin(uTime * aAttr.z + aAttr.x * 7.0) * 0.08;
      vTw = 0.5 + 0.5 * sin(uTime * (1.0 + aAttr.z * 2.0) + aAttr.x * 20.0);
      vHue = fract(aAttr.x * 0.31);
      vec4 mv = modelViewMatrix * vec4(p, 1.0);
      gl_PointSize = (2.5 + 5.0 * vTw * aAttr.z) * (1.0 + uFlare) * uPix * (2.2 / -mv.z);
      gl_PointSize = min(gl_PointSize, 14.0 * uPix);
      gl_Position = projectionMatrix * mv;
    }
  `,
  fragmentShader: /* glsl */`
    precision highp float;
    varying float vTw;
    varying float vHue;
    uniform float uFade, uFlare, uPrism;
    uniform vec3 uColA, uColB;
    vec3 spectral(float t) {
      return 0.5 + 0.5 * cos(6.28318 * (t + vec3(0.0, 0.33, 0.67)));
    }
    void main() {
      vec2 c = gl_PointCoord - 0.5;
      float d = smoothstep(0.5, 0.0, length(c));
      vec3 duo = mix(uColA, uColB, step(0.72, vHue));
      vec3 col = mix(duo, spectral(vHue), uPrism);
      // Aura sits below edge glow, above lit surface in the emissive hierarchy.
      gl_FragColor = vec4(col * d * (0.4 + vTw * 1.4) * (1.3 + uFlare * 1.6) * uFade, d * vTw * uFade);
    }
  `,
});
const aura = new THREE.Points(auraGeo, auraMat);
scene.add(aura);

// background dust (faint, non-blooming)
const DUST_N = 140;
const dustGeo = new THREE.BufferGeometry();
{
  const pos = new Float32Array(DUST_N * 3);
  for (let i = 0; i < DUST_N; i++) {
    pos[i * 3] = (rng() - 0.5) * 8;
    pos[i * 3 + 1] = (rng() - 0.5) * 8;
    pos[i * 3 + 2] = -1.5 - rng() * 4;
  }
  dustGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
}
const dust = new THREE.Points(dustGeo, new THREE.PointsMaterial({
  color: 0x8a80c0, size: 0.02, transparent: true, opacity: 0.35,
  depthWrite: false, blending: THREE.AdditiveBlending,
}));
scene.add(dust);

// backdrop glow plane (soft volumetric feel; hue leans toward focused rarity)
const backdropUniforms = {
  uTime: { value: 0 },
  uHalo: { value: new THREE.Vector3(0.06, 0.045, 0.15) },
};
const backdrop = new THREE.Mesh(
  new THREE.PlaneGeometry(30, 30),
  new THREE.ShaderMaterial({
    uniforms: backdropUniforms,
    depthWrite: false,
    vertexShader: `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
    fragmentShader: /* glsl */`
      precision highp float;
      varying vec2 vUv;
      uniform float uTime;
      uniform vec3 uHalo;
      void main() {
        vec2 p = vUv - 0.5;
        float d = length(p * vec2(1.15, 1.0));
        float core = smoothstep(0.55, 0.0, d);
        float breathe = 0.85 + 0.15 * sin(uTime * 0.5);
        vec3 col = vec3(0.012, 0.012, 0.034);
        col += uHalo * core * breathe;
        col += vec3(0.10, 0.065, 0.028) * smoothstep(0.35, 0.0, length(p - vec2(0.0, -0.32)));
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  })
);
backdrop.position.z = -7;
scene.add(backdrop);

// ---------------------------------------------------------------- SC2-A title
// WS1: the title is a living 3D scene (src/title.js) — depth-separated keyart
// layers under parallax, aurora ribbons + god rays, real foil cards drifting
// and flipping. DOM still owns the logo + menu text (hard rule).
const titleLayer = {
  fx: null, on: false, fade: 0,
  pointer: { x: 0, y: 0 },                 // -1..1 parallax input
};
const entryLayer = { fx: null, pending: null, onPerf: 0 };
let entryHitPlayed = false;
async function ensureEntryFX(onPerf = null) {
  if (Number.isFinite(onPerf)) entryLayer.onPerf = entryLayer.onPerf || onPerf;
  if (entryLayer.fx) return entryLayer.fx;
  if (entryLayer.pending) return entryLayer.pending;
  entryLayer.pending = Promise.resolve().then(async () => {
    const fx = createEntryFX({
      camera, renderer, seed: SEED, reduced: REDUCED, perfMode: () => perfMode,
      // read-only: the plunge ends in the living title's rift light
      titleLight: () => titleLayer.fx?.light || null,
      compileTarget: () => composer.renderTarget1,
      onLand: () => {
        const el = document.getElementById('entry');
        if (el && el.classList.contains('on')) {
          entryLanded = true;
          publishEntryReady();
          el.dispatchEvent(new CustomEvent('nomadmon-entry-land'));
        }
      },
    });
    entryLayer.fx = fx;
    scene.add(fx.group);
    // Compile the cold-path medal shaders while the opaque loader still owns
    // the screen. Otherwise a slow software/GPU compiler can consume the first
    // second after #entry.on and make that timestamp describe no real frame.
    fx.group.visible = true;
    let compileTimer = 0;
    try {
      const mode = globalThis.__nmEntryCompileMode;
      const compile = mode === 'reject' ? Promise.reject(new Error('entry compile rejection probe'))
        : mode === 'hang' ? new Promise(() => {}) : renderer.compileAsync(scene, camera);
      const timeout = new Promise((resolve) => {
        compileTimer = setTimeout(() => resolve('timeout'), 1200);
      });
      const result = await Promise.race([compile.then(() => 'ready'), timeout]);
      if (result === 'timeout') console.warn('[nomadmon-entry] shader warm-up timed out; continuing');
    } catch (error) {
      console.warn('[nomadmon-entry] shader warm-up failed; continuing', error);
    } finally {
      clearTimeout(compileTimer);
      fx.group.visible = false;
    }
    // the opening's post pass (shock refraction, heat, spectral zoom, flash)
    // sits right after the scene render, so bloom still sees its HDR flash
    if (fx.lensPass) composer.insertPass(fx.lensPass, 1);
    if (entryLayer.onPerf) fx.show(entryLayer.onPerf);
    // The metal body is immediately renderable; the crest texture and its
    // derived relief join asynchronously without delaying the <300ms reveal.
    loadTex(`./assets/web/crest.webp?${V}`).then((tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = MAX_TEXTURE_ANISOTROPY;
      fx.setCrest(tex);
    }).catch(() => {});
    return fx;
  }).finally(() => { entryLayer.pending = null; });
  return entryLayer.pending;
}
function fitEntryFX() { entryLayer.fx?.refit(camera); }
let entrancePlayed = false;                // SC2-B P3: slam once per page load
// SC5 WS2: the entrance (card scatter + wordmark slam) extracted so the title
// cinematic can hold it until the film's crossfade handoff.
// v331 FLICKER: the slam starts the logo + menu at opacity 0. When the title module lands AFTER the title is already
// on screen (a skipped dive, a slow phone, a cold go('title')), playing it then blanked the visible menu and replayed
// it in (a blink). titleSeenAt = when the title has been visibly up since (sampled per frame); late = no slam.
let titleSeenAt = 0;
(function titleSeen() {
  const st = document.getElementById('screenTitle'), veil = document.getElementById('veil');
  const up = !!st && st.classList.contains('open') && !document.body.classList.contains('entry-active') && !(veil && veil.classList.contains('on'));
  if (!up) titleSeenAt = 0; else if (!titleSeenAt) titleSeenAt = performance.now();
  requestAnimationFrame(titleSeen);
})();
function playTitleEntrance() {
  if (entrancePlayed || REDUCED || !titleLayer.on) return;
  if (!titleLayer.fx || !titleLayer.fx.built) return;
  if (titleSeenAt && performance.now() - titleSeenAt > 50) { entrancePlayed = true; return; }   // already on screen: no blink
  entrancePlayed = true;
  if (titleLayer.fx.entrance) titleLayer.fx.entrance();
  const lg = document.getElementById('titleLogo');
  lg.classList.add('slam');
  // FRONT DOOR 5: the dock tiles and caption follow the wordmark in (CSS)
  const st = document.getElementById('screenTitle');
  st.classList.add('tl-enter');
  setTimeout(() => { lg.classList.remove('slam'); st.classList.remove('tl-enter'); }, 2600);
}
async function ensureTitlePlane() {
  if (!titleLayer.fx) {
    const { createTitleFX } = await lazyImport(`./title.js?${V}`);
    titleLayer.fx = createTitleFX({
      buildCard, deck, seed: SEED, reduced: REDUCED, renderer, ver: V,
      disposeCard: disposeCardObj,
      artTexById: (id) => artTexCache.get(artTexKey(deck.findIndex(d => d.id === id))),
      // FRONT DOOR 5: the showcase is the player's own best cards
      owned: (id) => identity.ownedCount(id),
      level: (id) => identity.cardLevel(id),
      ensureArt: (d) => {
        const i = deck.indexOf(d);
        if (i < 0) return null;
        setArtTexKeyLive('title', artTexKey(i), true);
        return ensureArtTex(i);
      },
      onFeature: paintTitleFeature,
      perfMode: () => perfMode,
    });
    scene.add(titleLayer.fx.group);
  }
  // V2: the title sky is procedural (title.js prism void) — no key art fetch
  if (!titleLayer.fx.built) titleLayer.fx.build(null, camera);
  if (entryPending) {
    titleLayer.fx.uFade.value = 0;
    titleLayer.fx.group.visible = false;
  }
}
function fitTitlePlane() {
  if (titleLayer.fx) titleLayer.fx.refit(camera);
}
addEventListener('pointermove', (e) => {
  titleLayer.pointer.x = (e.clientX / innerWidth) * 2 - 1;
  titleLayer.pointer.y = (e.clientY / innerHeight) * 2 - 1;
});
addEventListener('deviceorientation', (e) => {
  if (e.gamma == null || e.beta == null) return;
  titleLayer.pointer.x = THREE.MathUtils.clamp(e.gamma / 35, -1, 1);
  titleLayer.pointer.y = THREE.MathUtils.clamp((e.beta - 45) / 45, -1, 1);
}, { passive: true });
function updateTitleLayer(t, dt) {
  // FRONT DOOR 5: the showcase stays dark under the medal's dive until its
  // reveal (entry-active lifts there); then it fades up as the medal clears
  const target = titleLayer.on && !document.body.classList.contains('entry-active') ? 1 : 0;
  titleLayer.fade += (target - titleLayer.fade) * (1 - Math.exp(-6 * dt));
  const fx = titleLayer.fx;
  if (!fx) return;
  fx.uFade.value = titleLayer.fade;
  fx.group.visible = titleLayer.fade > 0.01;
  if (fx.group.visible) fx.update(t, dt, titleLayer.pointer);
}

// ---------------------------------------------------------------- post chain
// HDR order: scene -> bloom (HDR) -> OutputPass (single ACES tone map + sRGB)
// -> optional display-referred arena LUT.
const composer = new EffectComposer(renderer);
const renderPass = new RenderPass(scene, camera);
composer.addPass(renderPass);
const bloomPass = q.get('bloom') === 'unreal'
  ? new UnrealBloomPass(new THREE.Vector2(innerWidth, innerHeight), 0.5, 0.42, 2.2)
  : new KawaseBloomPass(new THREE.Vector2(innerWidth, innerHeight), 0.5, 0.42, 2.2);
bloomPass.enabled = qualityStep === 0 && !NOPOST;
composer.addPass(bloomPass);
composer.addPass(new OutputPass());
const lutController = createLutController(q.get('lut'));
composer.addPass(lutController.pass);
let bloomTarget = 0.5;

// ---------------------------------------------------------------- camera fit
function fitCamera() {
  const aspect = innerWidth / innerHeight;
  camera.aspect = aspect;
  const fitH = (CH * 1.7) / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)));
  const fitW = (CW * 2.4) / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * aspect);
  camera.position.z = Math.max(fitH, fitW);
  camera.updateProjectionMatrix();
}
fitCamera();

// SC12 WS1: adaptive HUD tier — min viewport dimension picks phone / tablet /
// desktop; CSS scales all battle + pack-reveal chrome via --huds (1 / 1.6 / 1.9).
// Visual-only (never touches engine state or seed-driven variant selection).
function stampHudTier() {
  const m = Math.min(innerWidth, innerHeight);
  const tier = m < 560 ? 'phone' : m < 900 ? 'tablet' : 'desktop';
  if (document.body.dataset.hud !== tier) {
    document.body.dataset.hud = tier;
    document.documentElement.style.setProperty('--huds',
      tier === 'phone' ? '1' : tier === 'tablet' ? '1.6' : '1.9');
  }
  // SC17: odd-geometry tiers (visual only). geo-tall threshold sits at 2.25 so
  // the standard 390x844 phone (2.16) keeps its proven layout untouched while
  // 360x880 / 412x1000 / fold-cover 344x882 / 1080x2520-class enter the tier.
  const ar = innerWidth / innerHeight;
  document.body.classList.toggle('geo-tall', ar > 0 && 1 / ar >= 2.25);
  document.body.classList.toggle('geo-cover', innerWidth <= 360 && ar < 1);
  document.body.classList.toggle('geo-square', ar > 0.85 && ar < 1.25);
}
stampHudTier();

addEventListener('resize', () => {
  pixelCap = qualityPixelCap();
  applyQualityPixelRatio();
  renderer.setSize(innerWidth, innerHeight);
  composer.setSize(innerWidth, innerHeight);
  fitCamera();
  fitTitlePlane();
  fitEntryFX();
  stampHudTier();
});

// ---------------------------------------------------------------- DOM binding
const inspectEl = document.getElementById('inspect');
const nameplateEl = document.getElementById('nameplate');
const hintEl = document.getElementById('hint');
const npName = nameplateEl.querySelector('.name');
const npRarity = nameplateEl.querySelector('.rarity');
const npTag = document.getElementById('mythicTag');
const dotsEl = document.getElementById('dots');
const ssPanel = document.getElementById('ssPanel');

// P6 hard cap: 16 chars including spaces — nothing may ellipsize at runtime.
// SC4: alt printings carry the base name + suffix, cap 20 (render verified;
// the binder's own ellipsize assertion still polices the actual pixels).
for (const d of deck) {
  const cap = d.altOf ? 20 : 16;
  if (d.name.length > cap) console.error(`[nomadmon] NAME CAP VIOLATION: "${d.name}" (${d.name.length} > ${cap})`);
}

for (let i = 0; i < cards.length; i++) {
  const d = document.createElement('span');
  d.className = 'dot';
  dotsEl.appendChild(d);
}
const dotEls = [...dotsEl.children];

// SC18: the card back's RECORD area carries the FIRST-CLAIM engraving. It is
// prestige, not exclusivity — the card stays pullable by everyone forever, the
// registry only remembers who reached it first. Absent (renders nothing) until
// someone claims it, and for guests/offline, who never reach the registry.
function firstClaimLine(data) {
  const f = identity.firstClaims()[data.id];
  if (!f) return '';
  return `<div class="ss-first${f.mine ? ' mine' : ''}">` +
    `<span class="fc-k">First Claimed By</span><b>${f.mine ? 'YOU' : f.player}</b></div>`;
}

// P3 stat-sheet card back: dense DOM panel composited over the 3D back.
// { detail: true } (the Collection only) builds the simpler OBSIDIAN detail
// sheet into the same #ssPanel: name first, three stats, two moves, abilities
// as chips that explain themselves on tap, the story behind a disclosure.
function buildStatSheet(data, level = identity.cardLevel(data.id), { detail = false } = {}) {
  // Battle passes the level already carried by its deterministic state. That
  // matters for the remote opponent: competitive MP is normalized to level 1,
  // and consulting this device's collection for somebody else's card would
  // display the wrong stats. Collection/deck/pack callers omit the override
  // and keep the identity-backed behaviour they have always had.
  data = levelCard(data, level);
  const ty = TYPES[data.type];
  const rar = RARITY[data.rarity];
  const [basic, spec] = data.moves;
  const pips = (n, of = 3) => `<span class="pips">` +
    Array.from({ length: of }, (_, i) => `<i class="${i < n ? 'on' : ''}"></i>`).join('') + `</span>`;
  const kws = (data.keywords || []).map(k =>
    `<div class="kw"><b>${k}</b> — ${KEYWORDS[k]}</div>`).join('');
  // SC33: LEGENDARY, MYTHIC, ALT and ETERNAL cards each carry ONE signature
  // passive. It is always on, it is not a keyword, and it is the reason two
  // cards with the same stat line no longer play the same — so it gets its own
  // block on the back, above the shared keyword reminders.
  const pas = passiveFor(data);
  const passLine = pas
    ? `<div class="passline"><b>${pas.name}</b>${pas.text}</div>`
    : '';
  ssPanel.classList.toggle('haspass', !!pas);
  ssPanel.classList.toggle('detail', detail);
  ssPanel.style.setProperty('--tc', ty.color);
  ssPanel.style.setProperty('--rc', rar.color);
  if (detail) { ssPanel.innerHTML = detailSheetHTML(data, { ty, rar, basic, spec, pas, pips }); return; }
  // P5: long move names wrap (never ellipsize) and auto-shrink past 24 chars;
  // the Ultimate chip owns its own row so it can never collide with the name.
  const longCls = (n) => (n.length > 24 ? ' long' : '');
  const variantMark = data.variant === 'living'
    ? '<span class="vtag living">Living</span>'
    : data.variant === 'diarrhea' ? sigilSVG(15) : '';
  ssPanel.innerHTML = `
    <div class="ss-head">
      <div class="ss-name">${data.name}</div>
      <div class="ss-level">LVL ${data.level}</div>
      <div class="ss-type">${typeGlyph(data.type, 15)}${ty.label}</div>
      ${variantMark}
      <div class="ss-gem" title="${rar.label}"></div>
    </div>
    <div class="ss-stats">
      <div class="ss-hp">
        <div class="lab"><span>HP</span><b>${data.hp}</b></div>
        <div class="bar"><i style="width:${Math.min(100, (data.hp / 120) * 100)}%"></i></div>
      </div>
      <div class="ss-mini"><div class="v">${data.spd}</div><div class="k">Classic SPD</div></div>
      <div class="ss-mini"><div class="v">${pips(data.swapCost)}</div><div class="k">Classic swap</div></div>
    </div>
    <div class="ss-moves">
      <div class="mv">
        <div class="mrow"><span class="mname${longCls(basic.name)}">${basic.name}</span><span class="mdmg">${basic.dmg}</span></div>
        ${basic.desc ? `<div class="mdesc">${basic.desc}</div>` : ''}
      </div>
      <div class="mv spec${spec.ultimate ? ' ult' : ''}">
        <div class="mrow"><span class="mname${longCls(spec.name)}">${spec.name}</span><span class="mdmg">${spec.dmg}</span></div>
        ${spec.ultimate ? '<div class="mtagrow"><span class="mtag">Ultimate · 1× per battle</span></div>' : ''}
        <div class="meff">${spec.effect}</div>
        <div class="mcharge">Charge ${pips(spec.chargeRounds)}</div>
      </div>
    </div>
    ${passLine}
    ${kws ? `<div class="ss-kws">${kws}</div>` : ''}
    ${data.dex ? `<div class="ss-dex"><div class="dxh">Nomad-dex</div><div class="dxt">${data.dex}</div></div>` : ''}
    ${data.data ? `<div class="ss-data"><b>Data</b>${data.data.join(' · ')}</div>` : ''}
    <div class="ss-wr">
      <span>Weak</span><span class="wrv" style="color:${TYPES[ty.weakTo].color}">${typeGlyph(ty.weakTo, 13)}${TYPES[ty.weakTo].label}</span><span class="x2">×2</span>
      <span style="margin-left:auto">Resist</span><span class="wrv" style="color:${TYPES[ty.resists].color}">${typeGlyph(ty.resists, 13)}${TYPES[ty.resists].label}</span><span class="m2">−2</span>
    </div>
    <div class="ss-foot">
      <div class="ss-flav">“${data.flavor}”</div>
      <div class="ss-meta"><span>${data.set} · <b>${data.serial}</b></span><span>Record ${data.record || '0–0'}</span><span>ART: NOMAD FACES</span></div>
      ${firstClaimLine(data)}
    </div>`;
}

const esc = (t) => String(t ?? '').replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch]);
function detailSheetHTML(data, { ty, rar, basic, spec, pas, pips }) {
  const owned = identity.ownedCount(data.id) > 0;
  const weak = TYPES[ty.weakTo], res = TYPES[ty.resists];
  const chip = (cls, label, explain, color = '') =>
    `<button type="button" class="dt-chip ${cls}" aria-pressed="false" data-x="${esc(explain)}"` +
    `${color ? ` style="--cc:${color}"` : ''}>${label}</button>`;
  const chips = [
    pas ? chip('pass', esc(displayName(pas.name)), `${displayName(pas.name)}: ${pas.text}`) : '',
    ...(data.keywords || []).map(k => chip('kw', esc(displayName(k)), `${displayName(k)}: ${KEYWORDS[k]}`)),
    chip('wr', `${typeGlyph(ty.weakTo, 12)}Weak ×2`, `Takes double damage from ${weak.label} moves.`, weak.color),
    chip('wr', `${typeGlyph(ty.resists, 12)}Resist −2`, `Takes 2 less damage from ${res.label} moves.`, res.color),
  ].join('');
  const variant = data.variant === 'living' ? ' · Living' : data.variant === 'diarrhea' ? ' · Diarrhea Holo' : '';
  const f = identity.firstClaims()[data.id];
  const story = [
    data.dex ? `<p>${esc(data.dex)}</p>` : '',
    data.flavor ? `<p class="dt-flav">“${esc(data.flavor)}”</p>` : '',
    data.data ? `<p class="dt-data">${data.data.map(t => esc(displayName(t))).join(' · ')}</p>` : '',
  ].join('');
  return `
    <div class="dt-head">
      <img class="dt-thumb" src="${artSrc(data, V, 'thumb')}" alt="" style="object-position:${thumbPos(data)}">
      <div class="dt-id">
        <div class="ss-name">${esc(displayName(data.name))}</div>
        <div class="dt-sub"><span class="dt-rar">${data.altOf ? 'Mythic-Alt' : rar.label}</span>
          <span class="dt-type">${typeGlyph(data.type, 13)}${ty.label}</span><span>Lvl ${data.level}${variant}</span></div>
        ${owned ? '' : '<span class="dt-own">Not owned</span>'}
      </div>
    </div>
    <div class="dt-stats">
      <div><b>${data.hp}</b><span>HP</span></div>
      <div><b>${data.spd}</b><span>Speed</span></div>
      <div><b>${pips(data.swapCost)}</b><span>Swap cost</span></div>
    </div>
    <div class="dt-moves">
      <div class="dt-mv"><div class="dt-mrow"><span class="dt-mname">${esc(basic.name)}</span><span class="dt-dmg">${basic.dmg}</span></div>
        ${basic.desc ? `<div class="dt-mtext">${esc(basic.desc)}</div>` : ''}</div>
      <div class="dt-mv${spec.ultimate ? ' ult' : ''}"><div class="dt-mrow"><span class="dt-mname">${esc(spec.name)}</span>${spec.ultimate ? '<span class="dt-ult">Ultimate</span>' : ''}<span class="dt-dmg">${spec.dmg}</span></div>
        <div class="dt-mtext">${esc(spec.effect)}</div>
        <div class="dt-charge">Ready after ${spec.chargeRounds} round${spec.chargeRounds === 1 ? '' : 's'}${spec.ultimate ? ' · once per battle' : ''}</div></div>
    </div>
    <div class="dt-chips">${chips}</div>
    <div class="dt-explain" aria-live="polite"></div>
    ${story ? `<details class="dt-story"><summary>Story</summary>${story}</details>` : ''}
    <div class="dt-foot">${esc(displayName(data.set))} · ${esc(data.serial)} · Record ${data.record || '0–0'}${f ? ` · First claimed by <b>${f.mine ? 'you' : esc(f.player)}</b>` : ''}</div>`;
}
// ability chips explain themselves in one quiet line under the row; tapping
// the same chip again (or another) swaps or clears it
ssPanel.addEventListener('click', (e) => {
  const b = e.target.closest('.dt-chip');
  if (!b) return;
  const on = b.getAttribute('aria-pressed') !== 'true';
  for (const o of ssPanel.querySelectorAll('.dt-chip[aria-pressed="true"]')) o.setAttribute('aria-pressed', 'false');
  b.setAttribute('aria-pressed', on ? 'true' : 'false');
  const ex = ssPanel.querySelector('.dt-explain');
  if (ex) { ex.textContent = on ? b.dataset.x : ''; ex.classList.toggle('on', on); }
});

function bindCardDOM(c) {
  const { data, rar } = c;
  const ty = TYPES[data.type];
  npName.textContent = displayName(data.name);
  npRarity.textContent = `${rar.label} · ${ty.label}`;
  npRarity.style.color = rar.color;
  // SC2-B P6: PWR chip in the card-front DOM chip row (one formula, types.js)
  const pc = document.getElementById('pwrChip');
  const level = identity.cardLevel(data.id);
  pc.innerHTML = `Lvl ${level} · PWR <b>${effectivePwr(data, level)}</b>`;
  pc.style.color = rar.color;
  pc.style.borderColor = rar.color;
  guide.bindPwrBadge(pc, levelCard(data, level), effectivePwr(data, level));
  npTag.style.display = data.tag ? 'inline-block' : 'none';
  if (data.tag) npTag.textContent = data.tag;
  // P5 variant chip under the nameplate + ACTIVATE affordances
  const vt = document.getElementById('variantTag');
  if (data.variant === 'living') {
    vt.style.display = 'inline-flex'; vt.className = 'vtag living'; vt.textContent = 'Living';
  } else if (data.variant === 'diarrhea') {
    vt.style.display = 'inline-flex'; vt.className = 'vtag diarrhea';
    vt.innerHTML = `${sigilSVG(11)} Diarrhea Holo`;
  } else vt.style.display = 'none';
  // Diarrhea video stays on the living card. The duplicate full-screen lab
  // action is intentionally absent; it was the empty-window path.
  activateBtn.classList.remove('show');
  dotEls.forEach((d, i) => d.classList.toggle('on', i === cards.indexOf(c)));
  buildStatSheet(data);
}

// ---------------------------------------------------------------- focus / reveal
let focusIndex = THREE.MathUtils.clamp(cardsData.startIndex ?? 0, 0, cards.length - 1);
let focusPos = focusIndex;                 // smoothed
let revealT = 1.0;                         // normalized reveal envelope, 1 = settled
let inspecting = false;
let inspectT = 0;

function setFocus(i, { reveal = true } = {}) {
  const ni = THREE.MathUtils.clamp(i, 0, cards.length - 1);
  if (ni === focusIndex && reveal) return;
  focusIndex = ni;
  const c = cards[focusIndex];
  if (reveal) revealT = 0;
  // new card arrives upright with zoom reset
  manip.rx = 0; manip.ry = 0; manip.vx = 0; manip.vy = 0;
  manip.tx = 0; manip.ty = 0; manip.mode = 'settle'; manip.zeta = 1.0;
  manip.zoom = 1;
  c.foilMat.uniforms.uSweep.value = 0;
  bindCardDOM(c);
  // aura recolor + flare toward the focused rarity
  auraUniforms.uColA.value.set(...c.rar.trimA);
  auraUniforms.uColB.value.set(...c.rar.trimB);
  auraUniforms.uPrism.value = c.rar.prismatic >= 1 ? 1 : 0;
  backdropUniforms.uHalo.value.set(
    0.010 + c.rar.trimA[0] * 0.055, 0.010 + c.rar.trimA[1] * 0.045, 0.030 + c.rar.trimA[2] * 0.065);
  if (inspecting) setInspect(false);
}

// P6: inspect = the zoomed card itself + a slim FLIP/CLOSE strip at the screen
// edge. No duplicated info panel — the card front keeps its on-card DOM chips
// and the flipped back is the stat sheet.
function setInspect(on) {
  inspecting = on;
  inspectEl.classList.toggle('open', on);
  document.body.classList.toggle('inspecting', on);
  nameplateEl.style.opacity = on ? '0' : '1';
  if (on) paintFeedControl();
  else {
    feedQuoteSeq++;
    document.getElementById('spareSheet').classList.remove('open');
    document.getElementById('spareSheet').setAttribute('aria-hidden', 'true');
    document.getElementById('spareBtn').hidden = true;
    const feedBtn = document.getElementById('feedBtn');
    feedBtn.hidden = true;
    feedBtn.disabled = false;
    const tradeBtn = document.getElementById('tradeBtn');
    tradeBtn.hidden = true;
    tradeBtn.disabled = false;
  }
}
document.getElementById('closeBtn').addEventListener('click', () => setInspect(false));
document.getElementById('flipBtn').addEventListener('click', () => manipFlip());

// ---------------------------------------------------------------- P5 opt-in living-card WATCH overlay
// Living loops can be expanded by an explicit WATCH tap. Diarrhea cards route
// their WATCH FULL TAPE action to labVideo.url; music ducks only after a frame
// is actually ready, so the old empty-overlay regression cannot return.
const activateBtn = document.getElementById('activateBtn');
const labOverlay = document.getElementById('labOverlay');
const labVideoEl = document.getElementById('labVideoEl');
const labCap = document.getElementById('labCap');
audio.bindOutput(labVideoEl);           // a11y: master mute covers the WATCH overlay's sound
let labOpen = false;
let labShown = false;
let labToken = 0;
function openLab(dOverride = null, kind = 'lab') {
  const d = dOverride || (soloCard || cards[focusIndex]).data;
  const src = kind === 'clip'
    ? clipSrcUrl(d.clip, V)
    : (d.labVideo && d.labVideo.url) || null;
  if (!src || labOpen) return;
  labOpen = true;
  labShown = false;
  const token = ++labToken;
  labCap.innerHTML = kind === 'clip'
    ? `&#9654; Living — ${d.name}`
    : `${sigilSVG(15)} Diarrhea Holo — ${d.name}`;
  labVideoEl.loop = false;
  labVideoEl.src = src;
  audio.setOutputMuted(labVideoEl, false);   // wants sound; master mute still wins
  labVideoEl.volume = 0;                // no off-screen audio while media warms
  // A retained WATCH overlay also obeys the no-empty-media rule: the current
  // card remains visible until a decoded frame has actually been presented.
  const showReadyVideo = () => {
    if (!labOpen || labToken !== token || labShown) return;
    labShown = true;
    videoRelease();                     // never two visible videos
    labVideoEl.volume = 1;
    labOverlay.classList.add('show');
    audio.duckMusic(0.08, 0.4, 'watch');
  };
  const onPlaying = () => {
    if (!labOpen || labToken !== token) return;
    if (typeof labVideoEl.requestVideoFrameCallback === 'function')
      labVideoEl.requestVideoFrameCallback(showReadyVideo);
    else if (labVideoEl.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA)
      requestAnimationFrame(showReadyVideo);
    else labVideoEl.addEventListener('loadeddata', () => requestAnimationFrame(showReadyVideo), { once: true });
  };
  labVideoEl.addEventListener('playing', onPlaying, { once: true });
  labVideoEl.play().catch(closeLab);
}
function closeLab() {
  if (!labOpen) return;
  const wasShown = labShown;
  labOpen = false;
  labShown = false;
  labToken++;
  labVideoEl.pause();
  labVideoEl.removeAttribute('src');
  labVideoEl.load();
  labOverlay.classList.remove('show');
  if (wasShown) audio.unduckMusic(0.6, 'watch');
}
activateBtn.addEventListener('click', () => openLab());
labOverlay.addEventListener('click', closeLab);
labVideoEl.addEventListener('ended', closeLab);   // SC7: full plays dismiss themselves
labVideoEl.addEventListener('error', closeLab);
document.getElementById('prevBtn').addEventListener('click', () => setFocus(focusIndex - 1));
document.getElementById('nextBtn').addEventListener('click', () => setFocus(focusIndex + 1));
// Fan keys belong to the fan alone: Enter on a focused button or in a text
// field (the MP code box, the exit confirmation) used to flip the inspect strip
// on any screen, and Escape closed the binder, its filters and its card sheet
// in one go. Escape is now nav.js's — one press, one layer.
addEventListener('keydown', (e) => {
  try {   // (a key during boot can arrive before shellUI exists)
    if (shellUI.currentScreen() !== 'fan' || soloCard || battleActive || packActive
      || (nav.depth() > 1 && !inspecting)) return;
  } catch { return; }
  if (e.target && e.target.closest && e.target.closest('button, input, select, textarea, [contenteditable], [role="button"]')) return;
  if (e.key === 'ArrowLeft') setFocus(focusIndex - 1);
  else if (e.key === 'ArrowRight') setFocus(focusIndex + 1);
  else if (e.key === 'ArrowUp' || e.key === 'Enter') setInspect(!inspecting);
});

// ---------------------------------------------------------------- interaction
// P2 free-grab manipulation: drag rotates the active card on both axes with
// tracked angular velocity; release keeps the spin (inertia, exponential decay)
// and, once slow, a critically-damped spring settles to the nearest face
// (front OR back — a hard throw carries through full rotations and lands
// wherever momentum dies). Flip = spring with reduced damping (overshoot).
// All springs are frame-rate independent (semi-implicit Euler, dt clamped).
const manip = {
  rx: 0, ry: 0,          // current grab rotation (radians)
  vx: 0, vy: 0,          // angular velocity (rad/s)
  tx: 0, ty: 0,          // spring settle targets
  mode: 'settle',        // 'held' | 'free' | 'settle'
  zeta: 1.0,             // settle damping ratio (0.55 for flip overshoot)
  held: false, grab: 0,  // grab envelope 0..1 (scale + shadow feedback)
  zoom: 1, zoomNow: 1,   // pinch/wheel zoom, clamped
};
const MANIP_K = 64;                       // settle spring stiffness
const gyroTilt = { x: 0, y: 0 };          // gyro folds in additively when idle

function nearestFaceY(y) { return Math.round(y / Math.PI) * Math.PI; }
function manipFlip() {
  audio.ui('cardflip');                   // polC: the one card-flip sound (the focus view's flip uses it too)
  const base = manip.mode === 'settle' ? manip.ty : nearestFaceY(manip.ry);
  manip.ty = base + Math.PI;
  manip.tx = 0;
  manip.mode = 'settle';
  manip.zeta = 0.55;                      // underdamped -> visible overshoot
  manip.vy += 3.0;                        // kick into the flip
}
function updateManip(dt) {
  manip.grab += ((manip.held ? 1 : 0) - manip.grab) * (1 - Math.exp(-14 * dt));
  manip.zoomNow += (manip.zoom - manip.zoomNow) * (1 - Math.exp(-10 * dt));
  if (manip.mode === 'free') {
    manip.rx += manip.vx * dt;
    manip.ry += manip.vy * dt;
    const decay = Math.exp(-1.5 * dt);
    manip.vx *= decay; manip.vy *= decay;
    if (Math.hypot(manip.vx, manip.vy) < 1.5) {
      manip.mode = 'settle';
      manip.zeta = 1.0;                   // critically damped
      manip.ty = nearestFaceY(manip.ry);
      manip.tx = 0;
    }
  } else if (manip.mode === 'settle') {
    const c = 2 * manip.zeta * Math.sqrt(MANIP_K);
    manip.vx += (-MANIP_K * (manip.rx - manip.tx) - c * manip.vx) * dt;
    manip.vy += (-MANIP_K * (manip.ry - manip.ty) - c * manip.vy) * dt;
    manip.rx += manip.vx * dt;
    manip.ry += manip.vy * dt;
  }
}

// ---------------------------------------------------------------- SC20 gestures
// THE BUG this replaces: MY DECK had no carousel gesture at all — the fan was
// driven only by #prevBtn/#nextBtn and the arrow keys. Every pointer that
// landed anywhere on the full-screen canvas was fed straight into the card
// manipulator with NO hit test, so a horizontal swipe spun the card instead of
// scrolling, and any short contact (on the card OR on empty space) passed the
// `dragMoved < 12 && dt < 350` tap test — two of those inside 300ms flipped the
// active card. Hence "anywhere you touch the screen it just flips the card".
//
// The replacement is one small state machine with an AXIS LOCK. The first
// GEST.DECIDE px of travel choose the gesture, and it is locked for the rest of
// that pointer's life:
//   x axis  -> 'scroll'  CAROUSEL. Primary gesture. Wins ANYWHERE, including
//                        on top of the card. Momentum + snap on release.
//   y axis  -> 'card'    tilt/spin, but ONLY if the press started ON the card.
//   y axis off-card -> 'dead'  (nothing; the carousel never steals it either)
//   no travel -> 'pending' at release = TAP. On-card single = inspect,
//                        on-card double = flip, empty space = NOTHING.
// A gesture that ever left 'pending' can never end as a tap, so flings and
// momentum releases can never flip the card.
const GEST = {
  DECIDE: 10,     // px of travel before the axis locks
  BIAS: 1.15,     // |dx| must beat |dy| by this factor to claim the x axis
  TAP_PX: 10,     // total travel still counted as a tap
  TAP_MS: 250,    // ...and it must be at least this quick
  DBL_MS: 300,    // a second tap inside this window = flip
};
// HOW LONG THE FINGER WAS DOWN IS AN INPUT-CLOCK FACT, NOT A MAIN-THREAD ONE.
// GEST.TAP_MS used to be measured with performance.now() inside the handlers,
// which measures when JS got AROUND to running them. Under a heavy frame those
// two diverge by hundreds of milliseconds: a real 80ms tap was delivered to the
// page as down@27205 / up@27478 and classified 'release:slow-press -> nothing',
// so a tap on a busy frame silently did nothing. Pointer events carry a
// timeStamp stamped when the input actually happened, on the same timeline as
// performance.now(), so the duration below is the finger's, not the renderer's.
const evT = (e) => (typeof e.timeStamp === 'number' && e.timeStamp > 0 ? e.timeStamp : performance.now());
// ?gestlog=1 prints every classification decision (QA evidence for the machine)
const GESTLOG = /[?&]gestlog=1/.test(location.search);
function gestLog(what, extra) {
  if (!GESTLOG) return;
  const line = `[gest] ${what}${extra ? ' ' + extra : ''}`;
  (window.__gest = window.__gest || []).push(line);
  console.log(line);
}

// pointer state (supports one gesture pointer + pinch with two)
const pointers = new Map();               // id -> {x, y}
let lastTapAt = 0, tapTimer = 0;
let pinchDist = 0;
const gest = { id: -1, phase: 'idle', onCard: false, downX: 0, downY: 0, downT: 0 };
// continuous carousel position; drives focusPos directly while live
const fanScroll = { active: false, glide: false, pos: 0, vel: 0, lastT: 0 };
const FAN_MIN = -0.35;                                  // rubber ends
const fanMax = () => cards.length - 1 + 0.35;
const perCard = () => Math.min(innerWidth * 0.42, 260); // px of drag per card

// hit test against the focused card only — that is the one the tap rules and
// the vertical-spin rule are about
const gRay = new THREE.Raycaster();
const gNdc = new THREE.Vector2();
function hitFocusedCard(x, y) {
  const c = soloCard || cards[focusIndex];
  if (!c) return false;
  gNdc.set((x / innerWidth) * 2 - 1, -(y / innerHeight) * 2 + 1);
  gRay.setFromCamera(gNdc, camera);
  return gRay.intersectObject(c.group, true).length > 0;
}
// the carousel only exists in the fan (a binder-opened solo card has no
// neighbours to scroll to, and battle owns its own taps)
const carouselLive = () => !soloCard && !battleActive && cards.length > 1;

function onGestureDown(e) {
  if (packActive || deckActive) return;                 // pack mode owns its own pointer state
  pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (pointers.size === 1) {
    gest.id = e.pointerId;
    gest.phase = 'pending';
    gest.onCard = hitFocusedCard(e.clientX, e.clientY);
    gest.downX = e.clientX; gest.downY = e.clientY;
    gest.downT = evT(e);
    manip.vx = 0; manip.vy = 0;           // a touch stops any residual spin
    fanScroll.vel = 0; fanScroll.glide = false;  // ...and any carousel drift
    gestLog('down', `onCard=${gest.onCard}`);
  } else if (pointers.size === 2) {
    const [a, b] = [...pointers.values()];
    pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
    gest.phase = 'pinch';                 // two fingers = zoom, never a tap
    manip.held = false;
    fanScroll.active = false;
    gestLog('pinch');
  }
}
renderer.domElement.addEventListener('pointerdown', onGestureDown);
// SC20: the two carousel arrows sit right where a thumb rests, and they used
// to swallow any swipe that began on them (the gesture simply died). They stay
// tappable — a tap still fires their click — but a DRAG that starts on one now
// falls through to the carousel like every other pixel of the screen.
for (const id of ['prevBtn', 'nextBtn']) {
  const b = document.getElementById(id);
  if (b) b.addEventListener('pointerdown', onGestureDown);
}
addEventListener('pointermove', (e) => {
  if (packActive || deckActive) return;
  const p = pointers.get(e.pointerId);
  if (!p) return;
  const dx = e.clientX - p.x, dy = e.clientY - p.y;
  p.x = e.clientX; p.y = e.clientY;
  if (pointers.size === 2) {
    const [a, b] = [...pointers.values()];
    const d = Math.hypot(a.x - b.x, a.y - b.y);
    if (pinchDist > 0) manip.zoom = THREE.MathUtils.clamp(manip.zoom * (d / pinchDist), 1, 2.4);
    pinchDist = d;
    return;
  }
  if (e.pointerId !== gest.id) return;
  const now = performance.now();

  // --- axis lock: decided once, on the first GEST.DECIDE px of travel
  if (gest.phase === 'pending') {
    const tdx = e.clientX - gest.downX, tdy = e.clientY - gest.downY;
    if (Math.hypot(tdx, tdy) < GEST.DECIDE) return;
    if (Math.abs(tdx) > Math.abs(tdy) * GEST.BIAS && carouselLive()) {
      gest.phase = 'scroll';
      fanScroll.active = true; fanScroll.glide = false;
      fanScroll.pos = focusPos; fanScroll.vel = 0; fanScroll.lastT = now;
      manip.held = false;
      clearTimeout(tapTimer); lastTapAt = 0;   // a drag cancels a pending tap
      gestLog('lock:x -> scroll', `d=(${tdx.toFixed(0)},${tdy.toFixed(0)})`);
    } else if (gest.onCard) {
      gest.phase = 'card';
      manip.held = true; manip.mode = 'held';
      manip._lastMoveT = now;
      clearTimeout(tapTimer); lastTapAt = 0;
      gestLog('lock:y -> card', `d=(${tdx.toFixed(0)},${tdy.toFixed(0)})`);
    } else {
      gest.phase = 'dead';                     // off-card vertical drag: ignored
      gestLog('lock:y offcard -> dead');
    }
  }

  if (gest.phase === 'card') {
    const mdt = Math.max((now - (manip._lastMoveT || now - 16)) / 1000, 0.004);
    manip._lastMoveT = now;
    const K = 0.0085;
    manip.ry += dx * K;
    manip.rx = THREE.MathUtils.clamp(manip.rx + dy * K, -1.4, 1.4);
    // smoothed velocity estimate for release inertia
    manip.vy = manip.vy * 0.65 + (dx * K / mdt) * 0.35;
    manip.vx = manip.vx * 0.65 + (dy * K / mdt) * 0.35;
  } else if (gest.phase === 'scroll') {
    const mdt = Math.max((now - fanScroll.lastT) / 1000, 0.004);
    fanScroll.lastT = now;
    const pc = perCard();
    fanScroll.pos = THREE.MathUtils.clamp(fanScroll.pos - dx / pc, FAN_MIN, fanMax());
    fanScroll.vel = fanScroll.vel * 0.6 + (-dx / pc / mdt) * 0.4;
    fanSyncFocus();
  }
});
// keep the DOM nameplate / stat sheet / aura on the card actually under the
// carousel while it moves (DOM-only reading text rule still holds)
function fanSyncFocus() {
  const ni = THREE.MathUtils.clamp(Math.round(fanScroll.pos), 0, cards.length - 1);
  if (ni !== focusIndex) {
    setFocus(ni, { reveal: false });
    audio.play('tick', { gain: 0.22 });
  }
}
function endPointer(e) {
  if (packActive || deckActive) { pointers.delete(e.pointerId); return; }
  if (!pointers.has(e.pointerId)) return;
  pointers.delete(e.pointerId);
  if (pointers.size > 0) { pinchDist = 0; return; }
  pinchDist = 0;
  if (e.pointerId !== gest.id) { gest.phase = 'idle'; return; }
  const phase = gest.phase;
  gest.phase = 'idle'; gest.id = -1;

  if (phase === 'scroll') {
    // hand the carousel to momentum; this path can NEVER become a tap/flip
    fanScroll.active = false;
    fanScroll.glide = true;
    fanScroll.vel = THREE.MathUtils.clamp(fanScroll.vel, -16, 16);
    gestLog('release:fling', `vel=${fanScroll.vel.toFixed(2)}`);
    return;
  }
  if (phase === 'card') {
    manip.held = false;
    // throw: cap runaway velocity, then free spin
    const CAPX = 9, CAPY = 26;
    manip.vx = THREE.MathUtils.clamp(manip.vx, -CAPX, CAPX);
    manip.vy = THREE.MathUtils.clamp(manip.vy, -CAPY, CAPY);
    manip.mode = 'free';
    gestLog('release:throw');
    return;
  }
  if (phase === 'pending') {
    manip.held = false;
    if (battleActive) return;             // battle owns taps (beat advance)
    const dtms = evT(e) - gest.downT;
    const moved = Math.hypot(e.clientX - gest.downX, e.clientY - gest.downY);
    if (moved >= GEST.TAP_PX || dtms >= GEST.TAP_MS) {
      gestLog('release:slow-press -> nothing', `moved=${moved.toFixed(1)} dt=${dtms.toFixed(0)}`);
      return;
    }
    if (!gest.onCard) {                   // empty space never flips anything
      gestLog('tap:empty -> nothing');
      return;
    }
    manip.mode = 'settle'; manip.zeta = 1.0;
    manip.ty = nearestFaceY(manip.ry); manip.tx = 0;
    manip.vx = 0; manip.vy = 0;
    const now = performance.now();
    if (now - lastTapAt < GEST.DBL_MS) {  // double-tap on the card -> flip
      clearTimeout(tapTimer);
      lastTapAt = 0;
      gestLog('dbltap:card -> flip');
      manipFlip();
    } else {
      lastTapAt = now;
      tapTimer = setTimeout(() => {
        gestLog('tap:card -> inspect');
        setInspect(!inspecting);
      }, GEST.DBL_MS);
    }
    return;
  }
  // 'dead' / 'pinch' / 'idle': nothing to settle beyond releasing the grab
  manip.held = false;
  gestLog(`release:${phase} -> nothing`);
}
addEventListener('pointerup', endPointer);
addEventListener('pointercancel', endPointer);

// wheel zoom (desktop)
addEventListener('wheel', (e) => {
  if (packActive || deckActive) return;
  manip.zoom = THREE.MathUtils.clamp(manip.zoom * Math.exp(-e.deltaY * 0.0012), 1, 2.4);
}, { passive: true });

// zoom-driven inspect: overlay fades in past the threshold, out below it
let autoInspect = false;
function updateZoomInspect() {
  if (manip.zoomNow > 1.65 && !inspecting) { autoInspect = true; setInspect(true); }
  else if (manip.zoomNow < 1.45 && inspecting && autoInspect) { autoInspect = false; setInspect(false); }
}

// Gyroscope: the first valid source owns the session. Some Android devices
// deliver orientation and absolute-orientation on alternating baselines; using
// both produces a 60 Hz depth jitter. Motion is only installed as a 2.5 s
// fallback, and every event is low-pass filtered at the JUICY-proven 0.12.
let gyroActive = false;
let gyroBase = null;
const gyroRaw = { beta: 0, gamma: 0 };     // the latest unbaselined angles (the Duo lens reads them)
let gyroSource = null;
let gyroFallbackTimer = 0;
const orient = (e) => acceptGyro(e.beta, e.gamma, 'orientation');
const orientAbsolute = (e) => acceptGyro(e.beta, e.gamma, 'orientationabsolute');
const motion = (e) => {
  const g = e.accelerationIncludingGravity;
  if (!g || !Number.isFinite(g.x) || !Number.isFinite(g.y) || !Number.isFinite(g.z)) return;
  const deg = 180 / Math.PI;
  acceptGyro(Math.atan2(-g.y, g.z) * deg, Math.atan2(g.x, g.z) * deg, 'motion');
};
function keepOnlyGyroSource(source) {
  if (source !== 'orientation') removeEventListener('deviceorientation', orient);
  if (source !== 'orientationabsolute') removeEventListener('deviceorientationabsolute', orientAbsolute);
  if (source !== 'motion') removeEventListener('devicemotion', motion);
  clearTimeout(gyroFallbackTimer);
}
function acceptGyro(beta, gamma, source) {
  if (!Number.isFinite(beta) || !Number.isFinite(gamma)) return;
  if (gyroSource && gyroSource !== source) return;
  if (!gyroSource) { gyroSource = source; keepOnlyGyroSource(source); }
  gyroRaw.beta = beta; gyroRaw.gamma = gamma;
  // squad fix (Jack: "parallax tilt barely works if at all"): (1) read the tilt in SCREEN axes -- raw beta/gamma
  // swap roles in landscape, so a tablet's side-to-side tilt landed on the wrong axis; (2) the baseline drifts
  // toward the held angle (~3 s) -- it was captured ONCE at the first event, so holding the device differently
  // from boot pinned the tilt at its clamp and small motions did nothing. Presentation only.
  const { side, fwd } = screenTiltDeg();
  const now = performance.now();
  if (!gyroBase || gyroBase.angle !== screenAngle()) gyroBase = { side, fwd, t: now, angle: screenAngle() };   // re-base on rotate
  const dS = ((side - gyroBase.side + 540) % 360) - 180, dF = ((fwd - gyroBase.fwd + 540) % 360) - 180;
  const kb = 1 - Math.exp(-Math.min(0.25, (now - gyroBase.t) / 1000) / 3);   // ~3 s, whatever the event rate
  gyroBase.t = now; gyroBase.side += dS * kb; gyroBase.fwd += dF * kb;
  if (!gyroActive) gyroBtn.classList.remove('show');   // the sensor talks: no permission ask needed (Chrome)
  gyroActive = true;
  const x = THREE.MathUtils.clamp(dF * 0.014, -0.5, 0.5);
  const y = THREE.MathUtils.clamp(dS * 0.016, -0.55, 0.55);
  gyroTilt.x += (x - gyroTilt.x) * 0.12;
  gyroTilt.y += (y - gyroTilt.y) * 0.12;
}
function screenAngle() {
  const a = screen.orientation && Number.isFinite(screen.orientation.angle) ? screen.orientation.angle : Number(window.orientation) || 0;
  return ((Math.round(a / 90) * 90) % 360 + 360) % 360;
}
// the screen's tilt in degrees from world-up in SCREEN axes: side = the steering-wheel turn (well conditioned even
// for a tablet held near upright, unlike atan2(-upX, uz)), fwd = the pitch toward / away. Portrait at rest ~ gamma / beta.
function screenTiltDeg() {
  const r = Math.PI / 180, b = gyroRaw.beta * r, g = gyroRaw.gamma * r;
  const ux = -Math.sin(g) * Math.cos(b), uy = Math.sin(b), uz = Math.cos(g) * Math.cos(b);
  const a = screenAngle();
  const upX = a === 90 ? -uy : a === 180 ? -ux : a === 270 ? uy : ux;     // world-up . screen-x
  const upY = a === 90 ? ux : a === 180 ? -uy : a === 270 ? -ux : uy;     // world-up . screen-y
  return { side: Math.atan2(-upX, Math.hypot(upY, uz)) / r, fwd: Math.atan2(upY, uz) / r };
}
// v12c: the screen's SIDE tilt in degrees (its turn about the SCREEN's vertical
// axis), for the Duo lenticular. World-up in device axes, read in the screen's
// x/z plane, so it is the same side-to-side tilt in portrait and landscape
// (beta and gamma trade roles when the screen rotates). Portrait: = gamma.
// (dupduo: the maths lives in lens-tilt.js, where Node checks it)
function screenRollDeg() { return lensRollDeg(gyroRaw.beta, gyroRaw.gamma, screenAngle()); }
function installGyroSources() {
  if (REDUCED || gyroSource) return;
  addEventListener('deviceorientation', orient);
  addEventListener('deviceorientationabsolute', orientAbsolute);
  gyroFallbackTimer = setTimeout(() => {
    if (!gyroSource && 'DeviceMotionEvent' in window) addEventListener('devicemotion', motion);
  }, 2500);
}
const gyroBtn = document.getElementById('gyroBtn');
if (!REDUCED && typeof DeviceOrientationEvent !== 'undefined' &&
    typeof DeviceOrientationEvent.requestPermission === 'function') {
  // iOS permission must be requested by pointerup: it is still the active user
  // gesture, but never fires prematurely on pointerdown.
  gyroBtn.classList.add('show');
  // Chrome 15x has requestPermission too but sends events without it: listen now (iOS sends none until granted)
  installGyroSources();
  gyroBtn.addEventListener('pointerup', async () => {
    try {
      const r = await DeviceOrientationEvent.requestPermission();
      if (r === 'granted') {
        installGyroSources();
        gyroBtn.classList.remove('show');
        // SC22: same gesture order as the static hint in index.html — swipe
        // (the primary gesture) first. Keep the two copies in lockstep.
        hintEl.textContent = 'Tilt · swipe to browse · tap to inspect · double‑tap to flip';
      }
    } catch { /* stays on drag fallback */ }
  });
} else if (!REDUCED && 'DeviceOrientationEvent' in window) {
  installGyroSources();
}
addEventListener('nm-gyro-granted', () => { installGyroSources(); gyroBtn.classList.remove('show'); });

// Fine-pointer devices drive depth from the cursor over the WebGL surface.
// This input affects art depth only; the established card manipulation stays
// touch/drag-owned.
const hoverTilt = { x: 0, y: 0 };
const HOVER_DEPTH = matchMedia('(hover: hover) and (pointer: fine)').matches;
if (HOVER_DEPTH) {
  renderer.domElement.addEventListener('pointermove', (e) => {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    const r = renderer.domElement.getBoundingClientRect();
    hoverTilt.y = THREE.MathUtils.clamp(((e.clientX - r.left) / r.width - 0.5) * 0.42, -0.21, 0.21);
    hoverTilt.x = THREE.MathUtils.clamp(((e.clientY - r.top) / r.height - 0.5) * 0.42, -0.21, 0.21);
  });
  renderer.domElement.addEventListener('pointerleave', () => { hoverTilt.x = 0; hoverTilt.y = 0; });
}
const cardMotionTilt = () => gyroActive ? gyroTilt : hoverTilt;

// Existing title/settings drawer: one compact row owns both enablement and
// strength. Depth materials stay allocated when switched off, but their depth
// texture is not requested until the feature is enabled and visible.
const setDepth = document.getElementById('setDepth');
const setDepthAmt = document.getElementById('setDepthAmt');
function paintDepthSettings() {
  setDepth.classList.toggle('on', depthArt.settings.on);
  setDepthAmt.value = String(depthArt.settings.amt);
  setDepthAmt.disabled = !depthArt.settings.on;
}
setDepth.addEventListener('click', () => {
  depthArt.setSettings({ on: !depthArt.settings.on });
  paintDepthSettings();
});
setDepthAmt.addEventListener('input', () => {
  depthArt.setSettings({ amt: Number(setDepthAmt.value) });
});
paintDepthSettings();

// ---------------------------------------------------------------- FPS probe
// five-second window below 36fps may move one rung down: bloom first, then DPR
// in 0.1 steps. Recovery is deliberately slower (58+fps for ten seconds).
const PROBE_WINDOW = 5000;   // v126: five seconds sustained, not three
const SHED_BELOW = 36;      // v126 (Jack): 47 fps shed quality on a smooth tablet; only a real stutter (<36) may
const RESTORE_ABOVE = 58;
const RESTORE_HOLD = 10000;
// v298 HYSTERESIS (Jack's tablet flipped 4-11x a session in step with the volleys; every flip
// re-allocates the render targets = a stall of its own). Probe time, so it holds in QA verdicts too:
//  * a reversal (a shed right after a restore, or a restore right after a shed) waits >= 10 s;
//  * a restore that a slow window undoes within 30 s doubles the next restore's hold (10 -> 20 ->
//    30 s max; back to 10 s after a minute with no change), so a busy-calm-busy rhythm settles
//    instead of oscillating, and a calm device still promotes back reliably.
// Every rule only ever WITHHOLDS a shed today's probe would make: it never demotes more eagerly.
//  * an ISOLATED slow window (a burst between good ones: a volley) may cost bloom only; a pixel
//    rung needs a SUSTAINED miss: the streak that just shed is still slow, or a fresh streak has been
//    slow for 15 s -- recurring volley bursts never ratchet down.
const REVERSE_GAP = 10000, RESTORE_REGRET = 30000, RESTORE_HOLD_MAX = 30000;
let restoreHold = RESTORE_HOLD, sinceQualityMs = Infinity, lastQualityDir = 0;
let slowStreak = 0, shedThisStreak = false;
let probeStart = performance.now(), probeFrames = 0;
let probeGoodMs = 0, probeStableMs = 0;
let probeScreen = document.body.dataset.screen || '';
let perfMode = qualityStep > 0;
let perfPixelsReduced = qualityStep > 1;
const perfLog = { sheds: qualityStep, restores: 0, lastShedFps: null, lastShedAt: null, lastFps: null };
const FORCE_PERF = q.get('perf') === '1';   // SC18 QA: force the reduced tier

function applyQualityPixelRatio() {
  const dpr = Math.min(devicePixelRatio, pixelCap);
  renderer.setPixelRatio(dpr);
  composer.setPixelRatio(dpr);
  auraUniforms.uPix.value = dpr;
}

function maxQualityStep() {
  let max = 1;
  for (let i = 2; i < QUALITY_SCALES.length; i++) {
    if (qualityPixelCap(i) < qualityPixelCap(i - 1) - 0.001) max = i;
  }
  return max;
}

function persistQualityStep() {
  if (OFFLINE_RENDER || HAS_DEBUG_PIXEL_CAP) return;
  /* demotion is per-session only (see readStoredQualityStep) */
}

/** One quality transition chokepoint for measured, forced, and restored steps. */
function setQualityStep(next, { record = true, force = false } = {}) {
  next = THREE.MathUtils.clamp(Math.round(next), 0, maxQualityStep());
  if (OFFLINE_RENDER && next > 0) {
    window.__nomadmonCapture = { ...(window.__nomadmonCapture || {}), qualityViolation: 'PERF_SHED_ENGAGED' };
    throw new Error('OFFLINE_RENDER_PERF_SHED_ENGAGED');
  }
  const previous = qualityStep;
  if (!force && previous === next) return;
  qualityStep = next;
  perfMode = qualityStep > 0;
  perfPixelsReduced = qualityStep > 1;
  if (record && next > previous) {
    perfLog.sheds += next - previous;
    perfLog.lastShedFps = perfLog.lastFps;
    perfLog.lastShedAt = performance.now();
  } else if (record && next < previous) perfLog.restores += previous - next;
  bloomPass.enabled = qualityStep === 0 && !NOPOST;
  window.__nmOverlayLite = qualityStep > 1;   // v298: the 2D overlays go lite only on a pixel rung (sigil/trace.js overlayDpr)
  pixelCap = qualityPixelCap();
  applyQualityPixelRatio();
  composer.setSize(innerWidth, innerHeight);
  for (const c of cards) {
    // the authored density is NOT uniform across rarities, so restoring a
    // constant would flatten mythic/eternal -- remember each card's own value
    if (c.glintDensity0 === undefined) c.glintDensity0 = c.foilMat.uniforms.uGlintDensity.value;
    c.foilMat.uniforms.uGlintDensity.value = perfMode ? 18.0 : c.glintDensity0;
    if (c.sparks) { c.sparks.userData.shed = perfMode; if (perfMode) c.sparks.visible = false; }
  }
  const tier = !perfMode ? 'native' : perfPixelsReduced ? `step ${qualityStep}` : 'bloom off';
  console.info(`[nomadmon] quality ${tier}: bloom ${perfMode ? 'off' : 'on'}, dpr<=${pixelCap.toFixed(2)}`);
}

function setPerfMode(on, { reducePixels = false } = {}) {
  setQualityStep(on ? (reducePixels ? 2 : 1) : 0);
}

function resetProbe(now) {
  probeStart = now; probeFrames = 0; probeGoodMs = 0; probeStableMs = 0;
}

function qualityState() {
  return { step:qualityStep, pixelCap, dpr:renderer.getPixelRatio(),
    bloom:bloomPass.enabled, sheds:perfLog.sheds, restores:perfLog.restores };
}

function applyProbeVerdict(fps, elapsed, now = performance.now()) {
  perfLog.lastFps = Math.round(fps);
  sinceQualityMs += elapsed;
  if (fps < SHED_BELOW) {
    probeGoodMs = probeStableMs = 0;
    slowStreak++;
    const may = qualityStep === 0 || (slowStreak >= 2 && shedThisStreak) || slowStreak >= 3;
    if (qualityStep < maxQualityStep() && may && !(lastQualityDir < 0 && sinceQualityMs < REVERSE_GAP)) {
      if (lastQualityDir < 0 && sinceQualityMs < RESTORE_REGRET) restoreHold = Math.min(RESTORE_HOLD_MAX, restoreHold * 2);
      setQualityStep(qualityStep + 1);
      sinceQualityMs = 0; lastQualityDir = 1; shedThisStreak = true;
    }
    return qualityState();
  }
  slowStreak = 0; shedThisStreak = false;
  if (sinceQualityMs >= 60000) restoreHold = RESTORE_HOLD;
  probeStableMs += elapsed;
  probeGoodMs = fps >= RESTORE_ABOVE ? probeGoodMs + elapsed : 0;
  if (qualityStep > 0 && probeGoodMs >= restoreHold && sinceQualityMs >= REVERSE_GAP) {
    setQualityStep(qualityStep - 1);
    sinceQualityMs = 0; lastQualityDir = -1;
    resetProbe(now);
  } else if (probeStableMs >= RESTORE_HOLD) {
    // This is the last level proven sustainable on this device. A later boot
    // resumes here instead of repeating every failed rung from native.
    persistQualityStep();
    probeStableMs = 0;
  }
  return qualityState();
}

function probe(now) {
  if (OFFLINE_RENDER) return;
  // A forced pixel cap is a measurement mode: keep its requested DPR and
  // bloom implementation stable instead of letting the adaptive tier erase
  // the GPU load halfway through a benchmark.
  if (HAS_DEBUG_PIXEL_CAP) return;
  if (NOPOST) return;
  if (FORCE_PERF) { setPerfMode(true, { reducePixels: true }); return; }
  // Headless software rendering is not device evidence. Browser suites assert
  // startup quality, but cannot be allowed to manufacture a field shed.
  if (navigator.webdriver) return;
  // a backgrounded tab throttles rAF to ~1fps; that is not a slow device
  if (document.hidden) { resetProbe(now); return; }
  const screen = document.body.dataset.screen || '';
  if (screen !== probeScreen) {
    probeScreen = screen;
    noteQualityActivity(now);
  }
  // Screen changes and completed texture loads each start a fresh two-second
  // grace. No frames from that work are allowed into a quality verdict.
  if (now < qualityGraceUntil) {
    resetProbe(now);
    return;
  }
  probeFrames++;
  const elapsed = now - probeStart;
  if (elapsed < PROBE_WINDOW) return;
  const fps = probeFrames / (elapsed / 1000);
  probeStart = now; probeFrames = 0;
  applyProbeVerdict(fps, elapsed, now);
}

// Apply a persisted level to already-built shared cards without recording a
// drop in this visit. Fresh storage remains step zero / native.
setQualityStep(qualityStep, { record:false, force:true });

// ---------------------------------------------------------------- grab shadow
// Soft blob behind the active card; deepens while the card is held.
const shadowMesh = (() => {
  const c = document.createElement('canvas'); c.width = c.height = 256;
  const g = c.getContext('2d');
  const rg = g.createRadialGradient(128, 128, 10, 128, 128, 126);
  rg.addColorStop(0, 'rgba(0,0,0,0.9)'); rg.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = rg; g.fillRect(0, 0, 256, 256);
  const tex = new THREE.CanvasTexture(c);
  const m = new THREE.Mesh(
    new THREE.PlaneGeometry(CW * 2.1, CH * 1.7),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0.3, depthWrite: false })
  );
  m.renderOrder = -1;
  scene.add(m);
  return m;
})();

// ---------------------------------------------------------------- stat sheet
// P3: DOM stat sheet tracks the focused card's 3D flip state. Fades in once the
// back faces the camera beyond ~70%, out before the front returns; follows the
// card's screen position and scales with zoom (transform-scaled design frame).
// P6 LEGIBILITY CONTRACT: the sheet must be fully readable in BOTH
// orientations. Body text (8.7px in the design frame) keeps an effective size
// of ~11px — the sheet scales up beyond the 3D card silhouette when the card
// alone is too small, fit-to-viewport with the card as anchor. Height-
// constrained landscape reflows into a two-column 560x260 frame (.wide).
const SS_W = 300, SS_H = 428;            // portrait design frame
const SSW_W = 560, SSW_H = 260;          // landscape two-column frame
const SSN_W = 260;                       // SC27 narrow portrait frame (<420px wide)
const SS_MIN_SCALE = 11 / 8.7;           // ~11px effective body text
let ssOpacity = 0;
const _ssQ = new THREE.Quaternion(), _ssP = new THREE.Vector3();
const _ssFwd = new THREE.Vector3(), _ssToCam = new THREE.Vector3(), _ssNdc = new THREE.Vector3();
// SC31 registration scratch: face-on settle target + the card we hid, so the
// 'solo' regime can always put it back (see updateStatSheet).
const _ssM = new THREE.Matrix4(), _ssQT = new THREE.Quaternion(), _ssQP = new THREE.Quaternion();
const _ssUp = new THREE.Vector3(0, 1, 0);
let ssAlign = null;                      // last frame's registration, for debugSheetAlign()
// SC12: `force` pins the sheet fully visible once a flip has committed — the
// legibility contract must not hinge on instantaneous 3D backness (camera,
// gyro or a residual grab-spin can hold backness under threshold on device).
/**
 * SC27: can a READABLE stat sheet (>= the 11px-effective floor, SS_MIN_SCALE)
 * fit on this screen with these edges reserved?
 *
 * The deck picker asks before offering its double-tap flip. Some geometry —
 * notably 844x390, where SC25's chrome column already owns 44% of the width —
 * simply cannot hold a legible full sheet beside a visible PICK button. On
 * those shapes the flip is NOT offered, because shipping the sheet at 5px would
 * break the legibility rule and covering PICK would break SC25.
 */
/**
 * Which design frame this screen gets, and the scale it can reach.
 *
 * The NARROW frame is a last resort, not a phone default: it is used ONLY when
 * the standard 300px frame cannot reach the legibility floor on width. That
 * keeps pack reveals and MY DECK — which have the whole viewport and already
 * clear the floor — looking exactly as they did.
 */
function statSheetFrame(reserve = null) {
  const wide = innerWidth > innerHeight && innerHeight < 560;
  const resT = (reserve && reserve.top) || 0;
  const resB = (reserve && reserve.bottom) || 0;
  const resR = (reserve && reserve.right) || 0;
  const fitH = (innerHeight - 14 - resT - resB) / (wide ? SSW_H : SS_H);
  const widthFit = (W, m) => (innerWidth - m - resR) / W;
  if (wide) return { wide, narrow: false, W: SSW_W, H: SSW_H, marginX: 130, fitW: widthFit(SSW_W, 130), fitH };
  const std = widthFit(SS_W, 16);
  if (std >= SS_MIN_SCALE) return { wide, narrow: false, W: SS_W, H: SS_H, marginX: 16, fitW: std, fitH };
  return { wide, narrow: true, W: SSN_W, H: SS_H, marginX: 8, fitW: widthFit(SSN_W, 8), fitH };
}

function statSheetFits(reserve = null) {
  const f = statSheetFrame(reserve);
  return Math.min(f.fitW, f.fitH) >= SS_MIN_SCALE;
}

/**
 * `reserve` (SC27) — screen edges the sheet must stay out of, as
 * { top, bottom, right } pixels. The pack ceremony already needed this for its
 * bottom dock; the DECK PICKER needs it properly, because SC25 gave that screen
 * three EXCLUSIVE bands and a flipped sheet that spilled over the PICK button
 * would undo exactly what SC25 fixed. Defaults to the old behaviour.
 */
function updateStatSheet(c, dt, hidden, cam = camera, force = false, reserve = null) {
  const g = c.group;
  g.getWorldQuaternion(_ssQ);
  g.getWorldPosition(_ssP);
  _ssFwd.set(0, 0, 1).applyQuaternion(_ssQ);
  _ssToCam.copy(cam.position).sub(_ssP).normalize();
  const backness = -_ssFwd.dot(_ssToCam);            // 1 = back square to camera
  const target = hidden ? 0
    : force ? 1
    : THREE.MathUtils.clamp((backness - 0.70) / 0.18, 0, 1);
  ssOpacity += (target - ssOpacity) * (1 - Math.exp(-16 * dt));
  ssPanel.style.opacity = ssOpacity.toFixed(3);
  // SC31: the panel itself is opaque card stock now, so the 3D back beneath it
  // is driven to BLACK rather than half-lit. At .52 (the old value) the foil
  // crest read straight through the moves text — that is the "semi-translucent"
  // half of Jack's note, and no amount of alignment would have fixed it.
  c.backPlane.material.color.setScalar(1 - ssOpacity);
  // ...and the back FOIL sheen with it. This mesh sits just behind the back plane, i.e.
  // NEARER the camera than the back plane when the back is toward you, and it
  // blends ADDITIVELY — so darkening backPlane alone left the crest shimmer
  // painting straight over the sheet. It is the other half of the bleed-through.
  c.backFoilMat.uniforms.uIntensity.value *= (1 - ssOpacity);
  if (ssOpacity < 0.01) { ssAlign = null; return; }
  // SC27: the frame (and whether it narrows) is chosen by ONE function, shared
  // with statSheetFits(), so what the picker is promised and what it gets can
  // never drift apart.
  const frame = statSheetFrame(reserve);
  const { wide, narrow, W, H } = frame;
  ssPanel.classList.toggle('wide', wide);
  ssPanel.classList.toggle('narrow', narrow);
  // SC31 (1/3) SETTLE THE CARD FACE-ON while its back is up.
  //
  // The screenshot's "two rectangles at different angles" is this: the card
  // kept its coverflow tilt, its grab-spin and its idle sine while the DOM
  // sheet — which cannot rotate without either crossing the reserved PICK band
  // or skewing body text below the 11px floor — stayed square to the screen.
  // Rather than rotate the sheet, we settle the CARD, so the two agree.
  //
  // This lives here, in the one function all four surfaces (MY DECK fan, binder
  // solo, pack reveal, deck picker) already share, precisely so the four cannot
  // drift apart — and it runs AFTER each caller's layout pass and BEFORE its
  // render, so the frame that draws is the settled one.
  //
  // The blend is ABSOLUTE, not incremental: callers reassign rotation from
  // scratch every frame, so an exponential approach would never accumulate.
  // Blending by ssOpacity means "as visible as the sheet is, that square the
  // card is" — at full opacity the card is exactly face-on. It also self-
  // releases: `backness` above is read from the CALLER's rotation, before this
  // runs, so turning the card away lowers opacity, which loosens the settle.
  _ssM.lookAt(_ssP, cam.position, _ssUp);   // +Z away from cam => -Z (the back) at it
  _ssQT.setFromRotationMatrix(_ssM);
  if (g.parent) { g.parent.getWorldQuaternion(_ssQP); _ssQT.premultiply(_ssQP.invert()); }
  g.quaternion.slerp(_ssQT, ssOpacity);
  g.updateMatrixWorld(true);

  _ssNdc.copy(_ssP).project(cam);
  let sx = (_ssNdc.x * 0.5 + 0.5) * innerWidth;
  let sy = (-_ssNdc.y * 0.5 + 0.5) * innerHeight;
  // SC31 (2/3) the card's real face-on FOOTPRINT in CSS px — both axes.
  // The old code measured HEIGHT only (hPx * 0.94 / H), so the sheet's width
  // bore no relation to the card's width. Perspective px-per-world-unit is the
  // same on both axes, so one divisor serves both.
  const dist = cam.position.distanceTo(_ssP);
  const pxPerUnit = innerHeight / (2 * dist * Math.tan(THREE.MathUtils.degToRad(cam.fov / 2)));
  const wPx = CW * g.scale.x * pxPerUnit;
  const hPx = CH * g.scale.y * pxPerUnit;
  // uniform fit — the sheet fills the face without distorting a single glyph
  const cardS = Math.min(wPx / W, hPx / H);
  // contract: never below the readable minimum, never past the viewport.
  // Landscape reserves a right margin for the docked control strip.
  const resT = (reserve && reserve.top) || 0;
  const resB = (reserve && reserve.bottom) || 0;
  const resR = (reserve && reserve.right) || 0;
  const fitW = frame.fitW;
  // SC17: on tall/square geometry the pack ceremony's SAFE DOCK owns the
  // bottom band — a flipped stat-back caps its height above it so the
  // FLIP/Front control stays visible and tappable while the sheet is up.
  const bc = document.body.classList;
  const dockR = bc.contains('packback') &&
    (bc.contains('geo-tall') || bc.contains('geo-square'))
    ? (document.getElementById('packDock').offsetHeight || 74) + 6 : 0;
  const fitH = (innerHeight - 14 - dockR - resT - resB) / H;
  // SS_MIN_SCALE guarantees the >=11px legibility floor, so it is deliberately
  // NOT clamped away by the reserve — if a viewport is ever too small to hold a
  // readable sheet inside the zone, the sheet stays readable and the geometry
  // suite says so, rather than the text quietly shrinking below the floor.
  // SC31 (3/3) TWO EXPLICIT REGIMES. Which one fires is reported by
  // debugSheetAlign() so the suite asserts registration rather than eyeballing
  // it, and so this note can never quietly become false.
  //
  //  'onCard' — the card's own footprint is already big enough to carry a
  //     legible sheet (cardS >= SS_MIN_SCALE) AND that footprint clears the
  //     reserved bands. Then the sheet is pinned to the card's exact projected
  //     rect: same centre, same scale, no clamping. The card stays visible, and
  //     its lit edge ring frames the sheet like the rim of real card stock.
  //     This is the case Jack was looking at (MY DECK, hero card) and it is now
  //     an exact registration, not an approximation.
  //
  //  'solo' — it is not. The deck picker is the honest example: SC25's bands
  //     leave a 342px card zone, and a legible sheet needs 541px, so the sheet
  //     MUST be larger than the card there. Legibility is a hard rule and wins,
  //     exactly as it did before — but then the card underneath would be a
  //     smaller, offset rectangle behind an opaque panel, i.e. the very defect
  //     being fixed. So in this regime the card is HIDDEN while the sheet is
  //     up. Nothing bleeds through and nothing mis-maps, because there is no
  //     second rectangle at all. (This is the brief's stated alternative; it is
  //     used only where the first regime cannot hold both contracts at once.)
  const s = Math.max(Math.min(Math.max(cardS, SS_MIN_SCALE), fitW, fitH), 0.4);
  // card-anchored, clamped fully on-screen and outside any reserved bands
  const hw = (W * s) / 2, hh = (H * s) / 2;
  const edge = wide ? 123 : (narrow ? 4 : 7);
  sx = THREE.MathUtils.clamp(sx, (narrow ? 4 : 7) + hw, innerWidth - edge - resR - hw);
  sy = THREE.MathUtils.clamp(sy, 7 + resT + hh, innerHeight - 7 - dockR - resB - hh);
  ssPanel.style.transform = `translate(${sx.toFixed(1)}px, ${sy.toFixed(1)}px) scale(${s.toFixed(4)}) translate(-50%, -50%)`;

  // SC31 (3/3) BRING THE CARD TO THE SHEET.
  //
  // Everything above is the legibility-first placement this app has always
  // used, and it is why the two rectangles diverged: the sheet has to be big
  // enough to read (SS_MIN_SCALE) and has to clear the reserved bands, and on
  // real geometry the card is nowhere near that size. Measured on the shipping
  // build, MY DECK's hero card projects at cardS = 0.53 against a floor of
  // 1.264 — the sheet is necessarily 2.4x the card. No amount of moving the
  // SHEET can fix that; the sheet is where it has to be.
  //
  // So the card is driven onto the sheet instead: scaled so its face is exactly
  // the sheet's height, and moved so its centre is exactly the sheet's centre.
  // Physically this is a card being brought up to the eye to be read, which is
  // what a player does with a real one, and it is the only arrangement in which
  // BOTH contracts hold at once — the text stays >= the floor and the object
  // stays single. It also subsumes the two options the brief offered: where the
  // card is already big enough (cardS >= SS_MIN_SCALE) s == cardS and this is a
  // no-op, and nothing is ever hidden or faded.
  //
  // Stability: s is either cardS (identity) or one of SS_MIN_SCALE / fitW /
  // fitH, all constants for a given viewport — so the target below never
  // depends on the scale we just wrote, and cannot oscillate. Blending by
  // ssOpacity keeps it absolute against callers that reassign every frame.
  // Inscribe the card in the sheet, on the tighter of the two axes, with a
  // 1.5% safety margin. The sheet's design frame is not the card's 63:88 —
  // SC27's narrow portrait frame is 260 wide, the landscape frame is 560x260 —
  // so matching HEIGHT alone left the card overhanging by up to 59px a side on
  // a phone, i.e. a fat band of card back beside a narrow sheet. Taking the
  // smaller factor means the opaque sheet COVERS the card's whole footprint at
  // every viewport, so bleed-through is impossible by construction rather than
  // by tuning, and the sheet's own gold border and glow become the card edge.
  // The card is still there, lit, carrying its rarity bloom and aura around the
  // panel — it is read as one object, not deleted.
  const kTarget = 0.985 * Math.min((H * s) / (CH * pxPerUnit), (W * s) / (CW * pxPerUnit));
  const k = THREE.MathUtils.lerp(g.scale.y, kTarget, ssOpacity);
  g.scale.setScalar(k);
  // put its centre under the sheet's centre, at the same depth
  _ssNdc.set((sx / innerWidth) * 2 - 1, -((sy / innerHeight) * 2 - 1), _ssNdc.z).unproject(cam);
  g.position.lerp(g.parent ? g.parent.worldToLocal(_ssNdc) : _ssNdc, ssOpacity);
  g.updateMatrixWorld(true);
  // registration record — measured from where the card ACTUALLY ended up, not
  // from where it was asked to go, so debugSheetAlign() cannot flatter itself
  _ssP.setFromMatrixPosition(g.matrixWorld);
  _ssNdc.copy(_ssP).project(cam);
  const ppu = innerHeight / (2 * cam.position.distanceTo(_ssP) *
    Math.tan(THREE.MathUtils.degToRad(cam.fov / 2)));
  const ccx = (_ssNdc.x * 0.5 + 0.5) * innerWidth, ccy = (-_ssNdc.y * 0.5 + 0.5) * innerHeight;
  const cw = CW * g.scale.x * ppu, ch = CH * g.scale.y * ppu;
  ssAlign = {
    zoomed: s > cardS + 1e-4, scale: +s.toFixed(4), cardScale: +cardS.toFixed(4),
    minScale: +SS_MIN_SCALE.toFixed(4), opacity: +ssOpacity.toFixed(3),
    card: { x: ccx - cw / 2, y: ccy - ch / 2, w: cw, h: ch },
    sheet: { x: sx - (W * s) / 2, y: sy - (H * s) / 2, w: W * s, h: H * s },
  };
}

/**
 * SC31: the flipped sheet's registration against the card it belongs to, so the
 * suite can prove alignment numerically instead of from a screenshot. `dx`/`dy`
 * are centre offsets in CSS px (0 = perfectly concentric); `covers` is whether
 * the opaque sheet fully hides the card face, which is what makes bleed-through
 * impossible in the 'solo' regime.
 */
export function debugSheetAlign() {
  if (!ssAlign) return null;
  const a = ssAlign, cc = a.card, sh = a.sheet;
  return {
    ...a,
    dx: +((sh.x + sh.w / 2) - (cc.x + cc.w / 2)).toFixed(2),
    dy: +((sh.y + sh.h / 2) - (cc.y + cc.h / 2)).toFixed(2),
    // the sheet's height matches the card face exactly; the card is ~2% wider
    // (63:88 vs the 300:428 design frame), which is the rim that frames it
    dh: +(sh.h - cc.h).toFixed(2), dw: +(sh.w - cc.w).toFixed(2),
    // the whole point: the opaque sheet occupies the card's entire footprint
    covers: sh.x <= cc.x + 0.5 && sh.y <= cc.y + 0.5 &&
            sh.x + sh.w >= cc.x + cc.w - 0.5 && sh.y + sh.h >= cc.y + cc.h - 0.5,
  };
}

// ---------------------------------------------------------------- binder / solo
const binderEl = document.getElementById('binder');
const binderGrid = document.getElementById('binderGrid');
const binderVirt = document.getElementById('binderVirt');
const binderSticky = document.getElementById('binderSticky');
const soloBackBtn = document.getElementById('soloBack');
let soloCard = null;            // active binder-opened card object (non-featured)

const RARITY_ORDER = { eternal: 0, mythic: 1, legendary: 2, epic: 3, rare: 4, common: 5 };

// ---------------------------------------------------------------------------
// Collection v2 at GRAND ARCHIVE scale. The canvas remains WINDOWED: only the
// visible cells plus a fixed buffer are mounted. Search/filter/model work never
// runs in the scroll handler, and its reusable `want` set means scrolling only
// allocates the DOM nodes required by the window diff.
// ---------------------------------------------------------------------------
const BF = { q: '', type: null, rarity: null, person: null, own: null, holo: null, sort: 'rarity' };
let BV = null;   // { items, heads, order, total, cols, colW, cellH, mounted, want, shown }
const ctTilt = { x: 0, y: 0, raf: 0, px: 0, py: 0, host: null };   // COLLECTION 2 tilt state (see collTiltFrame)
// COLLECTION 2: one view toggle (grid <-> carousel), remembered per device
let binderView = (() => { try { return localStorage.getItem('nomadmon-coll-view') === 'car' ? 'car' : 'grid'; } catch { return 'grid'; } })();

function binderFuzzy(hay, tok) {   // subsequence match: "glbz" hits "golden buzzer"
  let i = 0;
  for (const ch of hay) if (ch === tok[i] && ++i === tok.length) return true;
  return false;
}
function binderPass(d) {
  if (BF.type && d.type !== BF.type) return false;
  if (BF.rarity && d.rarity !== BF.rarity) return false;
  if (BF.person && d.person !== BF.person) return false;
  if (BF.holo && !holoStatusOf(d, identity.cardLevel(d.id))) return false;   // v10: Holo STATUS
  if (BF.own) {
    const o = identity.ownedCount(d.id) > 0;
    if (BF.own === 'owned' ? !o : o) return false;
  }
  if (BF.q) {
    const hay = `${d.name} ${d.person || ''} ${d.id}`.toLowerCase();
    for (const tok of BF.q.toLowerCase().split(/\s+/)) {
      if (tok && !hay.includes(tok) && !binderFuzzy(hay, tok)) return false;
    }
  }
  return true;
}

const alphaCard = (a, b) => deck[a].name.localeCompare(deck[b].name) || a - b;
function binderSections(unfiltered = false) {
  const idxs = [];
  // v337 (Jack: "we shouldn't show cards you don't own... they just shouldn't be there at all"): the binder lists OWNED
  // cards only; Duos and Fusions keep their ? slots to call out what's still missing
  for (let i = 0; i < deck.length; i++) if (!deck[i].forged && (unfiltered || (identity.ownedCount(deck[i].id) > 0 && binderPass(deck[i])))) idxs.push(i);
  const sections = [];
  // INK DUEL: the player's kept champions lead the grid (owned only: champions are won, never collected)
  const champs = [];
  for (let i = 0; i < deck.length; i++) {
    const d = deck[i];
    if (isChampionCard(d) && identity.ownedCount(d.id) > 0 && (unfiltered || (BF.own !== 'missing' && binderPass(d)))) champs.push(i);
  }
  if (champs.length) {
    const g = championGrand();
    sections.push({ jump: 'CH', label: g.grand ? 'Grand Champion' : 'Champions', color: '#f0c463', champ: true,
      sub: g.grand ? `${g.owned} / ${g.total}` : `${g.owned} / ${g.total}`, idxs: champs });
  }
  if (BF.sort === 'type') {
    for (const type of Object.keys(TYPES)) {
      const group = idxs.filter(i => deck[i].type === type).sort(alphaCard);
      if (group.length) sections.push({ jump: TYPES[type].label.slice(0, 2).toUpperCase(),
        label: TYPES[type].label, color: TYPES[type].color, idxs: group });
    }
  } else if (BF.sort === 'alpha') {
    const sorted = idxs.sort(alphaCard);
    const groups = new Map();
    for (const i of sorted) {
      const initial = (/^[A-Z]/i.test(deck[i].name) ? deck[i].name[0] : '#').toUpperCase();
      if (!groups.has(initial)) groups.set(initial, []);
      groups.get(initial).push(i);
    }
    for (const [initial, group] of groups) sections.push({ jump: initial, label: initial,
      color: '#f0c463', idxs: group });
  } else {
    for (const rarity of Object.keys(RARITY_ORDER).sort((a, b) => RARITY_ORDER[a] - RARITY_ORDER[b])) {
      const group = idxs.filter(i => deck[i].rarity === rarity).sort(alphaCard);
      // (the Eternal head stays even with none owned: it carries the WISH control)
      const wishHead = rarity === 'eternal' && !BF.q && !BF.type && !BF.person && (!BF.rarity || BF.rarity === 'eternal');
      if (group.length || wishHead) sections.push({ jump: rarity === 'eternal' ? 'ET' : rarity.slice(0, 2).toUpperCase(),
        label: RARITY[rarity].label, color: RARITY[rarity].color, idxs: group, ...(group.length ? {} : { sub: 'None yet' }) });
    }
  }
  return sections;
}

// HOLOPACK: a card can replay its hologram (see startSoloHolo below).
// HOLO V2: capable = the offline bake exists for it (assets/holo2/index.json,
// 1,535 of 1,538 cards; uploaded by scripts/upload-holo2.sh, fetched per card
// only when a hologram opens). Until the index arrives, or if it never does,
// the V1 rule stands: legendary+ still art with a depth map.
let holo2Ids = null, holo2Asked = false;
function loadHolo2Index() {
  if (holo2Asked) return;
  holo2Asked = true;
  fetch('./assets/holo2/index.json').then(r => (r.ok ? r.json() : null)).then((j) => {
    if (!j || !j.ids) return;
    holo2Ids = new Set(Object.keys(j.ids));
    if (BV) buildBinderModel();               // remount tiles with their Holo chips
    if (binderSheetIndex >= 0) showBinderSheetAt(binderSheetIndex);
  }).catch(() => {});
}
// v10 HOLO STATUS of an owned fighter (see levelling.isHolo + the server flag).
function holoStatusOf(d, level) {
  return identity.ownedCount(d.id) > 0 && d.role !== 'support' && (isHolo(d, level) || identity.cardHoloFlag(d.id));
}
function holoCapable(d) {
  if (!d) return false;
  if (holo2Ids && holo2Ids.has(d.id)) return true;
  return (d.rarity === 'legendary' || d.rarity === 'mythic' || d.rarity === 'eternal')
    && !d.altOf && !d.clip && d.variant !== 'diarrhea' && depthArt.has(d);
}
// COLLECTION 2: the tile IS the card. Art fills a 5:7 card, the rarity is a
// thin rim of light (no box, no plate), POWER sits big in the corner with the
// LEVEL under its PWR label, and the name rides a gradient at the foot. No
// filters, blend modes or per-tile animation: a phone scrolls ~30 of these.
const CARD_RATIO = 1.4;
function cardStats(d) {
  const n = identity.ownedCount(d.id);
  const level = identity.cardLevel(d.id);
  return { n, level, power: effectivePwr(d, level) };
}
// OWNERSHIP is the only thing colour vs dim means (Jack: "why are some cards coloured and others dimmed?"):
// an owned card is always full colour; a card not owned yet is dimmed AND carries this lock chip, in the
// grid and in the carousel alike. (The squad is not shown here.)
const NOT_OWNED_ICON = '<svg class="ct-lockI" viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>';
const NOT_OWNED_CHIP = `<span class="ct-lock" title="Not owned yet">${NOT_OWNED_ICON}<span>Not owned</span></span>`;
function ownedDots(n) {
  return n > 1 ? `<span class="ct-dots" aria-hidden="true">${'<i></i>'.repeat(Math.min(n, 4))}</span>` : '';
}
// INK DUEL champion tile: its clip (poster first) inside the animated CHAMPION frame, the bonus chip
// (+8% / -3%) and its rank stars where a rarity would sit. Power is the live top-3 line.
function champTileInner(d, video) {
  const power = effectivePwr(d, 1);
  const grand = championGrand().grand;
  return `${championMediaHtml(d, { video })}`
    + `<span class="ct-pw"><b>${power}</b><span><i>PWR</i><i>\u2605${d.stars}</i></span></span>`
    + `<span class="ct-foot"><span class="bname">${displayName(d.name)}</span><span class="ct-meta">${championMetaHtml(d, grand)}</span></span>`
    + championFrameHtml(d, { grand });
}
function makeChampionCell(di) {
  const d = deck[di];
  ensureChampionCss();
  const cell = document.createElement('button');
  cell.type = 'button';
  cell.className = 'bcard champ';
  cell.dataset.di = di;
  cell.dataset.champion = d.champion;
  cell.style.setProperty('--cc', d.color || '#f0c463');
  const st = d.live().state;
  cell.setAttribute('aria-label', `${d.name}, Champion, power ${effectivePwr(d, 1)}, ${d.stars} stars${st && st.rank ? `, rank ${st.rank}` : ''}`);
  cell.innerHTML = champTileInner(d, true);
  wireForgedVideos(cell);
  cell.addEventListener('click', () => openBinderSheet(di, cell));
  return cell;
}
function makeBinderCell(di) {
  const d = deck[di];
  if (isChampionCard(d)) return makeChampionCell(di);
  const rar = RARITY[d.rarity];
  const { n, level, power } = cardStats(d);
  const holo = holoStatusOf(d, level);
  const cell = document.createElement('button');
  cell.type = 'button';
  cell.className = 'bcard r-' + d.rarity + (d.altOf ? ' balt' : '') + (n ? '' : ' unowned');
  cell.dataset.di = di;                        // SC2-B ownership repaint hook
  cell.style.setProperty('--rc', rar.color);
  const rarLabel = d.altOf ? 'Mythic-Alt' : rar.label;
  cell.setAttribute('aria-label', `${d.name}, ${rarLabel}, power ${power}${n ? `, level ${level}, owned ${n}` : ', not yet owned'}`);
  cell.innerHTML =
    `<img class="ct-img" loading="lazy" decoding="async" src="${artSrc(d, V, 'thumb')}" alt="" style="object-position:${thumbPos(d)}">` +
    `<span class="ct-pw"><b>${power}</b><span><i>PWR</i><i>${n ? `LV ${level}` : '—'}</i></span></span>` +
    (n ? '' : NOT_OWNED_CHIP) +
    `<span class="ct-foot"><span class="bname">${displayName(d.name)}</span>` +
    `<span class="ct-meta"><span class="ct-rar${d.rarity === 'eternal' ? ' bete' : ''}">${holo ? '<i class="ct-holo" title="Holo"></i>' : ''}${rarLabel}</span>${ownedDots(n)}</span></span>`;
  cell.addEventListener('click', () => openBinderSheet(di, cell));
  return cell;
}

const BGAP = 12, BHEAD_H = 40, BTEXT_H = 0;
function binderColumnCount(availW) {
  if (innerWidth <= 430) return 2;
  if (innerWidth <= 760) return 3;
  if (innerWidth <= 1100) return 4;
  return Math.max(5, Math.floor((availW + BGAP) / 236));
}
// The grid's content width. #binderGrid is display:none in the carousel view and on the Duos / Fusions /
// Trophies tabs, and the model is rebuilt from there too (the holo index landing, an ownership repaint after
// a battle, a resize). It used to fall through to `binderGrid.clientWidth - 32` = -32 (truthy), so every
// column width and row height went negative: the section heads piled up at the top, the cards mounted as
// zero-width slivers down the left edge, and the grid stayed empty when the player came back to it. Now a
// hidden grid lays out at the overlay's width, and binderVirtRO (below) rebuilds once the grid has a width.
function binderAvailW() {
  const w = binderVirt.clientWidth;
  if (w > 0) return w;
  return Math.max(240, (binderEl.clientWidth || innerWidth) - (innerWidth > 760 ? 64 : 32));
}
function buildBinderModel({ reset = false } = {}) {
  if (reset) binderGrid.scrollTop = 0;
  const availW = binderAvailW();
  const cols = binderColumnCount(availW);
  const gap = innerWidth > 760 ? 18 : BGAP;
  const colW = (availW - (cols - 1) * gap) / cols;
  const cellH = colW * CARD_RATIO + BTEXT_H;
  const items = [], heads = [], order = [];
  let y = 0, shown = 0;
  const filtering = !!(BF.q || BF.rarity || BF.person || BF.own || BF.holo);
  for (const s of binderSections()) {
    const head = { label: s.label, sub: s.sub || `${s.idxs.length}`, jump: s.jump,
      color: s.color, y, itemIndex: items.length };
    heads.push(head);
    items.push({ head, y });
    y += BHEAD_H;
    s.idxs.forEach((di, k) => {
      const row = Math.floor(k / cols), col = k % cols;
      items.push({ di, x: col * (colW + gap), y: y + row * (cellH + gap) });
      order.push(di);
    });
    y += Math.ceil(s.idxs.length / cols) * (cellH + gap) + 8;
    shown += s.idxs.length;
  }
  if (!shown) {
    items.push({ empty: true, y });   // (below the Eternal wish head when that is all there is)
    y = Math.max(y + 260, binderGrid.clientHeight);
  }
  const mounted = BV?.mounted ?? new Map();
  const want = BV?.want ?? new Set();
  BV = { items, heads, order, total: y, cols, colW, cellH, mounted, want, shown, filtering, availW };
  binderVirt.style.height = `${Math.ceil(y)}px`;
  paintBinderCounts();
  paintBinderActive();
  renderBinderWindow(true);
  // only while open: a remembered carousel view would otherwise build it during module init (before car exists)
  if (binderView === 'car' && binderEl.classList.contains('open')) buildCarousel();
}

function renderBinderWindow(force = false) {
  if (!BV) return;
  const top = binderGrid.scrollTop - 600;
  const bot = binderGrid.scrollTop + binderGrid.clientHeight + 600;
  const want = BV.want;
  want.clear();
  for (let i = 0; i < BV.items.length; i++) {
    const it = BV.items[i];
    const h = it.head ? BHEAD_H : it.empty ? 200 : BV.cellH;
    if (it.y + h >= top && it.y <= bot) want.add(i);
  }
  for (const [i, el] of BV.mounted) {
    if (!want.has(i) || force) { el.remove(); BV.mounted.delete(i); }
  }
  for (const i of want) {
    if (BV.mounted.has(i)) continue;
    const it = BV.items[i];
    let el;
    if (it.head) {
      el = document.createElement('div');
      el.className = 'bsection';
      el.innerHTML = `<span>${it.head.label}</span><span class="bsub">${it.head.sub}</span>`;
      el.style.cssText = `position:absolute;left:0;right:0;top:${it.y}px;--sc:${it.head.color};`;
      if (it.head.jump === 'ET' && BF.sort === 'rarity') {   // ETERNAL WISH: the Eternal page's wish control
        const w = document.createElement('button');
        w.type = 'button'; w.className = 'bwish'; w.textContent = 'Wish';
        w.setAttribute('aria-label', 'Choose your Eternal wish');
        w.addEventListener('click', (e) => { e.stopPropagation(); openWishPicker({ from: 'collection' }); });
        el.appendChild(w);
      }
    } else if (it.empty) {
      el = document.createElement('div');
      el.className = 'bempty';
      if (it.y) el.style.top = `${it.y + 2}px`;   // (under the Eternal wish head)
      if (!BF.q && !BF.rarity && !BF.person && !BF.holo) el.textContent = 'None collected here yet. Open packs to find them.';
      else {
        const lead = document.createTextNode(BF.q ? `No cards match “${BF.q}” — ` : 'No cards match these filters — ');
        const clear = document.createElement('button');
        clear.type = 'button'; clear.textContent = 'clear filters';
        clear.addEventListener('click', clearBinderFilters);
        el.append(lead, clear);
      }
    } else {
      el = makeBinderCell(it.di);
      el.style.cssText = `position:absolute;left:${it.x}px;top:${it.y}px;width:${BV.colW}px;height:${BV.cellH}px;--rc:${RARITY[deck[it.di].rarity].color}`
        + (isChampionCard(deck[it.di]) ? `;--cc:${deck[it.di].color || '#f0c463'}` : '');
    }
    binderVirt.appendChild(el);
    BV.mounted.set(i, el);
  }
  updateBinderSticky();
}

// scroll -> window update (rAF-throttled, passive)
let binderRaf = 0;
function renderBinderFrame() { binderRaf = 0; renderBinderWindow(); }
binderGrid.addEventListener('scroll', () => {
  if (binderRaf) return;
  binderRaf = requestAnimationFrame(renderBinderFrame);
}, { passive: true });
// the grid coming back from display:none (carousel -> grid, a forged tab -> a card tab) or changing width
// re-lays the model at its real width (its own height writes do not trigger this: width only)
if (typeof ResizeObserver === 'function') {
  new ResizeObserver(() => {
    const w = binderVirt.clientWidth;
    if (BV && w > 0 && Math.abs(w - BV.availW) > 0.5) buildBinderModel();
  }).observe(binderVirt);
}
addEventListener('resize', () => {
  if (BV) buildBinderModel();
  if (binderSheetIndex >= 0) layoutBinderSheet();
});

function updateBinderSticky() {
  if (!BV || !BV.heads.length) { binderSticky.style.visibility = 'hidden'; return; }
  let current = BV.heads[0];
  for (const h of BV.heads) { if (h.y <= binderGrid.scrollTop + 2) current = h; else break; }
  if (binderSticky.dataset.item !== String(current.itemIndex)) {
    binderSticky.dataset.item = current.itemIndex;
    binderSticky.querySelector('span').textContent = current.label;
    binderSticky.querySelector('small').textContent = current.sub;
    binderSticky.style.setProperty('--sc', current.color);
  }
  binderSticky.style.visibility = binderGrid.scrollTop > 4 ? 'visible' : 'hidden';
}

function paintBinderCounts() {
  let owned = 0;
  for (const d of deck) if (!d.forged && identity.ownedCount(d.id) > 0) owned++;
  const count = document.querySelector('#binder .bcount');
  count.textContent = `${owned} / ${SET_SIZE} owned`;
  count.dataset.shown = BV.shown;
  count.dataset.total = SET_SIZE;
  const result = document.getElementById('binderResult');
  result.textContent = `${BV.shown} shown`;
  result.classList.toggle('on', BV.filtering || BV.shown !== SET_SIZE);
}

const FILTER_META = {
  rarity: { label: 'Rarity', color: v => RARITY[v]?.color },
  type: { label: 'Type', color: v => TYPES[v]?.color },
  person: { label: 'Person' }, own: { label: 'Cards' }, sort: { label: 'Sort' },
};
function filterLabel(cat, val) {
  if (cat === 'rarity') return RARITY[val].label;
  if (cat === 'type') return TYPES[val].label;
  if (cat === 'own') return val === 'owned' ? 'Owned' : 'Missing';
  if (cat === 'sort') return val === 'alpha' ? 'A–Z' : val[0].toUpperCase() + val.slice(1);
  return val;
}
function paintBinderActive() {
  const active = document.getElementById('binderActive');
  active.textContent = '';
  let n = 0;
  for (const cat of ['rarity', 'person', 'own', 'sort']) {
    const val = BF[cat];
    if (!val || (cat === 'sort' && val === 'rarity')) continue;
    n++;
    const b = document.createElement('button');
    b.className = 'bactiveChip'; b.type = 'button'; b.dataset.cat = cat;
    b.textContent = `${filterLabel(cat, val)} ×`;
    const color = FILTER_META[cat].color?.(val);
    if (color) b.style.setProperty('--pc', color);
    b.setAttribute('aria-label', `Remove ${FILTER_META[cat].label} filter ${filterLabel(cat, val)}`);
    b.addEventListener('click', () => { BF[cat] = cat === 'sort' ? 'rarity' : null;
      syncBinderFilterChips(); buildBinderModel({ reset: true }); });
    active.appendChild(b);
  }
  if (BF.holo) n++;
  const filterBtn = document.getElementById('binderFilterBtn');
  filterBtn.classList.toggle('on', n > 0); filterBtn.dataset.count = n || '';
  // the search/filter row stays folded away unless asked for or in use
  binderEl.classList.toggle('tools-live', n > 0 || !!BF.q);
  document.getElementById('binderSearchBtn').classList.toggle('on', n > 0 || !!BF.q);
}

function syncBinderFilterChips() {
  for (const b of document.querySelectorAll('.bfilterChip')) {
    const val = b.dataset.val === 'all' ? null : b.dataset.val;
    b.classList.toggle('on', BF[b.dataset.cat] === val);
  }
}
function clearBinderFilters() {
  BF.q = ''; BF.rarity = null; BF.person = null; BF.own = null; BF.holo = null; BF.sort = 'rarity';
  document.getElementById('binderSearch').value = '';
  syncBinderHoloChip();
  syncBinderFilterChips(); buildBinderModel({ reset: true });
}

let binderFiltersBuilt = false;
function buildBinderFilters() {
  if (binderFiltersBuilt) return;
  binderFiltersBuilt = true;
  const specs = [
    ['rarity', 'Rarity', [['all', 'All'], ...Object.keys(RARITY_ORDER)
      .sort((a, b) => RARITY_ORDER[a] - RARITY_ORDER[b]).map(v => [v, RARITY[v].label])]],
    ['person', 'Person', [['all', 'All'], ...[...new Set(deck.map(d => d.person).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b)).map(v => [v, v])]],
    ['sort', 'Sort', [['rarity', 'Rarity'], ['type', 'Type'], ['alpha', 'A–Z']]],
  ];
  const root = document.getElementById('binderFilterGroups');
  for (const [cat, title, opts] of specs) {
    const group = document.createElement('section'); group.className = 'bfilterGroup';
    const h = document.createElement('h3'); h.textContent = title;
    const choices = document.createElement('div'); choices.className = 'bfilterOptions';
    for (const [val, label] of opts) {
      const b = document.createElement('button');
      b.className = 'bfilterChip'; b.type = 'button'; b.dataset.cat = cat; b.dataset.val = val;
      b.textContent = label;
      if (cat === 'rarity' && val !== 'all') b.style.setProperty('--pc', RARITY[val].color);
      if (cat === 'type' && val !== 'all') b.style.setProperty('--pc', TYPES[val].color);
      b.addEventListener('click', () => {
        BF[cat] = val === 'all' ? null : val;
        syncBinderFilterChips(); audio.ui('tick');
        buildBinderModel({ reset: true });
      });
      choices.appendChild(b);
    }
    group.append(h, choices); root.appendChild(group);
  }
  syncBinderFilterChips();
}

// The Holo toggle sits in the tool row, not in the sheet: it is the one filter
// Jack asked to be able to find without looking for it.
const binderHoloChip = document.getElementById('binderHoloChip');
function syncBinderHoloChip() {
  binderHoloChip.classList.toggle('on', !!BF.holo);
  binderHoloChip.setAttribute('aria-pressed', BF.holo ? 'true' : 'false');
}
binderHoloChip.addEventListener('click', () => {
  BF.holo = BF.holo ? null : 'on';
  syncBinderHoloChip(); audio.ui('tick');
  buildBinderModel({ reset: true });
});

function setBinderFilters(open) {
  if (open) buildBinderFilters();
  const scrim = document.getElementById('binderFilterScrim');
  scrim.classList.toggle('open', open); scrim.setAttribute('aria-hidden', String(!open));
}
document.getElementById('binderFilterBtn').addEventListener('click', () => setBinderFilters(true));
document.getElementById('binderFilterDone').addEventListener('click', () => setBinderFilters(false));
document.getElementById('binderFilterScrim').addEventListener('click', e => {
  if (e.target.id === 'binderFilterScrim') setBinderFilters(false);
});

// Search remains debounced so archive-scale fuzzy matching never joins typing's
// input event, while the 120ms budget stays inside the scale suite's 250ms gate.
{
  const search = document.getElementById('binderSearch');
  let debT = 0;
  search.addEventListener('input', () => {
    clearTimeout(debT);
    debT = setTimeout(() => { BF.q = search.value.trim(); buildBinderModel({ reset: true }); }, 120);
  });
}

buildBinderModel();
// SC23 PERF: when the Collection overlay went up over a 1234-card virtual
// scroller (~44,000px tall), the binder dropped from 60 to ~50 fps on an M4 at
// DPR2. Isolated cause: the overlay is a full-viewport backdrop-filter blur
// over the LIVE WebGL canvas, so every 3D frame forces the compositor to
// re-blur the whole viewport. Hiding either layer alone restored 60 fps, which
// is what proved it was the composite and not the scene or the DOM.
// Fix: once the overlay has finished fading in it is opaque enough to hide the
// scene entirely, so stop rendering behind it. openFromBinder() always closes
// the binder BEFORE showing a card, so nothing animated ever lives back there.
let binderOpaqueAt = 0;      // timestamp after which the overlay fully covers
let coveredFrames = 0;       // frames spent under the opaque overlay (the fan updates on every 6th)
function setBinder(open) {
  if (!open) { closeBinderSheet(); setBinderFilters(false); forgeUI.closeSheet(); if (trophyMod) trophyMod.closeTrophy(); }
  else { paintTrophyTab(); forgeUI.paintTabs(); forgeUI.renderPanel(); }
  binderOpaqueAt = open ? performance.now() + 420 : 0;   // 0.35s CSS fade + margin
  binderEl.classList.toggle('open', open);
  if (open) loadHolo2Index();
  // SC2-B Phase 2: the deck hotspot steps aside while the binder is up
  document.body.classList.toggle('binderopen', open);
  if (open) setTimeout(() => audio.screenVo('collection', 'screen-binder2'), 400);   // SC9-E + SC14 revisit
  if (open && academyOn()) {   // v245 the Academy's one gentle hint: an orbiting glint on its pill after the 5th match (cleared by opening it, or 3 visits)
    const m = onboard.battlesPlayed();
    document.getElementById('binderGuide')?.classList.toggle('academy-glint', academy.wantGlint(m));
    academy.noteVisit(m);
  }
  if (open) {
    startCollTilt();
    paintStrip();
    binderEl.classList.toggle('view-car', binderView === 'car');
    binderViewBtn.setAttribute('aria-pressed', binderView === 'car' ? 'true' : 'false');
    binderViewBtn.setAttribute('aria-label', binderView === 'car' ? 'Show as a grid' : 'Show as a carousel');
    if (binderView === 'car') requestAnimationFrame(() => buildCarousel(car.index));
  } else {
    closeFocus(); clearCarousel();
  }
}
// The Collection's X (and Back) goes home: the old MY DECK fan behind it was
// the "double carousel", and the carousel now lives inside the Collection.
function closeCollection() {
  setBinder(false);
  if (shellUI.currentScreen() === 'fan') shellUI.go('title');
}

// ======================================================================
// COLLECTION 2 — the type strip, the view toggle, the carousel, the focus.
// Jack: "Simplicity wins." One header (title + owned count + three quiet
// icons), one strip (All · the six types · Duos · Fusions) that a sideways
// swipe on the grid also walks, and ONE focus view shared by both views: tap a
// card and it takes over the screen; tap flips it to the shared stat-sheet
// back; press and hold lifts its hologram (src/forgeholo.js, the same HOLO V2
// + light column the Collection viewer uses); swipe pages; X/Back/Escape close.
// ======================================================================
const binderTabsEl = document.getElementById('binderTabs');
const binderViewBtn = document.getElementById('binderViewBtn');
const carTrack = document.getElementById('bcTrack');
const carVirt = document.getElementById('bcVirt');
const carCap = document.getElementById('bcCap');
const cfEl = document.getElementById('cfocus');
const cfCard = document.getElementById('cfCard');
const cfInfo = document.getElementById('cfInfo');
const cf = { open: false, list: [], index: 0, from: null, holo: null, flipping: false, fx: null };
const car = { list: [], index: 0, mounted: new Map(), itemW: 0, itemH: 0, raf: 0, qr: 0,
  pos: 0, vel: 0, target: 0, w: 12, t: 0, drag: null, sv: 0, lt: 0, lp: 0, tickT: 0, thrown: false,
  stepX: 0.4, vis: 2, dragStep: 200, wheelT: 0, restEl: null, sfx: false };

binderTabsEl.innerHTML = '<button type="button" role="tab" data-tab="cards" data-type="">All</button>'
  + Object.keys(TYPES).map(t => `<button type="button" role="tab" data-tab="cards" data-type="${t}" style="--c:${TYPES[t].color}"><i aria-hidden="true"></i>${TYPES[t].label}</button>`).join('')
  + '<button type="button" role="tab" data-tab="duos" class="forged">Duos<span class="ct"></span><span class="dot"></span></button>'
  + '<button type="button" role="tab" data-tab="fusions" class="forged">Fusions<span class="ct"></span><span class="dot"></span></button>'
  // v257 BOSS TROPHIES: the shelf's tab, shown once a trophy is owned (src/trophies.js)
  + '<button type="button" role="tab" data-tab="trophies" hidden><svg class="tpCup" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3h10v3h3v2a5 5 0 0 1-4.6 5A5 5 0 0 1 13 15.7V18h3v3H8v-3h3v-2.3A5 5 0 0 1 8.6 13 5 5 0 0 1 4 8V6h3z"/></svg>Trophies</button>';
// v257 the Trophies tab: hidden until the first trophy (a new player's strip stays as it was)
let trophyMod = null;
const loadTrophies = () => trophyMod ? Promise.resolve(trophyMod) : import('./trophies.js').catch(staleReload).then(m => { m.ensureCss(); return (trophyMod = m); });   // (a bundled lazy chunk, like the Codex)
function paintTrophyTab() {
  const b = binderTabsEl.querySelector('[data-tab="trophies"]');
  const n = Object.keys(identity.trophies()).length;
  if (b) b.hidden = n === 0;
  if (n) loadTrophies().catch(() => {});        // (its CSS styles the tab's cup)
  if (!n && forgeUI.currentTab() === 'trophies') forgeUI.setTab('cards');
}
function paintStrip() {
  const tab = forgeUI.currentTab();
  binderEl.dataset.tab = tab;
  for (const b of binderTabsEl.children) {
    const on = b.dataset.tab === tab && (tab !== 'cards' || (b.dataset.type || '') === (BF.type || ''));
    b.classList.toggle('on', on);
    b.setAttribute('aria-selected', on ? 'true' : 'false');
    if (on && binderEl.classList.contains('open')) {
      const want = b.offsetLeft - (binderTabsEl.clientWidth - b.offsetWidth) / 2;
      binderTabsEl.scrollTo({ left: Math.max(0, want), behavior: REDUCED ? 'auto' : 'smooth' });
    }
  }
}
function selectStrip(b) {
  const tab = b.dataset.tab;
  if (tab === 'cards') {
    const type = b.dataset.type || null;
    const changed = forgeUI.currentTab() !== 'cards' || BF.type !== type;
    BF.type = type;
    if (forgeUI.currentTab() !== 'cards') forgeUI.setTab('cards');
    else if (changed) audio.ui('tick');
    if (changed) buildBinderModel({ reset: true });
  } else if (forgeUI.currentTab() === tab) {
    forgeUI.explain(tab);                     // the open section's own tab explains it
  } else {
    forgeUI.setTab(tab);
  }
  paintStrip();
  if (binderView === 'car') buildCarousel(0);
}
binderTabsEl.addEventListener('click', (e) => { const b = e.target.closest('button[data-tab]'); if (b) selectStrip(b); });
function stepStrip(dir) {
  const btns = [...binderTabsEl.children].filter(b => !b.hidden);
  const next = btns[btns.findIndex(b => b.classList.contains('on')) + dir];
  if (next) selectStrip(next);
}
// a sideways swipe over the grid flips to the neighbouring section
for (const el of [binderGrid, document.getElementById('forgePanel')].filter(Boolean)) {
  let g = null;
  el.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse') return;
    g = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now() };
  }, { passive: true });
  el.addEventListener('pointerup', (e) => {
    if (!g || g.id !== e.pointerId) return;
    const dx = e.clientX - g.x, dy = e.clientY - g.y, dt = performance.now() - g.t;
    g = null;
    if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.7 && dt < 800) stepStrip(dx < 0 ? 1 : -1);
  }, { passive: true });
  el.addEventListener('pointercancel', () => { g = null; });
}

// ---- view toggle -----------------------------------------------------
function collList() {
  const tab = forgeUI.currentTab();
  if (tab === 'cards') return BV ? BV.order : [];
  return forgeUI.list(tab).map(id => deckIndexById.get(id)).filter(i => i != null);
}
const deckIndexById = new Map(deck.map((d, i) => [d.id, i]));
function firstVisibleIndex() {
  if (forgeUI.currentTab() !== 'cards' || !BV) return 0;
  const top = binderGrid.scrollTop;
  let k = 0;
  for (const it of BV.items) {
    if (it.di == null) continue;
    if (it.y + BV.cellH * 0.5 >= top) return k;
    k++;
  }
  return 0;
}
function revealInGrid(index) {
  if (forgeUI.currentTab() !== 'cards' || !BV) {
    document.querySelector(`#forgePanel .fgT[data-id="${deck[car.list[index]]?.id}"]`)?.scrollIntoView({ block: 'center' });
    return;
  }
  const di = BV.order[index];
  const it = BV.items.find(x => x.di === di);
  if (!it) return;
  const top = binderGrid.scrollTop, h = binderGrid.clientHeight;
  if (it.y < top + 8 || it.y + BV.cellH > top + h - 8) binderGrid.scrollTop = Math.max(0, it.y - (h - BV.cellH) / 2);
}
function setBinderView(v) {
  const from = binderView;
  binderView = v;
  try { localStorage.setItem('nomadmon-coll-view', v); } catch { /* private mode */ }
  binderEl.classList.toggle('view-car', v === 'car');
  binderViewBtn.setAttribute('aria-pressed', v === 'car' ? 'true' : 'false');
  binderViewBtn.setAttribute('aria-label', v === 'car' ? 'Show as a grid' : 'Show as a carousel');
  if (v === 'car') buildCarousel(from === 'grid' ? firstVisibleIndex() : car.index);
  else {
    clearCarousel();
    if (BV && Math.abs(binderVirt.clientWidth - BV.availW) > 0.5) buildBinderModel();
    if (from === 'car') revealInGrid(car.index);
    renderBinderWindow(true);
  }
}
binderViewBtn.addEventListener('click', () => { audio.ui('tick'); setBinderView(binderView === 'car' ? 'grid' : 'car'); });
document.getElementById('binderSearchBtn').addEventListener('click', () => {
  const on = !binderEl.classList.contains('tools-open');
  binderEl.classList.toggle('tools-open', on);
  document.getElementById('binderSearchBtn').setAttribute('aria-expanded', String(on));
  if (on) setTimeout(() => document.getElementById('binderSearch').focus({ preventScroll: true }), 30);
  if (BV) requestAnimationFrame(() => buildBinderModel());
});

// ---- carousel ---------------------------------------------------------
// The squad screen's carousel feel (src/deckforge.js), on the Collection's
// windowed list: the position is a float card index; a drag moves it 1:1, a
// release throws it with the finger's velocity into ONE critically damped
// spring that lands on a whole card. Every card that passes the centre gives
// the title carousel's tock + a light haptic tick (at most one per 45 ms, so a
// fling never machine-guns), the throw's landing a firmer one; iOS has no
// vibrate and degrades silently (haptics.js). Neighbours fan out on a gentle
// drum (scaled down, turned, receding) at FULL colour: in the Collection, dim
// means "not owned" and nothing else. Only the ~2 screens of cards in reach
// are mounted (at most two new ones a frame); per frame only transform,
// opacity and z-index are written; and only the card AT REST in the centre
// runs living layers (.rest, the v334 squad-screen lesson: a champion frame's
// animations on every card of a moving strip blew a tablet's tile budget).
const CAR_THROW = 0.3, CAR_ZETA = 0.74, CAR_FOCUS = 1.1, CAR_GAP1 = 0.74;
function stopCar() {
  if (car.raf) cancelAnimationFrame(car.raf);
  if (car.qr) cancelAnimationFrame(car.qr);
  car.raf = 0; car.qr = 0;
}
function clearCarousel() {
  stopCar();
  car.drag = null; car.restEl = null;
  for (const el of car.mounted.values()) el.remove();
  car.mounted.clear();
}
const carFanX = (a) => car.itemW * (CAR_GAP1 * Math.min(a, 1) + car.stepX * Math.max(0, a - 1));
function carFace(di) {
  const d = deck[di];
  if (isChampionCard(d)) { const el = makeChampionCell(di); el.classList.add('bc-item'); return el; }
  if (d.forged) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'bc-item bc-forged';
    el.innerHTML = forgeUI.faceHtml(d.id);
    forgeUI.wireFaces(el);   // its living video (a Duo's liquid cycle) plays while on screen
    return el;
  }
  const el = makeBinderCell(di);
  el.classList.add('bc-item');
  return el;
}
function carCaption() {
  const di = car.list[car.index];
  const d = deck[di];
  if (!d) { carCap.innerHTML = '<p class="bc-empty">Nothing here yet.</p>'; return; }
  if (isChampionCard(d)) { carCap.innerHTML = championCaption(d) + `<div class="bc-n">${car.index + 1} / ${car.list.length}</div>`; return; }
  if (d.forged) { carCap.innerHTML = forgeUI.captionHtml(d.id); return; }
  const { n, level, power } = cardStats(d);
  carCap.innerHTML = `<div class="bc-name">${displayName(d.name)}</div>`
    + `<div class="bc-sub"><span style="color:${RARITY[d.rarity].color}">${d.altOf ? 'Mythic-Alt' : RARITY[d.rarity].label}</span>`
    + `<span class="bc-type">${typeGlyph(d.type, 13)}${TYPES[d.type].label}</span></div>`
    + `<div class="bc-stat"><b>${power}</b><span>PWR</span><b>${n ? level : '—'}</b><span>LV</span>`
    + `${n > 1 ? `<b>×${n}</b><span>owned</span>` : n ? '<span class="bc-own">Owned</span>' : `<span class="bc-miss">${NOT_OWNED_ICON}Not owned yet</span>`}</div>`
    + `<div class="bc-n">${car.index + 1} / ${car.list.length}</div>`;
}
function buildCarousel(index = car.index) {
  if (!binderEl.classList.contains('open') && !carTrack.clientWidth) return;
  clearCarousel();
  if (!car.sfx) { car.sfx = true; audio.loadSfx(['tock']).catch(() => {}); }
  car.list = collList();
  const W = carTrack.clientWidth || innerWidth, H = carTrack.clientHeight || innerHeight * 0.6;
  // the centred card (CAR_FOCUS x) fills the strip's height; wide screens show a long fan either side
  let h = Math.min((H - 24) / CAR_FOCUS, 600), w = h / CARD_RATIO;
  const maxW = W * (W < 600 ? 0.66 : W >= 900 ? 0.27 : 0.38);
  if (w > maxW) { w = maxW; h = w * CARD_RATIO; }
  car.itemW = w; car.itemH = h;
  car.stepX = Math.max(0.2, Math.min(0.5, ((W / 2 / w - 0.25) / 0.755 - CAR_GAP1) / 3));
  car.dragStep = w * 0.62;                  // px of drag per card
  let vis = 1;
  const maxVis = W >= 900 ? 6 : 4;
  while (vis < maxVis && carFanX(vis) * 0.75 - w * 0.2 < W / 2) vis++;
  car.vis = vis;
  carVirt.style.setProperty('--kw', `${w.toFixed(1)}px`);
  const n = car.list.length;
  car.index = Math.max(0, Math.min(n - 1, index));
  car.pos = car.target = car.index; car.vel = 0; car.sv = 0; car.lt = 0;
  renderCarousel(true);
  carSettled();
}
function renderCarousel(force = false) {
  const n = car.list.length;
  if (!n) { clearCarousel(); carCaption(); return; }
  const now = performance.now();
  if (car.lt) {   // the strip's own speed leans the centred card a few degrees
    const dt = Math.max(0.001, (now - car.lt) / 1000);
    car.sv += (Math.max(-30, Math.min(30, (car.pos - car.lp) / dt)) - car.sv) * Math.min(1, dt * 14);
  }
  car.lt = now; car.lp = car.pos;
  const lean = Math.max(-1, Math.min(1, car.sv / 9));
  const near = Math.max(0, Math.min(n - 1, Math.round(car.pos)));
  const R = car.vis + 1;                    // +1: the next card is mounted before it fades in
  const lo = Math.max(0, near - R), hi = Math.min(n - 1, near + R);
  for (const [i, el] of car.mounted) if (i < lo || i > hi) { if (el === car.restEl) car.restEl = null; el.remove(); car.mounted.delete(i); }
  // mount nearest-first, at most two new cards a frame; the rest follow next frame
  let budget = force ? 99 : 2, pending = false;
  const idx = [];
  for (let i = lo; i <= hi; i++) idx.push(i);
  idx.sort((a, b) => Math.abs(a - car.pos) - Math.abs(b - car.pos));
  for (const i of idx) {
    let el = car.mounted.get(i);
    if (!el && budget <= 0) { pending = true; continue; }
    if (!el) {
      budget--;
      el = carFace(car.list[i]);
      el.dataset.ci = i;
      el.style.cssText += `;position:absolute;left:${(-car.itemW / 2).toFixed(1)}px;top:${(-car.itemH / 2).toFixed(1)}px;width:${car.itemW.toFixed(1)}px;height:${car.itemH.toFixed(1)}px`;
      carVirt.appendChild(el);
      car.mounted.set(i, el);
    }
    const o = i - car.pos, a = Math.abs(o), sg = o < 0 ? -1 : 1, nr = Math.min(a, 1), far = Math.max(0, a - 1);
    const x = sg * carFanX(a), z = -car.itemW * (0.5 * nr + 0.2 * far);
    const sc = 1 + (CAR_FOCUS - 1) * (1 - nr) * (1 - nr);
    const ry = sg * Math.min(52, 34 * nr + 4 * far) - lean * 8 * (1 - nr);
    el.style.transform = `translate3d(${x.toFixed(1)}px,0,${z.toFixed(1)}px) rotateY(${ry.toFixed(2)}deg) scale(${sc.toFixed(3)})`;
    const op = (a <= car.vis ? 1 : Math.max(0, 1 - (a - car.vis) / 0.7)).toFixed(2);   // only the fan's far edge fades
    if (el._op !== op) { el._op = op; el.style.opacity = op; }
    const zi = String(1000 - Math.round(a * 10));
    if (el._z !== zi) { el._z = zi; el.style.zIndex = zi; }
    el.tabIndex = i === near ? 0 : -1;
  }
  if (near !== car.index) { car.index = near; carOnCentre(); }
  else if (force || !carCap.childElementCount) carCaption();
  if (pending && !car.qr) car.qr = requestAnimationFrame(() => { car.qr = 0; renderCarousel(); });
}
// a new card in the centre: the detent (tock + tick), the caption, and the old centre stops living
function carOnCentre() {
  carCaption();
  if (car.restEl) { car.restEl.classList.remove('rest'); car.restEl = null; }
  const now = performance.now();
  if (now - car.tickT > 45) {
    car.tickT = now;
    haptics.buzz(5);
    audio.play('tock', { gain: 0.2, rate: 1 + Math.min(0.3, Math.abs(car.sv) * 0.012) });
  }
}
function carSettled() {
  const el = car.mounted.get(car.index);
  if (car.restEl && car.restEl !== el) car.restEl.classList.remove('rest');
  car.restEl = el || null;
  if (el) el.classList.add('rest');
}
function carTick(now) {
  car.raf = 0;
  const dt = car.t ? Math.min(0.034, (now - car.t) / 1000) : 1 / 60;
  car.t = now;
  if (car.drag) return;
  const x = car.pos - car.target;
  car.vel += (-car.w * car.w * x - 2 * CAR_ZETA * car.w * car.vel) * dt;
  car.pos += car.vel * dt;
  if (Math.abs(car.pos - car.target) < 0.0015 && Math.abs(car.vel) < 0.02) {
    car.pos = car.target; car.vel = 0; car.sv = 0;
    renderCarousel(); carSettled();
    if (car.thrown) { car.thrown = false; haptics.buzz(12); }
    return;
  }
  renderCarousel();
  car.raf = requestAnimationFrame(carTick);
}
// spring to card i (instant = jump there, e.g. under the opaque focus view)
function carGo(i, instant = false) {
  const n = car.list.length;
  if (!n) return;
  car.target = Math.max(0, Math.min(n - 1, Math.round(i)));
  car.w = Math.abs(car.target - car.pos) > 3 ? 8 : 12;   // long throws ease out a little slower
  car.thrown = !instant && (Math.abs(car.target - car.pos) > 0.02 || Math.abs(car.vel) > 0.3);
  if (instant || REDUCED) { stopCar(); car.pos = car.target; car.vel = 0; renderCarousel(true); carSettled(); return; }
  if (!car.raf) { car.t = 0; car.raf = requestAnimationFrame(carTick); }
}
function carQueue() { if (!car.qr) car.qr = requestAnimationFrame(() => { car.qr = 0; renderCarousel(); }); }
const carRubber = (o) => 0.6 * (1 - 1 / (o * 0.9 + 1));
carTrack.addEventListener('pointerdown', (e) => {
  if (!car.list.length || (e.pointerType === 'mouse' && e.button !== 0)) return;
  if (car.raf) { cancelAnimationFrame(car.raf); car.raf = 0; }
  car.vel = 0;
  try { carTrack.setPointerCapture(e.pointerId); } catch { /* synthetic */ }
  const t = performance.now();
  car.drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY, pos0: car.pos, t0: t, axis: null,
    el: e.target.closest('.bc-item'), s: [[t, e.clientX]] };
});
carTrack.addEventListener('pointermove', (e) => {
  const g = car.drag;
  if (!g || e.pointerId !== g.id) return;
  const dx = e.clientX - g.x0, dy = e.clientY - g.y0;
  if (!g.axis && Math.hypot(dx, dy) > 8) g.axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
  if (g.axis !== 'x') return;
  const now = performance.now();
  g.s.push([now, e.clientX]);
  while (g.s.length > 2 && now - g.s[0][0] > 90) g.s.shift();
  let p = g.pos0 - dx / car.dragStep;
  const max = car.list.length - 1;
  if (p < 0) p = -carRubber(-p); else if (p > max) p = max + carRubber(p - max);
  car.pos = p;
  carQueue();      // input only moves the position; the frame draws it
});
function carUp(e) {
  const g = car.drag;
  if (!g || e.pointerId !== g.id) return;
  car.drag = null;
  if (e.type === 'pointercancel' || g.axis === 'y') { carGo(Math.round(car.pos)); return; }
  if (!g.axis) {
    // tap: the centred card opens; a neighbour slides to the centre first
    const i = g.el ? +g.el.dataset.ci : NaN;
    if (performance.now() - g.t0 < 600 && Number.isFinite(i)) {
      if (i === car.index && Math.abs(car.pos - i) < 0.05) {
        carGo(i, true);
        binderSheetScroll = binderGrid.scrollTop;
        openFocus(car.list, i, g.el);
      } else carGo(i);
    } else carGo(Math.round(car.pos));
    return;
  }
  const [t0, x0] = g.s[0];
  const dt = (performance.now() - t0) / 1000;
  const v = dt > 0.005 ? -(e.clientX - x0) / dt / car.dragStep : 0;   // cards per second
  car.vel = Math.max(-60, Math.min(60, v));
  carGo(Math.round(car.pos + car.vel * CAR_THROW));
}
carTrack.addEventListener('pointerup', carUp);
carTrack.addEventListener('pointercancel', carUp);
// taps are handled on pointerup: a tile's own click (the grid's openBinderSheet) never fires in here.
// A keyboard activation (Enter / Space on the centred card: detail 0) opens it.
carTrack.addEventListener('click', (e) => {
  const el = e.target.closest('.bc-item');
  if (!el) return;
  e.preventDefault(); e.stopPropagation();
  if (e.detail === 0 && +el.dataset.ci === car.index) {
    binderSheetScroll = binderGrid.scrollTop;
    openFocus(car.list, car.index, el);
  }
}, true);
carTrack.addEventListener('wheel', (e) => {
  if (!car.list.length) return;
  e.preventDefault();
  let d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
  if (e.deltaMode === 1) d *= 40;
  if (car.raf) { cancelAnimationFrame(car.raf); car.raf = 0; }
  car.vel = 0;
  car.pos = Math.max(-0.3, Math.min(car.list.length - 0.7, car.pos + d / (car.dragStep * 0.6)));
  carQueue();
  clearTimeout(car.wheelT);
  car.wheelT = setTimeout(() => carGo(Math.round(car.pos)), 110);
}, { passive: false });
carTrack.addEventListener('keydown', (e) => {
  if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
    e.preventDefault(); carGo(Math.round(car.target) + (e.key === 'ArrowRight' ? 1 : -1));
  }
});
addEventListener('resize', () => { if (binderView === 'car' && binderEl.classList.contains('open')) buildCarousel(car.index); });

// ---- focus --------------------------------------------------------------
// INK DUEL champion: name, type, and the line the player reads — the live power, the star cost, the
// bonus over the top-three average (roll + ranks) and what it is the average of.
function championCaption(d) {
  const L = d.live();
  return `<div class="bc-name">${displayName(d.name)}</div>`
    + `<div class="bc-sub"><span style="color:${d.color || '#f0c463'}">Champion</span><span class="bc-type">${typeGlyph(d.type, 13)}${TYPES[d.type].label}</span></div>`
    + `<div class="bc-stat"><b>${effectivePwr(d, 1)}</b><span>PWR</span><b>\u2605${d.stars}</b><span>cost</span>`
    + `<b>${L.bonus < 0 ? '\u2212' : '+'}${Math.abs(Math.round(L.bonus * 100))}%</b><span>over top 3</span></div>`;
}
function focusFaceHtml(d) {
  if (isChampionCard(d)) {
    return `<span class="cf-art">${championMediaHtml(d, { size: 'full', autoplay: true })}<i class="cf-glare" aria-hidden="true"></i></span>`
      + `<span class="ct-pw"><b>${effectivePwr(d, 1)}</b><span><i>PWR</i><i>\u2605${d.stars}</i></span></span>`
      + `<span class="ct-foot"><span class="bname">${displayName(d.name)}</span><span class="ct-meta">${championMetaHtml(d, championGrand().grand)}</span></span>`
      + championFrameHtml(d, { grand: championGrand().grand });
  }
  if (d.forged) return forgeUI.faceHtml(d.id, 'full');
  const { n, level, power } = cardStats(d);
  const rarLabel = d.altOf ? 'Mythic-Alt' : RARITY[d.rarity].label;
  const foil = n && (d.rarity !== 'common' && d.rarity !== 'rare') ? '<i class="cf-foil" aria-hidden="true"></i>' : '';
  return `<span class="cf-art"><img class="cf-thumb" src="${artSrc(d, V, 'thumb')}" alt="" style="object-position:${thumbPos(d)}">`
    + `<img class="cf-full" alt="" decoding="async" src="${artSrc(d, V)}" style="object-position:${thumbPos(d)}">${foil}<i class="cf-glare" aria-hidden="true"></i></span>`
    + `<span class="ct-pw"><b>${power}</b><span><i>PWR</i><i>${n ? `LV ${level}` : '—'}</i></span></span>`
    + `<span class="ct-foot"><span class="bname">${displayName(d.name)}</span><span class="ct-meta"><span class="ct-rar">${rarLabel}</span>${ownedDots(n)}</span></span>`;
}
function focusInfoHtml(d) {
  if (isChampionCard(d)) {
    const L = d.live(), st = L.state || { rank: 0 };
    const names = L.top.map(id => cardsById[id]).filter(Boolean).map(c => displayName(c.name));
    return `<div class="cf-sub"><span style="color:${d.color || '#f0c463'}">Champion</span>`
      + `<span class="bc-type">${typeGlyph(d.type, 13)}${TYPES[d.type].label}</span></div>`
      + `<div class="cf-stats"><div class="cf-pw"><b>${effectivePwr(d, 1)}</b><span>Power</span></div>`
      + `<div class="cf-own"><b>${L.bonus < 0 ? '\u2212' : '+'}${Math.abs(Math.round(L.bonus * 100))}%</b><span>over top 3</span></div>`
      + `<div class="cf-own"><b>\u2605${d.stars}</b><span>Cost</span></div></div>`
      + `<p class="cf-hint">${names.length ? `Averages ${names.join(' · ')}` : 'Grows with your collection'}</p>`
      + `<p class="cf-hint">${st.rank ? `Rank ${st.rank} · ` : ''}Win Ink Duels with it to rank up</p>`;
  }
  if (d.forged) return forgeUI.focusInfoHtml(d.id);
  const { n, level, power } = cardStats(d);
  const p = levelProgress(identity.cardProgress(d.id).xp);
  const holo = holoStatusOf(d, level);
  const lift = holoCapable(d);
  return `<div class="cf-sub"><span style="color:${RARITY[d.rarity].color}">${d.altOf ? 'Mythic-Alt' : RARITY[d.rarity].label}</span>`
    + `<span class="bc-type">${typeGlyph(d.type, 13)}${TYPES[d.type].label}</span>${holo ? '<span class="cf-holo">Holo</span>' : ''}</div>`
    + `<div class="cf-stats"><div class="cf-pw"><b>${power}</b><span>Power</span></div>`
    + `<div class="cf-lv"><b>${n ? `Level ${level}` : 'Level 1'}</b><span class="cf-xp"><i style="width:${n ? Math.round(p.ratio * 100) : 0}%"></i></span>`
    + `<span>${!n ? 'Not owned yet' : p.maxed ? 'Max level' : `${p.current} / ${p.needed} XP`}</span></div>`
    + `<div class="cf-own"><b>${n ? `×${n}` : '0'}</b><span>${n === 1 ? 'Copy' : 'Copies'}</span></div></div>`
    + `<p class="cf-hint">Tap to flip${lift ? ' · Hold to lift the hologram' : ''}</p>`;
}
function paintFocus(dir = 0) {
  const d = deck[cf.list[cf.index]];
  if (!d) return;
  cfEl.style.setProperty('--rc', RARITY[d.rarity].color);
  cfEl.setAttribute('aria-label', `${d.name}, card ${cf.index + 1} of ${cf.list.length}`);
  cfCard.className = 'cf-card' + (isChampionCard(d) ? ' cf-champ' : d.forged ? ` cf-forged ${d.forged}` : ` r-${d.rarity}`);
  if (isChampionCard(d)) cfCard.style.setProperty('--cc', d.color || '#f0c463');
  lensG.base = null; lensG.p = 0;        // the Duo lens centres on how the device is held now
  cfCard.innerHTML = focusFaceHtml(d);
  if (isChampionCard(d)) wireForgedVideos(cfCard);
  else if (d.forged) forgeUI.wireFaces(cfCard);
  // FORGE PRISM: an owned Fusion in focus wears the forge's rainbow — the
  // shared Fusion FX layer (prismatic border, aura, god rays) on its face
  const fxHost = d.forged === 'fusion' && cfCard.querySelector('.fxF') ? cfCard.querySelector('.fgArt') : null;
  if (cf.fx && cf.fx !== fxHost) detachForgedFx(cf.fx);
  cf.fx = fxHost;
  if (fxHost) attachForgedFx(fxHost, d, { level: 0.85, prism: true });
  const full = cfCard.querySelector('.cf-full');
  if (full) {
    const show = () => full.classList.add('on');
    if (full.complete && full.naturalWidth) show(); else full.addEventListener('load', show, { once: true });
  }
  cfInfo.innerHTML = focusInfoHtml(d);
  duoFocusIntro();
  if (dir && !REDUCED) {
    cfCard.animate([{ transform: `translateX(${dir * 46}px)`, opacity: 0.2 }, { transform: 'none', opacity: 1 }],
      { duration: 240, easing: 'cubic-bezier(.2,.9,.3,1)' });
  }
}
function openFocus(list, index, fromEl = null) {
  if (!list.length) return;
  cf.list = list.slice(); cf.index = index; cf.from = fromEl; cf.open = true;
  paintFocus();
  cfEl.classList.add('on');
  document.body.classList.add('cfocus');
  audio.ui('tick');
  if (!REDUCED) cfCard.animate([{ transform: 'scale(.86)', opacity: 0 }, { transform: 'none', opacity: 1 }],
    { duration: 260, easing: 'cubic-bezier(.2,.9,.3,1)' });
  document.getElementById('cfClose').focus({ preventScroll: true });
}
function stepFocus(dir) {
  if (cf.list.length < 2) return;
  endFocusHolo();
  cf.index = (cf.index + dir + cf.list.length) % cf.list.length;
  audio.ui('tick');
  paintFocus(dir);
}
function closeFocus() {
  if (!cf.open) return;
  endFocusHolo();
  if (binderSheetIndex >= 0) {           // the back is up: put it away silently
    cf.open = false;
    closeBinderSheet();
  }
  cf.open = false;
  duoFocusIntroStop();
  if (cf.fx) { detachForgedFx(cf.fx); cf.fx = null; }
  cfStage.style.transform = ''; ctTilt.x = 0; ctTilt.y = 0;
  cfEl.classList.remove('on');
  document.body.classList.remove('cfocus', 'cf-back');
  const di = cf.list[cf.index];
  if (binderView === 'car') { if (car.list[car.index] !== di) { const i = car.list.indexOf(di); if (i >= 0) carGo(i, true); } }
  else if (forgeUI.currentTab() === 'cards' && BV) { binderGrid.scrollTop = binderSheetScroll; revealInGrid(BV.order.indexOf(di)); renderBinderWindow(); }
  const back = binderView === 'car' ? carVirt.querySelector(`.bc-item[data-ci="${car.index}"]`)
    : (deck[di]?.forged ? document.querySelector(`#forgePanel .fgT[data-id="${deck[di].id}"]`) : binderGrid.querySelector(`.bcard[data-di="${di}"]`));
  try { (back || (cf.from?.isConnected ? cf.from : null))?.focus({ preventScroll: true }); } catch { /* gone */ }
  cf.from = null;
}
// tap = flip to the shared stat-sheet back (a quarter turn out, the sheet a quarter turn in)
function flipToBack() {
  const d = deck[cf.list[cf.index]];
  if (!d || cf.flipping) return;
  if (forgeUI.isHidden(d.id)) {                        // a locked Fusion keeps its secret
    cfCard.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(-8px)' }, { transform: 'translateX(7px)' }, { transform: 'none' }], { duration: 260 });
    return;
  }
  cf.flipping = true;
  audio.ui('cardflip');
  const go = () => {
    cf.flipping = false;
    document.body.classList.add('cf-back');
    showBinderSheetAt(cf.index);
    if (!REDUCED) ssPanel.animate([{ transform: 'perspective(1400px) rotateY(-90deg)', opacity: 0.4 }, { transform: 'perspective(1400px) rotateY(0deg)', opacity: 1 }],
      { duration: 280, easing: 'cubic-bezier(.2,1.1,.4,1)' });
  };
  if (REDUCED) { go(); return; }
  cfCard.animate([{ transform: 'perspective(1400px) rotateY(0deg)' }, { transform: 'perspective(1400px) rotateY(90deg)' }],
    { duration: 160, easing: 'cubic-bezier(.5,0,1,.6)' }).onfinish = go;
}
function flipToFront(backIndex) {
  audio.ui('cardflip', { rate: 0.94 });
  document.body.classList.remove('cf-back');
  if (backIndex >= 0 && backIndex !== cf.index) { cf.index = backIndex; paintFocus(); }
  if (!REDUCED) cfCard.animate([{ transform: 'perspective(1400px) rotateY(-90deg)' }, { transform: 'perspective(1400px) rotateY(0deg)' }],
    { duration: 260, easing: 'cubic-bezier(.2,1.2,.4,1)' });
  try { cfCard.focus({ preventScroll: true }); } catch { /* fine */ }
}
// hold = the hologram lifts out of the focused card; release lowers it
function startFocusHolo() {
  const d = deck[cf.list[cf.index]];
  if (!d || d.forged || !holoCapable(d)) return;
  const art = cfCard.querySelector('.cf-art');
  if (!art) return;
  const full = cfCard.querySelector('.cf-full.on');
  const url = full ? full.src : artSrc(d, V);
  const r = art.getBoundingClientRect();
  const fx = (d.thumbFocal && d.thumbFocal.x) ?? d.focalX ?? 0.5, fy = (d.thumbFocal && d.thumbFocal.y) ?? 0.42;
  endFocusHolo();
  haptics.buzz(10);
  audio.playHoloPull({ calm: true });
  cfEl.classList.add('holding');
  cf.holo = forgeHolo(cfEl, d, url, { left: r.left, top: r.top, width: r.width, height: r.height }, fx, fy,
    (TYPES[d.type] || {}).color || '#b06bff', perfMode);
}
function endFocusHolo() {
  cfEl.classList.remove('holding');
  if (cf.holo) { const h = cf.holo; cf.holo = null; h.end(); }
}
// ---- tilt: on by default. The focused card leans with the device (the shared,
// smoothed + baselined gyro above; the cursor on desktop) and its light slides
// across it; grid tiles get a very subtle shared art parallax, gyro only. One
// rAF loop runs while the Collection is open; per frame it writes ONE
// transform on the focus stage + one on its glare, or a pair of CSS vars that
// the visible tiles' (layer-promoted, only while tilting) art reads.
const cfStage = cfEl.querySelector('.cf-stage');
// v240 DUO LENS, the invitation: an owned Duo opening in focus plays one gentle
// A -> B -> A sweep. Tilt is the hero, so: with no live tilt on iOS (motion
// permission not granted yet) a pulsing, wordless phone-rocking button sits on
// the card — tapping it asks for the permission and turns tilt on (then never
// shows again); denied, or no sensor at all -> the drag chevrons. With tilt
// live, the rocking-phone hint shows the first 3 times only. Any tilt / drag
// cancels the sweep instantly (forged-card duoLensTilt).
const IOS_ASK = typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function';
const lsNum = (k) => { try { return +localStorage.getItem(k) || 0; } catch { return 0; } };
const lsPut = (k, v) => { try { localStorage.setItem(k, String(v)); } catch { /* private mode */ } };
// the iOS motion permission ask (a tap is the gesture): granted -> tilt on; denied -> remembered
async function askTilt() {
  let ok = false;
  try { ok = (await DeviceOrientationEvent.requestPermission()) === 'granted'; } catch { /* no gesture / unsupported */ }
  if (ok) { dispatchEvent(new Event('nm-gyro-granted')); lensG.base = null; } else lsPut('nm-tilt-denied', 1);
  return ok;
}
function duoFocusIntroStop() {
  clearTimeout(cf.lensT); clearTimeout(cf.hintT);
  if (cf.sweep) { cf.sweep.stop(true); cf.sweep = null; }
  const h = cfCard.querySelector('.dTilt'); if (h) h.remove();
}
function duoFocusIntro() {
  duoFocusIntroStop();
  duoLensFollow(null);                  // focus drives its own lens (collTiltFrame)
  const art = cfCard.querySelector('.fgArt[data-duo]');
  if (!art || !forgeUI.ownsForged(art.dataset.duo)) return;
  cf.lensT = setTimeout(() => { if (cf.open && art.isConnected) cf.sweep = forgeUI.duoSweep(art, { dur: 1600 }); }, REDUCED ? 150 : 420);
  const hint = (ask) => {
    const el = document.createElement(ask ? 'button' : 'span');
    el.className = 'dTilt';
    el.innerHTML = ask || gyroActive ? TILT_SVG : DRAG_SVG;
    if (ask) { el.type = 'button'; el.setAttribute('aria-label', 'Turn on tilt'); } else el.setAttribute('aria-hidden', 'true');
    art.appendChild(el);
    return el;
  };
  const fade = (el, ms) => { cf.hintT = setTimeout(() => { el.classList.add('out'); cf.hintT = setTimeout(() => el.remove(), 480); }, ms); };
  if (IOS_ASK && !gyroActive && !REDUCED && !lsNum('nm-tilt-denied')) {
    const b = hint(true);
    b.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (await askTilt()) { b.remove(); return; }
      b.remove();
      if (art.isConnected) fade(hint(false), 2600);
    });
    return;
  }
  const n = lsNum('nm-lens-hints');
  if (n >= 3) return;
  lsPut('nm-lens-hints', n + 1);
  fade(hint(false), 2600);
}
const cfPtr = { x: 0, y: 0, t: 0 };
// v12c Duo LENTICULAR input -> lens position p (-1 face A .. 1 face B):
//  gyro    side tilt (screenRollDeg) against a baseline taken when the card
//          opens and slowly following the resting hold; ±LENS_DEG flips fully
//  finger  a sideways drag across the card scrubs A<->B (cfScrub)
//  cursor  the pointer's side offset (desktop)
// v240: ±10° flips fully (a modest tilt is the whole effect); the slow
// re-centring stays, so a resting hold never drifts the lens.
// dupduo: LENS_DEG and the baseline / easing are lens-tilt.js's (time-based now: the per-frame constants re-centred
// twice as fast on a 120 Hz phone)
const lensT = makeLensTracker(LENS_DEG);
const lensG = lensT.state;
const cfScrub = { on: false, p: 0 };
// the turn about the SCREEN's vertical axis from the gyroscope's rotation rate, integrated: the one reading that
// works with the device held upright (a tablet at a desk) -- gravity cannot see that turn, and screenRollDeg
// wraps past vertical. Android sends it without a prompt; without it the lens falls back to screenRollDeg.
// dupduo: iOS WebKit names rotationRate's axes x / y / z (alpha / beta / gamma), not the spec's z / x / y: reading
// `gamma` there integrated the spin in the screen's plane, so a real side tilt barely moved the lens. The axis order
// is guessed from the platform, then learned from the gravity-derived side tilt (lens-tilt.js makeYawAxis).
const lensYaw = { deg: 0, t: 0, at: -1e9, w: null };
const yawAxis = makeYawAxis(IOS_ASK && /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent));
addEventListener('devicemotion', (e) => {
  const rr = e.rotationRate;
  if (!rr || !Number.isFinite(rr.alpha) || !Number.isFinite(rr.beta) || !Number.isFinite(rr.gamma)) return;
  const now = performance.now(), dt = lensYaw.t ? Math.min(0.1, (now - lensYaw.t) / 1000) : 0;
  lensYaw.t = now; lensYaw.at = now;
  const a = screenAngle();
  const dS = yawRate(rr, a, false) * dt, dX = yawRate(rr, a, true) * dt;
  // learn the axis order over ~150 ms windows against the gravity side tilt (skipped near upright, where it is ill-conditioned)
  if (gyroSource && gyroSource !== 'motion') {
    const R = Math.PI / 180, up = Math.abs(Math.cos(gyroRaw.beta * R) * Math.cos(gyroRaw.gamma * R)) < 0.35;
    const w = lensYaw.w;
    if (up || !w || w.a !== a) lensYaw.w = up ? null : { a, t: now, roll: screenRollDeg(), s: 0, x: 0 };
    else {
      w.s += dS; w.x += dX;
      if (now - w.t >= 150) { yawAxis.learn(w.s, w.x, ((screenRollDeg() - w.roll + 540) % 360) - 180); lensYaw.w = null; }
    }
  }
  lensYaw.deg += yawAxis.xyz ? dX : dS;
});
function gyroLens(reset) {
  const src = performance.now() - lensYaw.at < 300 ? 'yaw' : 'roll';
  const r = lensT.read(src === 'yaw' ? lensYaw.deg : screenRollDeg(), src, performance.now(), reset);
  return { p: r.p, driving: r.driving, gyro: true };
}
// every other Duo on screen (forge reveal, Collection grid, squad carousel) follows the same tilt
setLensGyro({ live: () => gyroActive && !REDUCED, read: gyroLens });
function lensInput(held, src, tx, ty) {
  if (held) return { p: 0, driving: false };
  if (cfScrub.on) return { p: cfScrub.p, driving: true };
  if (gyroActive) return gyroLens(false);
  // a resting cursor off-centre holds the lens, but only a MOVING one cuts the invitation sweep short
  if (cf.sweep && !cf.sweep.done) return { p: ctTilt.y / 0.13, driving: performance.now() - cfPtr.t < 100 };
  return { p: ctTilt.y / 0.13, driving: Math.abs(src.y) > 0.035 || Math.abs(tx - ctTilt.x) + Math.abs(ty - ctTilt.y) > 0.01 };
}
cfEl.addEventListener('pointermove', (e) => {
  if (e.pointerType !== 'mouse') return;
  cfPtr.y = Math.max(-0.5, Math.min(0.5, (e.clientX / innerWidth - 0.5) * 0.9));
  cfPtr.x = Math.max(-0.5, Math.min(0.5, (e.clientY / innerHeight - 0.5) * 0.9));
  cfPtr.t = performance.now();
}, { passive: true });
cfEl.addEventListener('pointerleave', () => { cfPtr.x = 0; cfPtr.y = 0; });
function collTiltFrame() {
  ctTilt.raf = 0;
  if (!binderEl.classList.contains('open') || REDUCED) {
    if (ctTilt.host) { ctTilt.host.style.transform = ''; ctTilt.host = null; }
    return;
  }
  const src = gyroActive ? gyroTilt : cfPtr;
  if (cf.open) {
    const held = cfEl.classList.contains('holding') || cf.flipping || binderSheetIndex >= 0;
    const tx = held ? 0 : src.x, ty = held ? 0 : src.y;
    ctTilt.x += (tx - ctTilt.x) * 0.14;
    ctTilt.y += (ty - ctTilt.y) * 0.14;
    cfStage.style.transform = `perspective(1100px) rotateX(${(-ctTilt.x * 15).toFixed(2)}deg) rotateY(${(ctTilt.y * 17).toFixed(2)}deg)`;
    // v12b LENTICULAR: a Duo in focus turns from face A to face B as it tilts side to side
    if (cfCard.classList.contains('duo')) { const L = lensInput(held, src, tx, ty); forgeUI.duoTilt(cfCard, L.p, L.driving, L); }
    const glare = cfCard.querySelector('.cf-glare');
    if (glare) glare.style.transform = `translate3d(${(ctTilt.y * 46).toFixed(1)}%, ${(ctTilt.x * 46).toFixed(1)}%, 0)`;
  } else if (gyroActive) {
    // ONE transform on the visible view's viewport-sized scroller: the whole
    // grid floats a few px against the header. Per-tile art parallax was
    // measured and rejected: ~30 promoted tiles re-layering while scrolling
    // cost 10+ frames over 33 ms per fling at 4x CPU; this costs nothing.
    const px = -gyroTilt.y * 8, py = -gyroTilt.x * 8;
    if (Math.abs(px - ctTilt.px) > 0.2 || Math.abs(py - ctTilt.py) > 0.2) {
      ctTilt.px = px; ctTilt.py = py;
      const host = binderView === 'car' ? carTrack : forgeUI.currentTab() === 'cards' ? binderGrid : document.getElementById('forgePanel');
      if (ctTilt.host && ctTilt.host !== host) ctTilt.host.style.transform = '';
      ctTilt.host = host;
      if (host) host.style.transform = `translate3d(${px.toFixed(1)}px, ${py.toFixed(1)}px, 0)`;
    }
  }
  ctTilt.raf = requestAnimationFrame(collTiltFrame);
}
function startCollTilt() { if (!ctTilt.raf && !REDUCED) ctTilt.raf = requestAnimationFrame(collTiltFrame); }

{
  const HOLD_MS = 380;
  let g = null;
  cfEl.addEventListener('pointerdown', (e) => {
    if (!cf.open || (e.pointerType === 'mouse' && e.button !== 0) || e.target.closest('button, a')) return;
    const onCard = !!e.target.closest('#cfCard');
    const duo = onCard && !!cfCard.querySelector('.fgArt[data-duo]');     // an unlocked/ready Duo: drag scrubs its lens
    g = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now(), onCard, duo, held: false, dx: 0 };
    try { cfEl.setPointerCapture(e.pointerId); } catch { /* synthetic */ }
    if (onCard) g.timer = setTimeout(() => { if (g && !g.moved) { g.held = true; startFocusHolo(); } }, HOLD_MS);
  });
  cfEl.addEventListener('pointermove', (e) => {
    if (!g || e.pointerId !== g.id || g.held) return;
    const dx = e.clientX - g.x, dy = e.clientY - g.y;
    if (!g.moved && Math.hypot(dx, dy) > 10) { g.moved = true; clearTimeout(g.timer); }
    if (g.moved && g.duo && (g.scrub || Math.abs(dx) > Math.abs(dy))) {
      // v12c: a sideways drag on a Duo scrubs its lenticular instead of dragging the card
      g.scrub = true; cfScrub.on = true;
      cfScrub.p = Math.max(-1, Math.min(1, dx / (cfCard.getBoundingClientRect().width * 0.45)));
      if (REDUCED) forgeUI.duoTilt(cfCard, cfScrub.p, true);
      return;
    }
    if (g.moved && Math.abs(dx) > Math.abs(dy)) { g.dx = dx; cfCard.style.transform = `translateX(${dx * 0.55}px) rotate(${dx * 0.012}deg)`; }
  });
  const finish = (e, cancel) => {
    if (!g || (e && e.pointerId !== g.id)) return;
    const s = g; g = null;
    clearTimeout(s.timer);
    cfCard.style.transform = '';
    if (s.scrub) {
      cfScrub.on = false;
      lensG.p = cfScrub.p;                 // gyro picks up from where the finger left it
      if (REDUCED) setTimeout(() => forgeUI.duoTilt(cfCard, cfScrub.p, false), 1300);
      const fdx = e ? e.clientX - s.x : 0;
      // a quick flick still steps to the next card
      if (!cancel && performance.now() - s.t < 260 && Math.abs(fdx) >= 60) stepFocus(fdx < 0 ? 1 : -1);
      return;
    }
    if (s.held) { endFocusHolo(); return; }
    if (cancel) return;
    const dx = e.clientX - s.x, dy = e.clientY - s.y, dt = performance.now() - s.t;
    if (Math.abs(dx) >= 44 && Math.abs(dx) > Math.abs(dy) * 1.2) { stepFocus(dx < 0 ? 1 : -1); return; }
    if (Math.hypot(dx, dy) < 10 && dt < 600) {
      if (s.onCard) flipToBack();
      else if (e.target === cfEl || e.target.classList.contains('cf-stage')) closeFocus();   // backdrop
    }
  };
  cfEl.addEventListener('pointerup', (e) => finish(e, false));
  cfEl.addEventListener('pointercancel', (e) => finish(e, true));
  cfEl.addEventListener('contextmenu', (e) => e.preventDefault());
  cfEl.addEventListener('click', (e) => {
    const f = e.target.closest('.cf-forge');
    if (f) { forgeUI.openSheet(f.dataset.id); return; }
    const x = e.target.closest('.cf-explain');
    if (x) forgeUI.explain(x.dataset.kind);
  });
  document.getElementById('cfClose').addEventListener('click', closeFocus);
  cfCard.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); flipToBack(); }
  });
  addEventListener('keydown', (e) => {
    if (!cf.open || binderSheetIndex >= 0) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); stepFocus(e.key === 'ArrowRight' ? 1 : -1); }
  });
}

// The Collection consumes the same stat-sheet builder and the same #statSheet
// element passed to deck picker/battle through `ctx.sheet`; it does not create a
// second card back or a second copy of its reading text.
let binderSheetIndex = -1;
let binderSheetScroll = 0;
let binderSheetFrom = null;     // the tile that opened the sheet: focus returns to it
function layoutBinderSheet() {
  if (binderSheetIndex < 0) return;
  // The Collection's detail sheet is a real-size DOM panel laid out by CSS
  // (crisp text, no scaled design frame); only the card-back consumers scale.
  if (ssPanel.classList.contains('detail')) { ssPanel.style.transform = ''; ssPanel.style.opacity = '1'; return; }
  const frame = statSheetFrame();
  const bottomBand = 64;
  const scale = Math.max(0.1, Math.min(1.45,
    (innerWidth - 8) / frame.W, (innerHeight - bottomBand - 10) / frame.H));
  ssPanel.classList.toggle('wide', frame.wide);
  ssPanel.classList.toggle('narrow', frame.narrow);
  ssPanel.style.opacity = '1';
  ssPanel.style.transform = `translate(${(innerWidth / 2).toFixed(1)}px, ${((innerHeight - bottomBand) / 2).toFixed(1)}px) scale(${scale.toFixed(4)}) translate(-50%, -50%)`;
}
// THE FORGE opens the same sheet over its own card list (sheetList: deck
// indices); the Collection uses its filtered order.
let sheetList = null;
const sheetOrder = () => sheetList || (cf.open ? cf.list : (BV ? BV.order : []));
function showBinderSheetAt(index) {
  const order = sheetOrder();
  if (!order.length) return;
  binderSheetIndex = (index + order.length) % order.length;
  const d = deck[order[binderSheetIndex]];
  const wasOpen = document.body.classList.contains('binderinspect');
  buildStatSheet(d, undefined, { detail: true });
  document.body.classList.add('binderinspect');
  const sheet = document.getElementById('statSheet');
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-label', `${d.name} card details`);
  document.getElementById('binderSheetPrev').disabled = order.length < 2;
  document.getElementById('binderSheetNext').disabled = order.length < 2;
  document.getElementById('binderSheetHolo').hidden = !!sheetList || !holoCapable(d);
  ssOpacity = 0; ssAlign = null;
  layoutBinderSheet();
  // COLLECTION 2: the sheet is the BACK of the focused card; paging the back
  // pages the front with it
  if (cf.open && !sheetList && cf.index !== binderSheetIndex) { cf.index = binderSheetIndex; paintFocus(); }
  if (!wasOpen) document.getElementById('binderSheetClose').focus({ preventScroll: true });
}
// COLLECTION 2: a tile tap brings the card fully into focus (the sheet is its back)
function openBinderSheet(di, fromEl = null) {
  binderSheetScroll = binderGrid.scrollTop;
  openFocus(BV.order, Math.max(0, BV.order.indexOf(di)), fromEl);
}
// focus goes back to the tile of the card last shown (the player may have
// paged through the sheet), else to the tile that opened it
function focusBinderTile(di) {
  const cell = (di != null && binderGrid.querySelector(`.bcard[data-di="${di}"]`))
    || (binderSheetFrom && binderSheetFrom.isConnected ? binderSheetFrom : null);
  binderSheetFrom = null;
  try { cell?.focus({ preventScroll: true }); } catch { /* gone */ }
}
function openCardSheet(ids, at = 0) {
  sheetList = ids.map(id => deck.findIndex(d => d.id === id)).filter(i => i >= 0);
  binderSheetFrom = document.activeElement;
  showBinderSheetAt(at);
}
function closeBinderSheet() {
  if (binderSheetIndex < 0) return;
  if (sheetList) {
    sheetList = null;
    binderSheetIndex = -1;
    document.body.classList.remove('binderinspect');
    const sheet = document.getElementById('statSheet');
    for (const a of ['aria-label', 'role', 'aria-modal']) sheet.removeAttribute(a);
    ssPanel.classList.remove('detail');
    ssPanel.style.opacity = '0';
    ssOpacity = 0; ssAlign = null;
    try { if (binderSheetFrom && binderSheetFrom.isConnected) binderSheetFrom.focus({ preventScroll: true }); } catch { /* gone */ }
    binderSheetFrom = null;
    return;
  }
  const di = sheetOrder()[binderSheetIndex];
  const backIndex = binderSheetIndex;
  binderSheetIndex = -1;
  document.body.classList.remove('binderinspect');
  const sheet = document.getElementById('statSheet');
  for (const a of ['aria-label', 'role', 'aria-modal']) sheet.removeAttribute(a);
  ssPanel.classList.remove('detail');
  ssPanel.style.opacity = '0';
  ssOpacity = 0; ssAlign = null;
  if (cf.open) { flipToFront(backIndex); return; }
  binderGrid.scrollTop = binderSheetScroll;
  if (binderEl.classList.contains('open')) focusBinderTile(di);
}
document.getElementById('binderSheetPrev').addEventListener('click', () => showBinderSheetAt(binderSheetIndex - 1));
document.getElementById('binderSheetNext').addEventListener('click', () => showBinderSheetAt(binderSheetIndex + 1));
document.getElementById('binderSheetClose').addEventListener('click', closeBinderSheet);
{
  const sheet = document.getElementById('statSheet');
  let x0 = 0, y0 = 0, tracking = false, swipedAt = 0;
  sheet.addEventListener('pointerdown', e => {
    if (binderSheetIndex < 0 || e.target.closest('button, summary')) return;
    x0 = e.clientX; y0 = e.clientY; tracking = true;
  });
  sheet.addEventListener('pointerup', e => {
    if (!tracking || binderSheetIndex < 0) return;
    tracking = false;
    const dx = e.clientX - x0, dy = e.clientY - y0;
    if (Math.abs(dx) >= 44 && Math.abs(dx) > Math.abs(dy) * 1.15) {
      swipedAt = performance.now();
      showBinderSheetAt(binderSheetIndex + (dx < 0 ? 1 : -1));
    }
  });
  sheet.addEventListener('pointercancel', () => { tracking = false; });
  // the dimmed backdrop around the sheet closes it, like every other modal
  // (a swipe that ends off the panel is paging, not a dismiss)
  sheet.addEventListener('click', e => {
    if (binderSheetIndex < 0 || performance.now() - swipedAt < 400) return;
    // the focused card's back flips over again on a tap anywhere that is not a control
    const flipBack = cf.open && !sheetList && !e.target.closest('button, summary, details, a');
    if (e.target !== sheet && !flipBack) return;
    closeBinderSheet();
  });
}

// Kept as the programmatic solo-card entry used by the living-card regression
// probe. Player tile taps no longer take this route; they call openBinderSheet
// above and therefore land directly on the shared DOM stat sheet.
async function openFromBinder(di, { holo = false } = {}) {
  const sheetAt = binderSheetIndex;
  soloHoloScroll = binderSheetIndex >= 0 ? binderSheetScroll : binderGrid.scrollTop;
  setBinder(false);
  const fi = holo ? -1 : cards.findIndex(c => c.deckIndex === di);
  if (fi >= 0) { closeSolo(); setFocus(fi); return; }
  const c = await ensureSoloCard(di);
  setArtTexLive('solo', new Set([artTexKey(di)]));
  if (soloCard) scene.remove(soloCard.group);
  soloCard = c;
  scene.add(c.group);
  fan.visible = false;
  document.body.classList.add('solo');
  manip.rx = 0; manip.ry = 0; manip.vx = 0; manip.vy = 0;
  manip.tx = 0; manip.ty = 0; manip.mode = 'settle'; manip.zeta = 1.0; manip.zoom = 1;
  revealT = 0;
  c.foilMat.uniforms.uSweep.value = 0;
  bindCardDOM(c);
  auraUniforms.uColA.value.set(...c.rar.trimA);
  auraUniforms.uColB.value.set(...c.rar.trimB);
  auraUniforms.uPrism.value = c.rar.prismatic >= 1 ? 1 : 0;
  backdropUniforms.uHalo.value.set(
    0.010 + c.rar.trimA[0] * 0.055, 0.010 + c.rar.trimA[1] * 0.045, 0.030 + c.rar.trimA[2] * 0.065);
  if (inspecting) setInspect(false);
  // a card you do not own yet can still be previewed — and says so
  document.body.classList.toggle('solo-unowned', identity.ownedCount(deck[di].id) === 0);
  if (holo) { soloHoloReturn = sheetAt; startSoloHolo(c); }
}

// ---------------------------------------------------------------- HOLOPACK: hologram replay
// The intro shows a fighter peeling up out of its card exactly once. Here it
// is on demand: legendary+ cards with a depth map wear a "Holo" badge in the
// Collection; the stat sheet's Hologram button opens the 3D card, the figure
// peels up once (so the gesture is obvious), and "Hold to lift" raises it
// again for as long as it is held. Dragging the card tilts it, so the relief
// shows real parallax. holo.js is a lazy chunk: the Collection never pays for
// it until a hologram is opened.
let soloHolo = null;                  // { h, card, ready }
let soloHoloReturn = -1;              // binder sheet index to return to
let soloHoloScroll = 0;               // Collection scroll to restore on the way back
let soloHoloView = 0;                 // 0..1 eased: the hologram view pose
let soloHoloBand = null;              // free screen band (px), measured lazily
let holoQaPose = null;                // QA only: { rx, ry } pins the viewer card's tilt (holoQa)
addEventListener('resize', () => { soloHoloBand = null; });
const _hf = { s: 1, y: 0 };
// scale + y for a card at depth z so it fills the free band between the top
// chrome and the nameplate (70% of the viewport height at most; landscape is
// capped by width so the card never crowds the sides)
function holoFrame(z) {
  if (!soloHoloBand && document.body.classList.contains('soloholo')) {
    const back = soloBackBtn.getBoundingClientRect();
    const name = nameplateEl.querySelector('.name').getBoundingClientRect();
    const top = Math.max(back.bottom, 0) + 14;
    const bot = (name.height > 0 ? name.top : innerHeight * 0.8) - 18;
    soloHoloBand = { top, bot: Math.max(bot, top + innerHeight * 0.3) };
  }
  if (!soloHoloBand) { _hf.s = 1; _hf.y = 0; return _hf; }
  const B = soloHoloBand;
  const dist = Math.max(camera.position.z - z, 0.5);
  const visH = 2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * dist;
  // width cap: a phone is too narrow for 70% height (the card would bleed off
  // the sides), so portrait fills ~88% of the width; landscape keeps air
  const frac = Math.min(0.7, (B.bot - B.top) / innerHeight, (camera.aspect < 1 ? 0.88 : 0.42) * camera.aspect * CH / CW);
  _hf.s = frac * visH / CH;
  _hf.y = (1 - (B.top + B.bot) / innerHeight) * visH / 2;
  return _hf;
}
const holoLiftBtn = document.getElementById('holoLift');
async function startSoloHolo(c) {
  stopSoloHolo();
  const tok = soloHolo = { h: null, card: c, ready: false };
  document.body.classList.add('soloholo');
  let mod = null;
  try { mod = await import('./holo/holo2.js'); } catch { mod = null; }
  if (soloHolo !== tok || soloCard !== c || !mod) { if (soloHolo === tok) stopSoloHolo(); return; }
  const col = new THREE.Color(TYPES[c.data.type]?.color || '#b06bff');
  const t0 = performance.now();
  try {
    // HOLO V2 (baked matte/depth/normals + painted-out plate), V1 fallback for
    // the three unbaked cards. Jack: "they only turn up a little bit" — the
    // viewer lifts the figure about twice as far off the card (lift + relief),
    // grows it a little more, and throws a longer contact shadow, on a slow
    // smootherstep so it still reads as graceful rather than a pop.
    tok.h = mod.attachCardHolo2(THREE, c, {
      quality: perfMode || Math.min(innerWidth, innerHeight) < 600 ? 'phone' : 'desktop',
      // v149: the figure rises UP the card in its plane (clears the frame by a
      // fixed depth) instead of ~0.4 card heights out along the normal, which
      // slid it off the card at any tilt; twice as slow; a light column ties
      // its feathered feet to the card (holobeam.js)
      rimColor: [col.r * 1.6, col.g * 1.6, col.b * 1.6], style: 'peel', riseMs: 3400, settleMs: 2000,
      up: 0.26, clear: 0.1, lift: 0.03, relief: 0.08, pop: 0.05, shadow: 1.2, beam: true, smear: !perfMode });
  } catch { tok.h = null; }
  if (!tok.h) { stopSoloHolo(); return; }
  tok.h.ready.then(() => {
    if (soloHolo !== tok) return;
    tok.ready = true;
    holoLoadMs = Math.round(performance.now() - t0);
    document.body.classList.add('soloholo-ready');
    if (!REDUCED) tok.h.play({ hold: 6400 });      // one demonstration lift on open
  }, () => { if (soloHolo === tok) stopSoloHolo(); });
}
let holoLoadMs = null;                // QA: open -> figure ready (assets fetched)
function stopSoloHolo() {
  const tok = soloHolo;
  soloHolo = null;
  holoLatched = false;
  holoQaPose = null;
  document.body.classList.remove('soloholo', 'soloholo-ready', 'holo-held');
  soloHoloBand = null;
  holoLiftBtn.setAttribute('aria-pressed', 'false');
  paintHoloLift();
  try { tok?.h?.dispose(); } catch { /* already gone */ }
}
function holoHold(on) {
  if (!soloHolo?.ready) return;
  document.body.classList.toggle('holo-held', on);
  holoLiftBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
  if (REDUCED) soloHolo.h.rise(on ? 1 : 0);        // no motion: the lifted pose, held
  else soloHolo.h.lift(on);
}
// Hold = lift while held (as before). A quick TAP now latches the figure up
// until the next tap: a press-and-hold alone was the reason the hologram
// "only turns up a little bit" for anyone who simply tapped.
let holoLatched = false, holoDownAt = 0, holoDown = false;
const holoLiftLabel = holoLiftBtn.querySelector('span');
const holoLiftSub = holoLiftBtn.querySelector('small');
function paintHoloLift() {
  holoLiftLabel.textContent = holoLatched ? 'Tap to lower' : 'Tap or hold to lift';
  holoLiftSub.textContent = 'Drag the card to tilt';
}
paintHoloLift();
function holoPress() { holoDown = true; holoDownAt = performance.now(); holoHold(true); }
function holoUnpress(cancel = false) {
  if (!holoDown) return;
  holoDown = false;
  const tap = !cancel && performance.now() - holoDownAt < 300;
  if (tap) holoLatched = !holoLatched;
  else holoLatched = false;
  if (!holoLatched) holoHold(false);
  else holoLiftBtn.setAttribute('aria-pressed', 'true');
  paintHoloLift();
}
holoLiftBtn.addEventListener('pointerdown', (e) => {
  e.preventDefault();
  try { holoLiftBtn.setPointerCapture(e.pointerId); } catch { /* synthetic */ }
  holoPress();
});
holoLiftBtn.addEventListener('pointerup', () => holoUnpress());
for (const ev of ['pointercancel', 'lostpointercapture']) holoLiftBtn.addEventListener(ev, () => holoUnpress(true));
holoLiftBtn.addEventListener('keydown', (e) => {
  if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); holoPress(); }
});
holoLiftBtn.addEventListener('keyup', (e) => { if (e.key === ' ' || e.key === 'Enter') holoUnpress(); });
holoLiftBtn.addEventListener('contextmenu', (e) => e.preventDefault());   // long-press menu
document.getElementById('binderSheetHolo').addEventListener('click', () => {
  if (binderSheetIndex < 0) return;
  openFromBinder(BV.order[binderSheetIndex], { holo: true });
});
// leaving the solo card: a hologram opened from the stat sheet goes back to it
function backFromSolo() {
  const ret = soloHoloReturn;
  const di = soloCard?.deckIndex;
  closeSolo();
  setBinder(true);
  binderGrid.scrollTop = soloHoloScroll;
  renderBinderWindow();
  if (ret >= 0 && BV?.order.length) showBinderSheetAt(ret);
  else focusBinderTile(di);
}
function closeSolo() {
  stopSoloHolo();
  soloHoloReturn = -1;
  if (!soloCard) return;
  scene.remove(soloCard.group);
  soloCard = null;
  setArtTexLive('solo', null);
  fan.visible = true;
  document.body.classList.remove('solo', 'solo-unowned');
  const fi = Math.max(focusIndex, 0);
  focusIndex = -1; setFocus(fi, { reveal: false });
}
document.getElementById('collectionBtn').addEventListener('click', () => setBinder(true));
document.getElementById('binderClose').addEventListener('click', closeCollection);
// v11 COLLECTION: Cards · Duos · Fusions tabs and the forge sheets/ceremonies.
forgeUI.initForgeUI({
  deck, byId: Object.fromEntries(deck.map(d => [d.id, d])), V, artSrc, pwr: (d) => pwrOf(d),
  identity, audio, haptics, toast: (m) => toast(m),
  // the Forge hero's lenticular reads the app's gyro (and the iOS invite) like the Codex does
  tilt: { live: () => gyroActive && !REDUCED, get ask() { return IOS_ASK && !REDUCED && !lsNum('nm-tilt-denied') ? askTilt : null; } },
  // COLLECTION 2: a Duo/Fusion tile opens the shared focus view; tab changes repaint the strip
  onFocus: (id) => {
    const list = collList();
    const i = list.indexOf(deckIndexById.get(id));
    if (i >= 0) openFocus(list, i, document.querySelector(`#forgePanel .fgT[data-id="${id}"]`));
  },
  onTab: () => { paintStrip(); if (binderView === 'car') buildCarousel(0); },
  // v257 the Trophies shelf + its big view (the boss TIME TRIAL starts from there)
  renderTrophies: (panel) => {
    loadTrophies().then((m) => {
      if (forgeUI.currentTab() !== 'trophies') return;
      panel.innerHTML = m.panelHtml(m.shelf(identity.trophies()), (id) => (cardsById[id] ? artSrc(cardsById[id], V, 'thumb') : ''));
    }).catch(e => console.warn('[trophies]', e));
  },
  onTrophy: (cardId) => {
    if (!trophyMod) return;
    const row = trophyMod.shelf(identity.trophies()).find(r => r.cardId === cardId && r.owned);
    if (!row) return;
    trophyMod.openTrophy(row, { audio, imgOf: (id, size) => (cardsById[id] ? artSrc(cardsById[id], V, size) : ''),
      trial: campaignOn ? (levelId) => { setBinder(false); openCampaign({ trial: levelId }).catch(e => console.error('[campaign]', e)); } : null });
  },
  onForged: () => {
    refreshBinderOwnership(); paintDeckBadge(); paintEconomy();
    try { shellUI.deckApi && shellUI.deckApi.revalidate && shellUI.deckApi.revalidate(); } catch { /* deck repairs on next load */ }
  },
});
// v235 FORGE on the title: one tap opens the forge (the binder's Duos tab).
// The orbiting mark is CSS only; it lights for news (forgeUI.freshReady),
// computed when the title shows and when the boot state lands, never per
// frame. Opening the forge marks them seen (forgeUI.setTab).
function paintForgeMark() {
  let on = false;
  try { on = forgeUI.freshReady().length > 0; } catch { /* no forged data */ }
  document.getElementById('menuForge')?.classList.toggle('forge-ready', on);
}
document.getElementById('menuForge').addEventListener('click', (e) => {
  audio.unlock();
  if (battleLocked() || gate.ceremonyRunning) { lockedTap(e.currentTarget); return; }   // v245: sealed until Campaign unlocks
  audio.ui('forgepress');
  e.currentTarget.classList.remove('forge-ready');
  audio.menuQuip('forge');
  shellUI.go('fan', { after: () => { setBinder(true); forgeUI.setTab('duos'); } });
});
// v245 THE ACADEMY is parked (Jack: it becomes a card-strategy guide -- THE CODEX): the pill opens the Codex;
// the lesson hall + its glint live behind the QA flag localStorage nomadmon-qa-academy=1 (smokes)
const academyOn = () => { try { return localStorage.getItem('nomadmon-qa-academy') === '1'; } catch { return false; } };
document.getElementById('binderGuide').addEventListener('click', (e) => {
  setBinderFilters(false); e.currentTarget.classList.remove('academy-glint');
  if (academyOn()) academy.openHall(); else openCodex();
});  // SC8 -> the Codex
window.__nmOpenAcademy = () => (academyOn() ? academy.openHall() : openCodex());   // (the deck panel's pill, deckpicker.js)

// ---------------------------------------------------------------- THE CODEX
// The card-strategy guide (src/codex.js, its own lazy chunk). Exhibits are DOM / Canvas 2D on the
// game's own modules; while it is open the app renderer is paused (tick), and a level-up exhibit's
// WebGL canvas is released on close. Entry: the Collection pill, Settings, the scout sheet's why-links.
let codexMod = null, codexOpen = false;
let campaignMapUp = false;   // (the campaign atlas is open: src/campaign.js hide3D / show3D)
let duelUp = false;          // (INK DUEL is open: src/duel.js; see openDuel)
function closeCodexNow() { if (codexMod && codexMod.isOpen()) codexMod.closeCodex(); }
async function openCodex(opts = {}) {
  if (codexOpen) { if (codexMod) codexMod.goTo(opts.to); return; }
  audio.unlock();
  const m = codexMod || (codexMod = await import('./codex.js').catch(staleReload));
  codexOpen = true;
  try {
  m.openCodex({
    lib: codexLib, V, deck, byId: cardsById, artSrc, typeGlyph, TYPES, RARITY, identity, audio, haptics, guide, forgeUI,
    frameCanvas: (rarity) => getFrameTexture(RARITY[rarity] || RARITY.common, crestImg).image,
    loadLevelFx, perfMode: () => perfMode,
    creditsLeft: () => identity.creditsLeft(),
    openPack: () => { closeCodexNow(); setBinder(false); if (shellUI.currentScreen() === 'fan') packBtn.click(); else document.getElementById('menuPack').click(); },
    openWish: () => openWishPicker({ from: 'codex' }),   // ETERNAL WISH: the picker rides over the Codex
    tilt: { live: () => gyroActive && !REDUCED, ask: IOS_ASK && !REDUCED && !lsNum('nm-tilt-denied') ? askTilt : null, read: (reset) => gyroLens(reset) },
    reduced: REDUCED,
    onClose: () => { codexOpen = false; audio.ensureMusic(); },   // audiopolish: the screen rule after an overlay
  }, opts);
  } catch (err) { codexOpen = false; console.error('[codex] open failed', err); }   // never leave the app paused
}
window.__nmOpenCodex = (opts) => openCodex(opts);
// ACCOUNTS: the menu's last, small item -- LINK (another device, PIN, switch player). Never sealed.
document.getElementById('menuLink')?.addEventListener('click', () => { audio.unlock(); openAccountPage(); });
// v254 (Jack): the Codex is a main-menu option, sealed with the others until Campaign unlocks
document.getElementById('menuCodex').addEventListener('click', (e) => {
  audio.unlock();
  if (battleLocked() || gate.ceremonyRunning) { lockedTap(e.currentTarget); return; }
  audio.ui('mcoll');
  // the quip yields to the Codex's own first welcome (its hall pair would cut it off): it waits for a later visit
  const codexHeard = (() => { try { return !!(JSON.parse(localStorage.getItem(`nomadmon-codex-v1:${identity.playerKey() || 'guest'}`)) || {}).vo?.hall; } catch { return false; } })();
  openCodex().then(() => { if (codexHeard) audio.menuQuip('codex'); });
});
soloBackBtn.addEventListener('click', backFromSolo);

// ---------------------------------------------------------------- battle hook
// P7: interactive battle — decks come from the deck builder (player) and the
// shell's AI deck generator (opponent); results land on the interstitial.
let battle = null;              // module handle once loaded
let battleActive = false;
const battleBtn = document.getElementById('battleBtn');
const cardsById = Object.fromEntries(deck.map(d => [d.id, d]));
identity.setPlevelCards(cardsById);   // v12: a guest's one-time player-level migration reads card rarities

// SC2-A fix: playerSide was dropped here — the MP guest simulated as side A,
// mirroring the battle and desyncing on round 2. It now flows into battle ctx.
// SC32: guests have no server to pay them, so the victory screen still needs a
// number. This mirrors src/scaling.js battleReward() for display only.
function clientReward(won, bracket, driver, streak) {
  if (!won) return null;
  return battleReward({
    bracketKey: (bracket && bracket.key) || 'even',
    difficulty: (driver && driver.difficulty) || 'normal',
    streak: Math.max(0, (streak || 1) - 1),
    won: true,
  });
}

async function launchBattle(decks, { driver = { kind: 'ai' }, seed = (Date.now() % 0x7fffffff), playerSide = 'A', bracket = null, draft = null, barrageArena = null, bossSide = null, carryHp = null, gauntletLeg = null, replay = null, capture = false, replayTimeScale = null } = {}) {
  if (battleActive) return;
  // Stop the outgoing collection before the battle module or art preload can
  // yield. Otherwise the shared fan owns the cold-load frames before lineup.
  fan.visible = false; aura.visible = false; shadowMesh.visible = false;
  battleBtn.disabled = true;
  // Competitive MP is level-normalized. Both clients derive the same level-1
  // maps locally from the exchanged ids; persisted levels never enter lockstep.
  const battleDecks = (driver.kind === 'remote' || replay)
    ? Object.fromEntries(['A', 'B'].map(side => [side, {
      ...decks[side], levels: Object.fromEntries(decks[side].chars.map(id => [id, 1])),
      mastery: null,     // v10: collection mastery never enters MP lockstep
    }]))
    : decks;
  const lastDraw = window.__nm && window.__nm.lastDraw;
  const playerChars = (battleDecks[playerSide] && battleDecks[playerSide].chars) || [];
  const drawnIds = lastDraw && JSON.stringify(lastDraw.result) === JSON.stringify(playerChars)
    ? (lastDraw.drawn || []).slice() : [];
  const mod = battle || (battle = await lazyImport(`./battle.js?${V}`));
  // preload art textures for every character AND support in both decks
  // (SC2-A: spells fly on stage as real cards)
  const charIds = [...battleDecks.A.chars, ...battleDecks.B.chars,
    ...(battleDecks.A.supports || []), ...(battleDecks.B.supports || [])];
  const battleArtIdx = charIds.map(id => deck.findIndex(d => d.id === id)).filter(i => i >= 0);
  setArtTexLive('battle', new Set(battleArtIdx.map(i => artTexKey(i))));
  await Promise.all(battleArtIdx.map(i => ensureArtTex(i)));   // (i) only — map's index would become `size`
  // SC21: warm both decks' loops during the intro slam so the first fighter to
  // take the stage is already living rather than swapping in mid-round.
  prefetchClips(charIds.map(id => cardsById[id]), 10);
  setBinder(false); if (inspecting) setInspect(false);
  closeLab(); videoRelease();
  battleActive = true;
  document.body.classList.add('battling');
  const restore = () => {
    setArtTexLive('battle', null);
    battleActive = false;
    document.body.classList.remove('battling');
    perfmon.matchOver();          // v259: the match is over; its frame chunk goes out at idle
    battleBtn.disabled = false;
    renderPass.scene = scene;
    renderPass.camera = camera;
    bloomPass.strength = 0.5;
    composer.setSize(innerWidth, innerHeight);
    fitCamera();
  };
  try { await mod.startBattle({
    renderer, composer, renderPass, bloomPass, lut: lutController,
    maxAnisotropy: MAX_TEXTURE_ANISOTROPY,
    buildCard, depthArt, deck, cardsById,
    living: { acquire: videoAcquire, release: videoRelease, update: videoUpdate },
    // SC14: second channel — the enemy active card animates too (battle only)
    living2: {
      acquire: (c, opts) => livingVideo2.acquire(c, opts),
      release: () => livingVideo2.release(),
      update: (dt) => livingVideo2.update(dt),
    },
    // The battle inspector reuses the same DOM card back as the collection,
    // including its legibility-first card registration and geometry ladder.
    sheet: { build: buildStatSheet, update: updateStatSheet, fits: statSheetFits },
    decks: battleDecks, seed, driver, playerSide, bracket, draft, replay, replayTimeScale, drawnIds, barrageArena,   // v234 the Quick Match arena
    oppName: driver.kind === 'remote' && mpNames ? mpNames.opp : null,   // the opponent grade chip's name
    onReplayRound: replay ? (round, atSeconds) => {
      const captureState = window.__nomadmonCapture || (window.__nomadmonCapture = {});
      (captureState.roundStarts || (captureState.roundStarts = [])).push({ round: round.round, atSeconds });
    } : null,
    bossSide, carryHp, gauntletLeg,
    artTexById: (id) => artTexCache.get(artTexKey(deck.findIndex(d => d.id === id))),
    perfMode: () => perfMode,
    pixelCap: () => pixelCap,
    onEnd: restore,
    onFinish: (result) => {
      // v236 CAMPAIGN: its own result screen (stars / "Boss at 8%!" + retry); no Quick Match record,
      // streak or XP here -- the earned rewards go to campaignReward (B2 implements player XP)
      // v245 THE ACADEMY (practice): no record, reward, XP, streak, campaign progress or onboarding count --
      // the lesson's own result card, then Retry (a fresh attempt seed) or back to the hall
      if (driver.practice) {
        const bd = (window.__nm && window.__nm.barrageBreakdown) || {};
        if (window.__nm) window.__nm.campaignResult = null;
        academy.finish({ won: !!(result && result.won), kst: { adv: bd.adv | 0 } }).then((r) => {
          battle.exitBattle(); shellUI.go('title');
          if (r && r.next === 'retry') setTimeout(() => academy.retry(), 600); else setTimeout(() => academy.openHall(), 600);
        });
        return;
      }
      if (driver.campaign && campaignUI && !capture) {
        const res = (window.__nm && window.__nm.campaignResult) || { id: driver.campaign.rec.id, won: !!(result && result.won), lost: 3, rounds: 9, enemyHpPct: 50,
          trial: !!driver.campaign.trial, daily: !!driver.campaign.daily };   // (v257: a trial / the daily never lands on the map's progress)
        if (window.__nm) window.__nm.campaignResult = null;
        lastCampaignContrib = { id: res.id, ids: Array.isArray(res.contrib) ? res.contrib.slice(0, 5) : [] };   // UNDERDOG XP
        onboard.noteBattle();                  // ONBOARDING: a campaign match is a patient battle too
        // v331 FLICKER: the battle is left under the full veil, and what the result chose next (the map, the squad
        // screen, a retry) is built before the veil lifts (`then`; the veil waits on its promise, capped)
        campaignUI.onFinish(res, (then) => shellUI.go('title', { covered: () => battle.exitBattle(), holdVeil: !!then, after: then || undefined }));
        return;
      }
      // a win may level cards: warm the ceremony while the record round-trips
      if (!capture && result && result.won) loadLevelFx().then(m => m.prewarm()).catch(() => {});
      if (capture) {
        const captureState = window.__nomadmonCapture || {};
        window.__nomadmonCapture = { ...captureState, done: true, result,
          doneActionSeconds: Number.isFinite(captureState.audioCaptureOriginMs)
            ? +((Date.now() - captureState.audioCaptureOriginMs) / 1000).toFixed(6) : null };
        return;
      }
      // SC33: a gauntlet leg does not land on the normal interstitial — it either
      // advances to the next leg (carrying the survivors) or ends the run.
      if (gauntlet && gauntletLeg != null) {
        const localStreak0 = shellUI.recordStreak(!!result.won);
        identity.reportRecord(result.won ? 'win' : 'loss', {
          bracket: (bracket && bracket.key) || 'even', difficulty: 'normal',
          xp: result.stats && result.stats.xp,
          cards: decks[playerSide].chars,
          enemyPower: teamMedianEffectivePwr(battleDecks[playerSide === 'A' ? 'B' : 'A'].chars,
            cardsById, battleDecks[playerSide === 'A' ? 'B' : 'A'].levels),
          mode: driver.kind === 'remote' ? 'mp' : 'solo',
        }).then((award) => {
          paintPlayerChip();
          celebrate(award, null);
        }).catch(() => {});
        void localStreak0;
        if (gauntletLegDone(result, battle)) return;
      }
      // SC2-B records: every solo AND MP battle lands on the player's W-L
      // (server for players, localStorage tally for guest). Per-card RECORD
      // slots are deliberately skipped — player-level only; the card-back
      // RECORD line keeps its static data.record placeholder.
      // SC32: the streak and the battle reward. The local streak drives the
      // NEXT opponent draft; for named players the server is the authority and
      // its answer overwrites the local copy.
      const isSolo = driver.kind === 'ai';
      onboard.noteBattle();                  // ONBOARDING: counts toward the newcomer's patient battles
      const localStreak = isSolo ? shellUI.recordStreak(!!result.won) : shellUI.winStreak();
      if (isSolo) shellUI.nextOpponent();    // v232: a completed match draws a fresh opponent + fate seed
      if (isSolo) shellUI.recordSoloWin(!!result.won, identity.record().wins,
        identity.playerKey());
      identity.reportRecord(result.won ? 'win' : 'loss', {
        bracket: (bracket && bracket.key) || 'even',
        difficulty: (driver && driver.difficulty) || 'normal',
        xp: result.stats && result.stats.xp,
        // v12: the server settles MP XP from the room, not from this claim
        room: driver.kind === 'remote' && driver.code && driver.token ? { code: driver.code, token: driver.token } : null,
        cards: decks[playerSide].chars,
        enemyPower: teamMedianEffectivePwr(battleDecks[playerSide === 'A' ? 'B' : 'A'].chars,
          cardsById, battleDecks[playerSide === 'A' ? 'B' : 'A'].levels),
        mode: driver.kind === 'remote' ? 'mp' : 'solo',
        // v234 the daily mission (solo): one of the five that fought matches today's line
        missionMet: isSolo && missionMet(identity.todaysMission(), decks[playerSide].chars.map(id => cardsById[id])),
      }).then((award) => {
        paintPlayerChip();
        const srvStreak = identity.serverStreak();
        if (isSolo && srvStreak) shellUI.setWinStreak(srvStreak);
        const paid = identity.lastReward();
        shellUI.showBattleReward(paid || clientReward(result.won, bracket, driver, localStreak));
        refreshBinderOwnership();
        celebrate(award, shellUI.showPlayerXp(award && award.plevel));
      });
      // SC3 (c): MP victory brag — share sheet, never auto-posted
      const isRemote = driver.kind === 'remote';
      const share = isRemote && result.won && mpNames ? () => {
        const text = `🏆 ${mpNames.me} destroyed ${mpNames.opp} in Nomad-mon Go! ${location.origin}${location.pathname}`;
        if (navigator.share) navigator.share({ text }).catch(() => {});
        else window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
      } : null;
      // battle scene stays up behind the interstitial until CONTINUE/REMATCH
      shellUI.showInterstitial(result, {
        share,
        onContinue: () => {
          battle.exitBattle();          // calls onEnd -> restore
          shellUI.go('title');
        },
        // v331 FLICKER: the old battle is left and the new one built under the veil, which holds until it owns the
        // frame (the parade used to pop in over the results mid-fade)
        onRematch: driver.kind === 'ai' ? () => {
          shellUI.go('battle', { covered: () => battle.exitBattle(), holdVeil: true,
            after: () => launchBattle(decks, { driver, seed: (seed + 1013) % 0x7fffffff }) });
        } : (mpUI ? () => {
          // SC3 Phase 3: MP rematch — same room + decks, fresh server seed,
          // relaunches via the normal onMatched path once BOTH players accept
          battle.exitBattle();
          mpUI.requestRematch({
            onFail: () => { shellUI.go('title'); },
          });
        } : null),
      });
      if (driver.kind === 'remote' && driver.close) driver.close();
    },
  }); } catch (err) {
    restore();
    throw err;
  }
}

// ---------------------------------------------------------------- SC33 GAUNTLET
// BOSS GAUNTLET: three battles, no healing between them, ending at THE LAST
// NOMAD. The rule that makes it a gauntlet rather than three matches is
// ATTRITION — a character knocked out is gone for the rest of the run, and the
// survivors carry their exact HP forward. Nothing is ever healed.
//
// Difficulty was measured, not guessed (scripts/sc33-sim.js --gauntlet): the
// legs are DELIBERATELY easier than a standalone match, because three fights in
// a row with no healing compounds. A good referee clears it 32% of the time.
let gauntlet = null;

// Distinct cards the player actually owns — the gauntlet's unlock gate.
function ownedDistinct() {
  try { return deck.reduce((n, d) => n + (identity.ownedCount(d.id) > 0 ? 1 : 0), 0); }
  catch { return 0; }
}
function gauntletUnlocked() { return ownedDistinct() >= GAUNTLET_UNLOCK_CARDS; }

function startGauntlet(sel) {
  const levels = identity.cardLevels(sel.chars);
  gauntlet = {
    leg: 0,
    squad: sel.chars.map(id => ({ id, level: levels[id], hp: levelCard(cardsById[id], levels[id]).hp })),
    supports: [...sel.supports],
    mastery: sel.mastery || null,
    holo: sel.holo || [],
    seed: (Date.now() % 0x7fffffff),
  };
  runGauntletLeg();
}

let gauntletLastError = null;
async function runGauntletLeg() {
  try { await runGauntletLegInner(); }
  catch (e) { gauntletLastError = String(e && e.stack || e).slice(0, 300); throw e; }
}
async function runGauntletLegInner() {
  const g = gauntlet;
  if (!g) { gauntletLastError = 'no gauntlet state'; return; }
  const isBoss = g.leg === GAUNTLET_LEGS - 1;
  const A = { chars: g.squad.map(c => c.id), supports: g.supports, mastery: g.mastery || null, holo: g.holo || [],
    levels: Object.fromEntries(g.squad.map(c => [c.id, c.level || 1])) };
  let B, bracket = null;
  if (isBoss) {
    const lvl = Math.round(g.squad.reduce((n, c) => n + (c.level || 1), 0) / g.squad.length);
    B = { chars: [...BOSS_DECK.chars], supports: [...BOSS_DECK.supports],
      levels: Object.fromEntries(BOSS_DECK.chars.map(id => [id, lvl])) };
    // v11: a squad carrying a Duo/Fusion meets a Fusion at the boss — the one
    // whose power sits nearest the player's strongest forged card (min tier 12).
    const best = g.squad.map(c => cardsById[c.id]).filter(d => d && d.forged)
      .reduce((m, d) => Math.max(m, pwrOf(d)), 0);
    if (best) {
      const fz = deck.filter(d => d.forged === 'fusion' && (d.tier || 0) >= 12)
        .sort((a, b) => Math.abs(pwrOf(a) - best) - Math.abs(pwrOf(b) - best))[0];
      if (fz) { B.chars[B.chars.length - 1] = fz.id; B.levels = Object.fromEntries(B.chars.map(id => [id, lvl])); }
    }
  } else {
    const d = shellUI.aiDeck(undefined, {
      difficulty: 'normal', streak: 0,
      biasOverride: GAUNTLET_BIAS[g.leg],
      seed: (g.seed + g.leg * 7919) % 0x7fffffff,
      playerLevels: A.levels,
    });
    B = { chars: d.chars, supports: d.supports, levels: d.levels };
    bracket = d.bracket;
  }
  const launch = () => launchBattle({ A, B }, {
    driver: { kind: 'ai', difficulty: 'normal' },
    seed: (g.seed + g.leg * 1013) % 0x7fffffff,
    bracket, bossSide: isBoss ? 'B' : null,
    carryHp: g.leg > 0 ? g.squad : null,
    gauntletLeg: g.leg,
  });
  // shell.go() early-returns when the target screen is the CURRENT one, and
  // after leg 1 the shell is already on 'battle' — so routing legs 2 and 3
  // through go() dropped their `after` on the floor and the run simply stopped
  // with the panel dismissed and nothing behind it. Only the FIRST leg needs
  // the screen transition.
  if (shellUI.currentScreen() === 'battle') launch();
  else shellUI.go('battle', { after: launch });
}

// Called from onFinish when a gauntlet leg ends.
function gauntletLegDone(result, battleMod) {
  const g = gauntlet;
  if (!g) return false;
  const survivors = (result.survivors || []).filter(c => !c.koed).map(c => ({
    id: c.id, hp: c.hp, level: g.squad.find(x => x.id === c.id)?.level || 1,
  }));
  if (!result.won || !survivors.length) {
    const legsCleared = g.leg;
    const rew = gauntletReward(legsCleared);
    gauntlet = null;
    battleMod.exitBattle();
    shellUI.showGauntletEnd({ cleared: false, legs: legsCleared, reward: rew });
    if (rew.total) shellUI.showBattleReward({ packs: rew.total, parts: rew.parts });
    return true;
  }
  g.squad = survivors;
  g.leg++;
  if (g.leg >= GAUNTLET_LEGS) {
    const rew = gauntletReward(GAUNTLET_LEGS);
    gauntlet = null;
    battleMod.exitBattle();
    markGauntletCleared();
    shellUI.showGauntletEnd({ cleared: true, legs: GAUNTLET_LEGS, reward: rew });
    return true;
  }
  battleMod.exitBattle();
  shellUI.showGauntletLeg({
    next: g.leg, label: GAUNTLET_LABELS[g.leg], squad: g.squad, cardsById,
    onContinue: () => runGauntletLeg(),
  });
  return true;
}

// The persistent mark. Server-authoritative for named players (identity owns
// the call); guests keep a local flag so the chip still reads true for them.
const GAUNTLET_MARK_KEY = 'nomadmon-gauntlet-cleared';
function gauntletCleared() {
  if (identity.gauntletCleared && identity.gauntletCleared()) return true;
  try { return localStorage.getItem(GAUNTLET_MARK_KEY) === '1'; } catch { return false; }
}
function markGauntletCleared() {
  try { localStorage.setItem(GAUNTLET_MARK_KEY, '1'); } catch { /* private */ }
  if (identity.reportGauntlet) identity.reportGauntlet().then(paintPlayerChip).catch(() => {});
  paintPlayerChip();
}

// legacy Battle pill in the fan view = solo flow (deck screen)
// SC7 WS-A: honors the same pack gate as the title buttons
battleBtn.addEventListener('click', () => {
  if (battleLocked() || gate.ceremonyRunning) { lockedTap(); return; }
  shellUI.go('deck');
});

// ---------------------------------------------------------------- SC2-A deck carousel
let deckmod = null, deckActive = false;
let slotmod = null, slotActive = false;
let luckyPreload = { key: '', token: 0, textures: new Map(), resident: 0 };

// Start the complete pool's HTTP fetch as soon as 2+2 is assembled, then
// decode and pin a reel-sized resident set. This keeps the texture cache's
// 64-entry ceiling intact while guaranteeing the slot has nine real faces.
async function preloadLuckyDrawPool(picks, onProgress = () => {}) {
  const picked = new Set(picks || []);
  const pool = deck.filter(d => d.role !== 'support' && !isChampionCard(d) && identity.ownedCount(d.id) > 0 && !picked.has(d.id));
  const key = [...picked].sort().join('|');
  if (luckyPreload.key === key && luckyPreload.resident >= 9) return luckyPreload.resident;
  const token = luckyPreload.token + 1;
  luckyPreload = { key, token, textures: new Map(), resident: 0 };
  setArtTexLive('lucky-draw-preload', null);

  // Warm every pool URL into the immutable HTTP cache. Decoding every one
  // would defeat the app's texture LRU, so only the first twelve are resident.
  const residentCards = pool.slice(0, Math.max(9, Math.min(12, pool.length)));
  const warmQueue = pool.slice(residentCards.length);
  const warmWorker = async () => {
    while (warmQueue.length && luckyPreload.token === token) {
      const card = warmQueue.shift();
      try {
        const r = await fetch(artSrc(card, V, 'thumb'), { priority: 'low', credentials: 'omit' });
        if (r.ok) await r.arrayBuffer();
      } catch { /* best-effort HTTP warming */ }
    }
  };
  const conn = navigator.connection;
  if (!(conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))))
    void Promise.all([warmWorker(), warmWorker(), warmWorker(), warmWorker()]);
  const liveKeys = new Set(residentCards.map(card => artTexKey(deck.indexOf(card), 'thumb')));
  setArtTexLive('lucky-draw-preload', liveKeys);
  await Promise.all(residentCards.map(async card => {
    const tex = await ensureArtTex(deck.indexOf(card), 'thumb');
    if (luckyPreload.token !== token) return;
    luckyPreload.textures.set(card.id, tex);
    if (tex !== artFallbackTex) luckyPreload.resident++;
    onProgress(luckyPreload.resident);
  }));
  return luckyPreload.token === token ? luckyPreload.resident : 0;
}

async function showDeck3D() {
  // The dynamic import is a real paint window on a cold cache. Hide the fan
  // synchronously, before the deck renderer is available to take ownership.
  fan.visible = false; aura.visible = false; shadowMesh.visible = false;
  const mod = deckmod || (deckmod = await lazyImport(`./deckpicker.js?${V}`));
  if (deckActive) return;
  closeSolo(); setBinder(false); if (inspecting) setInspect(false);
  closeLab(); videoRelease();
  deckActive = true;
  document.body.classList.add('deckmode');
  mod.startDeck({
    renderer, composer, renderPass, bloomPass,
    buildCard: buildDeckPickerCard, deck,
    ensureArtTex: (d) => ensureArtTex(deck.indexOf(d), 'thumb'),
    living: { acquire: videoAcquire, release: videoRelease, update: videoUpdate },
    environment: scene.environment,
    // SC27: the picker can flip its hero card to the stat sheet (double-tap),
    // so it needs the same DOM back every other surface uses — reading text is
    // never a texture.
    sheet: { build: buildStatSheet, update: updateStatSheet, fits: statSheetFits },
    preloadDrawPool: preloadLuckyDrawPool,
    gateLocked: () => battleLocked(),
    fieldHistory: () => identity.fieldHistory(),
    shellDeck: shellUI.deckApi,
    owned: (id) => identity.ownedCount(id) > 0,   // SC2-B: pick owned only
    isGuest: () => identity.isGuest(),             // v11: guests field no forged cards online
    levelFor: (id) => identity.cardLevel(id),
    holoFlag: (id) => identity.cardHoloFlag(id),       // v10 HOLO (server-persisted)
    cardSheet: openCardSheet,                          // forge: long-press / "i" opens the card sheet
    // forge v158: the same per-rarity metal frame the 3D cards wear, as a 2D canvas
    frameCanvas: (rarity) => getFrameTexture(RARITY[rarity] || RARITY.common, crestImg).image,
    progressFor: (id) => identity.cardProgress(id),
    nextWinXp: (id) => {
      const sel = shellUI.selectedDeck();
      const ids = [...sel.chars];
      if (!ids.includes(id) && ids.length < 5) ids.push(id);
      const sample = ids.length ? ids : [id];
      return victoryXp(
        teamMedianEffectivePwr(sample, cardsById, identity.cardLevels(sample)),
        selectedDifficulty(),
      );
    },
    perfMode: () => perfMode,
    pixelCap: () => pixelCap,
    qualityActivity: noteQualityActivity,
    depthTilt: cardMotionTilt,
    onEnd: () => {
      setArtTexLive('deck-picker', null);
      deckActive = false;
      document.body.classList.remove('deckmode');
      renderPass.scene = scene;
      renderPass.camera = camera;
      bloomPass.strength = 0.5;
      composer.setSize(innerWidth, innerHeight);
      fitCamera();
    },
  });
  return mod.deckSettled ? mod.deckSettled() : null;   // v299 slotflash: the shell holds its veil on this
}
function hideDeck3D() {
  if (deckActive && deckmod) deckmod.endDeck();
}

async function startLuckyDraw(draw, onComplete) {
  const mod = slotmod || (slotmod = await lazyImport(`./slotdraw.js?${V}`));
  if (slotActive) return;
  slotActive = true;
  document.body.classList.add('slotmode');
  delete document.body.dataset.slotReady;
  const cycleCards = deck.filter(card => card.role !== 'support' && !isChampionCard(card) && identity.ownedCount(card.id) > 0);
  const started = mod.startSlot({
    renderer, composer, renderPass, bloomPass, buildCard, deck, draw, cycleCards,
    ensureArtTex: (card) => ensureArtTex(deck.indexOf(card), 'thumb'),
    preloadedTextures: luckyPreload.textures,
    preloadedCount: luckyPreload.resident,
    levelFor: (id) => identity.cardLevel(id),
    sheet: { build: buildStatSheet, update: updateStatSheet, fits: statSheetFits },
    // the reel strips print each card in its real per-rarity metal frame
    frameCanvas: (rarity) => getFrameTexture(RARITY[rarity] || RARITY.common, crestImg).image,
    onComplete,
    leaving: () => shellUI.currentScreen() !== 'draw',   // v299 slotflash: a launch took the shell off the slot screen
    onEnd: () => {
      delete document.body.dataset.slotReady;
      setArtTexLive('lucky-draw-preload', null);
      luckyPreload = { key: '', token: luckyPreload.token + 1, textures: new Map(), resident: 0 };
      slotActive = false;
      document.body.classList.remove('slotmode');
      renderPass.scene = scene; renderPass.camera = camera;
      bloomPass.strength = 0.5; composer.setSize(innerWidth, innerHeight); fitCamera();
    },
  });
  await started;
  if (slotActive) document.body.dataset.slotReady = '1';   // the slot scene owns the frame (the veil may lift)
}

// ---------------------------------------------------------------- pack hook (P4)
// Dupe -> XP level crossings seen during a pack session, merged per card (an
// OPEN ALL run can level the same card twice), played when the pack ends.
const packLevelUps = new Map();
function notePackLevelUps(dupes) {
  for (const [id, r] of Object.entries(dupes || {})) {
    if (!r || r.maxed || !(r.levelAfter > r.levelBefore)) continue;
    const prev = packLevelUps.get(id);
    packLevelUps.set(id, { id, beforeLevel: prev ? Math.min(prev.beforeLevel, r.levelBefore) : r.levelBefore,
      level: prev ? Math.max(prev.level, r.levelAfter) : r.levelAfter });
  }
}
function takePackLevelUps() { const rows = [...packLevelUps.values()]; packLevelUps.clear(); return rows; }
let pack = null;                // module handle once loaded
let packActive = false;
let packTexes = null;           // [front, back], loaded on first open
// econfix: ONE pack-entry attempt at a time, cancellable. Leaving the pack screen (Home, any
// route away) invalidates it; every await re-checks, so a cold load that finishes after the
// player left never spends a credit and never mounts the pack behind their back.
let packAttempt = 0;
function cancelPackEntry() { packAttempt++; if (!packActive) packBtn.disabled = false; }
const packBtn = document.getElementById('packBtn');
// v331 FLICKER: the flow is a function that resolves once the pack owns the frame (or the attempt ended), so the
// title's Open Pack can hold the shell veil on it (the veil used to lift on the bare collection for the whole load)
packBtn.addEventListener('click', () => { openPackFlow(); });
async function openPackFlow() {
  if (packActive || deckActive || slotActive || packBtn.disabled) return;   // (a disabled button never clicked)
  if (identity.creditsLeft() <= 0) { paintEconomy(); return; }   // SC2-B gate
  const attempt = ++packAttempt;
  const live = () => attempt === packAttempt && shellUI.currentScreen() === 'fan';
  const bail = () => { packBtn.disabled = false; if (attempt === packAttempt) packAttempt++; };
  // Pack assets and code are lazy. Suppress the fan before either await so a
  // cold pack opening never exposes the collection underneath it.
  fan.visible = false; aura.visible = false; shadowMesh.visible = false;
  packBtn.disabled = true;
  const packAudioUnlocked = audio.unlock(); // this click is the audio-unlock gesture
  // Deep links can reach this button without the title-menu handler. Keep the
  // same structural core gate here; contents-specific rarity/ceremony cues are
  // selected and awaited by pack.js once the draw is known.
  const packAudioReady = audio.loadPackCore();
  // econfix: every loading await sits inside one recovery path -- a failed module, texture or audio
  // load spends nothing and leaves the player on a live screen
  let mod;
  try {
    mod = pack || (pack = await lazyImport(`./pack.js?${V}`));
    if (!live()) { bail(); return; }
    if (!packTexes) {
      const texes = await Promise.all([
        loadTex(`./assets/web/pack-front.webp?${V}`),
        loadTex(`./assets/web/pack-back.webp?${V}`),
        loadTex(`./assets/web/pack-gold-front.webp?${V}`),   // SC4 GOLD wrapper
      ]);
      for (const t of texes) {
        t.colorSpace = THREE.SRGBColorSpace;
        t.anisotropy = MAX_TEXTURE_ANISOTROPY;
        t.minFilter = THREE.LinearMipmapLinearFilter;
        t.magFilter = THREE.LinearFilter;
        t.generateMipmaps = true;
      }
      packTexes = texes;
    }
    await Promise.all([packAudioReady, packAudioUnlocked]);
  } catch (err) {
    console.error('[nomadmon-pack] load failed:', err);
    const here = live();
    bail();
    if (here) {
      toast('The pack would not load. Check your connection and try again.');
      shellUI.go('title');
    }
    return;
  }
  if (!live()) { bail(); return; }         // the player went Home during the cold load: nothing spent
  setBinder(false); if (inspecting) setInspect(false); closeSolo();
  closeLab(); videoRelease();
  document.body.classList.add('packing');
  // SC22: body.packing is set BEFORE the build, so ANY throw inside startPack
  // strands the player behind pack chrome with no pack. Nothing that can
  // reject is allowed to leave that state on screen — see the catch below.
  let packOk;
  try {
  packOk = await mod.startPack({
    renderer, composer, renderPass, bloomPass,
    buildCard, deck,
    // SC2-B: identity spends the credit; server packs prescribe the 5 cards
    // (ids mapped to card objects here), guest packs keep the local roll.
    acquire: async () => {
      if (!live()) return { ok: false, cancelled: true };   // never spend for a screen the player left
      const grant = await identity.openPack();
      if (!grant.ok) {
        paintEconomy();
        // econfix: a signed-in pack shows only with the server's receipt; the same key retries safely
        if (grant.failed) toast('The pack did not open. Check your connection and tap Open again: you will not be charged twice.', 6000);
        return grant;
      }
      if (!live()) {                          // paid while the player left: the cards are theirs, no ceremony
        identity.refreshState().then(() => { refreshBinderOwnership(); paintEconomy(); paintDeckBadge(); });
        toast('Your pack is in your Collection.');
        return { ok: false, cancelled: true };
      }
      // SC4: gold rides along — the guest local roll draws 6 gold-odds cards
      return { ok: true, gold: !!grant.gold, first: !!grant.first, eternal: grant.eternal || null, cards: grant.cards ? grant.cards.map(c => cardsById[c.id]).filter(Boolean) : null };
    },
    // ETERNAL pity meter + WISH GRANTED (src/eternal-wish.js)
    eternalMeter: setEternalMeter, wishStamp, isWishGranted,
    onContents: (contents) => {
      if (identity.packsOpened() <= BATTLE_UNLOCK_PACKS) gatePackCards = contents.slice();   // v218: they fly into Battle after the pack
      setArtTexLive('pack', new Set(contents.map(d => deck.indexOf(d)).filter(i => i >= 0)
        .map(i => artTexKey(i))));
      // SC21: the tear and the first flip buy us several seconds — spend them
      // pulling this pack's loops so every reveal turns onto live video.
      prefetchClips(contents, 8);
      notePackLevelUps(identity.notePackContents(contents));
      if (packLevelUps.size) loadLevelFx().then(m => m.prewarm()).catch(() => {});
      refreshBinderOwnership();
      paintEconomy();
      paintDeckBadge();
    },
    makeDeckStack,               // SC2-B P2: the fly-in ceremony's deck stack
    living: { acquire: videoAcquire, release: videoRelease, update: videoUpdate },
    // SC7 WS-C: reveal-panel ACTIVATE/WATCH -> the full-video overlay
    lab: { open: openLab, isOpen: () => labOpen },
    // SC9-C: OPEN ALL montage — gate awareness + the TO BATTLE handoff
    // (the unlock ceremony itself fires on the title show, as always)
    gateLocked: () => battleLocked(),
    toBattle: () => shellUI.go('title'),
    // v331 FLICKER: Done -> home: the veil rises over the still-running pack, which tears down under it (it tore down
    // at once: the empty collection header / arrows showed through the 0.4 s veil fade)
    coverFirst: () => { if (shellUI.currentScreen() !== 'fan') return false; raiseVeil(); return true; },
    modeName: MODE_NAME,
    // P6: pack reveals can flip to the stat-sheet back (same legibility contract)
    sheet: { build: buildStatSheet, update: updateStatSheet },
    // SC18: the god-pull aftermath + the card back read the first-claim registry
    firstClaims: () => identity.firstClaims(),
    ensureArtTex: (d) => ensureArtTex(deck.indexOf(d)),
    packFrontTex: packTexes[0], packBackTex: packTexes[1], packGoldTex: packTexes[2],
    environment: scene.environment,
    gyro: () => gyroTilt,
    perfMode: () => perfMode,
    pixelCap: () => pixelCap,
    onEnd: () => {
      setArtTexLive('pack', null);
      packActive = false;
      document.body.classList.remove('packing');
      packBtn.disabled = false;
      renderPass.scene = scene;
      renderPass.camera = camera;
      bloomPass.strength = 0.5;
      composer.setSize(innerWidth, innerHeight);
      fitCamera();
      // Pack done -> home. The pack hid the fan at its start and nothing ever
      // re-showed it, so Done used to leave the player on an empty MY DECK
      // (header + name, no card). The fan is retired as a destination (the
      // Collection's X goes home too); go('fan') rebuilds it via prepFan.
      if (shellUI.currentScreen() === 'fan') shellUI.go('title');
      // SC2-B: pack done — refresh state so binder counts + credits are fresh
      identity.refreshState().then(() => {
        refreshBinderOwnership(); paintEconomy(); paintPlayerChip(); paintDeckBadge();
        // duplicates that levelled a card earn the full ceremony once the
        // reveal is over (the reveal itself keeps its inline LV UP chip)
        // v331 FLICKER: once the home veil is down (it fired mid-fade, over the veil)
        afterVeil(() => showLevelUps(takePackLevelUps()));
      });
    },
  });
  } catch (err) {
    // The ceremony could not be built (art fetch failed, asset 500, decode
    // error). Tear the pack chrome back down, hand the credit-free player a
    // designed message in house style, and leave them on a live screen.
    console.error('[nomadmon-pack] build failed, recovering:', err);
    try { pack.abortPack(); } catch { /* the module may not have engaged */ }
    setArtTexLive('pack', null);
    document.body.classList.remove('packing');
    packBtn.disabled = false;
    renderPass.scene = scene; renderPass.camera = camera;
    bloomPass.strength = 0.5;
    composer.setSize(innerWidth, innerHeight);
    fitCamera();
    audio.stopMusic({ fade: 1.0 });
    audio.playPool('title');
    toast('The foil would not tear — check your connection and try again.');
    packActive = false;
    if (shellUI.currentScreen() === 'fan') shellUI.go('title');   // the fan is hidden: go home
    paintEconomy();
    showLevelUps(takePackLevelUps());
    return;
  }
  packActive = packOk !== false;           // false = credit refused, mode never ran
  if (packActive && !live()) pack.abortPack();   // left while the pack was still building: never mount it
}

// ---------------------------------------------------------------- notices
// SC22: one designed way to tell the player something went wrong. Reuses the
// now-playing toast's shape (same position, same motion) with a .warn accent
// so a failure reads as part of the world, not a browser alert.
let noticeT = 0;
function toast(msg, ms = 4200) {
  const el = document.getElementById('npToast');
  if (!el) return;
  el.textContent = msg;
  el.classList.add('show', 'warn');
  clearTimeout(noticeT);
  noticeT = setTimeout(() => el.classList.remove('show', 'warn'), ms);
}

// LEVEL-UP CELEBRATIONS (src/levelup-fx.js, lazy): every place a card gains
// a level lands here. One crossing = the single ceremony; several at once (a
// win levels the whole five) = ONE group ceremony, never a queue of them.
// Star cost is asked of deckrules at both levels so a level-aware cost (Holo /
// Awakened raising it) shows up on its own; today they are equal and no star
// change is shown.
let levelFx = null;
let levelFxCover = false;
function qaLevelFx(kind, o = {}) {
  const pick = (rarity, skip = 0) => deck.filter(d => d.rarity === rarity && !d.forged)[skip] || deck[skip];
  const row = (d, from, to, sf, st) => ({ card: d, fromLevel: from, toLevel: to,
    starFrom: sf ?? starAt(d, from), starTo: st ?? starAt(d, to) });
  return loadLevelFx().then((mod) => {
    if (kind === 'player' || kind === 'player3') {
      const from = o.from ?? (kind === 'player3' ? 3 : 4), to = o.to ?? (kind === 'player3' ? 6 : from + 1);
      return mod.playerLevelUp({ fromLevel: from, toLevel: to, starsGained: o.stars ?? (to - from) });
    }
    if (kind === 'group') {
      const ds = o.ids ? o.ids.map(id => cardsById[id]).filter(Boolean)
        : [pick('legendary'), pick('epic'), pick('rare'), pick('common'), pick('epic', 1)];
      return mod.cardLevelUps(ds.map((d, i) => row(d, 2 + i, 3 + i + (i === 1 ? 1 : 0))));
    }
    // UNDERDOG XP: one card catching up three levels at once (1 -> 4), and the same inside a five-card group
    if (kind === 'underdog') { const d = (o.id && cardsById[o.id]) || pick('common'); return mod.cardLevelUp({ ...row(d, o.from ?? 1, o.to ?? 4), underdog: true }); }
    if (kind === 'underdog-group') {
      const ds = [pick('common'), pick('legendary'), pick('epic'), pick('rare'), pick('epic', 1)];
      return mod.cardLevelUps(ds.map((d, i) => (i ? row(d, 6, 7) : { ...row(d, 1, 4), underdog: true })));
    }
    const d = (o.id && cardsById[o.id]) || pick(kind === 'holo' ? 'epic' : 'rare');
    if (kind === 'holo') return mod.cardLevelUp(row(d, o.from ?? 4, o.to ?? 5, o.starFrom ?? 2, o.starTo ?? 3));
    return mod.cardLevelUp(row(d, o.from ?? 6, o.to ?? 7, o.starFrom, o.starTo));
  });
}
function loadLevelFx() {
  if (!levelFx) {
    levelFx = import('./levelup-fx.js').then((mod) => {
      mod.configure({
        version: V,
        face: (d) => {
          const rar = RARITY[d.rarity] || RARITY.common;
          return { art: artSrc(d, V), frame: getFrameTexture(rar, crestImg).image,
            back: backTexRaw && backTexRaw.image, rar, focalX: d.focalX ?? 0.5,
            // loadTex decodes ImageBitmaps pre-flipped for WebGL (imageOrientation 'flipY')
            backFlipped: typeof ImageBitmap !== 'undefined' && backTexRaw && backTexRaw.image instanceof ImageBitmap };
        },
        quality: () => ({ perfMode }),
        onCover: (on) => { levelFxCover = !!on; },
      });
      return mod;
    });
  }
  return levelFx;
}
const starAt = (d, level) => starCost(d, level);
// v12 PLAYER LEVEL: after a battle the player's level-up moment plays first
// (playerLevelUp, ~4 s, tap skips), then the card ceremonies (whose rows carry
// the levelled star cost, starAt above). `px` is what the results strip drew
// ({ fromLevel, toLevel }); a gauntlet leg has no strip, so read the award.
// v331: run fn once the shell veil is down (a ceremony never starts over a screen change), capped at 8 s
function afterVeil(fn) {
  const t0 = performance.now(), veil = document.getElementById('veil');
  const chk = () => { if (!veil.classList.contains('on') || performance.now() - t0 > 8000) fn(); else requestAnimationFrame(chk); };
  chk();
}
function celebrate(award, px) {
  let before = null, after = null;
  if (px) { before = px.fromLevel; after = px.toLevel; }
  else if (award && award.plevel && award.plevel.gained > 0) {
    before = plevelProgressOf(award.plevel.beforeXp).level;
    after = plevelProgressOf(award.plevel.xp).level;
  }
  const cards = () => showLevelUps(award && award.progress);
  // v331 FLICKER: a results ceremony (px: the results ring) is cancelled when the player leaves the results before its
  // timer: it used to fire over the veil / the next screen (Continue or Rematch inside the ~2.4 s ring fill)
  const stillHere = () => !px || (shellUI.currentScreen() === 'inter' && !document.getElementById('veil').classList.contains('on'));
  // the card ceremonies wait for the results ring to finish filling
  if (after == null || !(after > before)) { if (px) setTimeout(() => { if (stillHere()) cards(); }, px.ms ?? 0); else cards(); return; }
  setTimeout(() => {
    if (!stillHere()) { paintPlayerChip(); return; }
    loadLevelFx()
      .then(mod => mod.playerLevelUp({ fromLevel: before, toLevel: after, starsGained: budgetOf(after) - budgetOf(before) }))
      .catch(err => console.warn('[nomadmon] player level-up ceremony unavailable', err))
      .then(() => { paintPlayerChip(); return cards(); });
  }, px ? (px.ms ?? 1300) : 300);   // let the results ring fill first
}
// v12 PLAYER LEVEL QA (FX Lab / frame capture): the results XP ring on a
// stub interstitial, then the real level-up sequencing. No battle, no rng, and
// nothing is written: the XP shown is computed, never saved.
function qaResults({ won = true, beforeXp = null, gained = null } = {}) {
  const xp0 = beforeXp ?? identity.playerXp();
  const award = matchXpOf({ won, rounds: 8, dmgShare: won ? 3.9 : 2.8, kos: won ? 3 : 2, drawQ: 84, perfects: 3,
    difficulty: 'normal', mode: 'solo', bracket: 'even', firstWinOfDay: won });
  const g = gained ?? award.total;
  // a forced `gained` trims the parts from the last (as matchXp's cap does), so the breakdown sheet
  // adds up to the total it shows (it used to list 184 under a +60 total)
  const parts = award.parts.map(p => ({ ...p }));
  for (let i = parts.length - 1, over = award.total - g; i >= 0 && over > 0; i--) { const cut = Math.min(parts[i].xp, over); parts[i].xp -= cut; over -= cut; }
  const mvp = deck.find(d => d.role !== 'support' && d.rarity === 'legendary' && !d.forged) || deck[0];
  shellUI.showInterstitial({ won, stats: { mvp: { id: mvp.id, name: mvp.name, art: mvp.art, focalX: mvp.focalX, damage: 188 } } },
  { onContinue: () => shellUI.go('title') });
  const px = { beforeXp: xp0, xp: xp0 + g, gained: g, parts: parts.filter(p => p.xp > 0) };
  return new Promise(res => setTimeout(() => { celebrate({ plevel: px, progress: [] }, shellUI.showPlayerXp(px)); res(px); }, 900));
}
function showLevelUps(progress) {
  const crossed = (progress || []).filter(p => p && cardsById[p.id] && p.level > (p.beforeLevel || p.level));
  if (!crossed.length) return Promise.resolve();
  const rows = crossed.map((p) => {
    const card = cardsById[p.id];
    return { card, fromLevel: p.beforeLevel, toLevel: p.level,
      starFrom: starAt(card, p.beforeLevel), starTo: starAt(card, p.level), underdog: (p.underdog | 0) > 0 };
  });
  return loadLevelFx().then(mod => mod.cardLevelUps(rows))
    .catch(err => console.warn('[nomadmon] level-up ceremony unavailable', err));
}
function levelFxOpen() {
  const e = document.getElementById('levelUpFx');
  return !!e && e.classList.contains('on');
}

// ---------------------------------------------------------------- duplicate feed
// One capability-gated affordance lives in the existing focused-card strip.
// Every number and decision shown here comes from the quote/feed responses;
// this layer only animates those facts and hands level crossings to the shared
// ceremony above.
const feedBtn = document.getElementById('feedBtn');
const tradeBtn = document.getElementById('tradeBtn');
const spareBtn = document.getElementById('spareBtn');
const spareSheet = document.getElementById('spareSheet');
const feedFx = document.getElementById('feedFx');
let feedQuoteSeq = 0;
let feedBusy = false;
let feedMomentTimer = 0;
let feedHapticTimer = 0;
let feedCountRaf = 0;
let feedMomentResult = null;
let feedMomentCard = null;
let feedLandVariant = 0;
let feedSounds = [];
let tradeQuote = null;
let tradingUI = null;

function focusedCardData() { return (soloCard || cards[focusIndex])?.data || null; }

async function paintFeedControl() {
  const seq = ++feedQuoteSeq;
  const card = focusedCardData();
  spareSheet.classList.remove('open');
  spareSheet.setAttribute('aria-hidden', 'true');
  spareBtn.hidden = true;
  feedBtn.hidden = true;
  feedBtn.disabled = true;
  tradeBtn.hidden = true;
  tradeBtn.disabled = true;
  tradeQuote = null;
  if (!inspecting || feedBusy || !card
    || (!identity.canFeedDuplicates() && !identity.canTradeSpares())) return;
  const [feedResult, tradeResult] = await Promise.allSettled([
    identity.duplicateFeedQuote(card.id, card.rarity), trading.quote(card),
  ]);
  if (seq !== feedQuoteSeq || !inspecting || focusedCardData()?.id !== card.id) return;
  const quote = feedResult.status === 'fulfilled' ? feedResult.value : { visible: false, canFeed: false };
  tradeQuote = tradeResult.status === 'fulfilled' ? tradeResult.value : { visible: false, canTrade: false };
  if (quote.visible) {
    feedBtn.textContent = quote.actionLabel;
    feedBtn.disabled = !quote.canFeed;
    feedBtn.hidden = false;
  }
  if (tradeQuote.visible) {
    tradeBtn.textContent = tradeQuote.actionLabel;
    tradeBtn.disabled = !tradeQuote.canTrade;
    tradeBtn.hidden = false;
  }
  if (quote.visible || tradeQuote.visible) {
    spareBtn.textContent = tradeQuote.spareLabel || quote.spareLabel || 'SPARE';
    spareBtn.hidden = false;
  }
}

spareBtn.addEventListener('click', () => {
  const open = !spareSheet.classList.contains('open');
  spareSheet.classList.toggle('open', open);
  spareSheet.setAttribute('aria-hidden', String(!open));
  audio.ui('sheet', { rate: open ? 1 : 0.9 });
});

function mutationKey() {
  if (crypto.randomUUID) return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(18));
  return [...bytes].map(n => n.toString(16).padStart(2, '0')).join('');
}

function finishFeedMoment(skipped = false) {
  if (!feedMomentResult) return;
  clearTimeout(feedMomentTimer);
  clearTimeout(feedHapticTimer);
  cancelAnimationFrame(feedCountRaf);
  if (skipped) for (const sound of feedSounds) { try { sound.stop(); } catch { /* ended */ } }
  feedSounds = [];
  feedFx.classList.remove('on');
  const result = feedMomentResult;
  const card = feedMomentCard;
  feedMomentResult = null;
  feedMomentCard = null;
  feedBusy = false;
  feedFx.querySelector('.feed-total').textContent = result.progressAfter.label;
  refreshBinderOwnership();
  if (card && focusedCardData()?.id === card.id) bindCardDOM(soloCard || cards[focusIndex]);
  if (result.levelAfter > result.levelBefore) {
    setInspect(false);
    showLevelUps([{ id: result.cardId, beforeLevel: result.levelBefore,
      level: result.levelAfter, xp: result.xpAfter, gained: result.xpGained }]);
  } else if (inspecting) paintFeedControl();
}

function playFeedMoment(result, card) {
  feedMomentResult = result;
  feedMomentCard = card;
  const echo = feedFx.querySelector('.feed-echo img');
  echo.src = artSrc(card, V);
  echo.style.objectPosition = thumbPos(card);
  feedFx.querySelector('.feed-gain').textContent = result.gainLabel;
  feedFx.querySelector('.feed-spares').textContent = `${result.spareBeforeLabel}  →  ${result.spareAfterLabel}`;
  const total = feedFx.querySelector('.feed-total');
  total.textContent = result.progressBefore.label;
  const fill = feedFx.querySelector('.feed-fill');
  fill.style.transition = 'none';
  fill.style.transform = `scaleX(${result.progressBefore.ratio})`;
  void fill.offsetWidth;
  fill.style.transition = '';
  feedFx.classList.remove('on');
  void feedFx.offsetWidth;
  feedFx.classList.add('on');
  requestAnimationFrame(() => {
    const target = result.levelAfter > result.levelBefore ? 1 : result.progressAfter.ratio;
    fill.style.transform = `scaleX(${target})`;
  });

  feedLandVariant = (feedLandVariant % 3) + 1;
  feedSounds = [
    audio.play('feedcrack', { gain: 0.82 }),
    audio.play('feedsuction', { gain: 0.74, when: 0.08 }),
    audio.play(`feedland${feedLandVariant}`, { gain: 0.92, when: 0.42 }),
  ].filter(Boolean);
  const tier = Math.max(1, Math.min(6, Number(result.impactTier) || 1));
  feedHapticTimer = setTimeout(() => haptics.buzz(
    tier >= 5 ? [18, 24, 42] : tier >= 3 ? [15, 25, 28] : [12, 28, 20]), 420);

  const countStart = performance.now();
  const countMs = REDUCED ? 260 : 620;
  const count = (now) => {
    const t = Math.min(1, (now - countStart) / countMs);
    const xp = Math.round(result.xpBefore + (result.xpAfter - result.xpBefore) * (1 - (1 - t) ** 3));
    total.textContent = `${xp} XP`;
    if (t < 1) feedCountRaf = requestAnimationFrame(count);
    else total.textContent = result.progressAfter.label;
  };
  feedCountRaf = requestAnimationFrame(count);
  feedMomentTimer = setTimeout(() => finishFeedMoment(false), REDUCED ? 430 : 980);
}

feedBtn.addEventListener('click', async () => {
  const card = focusedCardData();
  if (!card || feedBusy || feedBtn.disabled) return;
  feedBusy = true;
  spareSheet.classList.remove('open');
  spareSheet.setAttribute('aria-hidden', 'true');
  feedQuoteSeq++;
  feedBtn.disabled = true;
  try {
    const result = await identity.feedDuplicate(card.id, mutationKey(), card.rarity);
    playFeedMoment(result, card);
  } catch (err) {
    feedBusy = false;
    toast(err.message || 'That spare is no longer available.');
    await identity.refreshState();
    refreshBinderOwnership();
    paintFeedControl();
  }
});
tradeBtn.addEventListener('click', () => {
  const card = focusedCardData();
  if (!card || !tradeQuote?.canTrade || tradeBtn.disabled || !tradingUI) return;
  spareSheet.classList.remove('open');
  spareSheet.setAttribute('aria-hidden', 'true');
  tradingUI.openComposer(card, tradeQuote);
});
document.addEventListener('pointerdown', () => {
  if (feedFx.classList.contains('on')) finishFeedMoment(true);
}, true);

// ---------------------------------------------------------------- audio chrome
const muteBtn = document.getElementById('muteBtn');
function paintMute() { muteBtn.textContent = audio.muted() ? '✕ ♪' : '♪'; }
// ONBOARDING: the music switch on the title and in battle (.musicTog)
const MUSIC_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
  'stroke-linejoin="round" aria-hidden="true"><path d="M9 18V5.5l11-2V16"/><circle cx="6" cy="18" r="3"/><circle cx="17" cy="16" r="3"/>';
function paintMusic() {
  const off = audio.musicMuted();
  for (const b of document.querySelectorAll('.musicTog')) {
    b.innerHTML = MUSIC_ICON + (off ? '<path d="M3 3l18 18"/></svg>' : '</svg>');
    b.classList.toggle('off', off);
    b.setAttribute('aria-pressed', off ? 'false' : 'true');
    b.setAttribute('aria-label', off ? 'Music off' : 'Music on');
  }
}
function placeTitleMusic() {
  const s = document.getElementById('titleSettings'), m = document.getElementById('titleMusic');
  if (!s || !m) return;
  const r = s.getBoundingClientRect();
  if (r.width) m.style.setProperty('--tmr', `${Math.round(innerWidth - r.left + 8)}px`);
  // polish E: the fullscreen button sits left of the music disc (and of the leaderboard pill when the top bar holds it)
  const f = document.getElementById('titleFullscreen');
  if (!f || f.hidden) return;
  let left = m.getBoundingClientRect().left;
  const lb = document.getElementById('titleLb'), lr = lb && lb.getBoundingClientRect();
  if (lr && lr.width && lr.top < 80) left = Math.min(left, lr.left);
  if (left) f.style.setProperty('--tfr', `${Math.round(innerWidth - left + 8)}px`);
}
paintMusic();
for (const b of document.querySelectorAll('.musicTog')) {
  b.addEventListener('click', (e) => {
    e.stopPropagation();
    audio.unlock();
    audio.setMusicMuted(!audio.musicMuted());
    paintMusic();
  });
  for (const ev of ['pointerdown', 'pointerup']) b.addEventListener(ev, (e) => e.stopPropagation());
}
addEventListener('resize', placeTitleMusic);
requestAnimationFrame(placeTitleMusic);
setTimeout(placeTitleMusic, 1500);
paintMute();
muteBtn.addEventListener('click', () => {
  audio.unlock();
  audio.setMuted(!audio.muted());
  paintMute();
});
// P6 SFX audition page (?audition=1 also lands there)
document.getElementById('auditionBtn').addEventListener('click', () => {
  location.href = `./audition.html?${V}`;
});
if (q.get('audition') === '1') location.replace(`./audition.html?${V}`);

// ---------------------------------------------------------------- fan layout + loop
// SC2-B checklist rule: unowned cards render as dark silhouettes in the fan
// and solo (binder-opened) views — art dimmed hard, name stays visible (DOM).
// SC9-B: SHOWCASE mode (cardless guest browsing the featured set) is a
// gallery, not a checklist — nothing silhouettes there.
// SC38 — THE ONLY WAY THE REPORTED `fanShowcase` ReferenceError CAN EXIST.
// Six runs have failed to reproduce it, so this is hardening, NOT a claimed
// fix. But the error string can only be produced one way: `fanShowcase` was a
// module-scope `let` declared ~670 lines BELOW this arrow, and a `let` sits in
// the temporal dead zone until its declaration is evaluated. Any call to
// ownDim() before that line throws exactly "Cannot access 'fanShowcase' before
// initialization". Today no such path exists — ownDim is only reached from
// tick(), and the rAF loop is not armed until the end of the module — so the
// hazard is latent, not live, and that is consistent with it never reproducing
// in a harness. The declaration now precedes its use, which removes the dead
// zone entirely. The neighbouring SC32 comment on fanComposition() records the
// same class of bug being reasoned about and left in place; this one is closed.
let fanShowcase = true;              // boot state: featured cards until known
const ownDim = (c) => (fanShowcase || identity.ownedCount(c.data.id) > 0) ? 1 : 0.15;
const clock = new THREE.Clock();
let lastNow = performance.now();

function tick(now) {
  requestAnimationFrame(tick);
  const t = clock.getElapsedTime();
  ALT_CLOCK.value = t;      // SC4: living-painting sway ticks in every mode
  const dt = Math.min((now - lastNow) / 1000, OFFLINE_RENDER ? 0.1 : 0.05);
  lastNow = now;
  lutController.update(dt);
  probe(now);
  // A level-up ceremony's veil fully covers the app: skip the scene for those
  // ~2.5 s (the canvas keeps its last frame underneath), so a phone pays for
  // one full-screen render, not two.
  if (levelFxCover) return;
  // THE CODEX is an opaque DOM overlay: the app's scene draws nothing under it (one GL context at a time)
  if (codexOpen) return;
  if (campaignMapUp) return;   // THE ATLAS (campaign map v2) draws its own scene; the app's pauses under it
  if (duelUp && !battleActive) return;   // INK DUEL: the title scene pauses under it (its fights run the battle stage below)

  // battle / pack / deck modes own the frame entirely (renderPass swap)
  // The results screen's own scene (src/results-bg.js) is opaque over the
  // finished battle, so the battle stops rendering underneath it.
  if (battleActive && battle && shellUI.currentScreen() === 'inter') return;
  if (battleActive && battle) { battle.updateBattle(now, dt); return; }
  if (packActive && pack) { pack.updatePack(now, dt); return; }
  if (deckActive && deckmod) { deckmod.updateDeck(now, dt); return; }
  if (slotActive && slotmod) { slotmod.updateSlot(now, dt); return; }

  // V2 INTRO: owns the frame while it plays. The entry dive (if still
  // running) keeps drawing over the intro's void; the title stays hidden until
  // the handoff, then fades in underneath the intro's own fade-out.
  if (introLayer.fx?.active) {
    const fx = introLayer.fx;
    backdrop.visible = fx.handedOff;       // the intro's void is the floor until then
    if (fx.handedOff) { updateTitleLayer(t, dt); updateDeckStack(t, dt); }
    else {
      if (titleLayer.fx) titleLayer.fx.group.visible = false;
      deckStack.group.visible = false;
    }
    camera.position.x = REDUCED ? 0 : Math.sin(t * 0.23) * 0.035;
    camera.position.y = REDUCED ? 0 : Math.cos(t * 0.31) * 0.025;
    camera.lookAt(0, 0, -2);
    dust.rotation.z = t * 0.012;
    backdropUniforms.uTime.value = t;
    if (entryLayer.fx?.active) entryLayer.fx.update(t, dt, titleLayer.pointer);
    fx.update(t, dt);
    composer.render();
    return;
  }

  // The front door shares the title renderer but owns the foreground until
  // its iris-through exit completes. The destination is already drawing
  // underneath, which guarantees the handoff cannot expose a black frame.
  if (entryLayer.fx?.active) {
    updateTitleLayer(t, dt);
    camera.position.x = REDUCED ? 0 : Math.sin(t * 0.23) * 0.035;
    camera.position.y = REDUCED ? 0 : Math.cos(t * 0.31) * 0.025;
    camera.lookAt(0, 0, -2);
    dust.rotation.z = t * 0.012;
    backdropUniforms.uTime.value = t;
    entryLayer.fx.update(t, dt, titleLayer.pointer);
    composer.render();
    return;
  }

  // P7 title screen owns the frame: keyart plate + dust + camera drift only
  if (titleLayer.on || titleLayer.fade > 0.01) {
    updateTitleLayer(t, dt);
    if (titleLayer.on) {
      camera.position.x = REDUCED ? 0 : Math.sin(t * 0.23) * 0.07;
      camera.position.y = REDUCED ? 0 : Math.cos(t * 0.31) * 0.05;
      camera.lookAt(0, 0, -2);
      dust.rotation.z = t * 0.012;
      backdropUniforms.uTime.value = t;
      updateDeckStack(t, dt);              // SC2-B P2: deck sits in the title comp
      composer.render();
      return;
    }
  }

  // PERF (v326 review): the opaque Collection used to skip only the final draw, so the hidden fan kept animating
  // and the focused living card kept decoding. While covered the video is paused and the fan's state update runs
  // on every 6th frame only: its smoothing still converges under the overlay (nothing is opened back there; see
  // setBinder), so the scene is settled when the overlay fades out, as before.
  const covered = !!binderOpaqueAt && now >= binderOpaqueAt && !soloCard;
  livingVideo.suspend(covered);
  if (!covered) coveredFrames = 0;
  else if (++coveredFrames % 6 !== 0) return;

  const focused = soloCard || cards[focusIndex];
  const isMythic = focused.rar.prismatic >= 1;

  // P5 LIVING: the focused card (fan or solo) owns the single video element;
  // anything else releases it. Lab overlay open = no living loop either.
  // (a hologram replaces the living loop: the figure is cut from the still art)
  const holoOwns = !!(soloHolo && soloHolo.card === focused);
  if (focused.data.clip && !labOpen && !holoOwns) { if (!covered) videoAcquire(focused); }   // never start a covered decode
  else if (livingVideo.card && (labOpen || holoOwns || livingVideo.card !== focused)) videoRelease();
  if (livingVideo.card && livingVideo.card !== focused) videoRelease();
  videoUpdate(dt);

  // smoothing
  updateManip(dt);
  updateZoomInspect();
  // SC20 carousel: a live drag drives focusPos directly, release glides on
  // momentum and then hands back to the snap lerp toward the integer focus
  if (fanScroll.active) {
    focusPos = fanScroll.pos;
  } else if (fanScroll.glide) {
    fanScroll.pos += fanScroll.vel * dt;
    fanScroll.vel *= Math.exp(-4.2 * dt);
    const hi = fanMax();
    if (fanScroll.pos < FAN_MIN) { fanScroll.pos = FAN_MIN; fanScroll.vel = 0; }
    else if (fanScroll.pos > hi) { fanScroll.pos = hi; fanScroll.vel = 0; }
    fanSyncFocus();
    focusPos = fanScroll.pos;
    if (Math.abs(fanScroll.vel) < 0.35) fanScroll.glide = false;   // snap takes over
  } else {
    focusPos += (focusIndex - focusPos) * 0.10;
  }
  inspectT += ((inspecting ? 1 : 0) - inspectT) * 0.08;
  if (!gyroActive) { gyroTilt.x *= 0.97; gyroTilt.y *= 0.97; }
  // gyro folds in only when the card is at rest and not held
  const gyF = (manip.mode === 'settle' && !manip.held) ? 1 - manip.grab : 0;
  const zoomS = 1 + (manip.zoomNow - 1) * 0.6;
  const grabS = 1 + manip.grab * 0.03;

  // reveal envelope (mythics bloom up slowly)
  revealT = Math.min(revealT + dt * (isMythic ? 0.55 : 0.9), 1);
  // SC2-A WS4: focused aura carries a resting tier presence, not just the
  // reveal spike — mythics radiate, legendaries smolder
  const tierFlare = isMythic ? 0.30 : focused.rar.particles ? 0.14 : 0;
  const flare = Math.exp(-revealT * 3.5) * 1.4 + tierFlare;

  // idle sway (focused card only; suppressed while held/spinning)
  const swayAmt = REDUCED ? 0 : (1 - inspectT) * (manip.mode === 'settle' ? 1 - manip.grab : 0);
  const swayY = Math.sin(t * 0.5) * 0.06 * swayAmt;
  const swayX = Math.cos(t * 0.7) * 0.03 * swayAmt;

  if (soloCard) {
    // binder-opened card: centered, fully manipulable
    const c = soloCard;
    const g = c.group;
    // HOLOPACK: a relief only reads off-axis — in hologram view the card comes
    // a little closer and tips back as the figure peels up, with a slow drift
    const holoOn = !!(soloHolo && soloHolo.card === c);
    soloHoloView += ((holoOn ? 1 : 0) - soloHoloView) * (1 - Math.exp(-4 * dt));
    const hr = holoOn && soloHolo.h?.figure ? soloHolo.h.figure.rise : 0;
    // ...and is framed big: ~70% of the phone's height, centred in the band
    // the DOM chrome leaves free (back pill above, nameplate + Hold below)
    const hf = holoFrame(0.9);
    // v149: the figure rises UP the card now, so the risen card steps down and
    // in a little to give the head room under the top chrome
    g.position.set(0, Math.sin(t * 0.8) * 0.02 + inspectT * 0.52 + soloHoloView * (hf.y - 0.1 * CH * hf.s * hr),
      inspectT * Math.max(camera.position.z - 4.3, 0) + soloHoloView * 0.9);
    // a lifted figure reads as depth only when the card turns under it: a
    // wider, slow drift while risen (presentation clock, never game rng)
    g.rotation.y = manip.ry + gyroTilt.y * gyF + swayY + (REDUCED ? 0 : 0.24 * hr * Math.sin(t * 0.5));
    g.rotation.x = manip.rx + gyroTilt.x * gyF + swayX - 0.26 * hr;
    if (holoQaPose) { g.rotation.y = holoQaPose.ry; g.rotation.x = holoQaPose.rx; }
    g.rotation.z = 0;
    c.depthUpdate(cardMotionTilt());
    g.scale.setScalar((1 + inspectT * 0.06) * zoomS * grabS * (1 + soloHoloView * (hf.s * (1 - 0.1 * hr) - 1)));
    c.foilPlane.visible = true;
    if (c.sparks && !perfMode) {
      c.sparks.visible = true;
      c.sparks.material.uniforms.uTime.value = t;
      c.sparks.material.uniforms.uFade.value = (1 - inspectT * 0.7) * THREE.MathUtils.smoothstep(revealT, 0.15, 0.8);
    }
    c.foilMat.uniforms.uTime.value = t;
    c.foilMat.uniforms.uIntensity.value = 1 - inspectT * 0.5;
    c.foilMat.uniforms.uSweep.value = revealT;
    c.backFoilMat.uniforms.uTime.value = t;
    c.backFoilMat.uniforms.uIntensity.value = 0.8;
    c.edgeMat.uniforms.uTime.value = t;
    c.edgeMat.uniforms.uDim.value = 1 - inspectT * 0.6;
    // an unowned card is previewed at full colour in the hologram (it is
    // marked "Not owned" in the DOM instead of being dimmed to a silhouette)
    const od = Math.max(ownDim(c), soloHoloView);
    c.artMat.color.setRGB(c.rar.glass[0] * od, c.rar.glass[1] * od, c.rar.glass[2] * od);
    c.frameMat.color.setRGB(1.45 * c.frameTint[0], 1.45 * c.frameTint[1], 1.45 * c.frameTint[2]);
    if (soloHolo?.h && soloHolo.card === c) soloHolo.h.update(dt, t);
  }
  else for (let i = 0; i < cards.length; i++) {
    const c = cards[i];
    const o = i - focusPos;                       // signed fan offset
    const ao = Math.abs(o);
    const f = THREE.MathUtils.clamp(1 - ao, 0, 1); // focus factor
    c.focusF = f;
    const oc = THREE.MathUtils.clamp(o, -2.2, 2.2);

    // fanned-hand pose
    const g = c.group;
    g.position.x = 1.06 * o * (1 - 0.07 * ao);
    g.position.z = -0.62 * Math.min(ao, 2.4) + f * inspectT * Math.max(camera.position.z - 4.3, 0);
    g.position.y = -0.07 * ao + f * (Math.sin(t * 0.8) * 0.02 + inspectT * 0.52);
    g.rotation.y = -0.30 * oc + f * (manip.ry + gyroTilt.y * gyF + swayY);
    g.rotation.x = f * (manip.rx + gyroTilt.x * gyF + swayX);
    g.rotation.z = -0.10 * oc;
    if (ao < 1.6) c.depthUpdate(cardMotionTilt());
    const s = (0.9 + 0.1 * f) * (1 + f * inspectT * 0.06)
      * (1 + f * (zoomS - 1)) * (1 + f * (grabS - 1));
    g.scale.setScalar(s);

    // FX budget: full foil/particles on the focused card only; background
    // cards keep frame + art + dim edge (cheap materials).
    const active = f > 0.04;
    c.foilPlane.visible = active;
    c.backFoilMat.visible = active;
    if (c.sparks && !c.sparks.userData.shed) {
      // SC23: eternal's aura is PERMANENT — every other tier's particles are a
      // focus reward that dies as the card leaves the front of the fan. An
      // eternal keeps a floor of aura at any focus weight, so it stays visibly
      // hotter than its neighbours from across the binder.
      const ete = c.rar.eternal;
      c.sparks.visible = active && (ete || f > 0.3);
      c.sparks.material.uniforms.uTime.value = t;
      const fade = f * (1 - inspectT * 0.7) * THREE.MathUtils.smoothstep(revealT, 0.15, 0.8);
      c.sparks.material.uniforms.uFade.value = ete ? Math.max(fade, 0.34 * f + 0.1) : fade;
    }
    if (active) {
      c.foilMat.uniforms.uTime.value = t;
      c.foilMat.uniforms.uIntensity.value = f * (1 - inspectT * 0.5);
      c.foilMat.uniforms.uSweep.value = i === focusIndex ? revealT : 0;
      c.backFoilMat.uniforms.uTime.value = t;
      c.backFoilMat.uniforms.uIntensity.value = f * 0.8;
    }
    c.edgeMat.uniforms.uTime.value = t;
    c.edgeMat.uniforms.uDim.value = (0.35 + 0.65 * f) * (1 - inspectT * 0.6 * f);
    // SC9-B brightness: unfocused neighbors rest ~55% (was near-black at .32),
    // and the FOCUSED art gets a small exposure lift (+12%, light not post).
    const dim = (0.55 + 0.45 * f) * (1 + f * 0.12) * ownDim(c);
    c.artMat.color.setRGB(c.rar.glass[0] * dim, c.rar.glass[1] * dim, c.rar.glass[2] * dim);
    const fk = 1.45 * (0.70 + 0.30 * f);
    c.frameMat.color.setRGB(fk * c.frameTint[0], fk * c.frameTint[1], fk * c.frameTint[2]);
  }

  // subtle camera drift
  camera.position.x = Math.sin(t * 0.23) * 0.05;
  camera.position.y = Math.cos(t * 0.31) * 0.04 + inspectT * 0.5;
  const fg = focused.group;
  camera.lookAt(fg.position.x * 0.2, fg.position.y * inspectT, fg.position.z * inspectT);

  // shared aura tracks the focused card
  aura.position.x += (fg.position.x - aura.position.x) * 0.12;
  aura.position.z = fg.position.z * 0.9;
  aura.scale.setScalar(fg.scale.x);
  auraUniforms.uTime.value = t;
  auraUniforms.uFade.value = 1 - inspectT;
  auraUniforms.uFlare.value = flare;

  // mythic focus slowly blooms up; others rest at base (single owner: this scalar)
  bloomTarget = isMythic && !inspecting ? 0.5 + 0.35 * THREE.MathUtils.smoothstep(revealT, 0.2, 1.0) : 0.5;
  bloomPass.strength += (bloomTarget - bloomPass.strength) * 0.05;

  backdropUniforms.uTime.value = t;
  dust.rotation.z = t * 0.008;
  updateDeckStack(t, dt);                  // SC2-B P2: deck in the fan corner too

  // grab shadow: sits behind the active card, deepens while held
  shadowMesh.position.set(fg.position.x, fg.position.y - 0.22, fg.position.z - 0.65);
  shadowMesh.scale.setScalar(fg.scale.x * (1 + manip.grab * 0.12));
  shadowMesh.material.opacity = 0.28 + manip.grab * 0.34;

  // P3 stat-sheet back overlay tracks the focused/solo card's flip state. A
  // Collection inspection owns that same DOM sheet directly, so the 3D tracker
  // must not overwrite its centered transform while the grid remains behind it.
  if (binderSheetIndex < 0) updateStatSheet(focused, dt, binderEl.classList.contains('open'));

  // Every state update above still runs — only the DRAW is skipped, so the
  // scene is already correct the instant the overlay starts fading out.
  if (binderOpaqueAt && now >= binderOpaqueAt && !soloCard) return;
  composer.render();
}

{ const start = focusIndex; focusIndex = -1; setFocus(start); revealT = 0; }

// ---------------------------------------------------------------- SC7 WS-A: pack gate
// Battle is EARNED. Solo + Multiplayer stay SEALED until the player has opened
// BATTLE_UNLOCK_PACKS packs (server truth = /state packsOpened; guest = the
// local lifetime counter). New-player math: 6 signup + 3 daily = 9 on day one.
// v13 (Jack, 2026-10-04): the seal lifts after THREE packs, not nine. Pack 1
// is support-free (a legal squad by construction), pack 3 is always GOLD (so
// every player owns a Legendary+ before the first fight), and the server tops
// up a starter pack if a hand is somehow still not legal at the unlock. The
// other six day-one packs become the reward loop between early battles.
// The UNLOCK CEREMONY fires ONCE (persisted) on the first title show after
// the 3rd pack — seals crack, burn away in gold, the buttons ignite.
const BATTLE_UNLOCK_PACKS = 3;
const K_GATE_CEREMONY = 'nomadmon-gate-ceremony';
const K_GATE_MID = 'nomadmon-gate-mid';
const gate = { wasLocked: false, lastMock: -1e9, ceremonyRunning: false };
const gateChipEl = document.getElementById('gateChip');
// v245 (Jack: "your only option initially is open packs"): the Forge is sealed with them
const sealedBtns = () => [document.getElementById('menuSolo'), document.getElementById('menuMp'), document.getElementById('menuForge'), document.getElementById('menuCodex')].filter(Boolean);

function battleLocked() { return identity.packsOpened() < BATTLE_UNLOCK_PACKS; }
function ceremonyPending() {
  return gate.wasLocked && !battleLocked() &&
    localStorage.getItem(K_GATE_CEREMONY) !== '1';
}

// v218 GATE CHARGE: packs give you fighters, fighters unlock Battle — shown,
// not said. After each of the first three packs the new fighters fly as mini
// cards into the sealed Battle button, which charges one stage (a lit segment
// under it + a pip in the chip); the 3rd charge hands off to the unlock
// ceremony. `charged` lags packsOpened() until the flight has been seen.
const K_GATE_CHARGED = 'nomadmon-gate-charged';
let gatePackCards = null;            // the last pack's cards (onContents)
let gateChargeRunning = false;
function gateCharged() { try { return +(localStorage.getItem(K_GATE_CHARGED) || 0); } catch { return BATTLE_UNLOCK_PACKS; } }
function setGateCharged(n) { try { localStorage.setItem(K_GATE_CHARGED, String(n)); } catch { /* private */ } }
function paintGateCharge(lit) {
  // titlelock (Jack): the three-dash meter under Campaign is retired; at rest a sealed item reads
  // locked by look alone (dim + lock emblem) and the progress surfaces only on a sealed tap
  const b = document.getElementById('menuSolo');
  if (b) b.dataset.charge = String(lit);
}
/** Runs the flight if a stage is owed; returns the ms the title should wait. */
function maybeGateCharge() {
  const n = Math.min(identity.packsOpened(), BATTLE_UNLOCK_PACKS);
  const c = gateCharged();
  if (c >= n || gateChargeRunning) return 0;
  const btn = document.getElementById('menuSolo');
  const cards = (gatePackCards || []).filter(d => d && d.role !== 'support').slice(0, 5);
  gatePackCards = null;
  // no flight owed a picture (veteran, reload, reduced motion): just record it; the
  // next title repaint shows it (paintGate is not safe this early in boot)
  if (!gate.wasLocked || REDUCED || !btn || !cards.length) { setGateCharged(n); return 0; }
  gateChargeRunning = true;
  const START = 700, STAG = 110, FLY = 780;
  setTimeout(() => {
    const r = btn.getBoundingClientRect();
    const tx = r.left + r.width / 2, ty = r.top + r.height / 2;
    cards.forEach((d, i) => {
      const el = document.createElement('img');
      el.className = 'gfly'; el.alt = ''; el.src = artSrc(d, V, 'thumb');
      const sx = innerWidth / 2 + (i - (cards.length - 1) / 2) * Math.min(64, innerWidth / 6.5), sy = innerHeight * 0.42;
      el.style.left = `${sx}px`; el.style.top = `${sy}px`;
      document.body.appendChild(el);
      const a = el.animate([
        { transform: 'translate(-50%,-50%) scale(.6) rotate(0deg)', opacity: 0 },
        { transform: 'translate(-50%,-50%) scale(1) rotate(0deg)', opacity: 1, offset: 0.18 },
        { transform: `translate(calc(-50% + ${(tx - sx) * 0.35}px), calc(-50% + ${(ty - sy) * 0.15 - 60}px)) scale(.9) rotate(${(i % 2 ? 1 : -1) * 12}deg)`, opacity: 1, offset: 0.5 },
        { transform: `translate(calc(-50% + ${tx - sx}px), calc(-50% + ${ty - sy}px)) scale(.18) rotate(${(i % 2 ? -1 : 1) * 30}deg)`, opacity: 0.2 },
      ], { duration: FLY, delay: i * STAG, easing: 'cubic-bezier(.5,0,.75,.4)', fill: 'forwards' });
      a.onfinish = () => {
        el.remove();
        btn.classList.remove('gzap'); void btn.offsetWidth; btn.classList.add('gzap');
        haptics.buzz([8]);
        audio.ui('count', { rate: 1.1 + i * 0.08 });
      };
    });
    setTimeout(() => {                       // the stage lands: segment + pip light, a burst
      setGateCharged(n);
      gateChargeRunning = false;
      paintGate();
      gateEmberBurst(btn, n >= BATTLE_UNLOCK_PACKS ? 22 : 12);
      if (n === 1) setTimeout(() => audio.vo('gate-charged'), 400);   // Shao: Your army grows. The arena stirs.
      gateShockRing(btn);
      audio.play('flare', { gain: 0.45 });
      haptics.buzz([20, 40, 30]);
      btn.classList.remove('gstage'); void btn.offsetWidth; btn.classList.add('gstage');
    }, (cards.length - 1) * STAG + FLY + 60);
  }, START);
  return START + (cards.length - 1) * STAG + FLY + 500;
}
window.__nmGateCharge = { maybeGateCharge, gateCharged };   // QA handle

function paintGate() {
  const n = Math.min(identity.packsOpened(), BATTLE_UNLOCK_PACKS);
  // v218: what the title SHOWS lags packsOpened() until the charge flight lands
  const lit = Math.min(n, gateCharged());
  if (battleLocked() || ceremonyPending() || gate.ceremonyRunning) paintGateCharge(lit);
  if (battleLocked()) {
    gate.wasLocked = true;
    for (const b of sealedBtns()) b.classList.add('sealed');
    battleBtn.classList.add('sealed');
    menuPackEl.classList.add('gatehero');
    // ONBOARDING: progress you can SEE — one pip per pack, lit when opened
    // titlelock: the chip is the sealed-tap message (pips + "Open 2 more packs"), invisible at rest
    const left = BATTLE_UNLOCK_PACKS - lit;
    gateChipEl.innerHTML = `<span class="gwrap"><span class="gpips" aria-hidden="true">${Array.from({ length: BATTLE_UNLOCK_PACKS },
      (_, i) => `<i class="${i < lit ? 'on' : ''}"></i>`).join('')}</span>` +
      `<span class="gtxt">Open ${left}${lit ? ' more' : ''} pack${left === 1 ? '' : 's'} to unlock</span>` +
      `<span class="gsr"> ${MODE_NAME} (${lit}/${BATTLE_UNLOCK_PACKS})</span></span>`;
    gateChipEl.classList.add('show');
    // QA-A: before the unlock the info line retires ("9 packs ready"); titlelock: the
    // gate message is not shown at rest either, only on a sealed tap
    document.getElementById('screenTitle')?.classList.add('gate-quiet');
    // v218: a newcomer's first title, Shao says why packs matter, once
    if (n === 0 && localStorage.getItem('nomadmon-gate-fighters') !== '1') {
      try { localStorage.setItem('nomadmon-gate-fighters', '1'); } catch { /* private */ }
      setTimeout(() => audio.vo('gate-fighters'), 1600);    // Open them up! You need fighters!
    }
    // Shao acknowledges the grind once at the midpoint
    if (n === 2 && localStorage.getItem(K_GATE_MID) !== '1') {   // v13: one pack from the 3-pack unlock
      try { localStorage.setItem(K_GATE_MID, '1'); } catch { /* private */ }
      setTimeout(() => audio.vo('progress-mid'), 900);
    }
  } else if (ceremonyPending() || gate.ceremonyRunning) {
    document.getElementById('screenTitle')?.classList.remove('gate-quiet');
    // hold the sealed presentation — the ceremony owns the un-sealing
    gateChipEl.textContent = `${MODE_NAME} unlocked · ${BATTLE_UNLOCK_PACKS}/${BATTLE_UNLOCK_PACKS}`;
  } else {
    document.getElementById('screenTitle')?.classList.remove('gate-quiet');
    for (const b of sealedBtns()) b.classList.remove('sealed');
    battleBtn.classList.remove('sealed');
    menuPackEl.classList.remove('gatehero');
    gateChipEl.classList.remove('show');
    // veteran path: was never locked this session -> the ceremony never fires
    if (!gate.wasLocked) { try { localStorage.setItem(K_GATE_CEREMONY, '1'); } catch { /* private */ } }
  }
}

// tapping a sealed button: shake + Shao's heckle (four lines, random, rate-limited so spam-taps
// don't stack the disrespect)
function lockedTap(btn = null) {
  if (gate.ceremonyRunning) return;
  // v245: the tapped item nudges (all of them when unknown) and the gate chip pulses: wordless
  for (const b of btn ? [btn] : sealedBtns()) {
    b.classList.remove('shake'); void b.offsetWidth; b.classList.add('shake');
  }
  // titlelock (Jack): the message fades in under the tapped item for ~2 s, then fades out
  const at = btn || document.getElementById('menuSolo');
  if (at) {
    const r = at.getBoundingClientRect(), c = gateChipEl.getBoundingClientRect();
    const w = gateChipEl.querySelector('.gwrap')?.offsetWidth || 0;
    const cx = Math.max(12 + w / 2, Math.min(innerWidth - 12 - w / 2, r.left + r.width / 2));
    gateChipEl.style.setProperty('--gx', `${Math.round(cx - (c.left + c.width / 2))}px`);
  }
  gateChipEl.classList.remove('nudge'); void gateChipEl.offsetWidth; gateChipEl.classList.add('nudge');
  gateChipEl.classList.add('tell');
  clearTimeout(gate.tellT); gate.tellT = setTimeout(() => gateChipEl.classList.remove('tell'), 2000);
  haptics.buzz([16, 40, 16]);
  audio.ui('deny');
  // SHAO MENU QUIPS: one heckle per tap at most (it tells a newcomer what to do), never stacked
  // (a busy channel skips it), and a 6 s cooldown from the last request so spam-taps stay quiet
  const now = performance.now();
  if (now - gate.lastMock > 6000) {
    gate.lastMock = now;
    audio.menuQuip('locked');
  }
}

function gateEmberBurst(el, count) {
  const r = el.getBoundingClientRect();
  for (let i = 0; i < count; i++) {
    const s = document.createElement('span');
    s.className = 'gateEmber';
    const a = Math.random() * Math.PI * 2, d = 70 + Math.random() * 170;
    s.style.left = `${r.left + r.width / 2 + (Math.random() - 0.5) * r.width * 0.85}px`;
    s.style.top = `${r.top + r.height / 2 + (Math.random() - 0.5) * r.height * 0.7}px`;
    s.style.setProperty('--dx', `${Math.cos(a) * d}px`);
    s.style.setProperty('--dy', `${Math.sin(a) * d - 50}px`);
    s.style.animationDelay = `${(Math.random() * 0.16).toFixed(3)}s`;
    document.body.appendChild(s);
    setTimeout(() => s.remove(), 1500);
  }
}
function gateShockRing(el) {
  const r = el.getBoundingClientRect();
  const ring = document.createElement('span');
  ring.className = 'gateShock';
  ring.style.left = `${r.left + r.width / 2}px`;
  ring.style.top = `${r.top + r.height / 2}px`;
  document.body.appendChild(ring);
  setTimeout(() => ring.remove(), 1100);
}

// THE UNLOCK CEREMONY — the tutorial's reward moment. Seals CRACK (emissive
// fracture lines build), then burn away in gold light + shockwave + embers;
// the buttons ignite to full color under Shao's proclamation.
function runUnlockCeremony() {
  if (gate.ceremonyRunning) return;
  gate.ceremonyRunning = true;
  try { localStorage.setItem(K_GATE_CEREMONY, '1'); } catch { /* private */ }
  const btns = sealedBtns();
  audio.vo('unlock-ceremony', { interrupt: true });
  haptics.buzz([30, 60, 30, 60, 90]);
  gateChipEl.classList.add('cracking');
  for (const b of btns) b.classList.add('cracking');
  setTimeout(() => {                       // the seals give way
    unlockBeat();
    audio.play('flare', { gain: 1.0 });
    audio.play('slam', { gain: 0.7 });
    haptics.buzz(haptics.PATTERNS.victory || [40, 80, 120]);
    btns.forEach((b, i) => setTimeout(() => {   // v245: each sealed item is revealed in turn
      b.classList.add('igniting');
      gateShockRing(b);
      gateEmberBurst(b, 26);
    }, i * 320));
    gateChipEl.classList.remove('show', 'cracking');
  }, 1900);
  setTimeout(() => {                       // ignited: full color pops in, one after another
    btns.forEach((b, i) => setTimeout(() => {
      b.classList.remove('sealed', 'cracking', 'igniting');
      b.classList.add('ignited');
    }, i * 320));
    battleBtn.classList.remove('sealed');
    menuPackEl.classList.remove('gatehero');
  }, 2650);
  setTimeout(() => {
    for (const b of btns) b.classList.remove('ignited');
    gate.ceremonyRunning = false;
    paintGate();
  }, 5200);
}
// ONBOARDING (play-test: "there should be some sort of announcement"): the
// seals giving way is also a full-screen BATTLE UNLOCKED beat, so the moment
// reads without the narrator. DOM only, self-removing, never blocks input.
function unlockBeat() {
  const el = document.createElement('div');
  el.id = 'unlockBeat';
  el.setAttribute('role', 'status');
  el.innerHTML = `<span class="ub-k">${MODE_NAME}</span><span class="ub-v">Unlocked</span>`;
  document.body.appendChild(el);
  requestAnimationFrame(() => el.classList.add('on'));
  setTimeout(() => el.classList.add('out'), 2300);
  setTimeout(() => el.remove(), 3100);
}
function maybeUnlockCeremony() {
  const wait = maybeGateCharge();            // v218: the 3rd charge lands first
  if (ceremonyPending() && !gate.ceremonyRunning) setTimeout(runUnlockCeremony, 650 + wait);
}
window.__nmGate = { paintGate, runUnlockCeremony, battleLocked, gate };   // QA handle

// ---------------------------------------------------------------- V2 INTRO
// THE INTRO (NOMAD-MON GO! 2) replaces the SC5 title film. A ~7s real-time
// prism cinematic on the shared renderer (src/intro.js, bundled into the lazy
// title chunk): void -> one spark -> spectral fan -> the hand bursts out ->
// three fighters RISE out of their cards (holo.js) -> vortex, flash, and the
// title logo slams under the shock ring. Plays ONCE PER DEVICE (localStorage
// flag set when it starts); Settings keeps the "Intro" toggle (same
// nomadmon-cine key) and adds "Replay Intro". One tap anywhere skips to the
// flash, so a skip still lands on the slam. Reduced motion: one still
// composition for ~2.5s. Deep links / ?view shortcuts never play it. If the
// chunk is not ready by the dive's reveal point the intro is simply skipped.
const INTRO_SEEN = 'nomadmon-intro2-seen';
// PROGRESS GATE (src/progress-gate.js): a returning player on a new device never sees the new-player film, prologue,
// campaign opening or first-time guide again. "Seen" is derived from the ACCOUNT's synced progress, applied when the
// account state lands (boot, sign-in); a decision that comes before it waits for it (bounded).
let idStateP = null;                  // the boot /state fetch of a signed-in device (set below, in the shell boot)
const signedInUnsynced = () => { try { return identity.hasIdentity() && !identity.isGuest() && !identity.state(); } catch { return false; } };
const awaitAccount = (ms = 2500) => (signedInUnsynced() ? Promise.race([idStateP || identity.refreshState(), new Promise((r) => setTimeout(r, ms))]).catch(() => null) : Promise.resolve());
function applyProgressGate() {
  let v = null;
  try { v = veteranSeen(identity.state(), { plevelOf: plevelFromXp }); } catch { v = null; }
  if (!v || !v.veteran) return false;
  try { localStorage.setItem(INTRO_SEEN, '1'); localStorage.setItem(prologueKey(), '1'); } catch { /* private mode */ }
  try { onboard.markVeteran(); } catch { /* */ }
  import('./cutscene-player.js').then((m) => m.markCutscenesSeen(v.cutscenes)).catch(() => {});
  return true;
}
let cinematicActive = false;          // an intro is due or playing: the slam waits
let entryPending = true;              // SC6: the ENTRY SCREEN owns the cold open
const cineAfter = [];                 // UI deferred to the intro handoff
const introLayer = { fx: null, hideStack: false };
const introEl = document.getElementById('intro2');
initPrismTilt({ reduced: REDUCED });
function introDue() {
  try { return localStorage.getItem('nomadmon-cine') !== '0' && !localStorage.getItem(INTRO_SEEN); }
  catch { return false; }
}
function startIntro({ revealDelay = 0, revealWhen = null, onReveal = null, replay = false } = {}) {
  const revealAt = revealWhen || new Promise((resolve) => setTimeout(resolve, revealDelay));
  let revealed = false, started = false, over = false;
  const reveal = () => { if (!revealed) { revealed = true; onReveal?.(); } };
  audio.playPool('title');
  const finish = () => {                 // intro never ran: plain title entrance
    if (over) return;
    over = true;
    cinematicActive = false;
    titleLayer.on = true;
    reveal();
    ensureTitlePlane().then(playTitleEntrance).catch(() => {});
    for (const fn of cineAfter.splice(0)) fn();
  };
  const handoff = () => {
    titleLayer.on = true;
    titleLayer.fade = 0;
    document.body.classList.remove('intro-active');
    introEl.classList.add('out');
    ensureTitlePlane().then(playTitleEntrance).catch(() => {});
  };
  const done = () => {
    over = true;
    cinematicActive = false;
    introEl.classList.remove('on', 'out');
    const fx = introLayer.fx;
    introLayer.fx = null;
    backdrop.visible = true;
    setTimeout(() => fx?.dispose(), 0);
    for (const fn of cineAfter.splice(0)) fn();
  };
  let bail = 0;
  revealAt.then(() => { bail = setTimeout(() => { if (!started) finish(); }, 480); });
  // PROGRESS GATE: a signed-in device waits (bounded) for its account; a veteran gets the plain title entrance
  const ready = Promise.all([lazyImport(`./title.js?${V}`), replay ? null : awaitAccount()]).then(([mod]) => {
    if (over) return null;
    if (!replay && applyProgressGate()) { revealAt.then(finish); return null; }
    const quality = perfMode || Math.min(innerWidth, innerHeight) < 600 ? 'phone' : 'desktop';
    const cast = mod.pickIntroCast(deck, { seed: SEED, hasDepth: (d) => depthArt.has(d) });
    const fx = mod.createIntro({
      camera, buildCard, disposeCard: disposeCardObj, cast, quality, seed: SEED,
      reduced: REDUCED, perf: perfMode,
      artTex: (d) => ensureArtTex(deck.indexOf(d)),
      sfx: (name, o = {}) => audio.play(name, o),
    });
    scene.add(fx.group);
    introLayer.fx = fx;
    return fx;
  });
  revealAt.then(() => ready.then((fx) => {
    if (!fx || over) return;
    clearTimeout(bail);
    started = true;
    try { localStorage.setItem(INTRO_SEEN, '1'); } catch {}
    entrancePlayed = false;               // Replay: the slam belongs to the intro
    titleLayer.fade = 0;
    introEl.classList.remove('out');
    introEl.classList.add('on');
    document.body.classList.add('intro-active');
    fx.play({ handoff, done });
    reveal();
  }).catch(finish));
}
introEl.addEventListener('pointerdown', () => {
  audio.unlock();
  introLayer.fx?.skip();
});
// Settings > Replay Intro (title screen only; the flag stays set)
// ---------------------------------------------------------------- v245 THE PROLOGUE
// Plays on an Open Pack tap for a player who has never opened a pack (per player: the true guest, Supreme
// and each name keep their own). The key is written at the START, so a crash or reload never replays
// it. The tap is the gesture: audio unlocks, and on Safari 16.4+ the audio session becomes 'playback'
// (narration plays with the silent switch on), restored to 'auto' when it ends or is skipped.
const K_PROLOGUE = 'nomadmon-prologue';
const prologueKey = () => { let k = 'guest'; try { k = identity.playerKey() || 'guest'; } catch { /* boot */ } return `${K_PROLOGUE}:${k}`; };
function prologueSeen() { try { return localStorage.getItem(prologueKey()) === '1'; } catch { return !!window.__nmPrologueSeen; } }
async function playPrologue(force = false) {
  const AS = navigator.audioSession;
  let asWas = null;
  try { if (AS && 'type' in AS) { asWas = AS.type; AS.type = 'playback'; } } catch { asWas = null; }
  try {
    audio.unlock();
    const m = await import('./cutscene-player.js');   // (bundled chunk, as the ?cutscene= route)
    const r = await (force ? m.replayCutscene('prologue') : m.playCutscene('prologue', { force: true }));
    if (window.__nm) window.__nm.prologueLast = r;
    return r;
  } finally {
    try { if (AS && asWas != null) AS.type = 'auto'; } catch { /* */ }
    if (window.__nm) window.__nm.audioSessionType = AS ? AS.type : 'absent';
  }
}
async function maybePrologue() {
  await awaitAccount();                 // PROGRESS GATE: never decide on a signed-in device's empty local copy
  applyProgressGate();
  if (identity.packsOpened() !== 0 || prologueSeen()) return null;
  try { localStorage.setItem(prologueKey(), '1'); } catch { window.__nmPrologueSeen = true; }   // private mode: once a session
  return playPrologue(false);
}

function replayIntro() {
  if (cinematicActive || entryPending || shellUI.currentScreen() !== 'title') return false;
  cinematicActive = true;
  startIntro({ revealDelay: 0, replay: true });
  return true;
}

// ---------------------------------------------------------------- P7 shell boot
let mp = null, mpUI = null;
let mpNames = null;            // SC3: {me, opp} for the current/last MP match
const shellSetMpDeckMode = setMpDeckMode;
const shellProxy = {
  go: (...a) => shellUI.go(...a),
  boot: (...a) => shellUI.boot(...a),
};
const shellUI = initShell({
  placeTitleChrome: () => placeTitleMusic(),   // polish E: re-seat the top bar when the fullscreen button appears
  deck,
  // SC36: the Settings drawer reports the live graphics tier so the perf shed
  // can be READ OFF THE SCREEN. SC34 added a post-boot shed (six 3s windows
  // under 45fps -> bloom off, then a viewport-aware DPR budget if slowness
  // persists). The drawer reports the effective render ratio, so the answer to
  // "did this screen soften?" remains readable without remote devtools.
  quality: () => ({ reduced: perfMode, dpr: +renderer.getPixelRatio().toFixed(2),
                    pixelsReduced: perfPixelsReduced,
                    devDpr: +devicePixelRatio.toFixed(2), nativeDpr: +NATIVE_PIXEL_CAP.toFixed(2),
                    bloom: bloomPass.enabled, step: qualityStep,
                    w: innerWidth, h: innerHeight,
                    // SC38c — the STICKY half. See perfLog in this file: the
                    // live tier self-heals in ~6s on the title screen, which is
                    // the only place Settings can be opened from, so the live
                    // value alone cannot answer "did this device ever shed?".
                    sheds: perfLog.sheds, restores: perfLog.restores,
                    lastShedFps: perfLog.lastShedFps,
                    shedAgoMs: perfLog.lastShedAt == null ? null
                      : Math.max(0, performance.now() - perfLog.lastShedAt),
                    fps: perfLog.lastFps }),
  // SC2-B identity hooks
  owned: (id) => identity.ownedCount(id) > 0,
  // v12 PLAYER LEVEL: the forge's three-pick budget and the level beside it
  soloBudget: () => identity.playerBudget(),
  playerLevel: () => identity.playerLevel(),
  isGuest: () => identity.isGuest(),   // v12: online, a guest's supports must be starters (server-checked)
  canOpenPack: () => identity.creditsLeft() > 0,
  // SC7 WS-A: the pack gate (ceremony window also blocks navigation)
  battleLocked: () => battleLocked() || gate.ceremonyRunning,
  lockedTap: (btn) => lockedTap(btn || null),
  openPlayerPicker: () => openPicker(),
  replayIntro: () => replayIntro(),
  prologue: () => maybePrologue(),
  replayPrologue: () => { if (shellUI.currentScreen() === 'title') playPrologue(true); },
  canUseWhatsAppChallenges: () => identity.canUseWhatsAppChallenges(),
  createWhatsAppLinkCode: () => identity.createWhatsAppLinkCode(),
  syncWhatsAppDeck: (selection) => identity.syncWhatsAppDeck(selection),
  notify: (message, ms) => toast(message, ms),
  showDeck3D, hideDeck3D,
  showTitle3D: () => {
    titleLayer.on = !entryPending;
    // SC2-B P3 entrance: first title show per page load — cards scatter, then
    // the wordmark slams in (CSS) synced with the 3D shockwave ring (title.js).
    // SC5/V2: while the intro covers the screen the entrance waits for its
    // handoff (startIntro fires it under the flash).
    // SC6: while the ENTRY SCREEN is up it waits for the entry tap.
    if (!entryPending) ensureTitlePlane().then(() => {
      titleLayer.fx?.refreshCast?.();
      if (!cinematicActive) playTitleEntrance();
    });
    try { paintTitleDash(); } catch { /* boot order: economy paints it shortly */ }
    paintForgeMark();
    closeSolo(); setBinder(false); if (inspecting) setInspect(false);
    closeLab(); videoRelease();
    fan.visible = false; aura.visible = false; shadowMesh.visible = false;
    maybeUnlockCeremony();     // SC7: 9th pack banked -> the seals break HERE
  },
  endLuckyDraw: () => { if (slotActive && slotmod) slotmod.endSlot(); },   // v299 slotflash: shell.go tears the slot down under the veil
  hideTitle3D: (collectionOwnsFrame = false) => {
    titleLayer.on = false;
    // Only an explicit COLLECTION target may reveal the shared fan. Deck,
    // pack and battle own the handoff even while their renderer is loading.
    fan.visible = !!collectionOwnsFrame && !soloCard;
    aura.visible = !!collectionOwnsFrame;
    shadowMesh.visible = !!collectionOwnsFrame;
  },
  openPack: () => openPackFlow(),   // v331: the promise (the title's Open Pack holds its veil on it)
  prepFan: () => rebuildFan(),               // SC9-B: MY DECK composition
  // SC22: the title's COLLECTION entry has to be able to reach the binder.
  // At <=400px the deck hotspot is hidden (index.html "COLLECTION text entry
  // remains the route") and SC9-A later killed #topLeft/#collectionBtn
  // outright — which left the 608-card binder with NO route at all on a phone.
  openBinder: () => setBinder(true),
  fieldHistory: () => identity.fieldHistory(),
  recordFielded: (ids) => { const h = identity.recordFielded(ids); refreshBinderOwnership(); return h; },
  startLuckyDraw,
  // v233: the squad screen shows the SAME opponent startSolo will draft (it never depends on the picks)
  previewOpponent: () => soloOpponent([]),
  // v236 CAMPAIGN (dev flag): Battle opens the map
  campaignOn: () => campaignOn,
  // the quip comes once the map is up, after any story cutscene (whose narration would cut it off)
  openCampaign: () => { openCampaign().then(() => audio.menuQuip('campaign')).catch(e => console.error('[campaign]', e)); },
  startSolo: (sel, difficulty = 'normal') => {
    // SC32: aiDeck now returns the DRAFT — the opponent's cards plus the bracket
    // they were drawn into and the power ratio behind it. The battle needs the
    // bracket for the team reveal and for the post-battle reward.
    const levels = identity.cardLevels(sel.chars);
    // v14: the default (NORMAL) opponent scales with the player level, 0-20
    const soloLevel = difficulty === 'normal' ? identity.playerLevel() : null;
    const ai = soloOpponent(sel.chars, difficulty);
    return launchBattle({ A: { chars: [...sel.chars], supports: [...sel.supports], levels, mastery: sel.mastery || null, holo: sel.holo || [] },
      B: { chars: ai.chars, supports: ai.supports, levels: ai.levels } },
      { driver: { kind: 'ai', difficulty, ...(soloLevel != null ? { level: soloLevel } : {}) }, bracket: ai.bracket, draft: ai, barrageArena: ai.arena || null });
  },
  openMultiplayer: (joinCode = null) => joinCode
    ? openMultiplayer(joinCode) : (tradingUI ? tradingUI.openSocial() : openMultiplayer()),
  // SC33 boss gauntlet hooks
  startGauntlet: (sel) => startGauntlet(sel),
  gauntletUnlocked: () => gauntletUnlocked(),
  gauntletCleared: () => gauntletCleared(),
  ownedDistinct: () => ownedDistinct(),
  // SC3 (e): leaderboard data hooks
  fetchPlayers: () => identity.fetchPlayers(),
  playerName: () => identity.currentPlayer(),
  playerKey: () => identity.playerKey(),              // econfix: the Lucky Draw deal is per identity
  paintMute,
});
initTitleCarousel();                       // v152: the title menu is a text carousel
// INK DUEL (DUEL_SPEC.md): the DUEL button + the ?duel=CODE friend link open the lazy duel chunk
// (src/duel.js). The title scene pauses under it (duelUp) like the campaign atlas does.
async function openDuel(opts = {}) {
  if (duelUp) return;
  duelUp = true;
  try { audio.unlock(); } catch { /* */ }
  try {
    const m = await lazyImport(`./duel.js?${V}`);
    await m.openDuel({ V, ...opts, onExit: () => { duelUp = false; audio.ensureMusic(); },
      // a duel win KEEPS the champion: the one champion store (identity / src/champion.js), then the
      // Collection model rebuilds so the card is there the moment the player looks
      onChampion: async (id, grant) => {
        if (!CHAMPIONS.some((c) => c.id === id)) return null;
        const r = await identity.applyDuelChampion(id, grant);
        if (r && BV) buildBinderModel();
        return r;
      },
      // the duel's fights run in the battle's own 3D arena (src/duel-stage.js): both champions as real battle
      // cards, the stage world, the set pieces, the finisher. onStage(api) fires once the arena is built.
      stage: {
        start: ({ A, B, side, seed, onStage }) => {
          const decks = { A: { chars: [championCardId(A)], supports: [] }, B: { chars: [championCardId(B)], supports: [] } };
          if (!cardsById[decks.A.chars[0]] || !cardsById[decks.B.chars[0]]) return false;
          const go = () => launchBattle(decks, { driver: { kind: 'duel', onStage }, seed, playerSide: side });
          if (shellUI.currentScreen() === 'battle') go(); else shellUI.go('battle', { after: go });
          return true;
        },
        stop: () => {
          if (battleActive && battle) { try { battle.exitBattle(); } catch (e) { console.warn('[duel] stage exit', e); } }
          if (shellUI.currentScreen() === 'battle') shellUI.go('title');
        },
      } });
  } catch (e) { duelUp = false; console.error('[duel]', e); }
}
document.getElementById('menuDuel')?.addEventListener('click', () => { audio.ui('mmp'); openDuel(); });   // polC: not Campaign's press

// v245 THE ACADEMY: a lesson = its level record + the loaner squad, launched in PRACTICE (the runner rides
// the driver; barrage.js awaits its one-tap beats); A4's captain is set the way the squad screen sets it
academy.initAcademy({
  launch: ({ lesson, squad, foe, attemptSeed }, runner) => {
    const lv = (ids) => Object.fromEntries(ids.map(id => [id, 1]));
    const go = () => {
      if (window.__nm) window.__nm.squadCaptain = lesson.captain || null;
      launchBattle({ A: { chars: squad, supports: [], levels: lv(squad), mastery: null, holo: [] }, B: { chars: foe, supports: [], levels: lv(foe) } },
        { driver: { kind: 'ai', difficulty: 'normal', level: 0, campaign: { rec: lesson.rec, startPhase: 0, help: false, attemptSeed }, practice: true, academy: runner },
          seed: attemptSeed, barrageArena: null });
    };
    if (shellUI.currentScreen() === 'battle') go(); else shellUI.go('battle', { after: go });
  },
  openGuide: () => guide.openGuide(),
  replayPrologue: () => playPrologue(true),
  report: (id) => identity.reportAcademy(id).then((r) => { if (r && r.packGranted) paintEconomy(); return r; }),   // econfix: the +1 pack shows at once
});

// v236 CAMPAIGN: the map module (lazy, flag only) and a level's launch
let lastCampaignContrib = null;   // UNDERDOG XP: { id, ids } of the last campaign result (the reward hook reads it)
async function openCampaign(opts = {}) {
  if (!campaignUI) {
    const m = await lazyImport(`./campaign.js?${V}`);
    campaignUI = m.initCampaign({
      V, cardsById, artSrc, displayName, typeGlyph, audio,
      ownedIds: () => deck.filter(d => d.role !== 'support' && !d.forged && identity.ownedCount(d.id) > 0).map(d => d.id),
      enterSquad: (cc) => shellUI.campaignSquad(cc),
      launch: (rec, sel, att) => {
        const levels = identity.cardLevels(sel.chars);
        const go = () => launchBattle({ A: { chars: [...sel.chars], supports: [...sel.supports], levels, mastery: sel.mastery || null, holo: sel.holo || [] },
          B: m.enemyDeck(rec) },
          { driver: { kind: 'ai', difficulty: 'normal', level: Math.floor(rec.D || 0), campaign: { rec, startPhase: att.startPhase | 0, help: att.help || 0, attemptSeed: att.attemptSeed,
            hot: !!att.hot, trial: !!att.trial, daily: !!att.daily } },   // v257 Hot Hand / a boss Time Trial / the Daily Doodle
            seed: att.attemptSeed, barrageArena: rec.arena || null });
        if (shellUI.currentScreen() === 'battle') return go(); return shellUI.go('battle', { holdVeil: true, after: go });   // v299: veil holds until the battle owns the frame (v331: returned, for a held veil)
      },
      quickMatch: () => { shellUI.campaignSquad(null); shellUI.quickMatch(); },
      home: () => { shellUI.campaignSquad(null); paintTitleDash(); audio.ensureMusic(); },   // audiopolish: the screen rule after the map
      // the earned rewards -> B2's ledger (src/campaign-xp.js): player XP for first clears / first stars /
      // bosses only, card XP, the daily first-win + mission; a loss reports 0 stars (pays nothing)
      reward: async (rec, { stars, cards, assist }) => {
        const B = m.enemyDeck(rec);
        // UNDERDOG XP: the cards that contributed (barrage.js campaignResult.contrib, kept by onFinish below)
        const contrib = lastCampaignContrib && lastCampaignContrib.id === rec.id ? lastCampaignContrib.ids : [];
        const award = await campaignLedger(rec.id, { stars, cards, contrib, rec, assist: assist === true,
          enemyPower: teamMedianEffectivePwr(B.chars, cardsById, B.levels), ...(stars ? { missionMet: missionMet(identity.todaysMission(), cards.map(id => cardsById[id]).filter(Boolean)) } : {}) });
        paintPlayerChip();
        if (award && (award.packGranted || (award.gastroPack || []).length)) paintEconomy();   // v243 a world boss's first clear paid a pack
        if (award && !award.error) celebrate(award, null);
        return award;
      },
      // v257 the DAILY DOODLE's XP (once per UTC day; the daily first-win + mission like any campaign win)
      doodleReward: async ({ won, cards }) => {
        const award = await identity.reportDoodle(won, cards, won && missionMet(identity.todaysMission(), cards.map(id => cardsById[id]).filter(Boolean)));
        paintPlayerChip();
        if (award && award.plevel) celebrate(award, null);
        return award;
      },
      // v257 a STAR CHEST's pack: the map closes and the real pack opening runs (the title's Open Pack)
      openPack: () => { shellUI.campaignSquad(null); paintTitleDash(); paintEconomy(); setTimeout(() => document.getElementById('menuPack').click(), 60); },
      paintEconomy: () => paintEconomy(),
      questReward: (list) => questReward(list),   // questduo: the quest card ceremony (src/quest-card.js)
      questPending: () => questPending(),
      hide3D: () => { campaignMapUp = true; }, show3D: () => { campaignMapUp = false; },   // map v2: the atlas owns the only live GL frame while it is up
    });
  }
  if (opts.initOnly) return campaignUI;   // (questduo QA: the result screen without the map)
  return campaignUI.openMap(opts);
}

// ---------------------------------------------------------------- questduo QUEST CARDS
// The world-1 quest rewards (data/quest-cards.json; granted by the ledger in identity.reportCampaign). Each grant
// plays its ceremony once per identity (src/quest-card.js, its own lazy chunk on the Codex's lib), then the
// ownership repaints: the Forge's Duos / Fusions 1/24, the binder, the squad pool.
let questMod = null;
const questSeenKey = () => `nomadmon-quest-seen-v1:${identity.playerKey() || 'guest'}`;
const questSeen = () => { try { return JSON.parse(localStorage.getItem(questSeenKey())) || {}; } catch { return {}; } };
let questRunning = false;
/** Quest grants THIS device received whose ceremony it has not shown (an outbox replay, a crash mid-ceremony).
 *  dupduo: never "granted on the account" -- a second device replayed the ceremony for a card already owned. */
const questPending = () => identity.questUnshown().filter(q => !questSeen()[q.key] && identity.ownedCount(q.id) > 0);
function questFeed(n = 4) {   // the Fusion's ignition: four commons, the player's own when they have them
  const commons = deck.filter(d => d.rarity === 'common' && d.role !== 'support' && !d.forged && d.art);
  const own = commons.filter(d => identity.ownedCount(d.id) > 0);
  return [...own, ...commons.filter(d => !own.includes(d))].slice(0, n);
}
async function questReward(list, qa = null) {   // qa: { force } (__nm.quest.play: replays, never marks seen)
  let go = 'continue', kind = null;
  for (const q of list || []) {
    const d = cardsById[q.id], r = codexLib.recipe(q.id);
    if (!d || !r || questRunning) continue;
    if (questSeen()[q.key] && !(qa && qa.force)) { identity.questShown(q.key); continue; }
    // seen once the first lesson beat is done (or the ceremony ends): a crash before that replays it next time
    const markSeen = () => { if (qa && qa.force) return; identity.questShown(q.key); const s = questSeen(); if (!s[q.key]) { s[q.key] = Date.now(); try { localStorage.setItem(questSeenKey(), JSON.stringify(s)); } catch { /* private mode */ } } };
    const m = questMod || (questMod = await import('./quest-card.js').catch(staleReload));
    questRunning = true;
    audio.unlock();
    kind = r.kind;
    go = await m.playQuestCeremony({
      lib: codexLib, kind: r.kind, card: d, recipeText: r.reqText || null,
      srcs: r.kind === 'duo' ? r.sources.map(id => cardsById[id]).filter(Boolean) : null,
      feed: r.kind === 'fusion' ? questFeed() : null,
      artSrc: (c, size) => artSrc(c, V, size), audio, haptics, reduced: REDUCED,
      tilt: { live: () => gyroActive && !REDUCED, ask: IOS_ASK && !REDUCED && !lsNum('nm-tilt-denied') ? askTilt : null },
      vo: QUEST_VO[q.key] || null, onSeen: markSeen,
      // dupduo: a grant of a card the player already had says so (+N XP on its level bar, the pack's dupe chip)
      already: q.already ? { dupe: q.dupe || null } : null,
    }).finally(() => { questRunning = false; });
    markSeen();
    if (go === 'forge') break;
  }
  refreshBinderOwnership(); paintDeckBadge(); paintEconomy();
  try { forgeUI.paintTabs(); shellUI.deckApi && shellUI.deckApi.revalidate && shellUI.deckApi.revalidate(); } catch { /* repaints on next open */ }
  if (go === 'forge') window.__nm.forge.tab(kind === 'fusion' ? 'fusions' : 'duos');
  return go;
}
// optional narration (scripts/gen-quest-vo.mjs: Chronicler on the arrival, Shao on the lesson; narrator toggle respected)
const QUEST_VO = { duo: { arrive: 'assets/vo/quest-duo-arrive.mp3', lesson: 'assets/vo/quest-duo-lesson.mp3' }, fusion: { arrive: 'assets/vo/quest-fusion-arrive.mp3', lesson: 'assets/vo/quest-fusion-lesson.mp3' } };

// v232 CARDS MATTER: the opponent is drafted against the REFERENCE (your top 9 owned
// inside the star budget + the reels' expected value) and the stored opponent seed --
// never against the picks or the fate cards you ended up with (Quick Match: streak kept)
function soloOpponent(chars, difficulty = 'normal') {
  const soloLevel = difficulty === 'normal' ? identity.playerLevel() : null;
  const ownedChars = deck.filter(d => d.role !== 'support' && identity.ownedCount(d.id) > 0);
  const reference = referenceRanks({ owned: ownedChars, levels: identity.cardLevels(ownedChars.map(d => d.id)),
    starBudget: identity.playerBudget() });
  const ai = shellUI.aiDeck(undefined, {
    difficulty, playerLevels: identity.cardLevels(chars), existingWins: identity.record().wins, playerChars: [...chars],
    playerKey: identity.playerKey(), soloLevel, reference: reference.length ? reference : null,
  });
  ai.reference = reference;
  return ai;
}

// SC3: standalone so the ?join deep link can reach it too
// LAUNCH (Jack 2026-10-09): multiplayer is off until the MP flow is fixed (Friends is off the title,
// see index.html #titleRetired). Every route in (menu, ?join=, Live Battle) stops here; flip to restore.
const MP_OFF = true;
async function openMultiplayer(joinCode = null) {
  if (MP_OFF) return;
  const m = mp || (mp = await lazyImport(`./mp.js?${V}`));
  if (!mpUI) {
    mpUI = m.initMp({
      shell: shellProxy,
      setMpDeckMode: (fn, opts) => shellSetMpDeckMode(fn, opts),
      playerName: () => identity.currentPlayer() || 'Guest',
      playerId: () => identity.currentPlayer(),          // v12: the server reads a named player's level
      playerLevel: () => identity.playerLevel(),
      authHeader: () => identity.authHeader(),          // v10: lets the server confirm Holo claims
      onMatched: ({ decks, seed, driver, playerSide, names }) => {
        mpNames = names || null;
        shellSetMpDeckMode(null);
        shellProxy.go('battle', {
          after: () => launchBattle(decks, { driver, seed, playerSide }),
        });
      },
    });
  }
  if (joinCode) mpUI.openJoin(joinCode);
  else mpUI.open();
}
tradingUI = trading.initTrading({
  deck, thumbPos, toast,
  refreshOwnership: () => {
    refreshBinderOwnership();
    const card = focusedCardData();
    if (card) bindCardDOM(soloCard || cards[focusIndex]);
    if (inspecting) paintFeedControl();
  },
  // polB: no Live Battle door in the trade inbox while MP is off; its empty state opens the Collection
  openLiveBattle: MP_OFF ? null : () => openMultiplayer(),
  openCollection: () => document.getElementById('menuCollection').click(),
});
setTimeout(() => tradingUI.refreshBadge(), 1000);
document.getElementById('homeBtn').addEventListener('click', () => {
  cancelPackEntry();
  if (slotActive && slotmod) slotmod.endSlot();
  shellUI.go('title');
});
// SC9-A: the crest HOME MEDALLION — the ONE home affordance on every
// non-title screen. Closes whatever is up (guide, binder, a running pack,
// the deck picker) and rides the shared veil back to the title. MP deck
// picks route through deckHome (back to the cancellable MP lobby).
document.getElementById('homeMedal').addEventListener('click', () => {
  audio.unlock();
  audio.ui('tick');
  guide.closeGuide();
  setBinder(false);
  cancelPackEntry();                                 // econfix: a pack still loading never opens
  if (packActive && pack) pack.abortPack();          // ends the ceremony clean
  if (slotActive && slotmod) slotmod.endSlot();
  if (shellUI.currentScreen() === 'deck' || shellUI.currentScreen() === 'draw') { shellUI.deckHome(); return; }
  shellUI.go('title');
});
// SC3 (b): ?join=CODE deep link — pick a name first if needed, then straight
// into the room -> deck pick. Consumed once (pendingJoin) after the picker.
const deepJoinCode = (() => {
  const c = String(q.get('join') || '').trim().toUpperCase();
  return !MP_OFF && /^[A-Z2-9]{4}$/.test(c) ? c : null;
})();
let pendingJoin = deepJoinCode;
function consumePendingJoin() {
  if (!pendingJoin) return false;
  const code = pendingJoin;
  pendingJoin = null;
  openMultiplayer(code);
  return true;
}
// SC6: destination actions defer to the ENTRY SCREEN tap — one tap in, ever.
let entryProceed = null;
{
  const bv = q.get('view');
  // SC5 WS2: only a plain title boot gets the cinematic — deep links and view
  // shortcuts go straight to their destination. Session + setting + motion
  // gates all live here; the flag must be set BEFORE boot() so the entrance
  // slam defers (module code below runs synchronously ahead of any promise).
  // ?duel=CODE (a friend's INK DUEL invite): the invitee goes through the usual front door first (a new player
  // types a name in the NAME FORGE, Shao roasts it) -- no intro cinematic -- then lands straight in the duel
  const duelInvite = /^[A-Z2-9]{5}$/.test(String(q.get('duel') || '').toUpperCase()) ? String(q.get('duel')).toUpperCase() : null;
  cinematicActive = !deepJoinCode && !duelInvite && !bv && introDue();
  if (duelInvite) { shellUI.boot('title'); entryProceed = () => setTimeout(() => openDuel({ code: duelInvite }), 350); }
  else if (deepJoinCode) { shellUI.boot('title'); entryProceed = () => setTimeout(consumePendingJoin, 350); }
  else if (bv === 'leaderboard') { shellUI.boot('title'); entryProceed = () => setTimeout(() => shellUI.go('lb'), 300); }
  else if (bv === 'fan' || bv === 'collection') shellUI.boot('fan');
  else if (bv === 'pack') { shellUI.boot('fan'); entryProceed = () => setTimeout(() => packBtn.click(), 250); }
  else if (bv === 'battle') { shellUI.boot('fan'); entryProceed = () => setTimeout(() => battleBtn.click(), 250); }
  else if (bv === 'deck') shellUI.boot('deck');
  else shellUI.boot('title');
}

// ---------------------------------------------------------------- SC2-B identity UI
// Player chip, NAME PICK overlay, binder ownership checklist, pack economy
// labels. All reading-size text is DOM (hard rule).

// binder checklist repaint: owned = full color + xN badge; unowned = dark
// silhouette (CSS) with a centered "?" — still tappable to inspect.
function refreshBinderOwnership() {
  // COLLECTION 2: tiles carry power/level/copies, so a rebuild (windowed:
  // only the mounted cells remount) is the repaint
  if (BV) buildBinderModel();
  if (cf.open && binderSheetIndex < 0) paintFocus();
}

const playerChip = document.getElementById('playerChip');
function paintPlayerChip() {
  const r = identity.record();
  playerChip.textContent = '';
  // v12 PLAYER LEVEL: the level ring leads the chip (number + progress arc)
  const px = plevelProgressOf(identity.playerXp());
  playerChip.insertAdjacentHTML('beforeend', levelRingHtml(px.level, px.ratio));
  playerChip.setAttribute('aria-label', `Switch player. Level ${px.level}`);
  const tl = document.getElementById('titleLevel');
  if (tl) {
    // QA-A: a bare "0" ring explained nothing; the ring appears with the first XP
    tl.innerHTML = `${px.level > 0 || px.ratio > 0 ? levelRingHtml(px.level, px.ratio) : ''}<span class="tlname"></span>`;
    tl.querySelector('.tlname').textContent = identity.displayName();
    tl.setAttribute('aria-label', `${identity.displayName()}, level ${px.level}`);
  }
  const b = document.createElement('b');
  b.textContent = identity.displayName();
  const s = document.createElement('span');
  s.textContent = `${r.wins}–${r.losses}`;
  playerChip.append(b, s);
  // SC33: the persistent gauntlet mark rides the player chip.
  if (gauntletCleared()) {
    const g = document.createElement('i');
    g.className = 'chipmark';
    g.title = 'Boss Gauntlet cleared';
    g.textContent = '\u2605';
    playerChip.append(g);
  }
  try { shellUI.paintGauntletButton(); } catch { /* shell may not be up yet */ }
}

// ---- SC2-B Phase 2: the physical deck ----
// A stack of jittered card-back planes living in the lower-right of the title
// AND fan compositions. Its tap target is the DOM hotspot #deckHot (simpler +
// more reliable than a raycast) with the owned-count badge beside it. Tapping
// bursts the stack outward as the collection opens — the ritual. pack.js
// borrows makeDeckStack for the post-spread fly-in ceremony.
function makeDeckStack(n = 12) {
  const group = new THREE.Group();
  const cards = [];
  const geo = new THREE.PlaneGeometry(CW, CH);
  for (let i = 0; i < n; i++) {
    const mat = new THREE.MeshStandardMaterial({
      map: backTex, metalness: 0.35, roughness: 0.55, transparent: true,
    });
    const m = new THREE.Mesh(geo, mat);
    m.userData.home = {
      x: (rng() - 0.5) * 0.05, y: (rng() - 0.5) * 0.05, z: i * 0.016,
      rz: (rng() - 0.5) * 0.14,
    };
    const h = m.userData.home;
    m.position.set(h.x, h.y, h.z);
    m.rotation.z = h.rz;
    group.add(m);
    cards.push(m);
  }
  return { group, cards };
}

const deckStack = makeDeckStack(12);
deckStack.group.visible = false;
scene.add(deckStack.group);
const deckState = { pulse: 0, burst: null, hover: 0, hoverT: 0 };
const deckHotEl = document.getElementById('deckHot');
const deckBadgeEl = document.getElementById('deckBadge');
function paintDeckBadge() {
  // QA-A: no stray count before the player has opened a pack (the starter supports are not "cards")
  const n = deck.reduce((a, d) => a + identity.ownedCount(d.id), 0);
  deckBadgeEl.textContent = n > 0 && identity.packsOpened() > 0 ? n : '';
  syncPileFaces();
}

// title12: on the title the pile is the Collection door, and its top three
// cards are the player's REAL best cards (rarity-ranked, like the old
// Collection tile thumbs): thumb art + the rarity's metal frame, fanned a
// little, with one foil glint sweeping the top card. Title only — the fan
// corner keeps the plain backs.
const pileFaces = { group: new THREE.Group(), key: null, cards: [], glint: null };
pileFaces.group.visible = false;
deckStack.group.add(pileFaces.group);
const PILE_Z0 = deckStack.cards.length * 0.016 + 0.02;
const pileGlintMat = new THREE.ShaderMaterial({
  transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
  uniforms: { uT: { value: 0 } },
  vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
  fragmentShader: `varying vec2 vUv; uniform float uT;
    const vec2 SZ = vec2(${CW.toFixed(3)}, ${CH.toFixed(3)});
    const float RR = ${CR.toFixed(3)};
    void main(){
      vec2 q = abs((vUv - 0.5) * SZ) - (SZ * 0.5 - RR);
      float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - RR;
      float inside = 1.0 - smoothstep(-0.012, 0.0, sd);
      float ph = fract(uT / 4.6) * 2.2 - 0.6;            // sweep, then rest
      float d = (vUv.x * 0.8 + vUv.y * 0.6) - ph * 1.4;
      float band = exp(-d * d * 260.0) * 0.55 + exp(-d * d * 30.0) * 0.12;
      gl_FragColor = vec4(vec3(1.0, 0.94, 0.80) * band * inside, 1.0);
    }`,
});
function pileBest() {
  const RANK = { eternal: 0, mythic: 1, legendary: 2, epic: 3, rare: 4, common: 5 };
  return deck.map((d, i) => i)
    .filter((i) => identity.ownedCount(deck[i].id) > 0 && !deck[i].clip && deck[i].role !== 'support')
    .sort((a, b) => (RANK[deck[a].rarity] ?? 6) - (RANK[deck[b].rarity] ?? 6)).slice(0, 3).reverse();
}
async function syncPileFaces() {
  if (!deck || !crestImg) return;
  const best = pileBest();
  const key = best.join('|');
  if (key === pileFaces.key) return;
  pileFaces.key = key;
  setArtTexLive('titlePile', new Set(best.map((i) => artTexKey(i, 'thumb'))));
  const texs = await Promise.all(best.map((i) => ensureArtTex(i, 'thumb')));
  if (pileFaces.key !== key) return;
  for (const c of pileFaces.cards) {
    pileFaces.group.remove(c);
    c.traverse((o) => { if (o.material && o.material !== pileGlintMat) o.material.dispose(); });
  }
  pileFaces.cards = [];
  pileFaces.glint = null;
  best.forEach((i, k) => {
    const d = deck[i];
    const g = new THREE.Group();
    const art = new THREE.Mesh(new THREE.PlaneGeometry(CW * 0.94, CH * 0.94),
      new THREE.MeshBasicMaterial({ map: coverTexture(texs[k], (CW * 0.94) / (CH * 0.94), d.focalX ?? 0.5),
        transparent: true }));
    const frameMat = new THREE.MeshStandardMaterial({ map: getFrameTexture(RARITY[d.rarity] || RARITY.common, crestImg),
      transparent: true, metalness: 0.82, roughness: 0.3, envMapIntensity: 0.78, alphaTest: 0.02 });
    frameMat.color.setScalar(1.45);
    const frame = new THREE.Mesh(new THREE.PlaneGeometry(CW, CH), frameMat);
    frame.position.z = 0.004;
    g.add(art, frame);
    const off = k - (best.length - 1);              // 0 = top card, -1, -2 below
    g.userData.home = { x: off * -0.09, y: off * 0.03, z: PILE_Z0 + k * 0.024, rz: off * -0.11 };
    if (k === best.length - 1) {
      const glint = new THREE.Mesh(new THREE.PlaneGeometry(CW, CH), pileGlintMat);
      glint.position.z = 0.008;
      g.add(glint);
      pileFaces.glint = glint;
    }
    pileFaces.group.add(g);
    pileFaces.cards.push(g);
  });
  poseFaces(0);
}
function poseFaces(hover) {
  for (const g of pileFaces.cards) {
    const h = g.userData.home;
    g.position.set(h.x * (1 + hover * 1.4), h.y + hover * 0.05, h.z);
    g.rotation.set(0, 0, h.rz * (1 + hover * 1.2));
  }
}
function setFaceOpacity(g, a) {
  g.traverse((o) => { if (o.material && o.material !== pileGlintMat) o.material.opacity = a; });
}
deckHotEl.addEventListener('pointerenter', () => { deckState.hoverT = 1; });
deckHotEl.addEventListener('pointerleave', () => { deckState.hoverT = 0; });
deckHotEl.addEventListener('pointerdown', () => { deckState.hoverT = 1; });
function resetDeckStack() {
  for (const m of deckStack.cards) {
    const h = m.userData.home;
    m.position.set(h.x, h.y, h.z);
    m.rotation.set(0, 0, h.rz);
    m.material.opacity = 1;
  }
  for (const g of pileFaces.cards) setFaceOpacity(g, 1);
  poseFaces(deckState.hover);
}
deckHotEl.addEventListener('click', () => {
  audio.unlock();
  if (deckState.burst) return;
  audio.play('whoosh', { gain: 0.7 });
  haptics.buzz(15);
  const B = { t: 0, v: [], w: [] };
  for (let i = 0; i < deckStack.cards.length + pileFaces.cards.length; i++) {
    // fan out up-and-left, toward where the collection grid lands
    B.v.push(new THREE.Vector3(
      -(1 + Math.random() * 4), 1.5 + Math.random() * 4.5, 0.5 + Math.random() * 1.5));
    B.w.push((Math.random() - 0.5) * 9);
  }
  deckState.burst = B;
  setTimeout(() => {
    // title12: the title pile is the Collection door (fan + binder, its sfx)
    if (shellUI.currentScreen() === 'title') document.getElementById('menuCollection').click();
    else setBinder(true);
  }, 380);
});
function updateDeckStack(t, dt) {
  const onTitle = titleLayer.on;
  // title12: the title shows the pile again (FRONT DOOR 5 had retired it) —
  // it is the Collection door, anchored to the #deckHot box (CSS places it)
  const show = (!soloCard && !battleActive && !packActive && !deckActive && !slotActive &&
    !binderEl.classList.contains('open') && !document.body.classList.contains('entry-active'));
  deckStack.group.visible = show;
  pileFaces.group.visible = show && onTitle;
  if (!show) {
    if (deckState.burst) { resetDeckStack(); deckState.burst = null; }
    return;
  }
  const z = 0.6;
  const dist = Math.max(camera.position.z - z, 1.5);
  const tv = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
  const hh = tv * dist, hw = hh * camera.aspect;
  let s, nx, ny;
  const hot = onTitle ? deckHotEl.getBoundingClientRect() : null;
  if (hot && hot.height) {
    s = (hot.height * 0.74 * 2 * dist * tv) / innerHeight / CH;
    nx = -1 + (2 * (hot.left + hot.width / 2)) / innerWidth;
    ny = 1 - (2 * (hot.top + hot.height / 2)) / innerHeight;
  } else {
    // fan corner: the DOM hotspot footprint (center ~58px from right, ~148px up)
    s = THREE.MathUtils.clamp((110 * 2 * dist * tv) / innerHeight / CH, 0.24, 0.5);
    nx = 1 - (2 * 58) / innerWidth;
    ny = -1 + (2 * 148) / innerHeight;
  }
  deckState.pulse = Math.max(0, deckState.pulse - 3 * dt);
  deckState.hover += (deckState.hoverT - deckState.hover) * Math.min(1, dt * 10);
  const lift = onTitle ? deckState.hover : 0;
  deckStack.group.position.set(nx * hw,
    ny * hh + (REDUCED ? 0 : Math.sin(t * 0.9) * 0.05 * s) + lift * 0.1 * s, z);
  deckStack.group.scale.setScalar(s * (1 + deckState.pulse * 0.12 + lift * 0.06));
  deckStack.group.rotation.set(-0.06, REDUCED ? 0 : Math.sin(t * 0.5) * 0.08, 0.06);
  if (!REDUCED) pileGlintMat.uniforms.uT.value = t;
  // catches the aurora light while the title surges
  const glow = 1 + (onTitle && titleLayer.fx ? titleLayer.fx.surgeNow * 0.35 : 0);
  for (const m of deckStack.cards) m.material.color.setScalar(glow);
  if (deckState.burst) {
    const B = deckState.burst;
    B.t += dt;
    const fade = Math.max(0, 1 - B.t / 0.6);
    const n = deckStack.cards.length;
    for (let i = 0; i < n + pileFaces.cards.length; i++) {
      const m = i < n ? deckStack.cards[i] : pileFaces.cards[i - n];
      if (!m || !B.v[i]) continue;
      m.position.addScaledVector(B.v[i], dt);
      m.rotation.z += B.w[i] * dt;
      m.rotation.y += B.w[i] * 0.6 * dt;
      if (i < n) m.material.opacity = fade; else setFaceOpacity(m, fade);
    }
    if (B.t > 0.7) { resetDeckStack(); deckState.burst = null; }
  } else if (onTitle) poseFaces(deckState.hover);
}

// ---------------------------------------------------------------- SC9-B MY DECK
// The fan view is reframed as MY DECK: the active battle deck (5) leads, then
// every owned card (rarity-sorted); a cardless guest gets the featured set as
// a SHOWCASE gallery instead. Rebuilt on fan entry / player switch / pack end
// (no-op when the composition is unchanged). DOM header owns the reading text.
let fanBuilding = false;
const FAN_MAX = 40;
// SC32: a function DECLARATION, not a `const` arrow, on purpose. fanComposition()
// is itself hoisted, so a const comparator declared below it is only safe as
// long as nobody ever calls fanComposition during module evaluation — and the
// failure mode if they do is a temporal-dead-zone ReferenceError that takes out
// the whole collection view. (No such call path exists today; every go('fan')
// sits inside an event listener. This closes the door rather than trusting it
// to stay shut.) Hoisted declarations have no TDZ, so call order cannot break it.
function fanSort(a, b) {
  return (RARITY_ORDER[deck[a].rarity] - RARITY_ORDER[deck[b].rarity]) || a - b;
}

function fanComposition() {
  const sel = shellUI.deckApi.sel();
  const list = [];
  const seen = new Set();
  for (const id of [...sel.chars, ...sel.supports]) {
    const i = deck.findIndex(d => d.id === id);
    if (i >= 0 && !seen.has(i)) { seen.add(i); list.push(i); }
  }
  const owned = deck.map((d, i) => identity.ownedCount(d.id) > 0 ? i : -1)
    .filter(i => i >= 0 && !seen.has(i)).sort(fanSort);
  for (const i of owned) { if (list.length >= FAN_MAX) break; list.push(i); }
  if (!list.length) return { list: featuredIdx.slice(), showcase: true };
  return { list, showcase: false };
}

function paintFanHead(want = null) {
  const showcase = want ? want.showcase : fanShowcase;
  const totalOwned = deck.reduce((a, d) => a + identity.ownedCount(d.id), 0);
  document.getElementById('fhTitle').textContent = showcase ? 'Showcase' : 'My Deck';
  document.getElementById('fhCount').textContent = showcase
    ? 'featured set' : `${totalOwned} card${totalOwned === 1 ? '' : 's'}`;
  const r = identity.record();
  document.getElementById('fhPlayer').textContent =
    `${identity.displayName()} · ${r.wins}–${r.losses}`;
}

// full disposal of one fan card object (shared resources survive: edge rim
// geometries, per-rarity frame textures, art texture cache, card back texture)
function disposeCardObj(c) {
  fan.remove(c.group);
  c.group.traverse((o) => {
    if (o.geometry && !o.geometry.userData.sharedCardRim) o.geometry.dispose();
  });
  for (const m of [c.artMat, c.frameMat, c.foilMat, c.backFoilMat, c.edgeMat, c.wallMat]) m.dispose();
  c.backPlane.material.dispose();
  if (c.videoPlane) c.videoPlane.material.dispose();
  if (c.sparks) c.sparks.material.dispose();
}

async function rebuildFan(force = false) {
  const want = fanComposition();
  paintFanHead(want);
  const cur = cards.map(c => c.deckIndex);
  const same = fanShowcase === want.showcase && cur.length === want.list.length &&
    cur.every((v, i) => v === want.list[i]);
  if ((!force && same) || fanBuilding) return;
  fanBuilding = true;
  const priorLive = new Set(artTexLiveScopes.get('fan') || []);
  const nextLive = new Set(want.list.map(i => artTexKey(i, 'thumb')));
  setArtTexLive('fan', new Set([...priorLive, ...nextLive]));
  try {
    await Promise.all(want.list.map(i => ensureArtTex(i, 'thumb')));
    videoRelease();
    closeSolo();
    for (const c of cards) disposeCardObj(c);
    cards.length = 0;
    fanShowcase = want.showcase;
    for (const di of want.list) {
      const c = buildCard(deck[di], artTexCache.get(artTexKey(di, 'thumb')));
      c.deckIndex = di;
      fan.add(c.group);
      cards.push(c);
    }
    setArtTexLive('fan', nextLive);
    // dots rebuild (hidden past 12 cards — a 40-dot strip is noise)
    dotsEl.innerHTML = '';
    for (let i = 0; i < cards.length; i++) {
      const d = document.createElement('span');
      d.className = 'dot';
      dotsEl.appendChild(d);
    }
    dotsEl.style.display = cards.length > 12 ? 'none' : '';
    dotEls.length = 0;
    dotEls.push(...dotsEl.children);
    focusIndex = -1; focusPos = 0;
    setFocus(0);
    revealT = 0;
    console.info(`[nomadmon] MY DECK rebuilt: ${want.showcase ? 'SHOWCASE' : `${want.list.length} cards (deck-first)`}`);
  } catch (err) {
    setArtTexLive('fan', priorLive);
    throw err;
  } finally { fanBuilding = false; }
}
depthArt.onIndexChange(() => { void rebuildFan(true); });

// ---- pack economy labels: credits left, or a ticking countdown at 0 ----
const menuPackEl = document.getElementById('menuPack');
const menuPackSub = menuPackEl.querySelector('.sub');
const packCreditsEl = document.getElementById('packCredits');
function fmtCountdown(v) {
  const ts = typeof v === 'number' ? v : Date.parse(v || '');
  const ms = Math.max(0, (ts || 0) - Date.now());
  const h = Math.floor(ms / 3600e3);
  const m = Math.floor((ms % 3600e3) / 60e3);
  return h > 0 ? `${h}h ${m}m` : `${Math.max(m, 1)}m`;
}
let econRefreshing = false;
function paintEconomy() {
  const n = identity.creditsLeft();
  if (n > 0) {
    // SC4 anticipation: the counter knows the next pack is GOLD — say so
    const goldNext = identity.nextPackGold();
    menuPackSub.textContent = goldNext ? 'NEXT PACK: GOLD ✨' : `${n} pack${n === 1 ? '' : 's'} ready`;
    menuPackEl.classList.toggle('goldnext', goldNext);
    menuPackEl.classList.remove('nopacks');
    menuPackEl.classList.add('packs-ready');             // title-carousel.js: the wisp + rainbow
    packBtn.classList.toggle('goldnext', goldNext);       // fan chip glows too
    packCreditsEl.textContent = goldNext ? `· ${n} ✨` : `· ${n}`;
  } else {
    menuPackEl.classList.remove('goldnext');
    packBtn.classList.remove('goldnext');
    const cd = fmtCountdown(identity.nextGrantUtc());
    menuPackSub.textContent = `new packs in ${cd}`;
    menuPackEl.classList.add('nopacks');
    menuPackEl.classList.remove('packs-ready');
    packCreditsEl.textContent = `· ${cd}`;
    // a signed-in player's grant may have landed — ask the server once
    const ts = identity.nextGrantUtc();
    const passed = (typeof ts === 'number' ? ts : Date.parse(ts || '')) <= Date.now();
    if (passed && !identity.isGuest() && !econRefreshing) {
      econRefreshing = true;
      identity.refreshState().then(() => { econRefreshing = false; paintEconomy(); });
    }
  }
  if (!packActive) packBtn.disabled = n === 0;
  paintGate();                             // SC7: gate presentation rides every repaint
  paintTitleDash();
}

// ---- FRONT DOOR 5: the title dock is a living dashboard ----
// ONE item leads (its .sub is the info line and Enter activates it; every
// item is the same size), chosen by what gets this player moving: the gate or
// packs waiting -> Open Pack; otherwise -> Solo Battle.
// Every other tile carries live content (record, collection progress with
// real thumbnails). Presentation only: reads state, never writes it.
function titleLeadId() {
  if (battleLocked()) return 'menuPack';
  // ONBOARDING: just unlocked and never fought — the next thing is Battle,
  // even with packs waiting (guests can hold dozens)
  if (!onboard.skipped()) { const r = identity.record(); if (!(r.wins || r.losses)) return 'menuSolo'; }
  if (identity.creditsLeft() > 0) return 'menuPack';
  return 'menuSolo';
}
let collThumbKey = '';
function paintTitleDash() {
  const titleMenuEl = document.getElementById('titleCarousel');
  if (!titleMenuEl || !deck) return;
  const lead = titleLeadId();
  placeTitleMusic();
  for (const b of titleMenuEl.querySelectorAll('button')) b.classList.toggle('lead', b.id === lead);
  titleMenuEl.dataset.lead = lead;
  // QA-A: just unlocked with packs still waiting, the attention (wisp + rainbow)
  // moves from Open Pack to Campaign: that is the next thing to do
  const toCampaign = lead === 'menuSolo' && identity.creditsLeft() > 0;
  document.getElementById('menuSolo')?.classList.toggle('cta-next', toCampaign);
  menuPackEl.classList.toggle('cta-yield', toCampaign);
  // Solo: the record, and a streak when there is one
  const r = identity.record();
  const streak = identity.serverStreak();
  const soloSub = document.getElementById('soloSub');
  const mission = identity.todaysMission();   // v234 the daily mission: one line + an icon, no new tap
  if (soloSub) soloSub.textContent = mission ? `\u25ce ${mission.text}` : campaignOn ? campaignNextLine() : (r.wins || r.losses)
    ? `${r.wins}\u2013${r.losses} record${streak > 1 ? ` \u00b7 ${streak} win streak` : ''}`
    : 'vs the machine';
  // Collection: distinct owned / set, a progress bar, the best three as thumbnails
  const owned = deck.filter((d) => !d.forged && identity.ownedCount(d.id) > 0);
  const total = SET_SIZE || 1;
  const collSub = document.getElementById('collSub');
  if (collSub) collSub.textContent = `${owned.length}/${total}`;
  const bar = document.getElementById('collBar');
  if (bar) bar.style.transform = `scaleX(${Math.max(0.02, owned.length / total).toFixed(4)})`;
  const RANK = { eternal: 0, mythic: 1, legendary: 2, epic: 3, rare: 4, common: 5 };
  const best = owned.filter((d) => !d.clip && d.role !== 'support')
    .sort((a, b) => (RANK[a.rarity] ?? 6) - (RANK[b.rarity] ?? 6)).slice(0, 3);
  const key = best.map((d) => d.id).join('|');
  const host = document.getElementById('collThumbs');
  if (host && key !== collThumbKey) {
    collThumbKey = key;
    host.textContent = '';
    for (const d of best) {
      const img = document.createElement('img');
      img.alt = ''; img.decoding = 'async'; img.loading = 'lazy';
      img.src = artSrc(d, V, 'thumb');
      img.style.objectPosition = thumbPos(d);
      host.appendChild(img);
    }
  }
}
// the showcase caption: which card stands centre stage (DOM text, never canvas)
function paintTitleFeature(d) {
  const wrap = document.getElementById('titleFeature');
  if (!wrap) return;
  if (!d) { wrap.hidden = true; return; }
  document.getElementById('titleFeatureName').textContent = displayName(d.name);
  wrap.hidden = false;
}


// ---- NAME PICK overlay (friends-trust, no auth) ----
const pickerEl = document.getElementById('pickerOverlay');
const pickerList = document.getElementById('pickerList');
const pickerNote = document.getElementById('pickerNote');
let pickerBusy = false;
function pickerRow(name, meta, claimed, guest, fn) {
  const row = document.createElement('button');
  row.type = 'button';
  row.className = 'pkrow' + (guest ? ' guest' : '');
  const nm = document.createElement('span');
  nm.className = 'pkname'; nm.textContent = name;
  row.appendChild(nm);
  if (meta) {
    const mt = document.createElement('span');
    mt.className = 'pkmeta'; mt.textContent = meta;
    row.appendChild(mt);
  }
  if (claimed) {
    const cl = document.createElement('span');
    cl.className = 'pkclaimed'; cl.textContent = 'claimed';
    row.appendChild(cl);
  }
  row.addEventListener('click', fn);
  pickerList.appendChild(row);
}
// ACCOUNTS (2026-10): there is no player picker any more. "Switch" / the player chip open the account
// pages: LINK for a signed-in player (its "Switch player" leads to sign-in), sign-in otherwise.
function afterIdentityChange() {
  applyProgressGate();
  paintPlayerChip(); refreshBinderOwnership(); paintEconomy(); paintDeckBadge();
  shellUI.deckApi.reload(); rebuildFan(); tradingUI.refreshBadge();
}
function openAccountSwitch() {
  const signedIn = () => { identity.refreshState().then(afterIdentityChange); afterIdentityChange(); toast(`Signed in as ${identity.displayName()}`); };
  openSignIn({
    onSignedIn: signedIn,
    // Switch player -> "Start a new account" (handing the shared tablet to a friend): the NAME FORGE makes the account
    // and signs this device into it; its "Link an existing account" returns to this sign-in page (whose Back keeps
    // the current player)
    onNew: () => openNameForge({ onDone: signedIn, onHaveAccount: openAccountSwitch }),
  });
}
function openAccountPage(opts = {}) {
  if (identity.currentPlayer()) return openLinkPage({ ...opts, onSwitch: openAccountSwitch });
  return openAccountSwitch();
}
initAccount({ notify: (m, ms) => toast(m, ms), onSwitch: openAccountSwitch });
async function openPicker() {
  if (!identity.qaLocalIdentities()) { openAccountPage(); return; }   // the old picker survives for local QA only
  pickerEl.classList.add('open');
  document.getElementById('pickerClose').hidden = !identity.hasIdentity();
  pickerNote.textContent = '';
  pickerList.innerHTML = '<div class="pk-loading">Summoning the roster…</div>';
  let players = null;
  try { players = await identity.fetchPlayers(); } catch { /* offline */ }
  if (!pickerEl.classList.contains('open')) return;
  pickerList.innerHTML = '';
  if (!players) pickerNote.textContent = 'Offline — guest only for now';
  for (const p of players || []) {
    const rec = (p.wins || p.losses) ? `${p.wins || 0}–${p.losses || 0} · ` : '';
    pickerRow(p.name, `${rec}${p.cards || 0} cards`, !!p.claimed, false,
      () => choosePlayer(p.name));
  }
  if (identity.qaLocalIdentities()) pickerRow('Guest', 'this device only', false, true, () => choosePlayer('GUEST'));
  if (supremeVisible()) {
    pickerRow('Supreme', '50 packs · testing', false, true, () => choosePlayer('SUPREME'));
    pickerList.lastChild.classList.add('supreme');
  }
}
async function choosePlayer(name) {
  if (pickerBusy) return;
  pickerBusy = true;
  await identity.selectPlayer(name);
  // dismiss with the shared veil feel (same hand turning the page)
  const veil = document.getElementById('veil');
  veil.classList.add('on');
  setTimeout(() => {
    pickerEl.classList.remove('open');
    paintPlayerChip();
    refreshBinderOwnership();
    paintEconomy();
    paintDeckBadge();
    shellUI.deckApi.reload();            // re-filter the stored deck selection
    rebuildFan();                        // SC9-B: MY DECK follows the player
    tradingUI.refreshBadge();
    setTimeout(() => veil.classList.remove('on'), 80);
    pickerBusy = false;
    consumePendingJoin();                // SC3: ?join deep link resumes here
  }, 400);
}
playerChip.addEventListener('click', openPicker);
// backdrop tap closes the picker — only once an identity already exists
pickerEl.addEventListener('click', (e) => {
  if (e.target === pickerEl && identity.hasIdentity()) pickerEl.classList.remove('open');
});

// ---------------------------------------------------------------- SC6 ENTRY SCREEN
// The front door: the first interactive surface after the loader on EVERY
// cold load. New/unidentified player: the NAME PICK list IS the entry screen.
// Returning player: one big TAP TO ENTER — CONTINUE AS <NAME> panel (with a
// switch-player link). Either way the tap is the audio-unlock gesture, and
// the primed title theme detonates at full volume on it — THE BANG — then
// the cinematic (if session-ungated) or the title entrance slam rolls with
// the music already blasting. In-session title re-entries never re-show it.
const entryEl = document.getElementById('entry');
const entryListEl = document.getElementById('entryList');
const entryGuestSlotEl = document.getElementById('entryGuestSlot');
const entryNoteEl = document.getElementById('entryNote');
let entryRosterExpected = null;
let entryLanded = false;

function publishEntryReady() {
  if (!entryLanded || !entryAudioSettled || !entryEl.classList.contains('on')) return;
  entryEl.classList.add('ready');
}

function signalEntryHandoff(destination) {
  entryEl.dispatchEvent(new CustomEvent('nomadmon-entry-handoff', {
    detail: { destination }, bubbles: true,
  }));
}

// ENTER GATE ambient: a seamless glass-chime + airy-pad bed under the
// medallion, well under the gong (file -24 LUFS x 0.45). Starts on the gate tap
// (touch) or the first non-entering interaction (desktop); never after entry.
// Loops the file's 0.1-10.1 s window, which is exactly one period whatever
// encoder delay the decoder kept (scripts/gen-entry-ambient.py).
const ENTRY_AMBIENT = { gain: 0.45, fadeIn: 1.5, loopStart: 0.1, loopEnd: 10.1 };
let entryAmbient = null;
let entryAmbientWanted = false;
function startEntryAmbient() {             // call inside a user gesture
  if (!entryPending || entryAmbientWanted) return;
  entryAmbientWanted = true;
  Promise.all([audio.unlock({ flushPending: false }), audio.loadSfx(['entryambient'])]).then(() => {
    if (!entryPending || !entryAmbientWanted || entryAmbient) return;
    entryAmbient = audio.playBed('entryambient', ENTRY_AMBIENT);
  }).catch(() => {});
}
function stopEntryAmbient(fade = 0.6) {
  entryAmbientWanted = false;
  entryAmbient?.stop(fade);
  entryAmbient = null;
}

function entryEnter(name) {              // name to claim, or null = keep identity
  if (!entryPending) return;
  const tapPerf = performance.now();
  entryLayer.fx?.markTap(tapPerf);
  entryPending = false;
  // ENTER GATE: fullscreen now belongs to the ENTER GATE's tap (src/gate.js), never
  // this one — requesting it here blinked the screen mid-transition.
  stopEntryAmbient(0.6);                 // the bed bows out as the gong lands
  const ready = preloadEntryAudio();
  const unlocked = audio.unlock();
  audio.playPool('title');               // THE BANG (primed track, no fade-in)
  // A decoded buffer may be scheduled while WebKit's context is still
  // suspended; the gesture's resume then releases it without a promise hop.
  // If a truly cold tap beat decode, the joined fallback still plays the cue.
  entryHitPlayed = !!audio.play('entryhit', { gain: 0.96, arm: true });
  // FRONT DOOR 6: the spell layers ride the gong, sample-locked to the visual
  // beats (strike at the tap, crackle-boom at the climax, boom/whoosh on the
  // dive). Reduced motion keeps the gong alone, matching its calm exit.
  const spell = { charge: null, plunge: null };
  let skipped = false;
  const playSpell = () => {
    if (REDUCED || skipped) return;
    // a cold tap that beat decode joins late: keep the beats on the visuals
    const late = (performance.now() - tapPerf) / 1000;
    // title10: a late join no longer drops the strike cue — it starts at the
    // matching offset, so the crackle-boom still lands on the climax
    if (late < 0.25) spell.charge = audio.play('entrycharge', { arm: true });
    else if (late < ENTRY_BEATS.climax - 0.05) spell.charge = audio.play('entrycharge', { offset: late });
    spell.plunge = audio.play('entryplunge', { when: Math.max(0, ENTRY_BEATS.plunge - late), arm: true });
  };
  playSpell();
  Promise.all([unlocked, ready]).then(() => {
    if (!entryHitPlayed)
      entryHitPlayed = !!audio.play('entryhit', { gain: 0.96 });
    if (!spell.plunge) playSpell();
  }).catch(() => {});
  const fadeCue = (src) => {
    try {
      const t = src.context.currentTime;
      src.gainNode.gain.setTargetAtTime(0, t, 0.025);
      src.stop(t + 0.2);
    } catch { /* already stopped */ }
  };
  // SC14: a RETURNING player (kept identity) occasionally gets a welcome-back
  // line instead of the generic title call — after the bang, never delaying it
  const returning = !name && identity.currentPlayer() && !identity.isGuest();
  const alt = returning && Math.random() < 0.3
    ? (Math.random() < 0.5 ? 'entry-return1' : 'entry-return2') : null;
  greet(cinematicActive ? 1400 : 600, alt);   // Shao's title call layers on top
  setTimeout(() => tradingUI.refreshBadge(), 240);
  const exitDone = ensureEntryFX(entryLayer.onPerf).then((fx) => fx.startExit());
  entryEl.classList.add('out');
  // FRONT DOOR 5: the reveal is a gate, opened by the authored dive timing or
  // early by a tap (skip). The skip tap's own click is swallowed so it cannot
  // land on a title tile that the reveal just exposed under the finger.
  const exitStarted = performance.now();
  let openReveal;
  const revealGate = new Promise((resolve) => { openReveal = resolve; });
  const revealTimer = setTimeout(() => openReveal(),
    (entryLayer.fx?.revealSeconds?.() ?? EXIT_REVEAL) * 1000);
  const skipDive = (e) => {
    if (performance.now() - exitStarted < 120) return;
    // a skip lands straight on the dive: the build-up stops, the boom plays now
    if (entryLayer.fx?.skipExit?.() && !skipped) {
      skipped = true;
      if (spell.charge) fadeCue(spell.charge);
      if (spell.plunge && performance.now() - exitStarted < ENTRY_BEATS.plunge * 1000 - 30) {
        fadeCue(spell.plunge);
        spell.plunge = audio.play('entryplunge');
      }
    }
    clearTimeout(revealTimer);
    openReveal();
    const eat = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
    addEventListener('click', eat, { capture: true, once: true });
    setTimeout(() => removeEventListener('click', eat, { capture: true }), 700);
    e.preventDefault?.();
  };
  addEventListener('pointerdown', skipDive, { capture: true });
  revealGate.then(() => removeEventListener('pointerdown', skipDive, { capture: true }));
  const atDiveReveal = (fn) => revealGate.then(fn);
  // Prepare the destination under the opaque entry stage, then reveal it only
  // once the medal fills the lens. The title group does not exist before tap.
  if (cinematicActive) {
    // The usable title DOM becomes the destination synchronously beneath the
    // opaque dive, while the optional film may still be waiting on decode.
    // The film itself is not exposed until `playing`; failure simply leaves
    // the already-active title in place.
    titleLayer.on = true;
    signalEntryHandoff('title');
    startIntro({
      revealWhen: revealGate,
      onReveal: () => document.body.classList.remove('entry-active'),
    });
  }
  else if (shellUI.currentScreen() === 'title') {
    // Activate the usable title synchronously under the still-opaque entry
    // dive. Timers are not a handoff contract on a loaded renderer; the entry
    // layer continues to own the pixels until its authored reveal point.
    titleLayer.on = true;
    signalEntryHandoff('title');
    atDiveReveal(() => document.body.classList.remove('entry-active'));
    ensureTitlePlane().then(() => {
      atDiveReveal(playTitleEntrance);
    }).catch((err) => {
      // Title key art is optional to the usable DOM destination. Preserve the
      // bounded handoff and avoid an unhandled rejection if its build fails.
      console.warn('[nomadmon] optional title FX unavailable:', err);
    });
  } else atDiveReveal(() => {
    document.body.classList.remove('entry-active');
    signalEntryHandoff(shellUI.currentScreen());
  });
  exitDone.finally(() => entryEl.classList.remove('on', 'out', 'ready'));
  const idP = name
    ? identity.selectPlayer(name).then(() => {
        applyProgressGate();
        paintPlayerChip(); refreshBinderOwnership(); paintEconomy();
        paintDeckBadge(); shellUI.deckApi.reload(); rebuildFan(); tradingUI.refreshBadge();
      })
    : Promise.resolve();
  if (entryProceed) {
    const fn = entryProceed; entryProceed = null;
    Promise.all([idP, revealGate]).then(() => fn());
  }
}

// SC43: `host` is explicit. GUEST is mounted into #entryGuestSlot — a flex:none
// sibling BELOW the scrolling roster — never into #entryList. It was the last
// row of that scroller and became untappable at any shape where the roster
// overflowed (measured failing at 5 of 9 shapes on LIVE v54).
// v251 SUPREME is hidden: ?supreme=1 reveals it on this device (remembered),
// ?supreme=0 hides it again.
function supremeVisible() {
  const K = 'nomadmon-supreme-link';
  try {
    const q = new URLSearchParams(location.search).get('supreme');
    if (q === '1') localStorage.setItem(K, '1');
    else if (q === '0') localStorage.removeItem(K);
    return localStorage.getItem(K) === '1';
  } catch { return false; }
}
function entryChip(name, rec, guest, fn, host) {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'enchip' + (guest ? ' guest' : '');
  const nm = document.createElement('span');
  nm.className = 'enname'; nm.textContent = name;
  const mt = document.createElement('span');
  mt.className = 'enrec'; mt.textContent = rec;
  b.append(nm, mt);
  b.addEventListener('click', () => fn());
  (host || entryListEl).appendChild(b);
}

// ACCOUNTS (2026-10): the new-player pane. No roster, no guest: one big TAP TO BEGIN opens the NAME FORGE
// (src/account.js), and a secondary "I already have an account" opens sign-in (name + PIN, or this device's
// code for a signed-in device to approve). Either way ends signed in, and the usual BANG + dive follows.
// The local-only QA identities (GUEST / SUPREME) keep their chips: GUEST on a localhost QA page only,
// SUPREME behind its secret link as before.
function entryAccountDone() {
  if (!entryPending) return;
  entryEnter(identity.currentPlayer());
}
// ACCOUNTS v2 (Jack): a device with no account goes TAP TO ENTER -> straight into the NAME FORGE (only the
// name and a small "Link an existing account") -> Shao's roast -> the MEDALLION (only the medal, its tap
// and a quiet switch link) -> the usual BANG and dive. `entryNeedsAccount` is decided once at boot.
let entryNeedsAccount = false;
function entryFirstRunForge() {
  if (!entryPending) return;
  openNameForge({ onDone: entryShowMedallion, onHaveAccount: entryFirstRunSignIn });
}
function entryFirstRunSignIn() {
  if (!entryPending) return;
  openSignIn({ onSignedIn: entryShowMedallion, onBack: entryFirstRunForge, onNew: entryFirstRunForge });
}
let entryContinueWired = false;
function entryShowMedallion() {
  if (!entryPending) return;
  const name = identity.displayName();
  document.getElementById('entryPickWrap').hidden = true;
  document.getElementById('entryName').textContent = name;
  document.getElementById('entrySwitch').textContent = `Not ${name}? Switch account`;
  document.getElementById('entryContinue').hidden = false;
  entryWireContinue(true);
}
function entryWireContinue(fresh) {
  if (entryContinueWired) return;
  entryContinueWired = true;
  // a session that signed in here enters through selectPlayer (state + repaint); a returning one keeps its identity
  document.getElementById('entryGo').addEventListener('click', () => entryEnter(fresh ? identity.currentPlayer() : null));
  document.getElementById('entrySwitch').addEventListener('click', () => (identity.currentPlayer()
    ? openSignIn({ onSignedIn: () => { fresh = true; entryShowMedallion(); }, onBack: () => {}, onNew: () => { if (entryPending) openNameForge({ onDone: () => { fresh = true; entryShowMedallion(); }, onHaveAccount: entryFirstRunSignIn }); } })
    : entryShowList()));
  const crest = document.querySelector('#entry .en-crest');
  crest?.addEventListener('click', () => {
    if (!document.getElementById('entryContinue').hidden) document.getElementById('entryGo').click();
  });
}
function entryOpenSignIn() {
  openSignIn({ onSignedIn: entryAccountDone, onBack: () => {},
    onNew: () => { if (entryPending) openNameForge({ onDone: entryAccountDone, onHaveAccount: entryOpenSignIn }); } });
}
function entryShowList() {
  document.getElementById('entryContinue').hidden = true;
  document.getElementById('entryPickWrap').hidden = false;
  entryListEl.innerHTML = '';
  entryGuestSlotEl.innerHTML = '';
  if (identity.qaLocalIdentities()) entryChip('Guest', 'this device only', true, () => entryEnter('GUEST'), entryGuestSlotEl);
  // v241 SUPREME: the testing sandbox (50 packs that never go down).
  // v251 (Jack): hidden unless this device opened the secret link (?supreme=1).
  if (supremeVisible()) {
    entryChip('Supreme', '50 packs · testing', true, () => entryEnter('SUPREME'), entryGuestSlotEl);
    entryGuestSlotEl.lastChild.classList.add('supreme');
  }
  entryNoteEl.textContent = '';
  entryRosterExpected = 0;
}
document.getElementById('entryNew')?.addEventListener('click', () => {
  if (!entryPending) return;
  startEntryAmbient();
  openNameForge({ onDone: entryAccountDone, onHaveAccount: entryOpenSignIn });
});
document.getElementById('entryHave')?.addEventListener('click', () => { if (entryPending) entryOpenSignIn(); });

// SC43: the roster's "there is more below" fade. Measured, not assumed — it is
// off when nothing overflows and off once you have reached the bottom, so the
// cue never claims content that is not there. GUEST is NOT in this container,
// so nothing about this affects whether GUEST can be reached.
function entryRosterCue() {
  const el = entryListEl;
  if (!el) return;
  const more = el.scrollHeight - el.clientHeight - el.scrollTop > 4;
  el.classList.toggle('more', more);
}
entryListEl?.addEventListener('scroll', entryRosterCue, { passive: true });
addEventListener('resize', entryRosterCue);

// ACCOUNTS: ?login=<one-time token> (another device's LINK, or Jack's admin link) signs this device in
// before the front door decides between CONTINUE and the new-player pane.
{
  let login = await identity.consumeLoginParam();
  if (login && login.pending) login = await confirmLoginSwitch(login.current) ? await identity.redeemLoginToken(login.pending).then(() => 'ok', () => 'bad') : null;
  if (login === 'bad') setTimeout(() => toast('That login link has expired or was already used. Make a new one on your other device.', 9000), 2500);
  // a #login link opened into an already-open tab only changes the hash: start again so it is consumed
  addEventListener('hashchange', () => { if (/(^|[#&])login=/.test(location.hash)) location.reload(); });
}
{
  audio.primePool('title');              // warm the session's track NOW
  const cur = identity.hasIdentity() ? identity.displayName() : null;
  if (cur) {
    document.getElementById('entryName').textContent = cur;
    document.getElementById('entryContinue').hidden = false;
    document.getElementById('entrySwitch').textContent = identity.currentPlayer() ? `Not ${cur}? Switch account` : `Playing as ${cur} · switch`;
    entryWireContinue(false);
  } else {
    // no account and no local QA identity: the gate leads straight into the name forge
    entryNeedsAccount = !identity.qaLocalIdentities() && !supremeVisible() && !window.__nmNoGate;
    if (entryNeedsAccount) {
      document.getElementById('entryContinue').hidden = true;
      document.getElementById('entryPickWrap').hidden = true;
    } else entryShowList();
  }
  const activateEntry = (fx = null) => {
    const entryOnPerf = performance.now();
    entryLayer.onPerf = entryOnPerf;
    document.body.classList.add('entry-active');
    entryEl.classList.add('on');
    deckStack.group.visible = false;
    // Keep decodeAudioData off the loader → first-entry-frame handoff. The
    // entry is already visible during this delay and remains up for the tap.
    setTimeout(() => preloadEntryAudio().catch(() => {}), 240);
    fx?.show(entryOnPerf);
    if (!fx) {
      entryLanded = true;
      publishEntryReady();
    }
    // Keep the loader's identical dark ground through a real entry frame. This
    // is scheduled from entry activation (not module completion), so unrelated
    // synchronous boot work cannot make the visual handoff start early.
    setTimeout(() => requestAnimationFrame(() => {
      document.getElementById('loader')?.classList.add('done');
    }), 300);
  };
  // Construct the medal while the loader still owns the stage, then publish
  // #entry.on only when the front door can render. This keeps the activation
  // timestamp honest even on a cold GPU/compiler path.
  const fxReady = ensureEntryFX().catch((error) => {
    console.error('[nomadmon-entry]', error);
    return null;
  });
  if (entryGateWanted() || entryNeedsAccount) {
    // ENTER GATE (touch / ?fs=1): its tap goes fullscreen + unlocks audio
    // and starts the ambient bed; the medal is activated (arrival and all) only
    // once the gate has dimmed, so its intro plays after the gate, not behind it.
    // ACCOUNTS v2: a device with no account always gets the gate (desktop too, without fullscreen) and its
    // tap opens the name forge inside the same gesture (audio + the phone keyboard need one).
    const gate = openEntryGate({ reduced: REDUCED, fullscreen: entryGateWanted(),
      onTap: () => { startEntryAmbient(); if (entryNeedsAccount) entryFirstRunForge(); } });
    document.body.classList.add('entry-active');   // title chrome stays dark under the gate
    document.getElementById('loader')?.classList.add('done');   // the gate is the ground now
    Promise.all([fxReady, gate.passed]).then(([fx]) => gate.leave(() => activateEntry(fx)));
  } else {
    fxReady.then(activateEntry);
    // Desktop: the bed starts on the first interaction that does not itself
    // enter (a click that enters has already cleared entryPending by now).
    const firstInput = (e) => {
      if (e.type === 'keydown' && /^(Enter| |Escape|Tab)$/.test(e.key)) return;
      removeEventListener('click', firstInput);
      removeEventListener('keydown', firstInput);
      startEntryAmbient();
    };
    addEventListener('click', firstInput);
    addEventListener('keydown', firstInput);
  }
}
if (identity.hasIdentity() && !identity.isGuest()) {
  // The boot /state fetch times out at 6 s (identity.api). A busy first load
  // (shader compiles, a slow phone) can stall the main thread past that, and a
  // single miss used to leave a signed-in player looking at an EMPTY local
  // collection for the whole session. Retry a few times, then repaint.
  const bootState = (tries = 0) => identity.refreshState().then(() => {
    applyProgressGate();
    paintPlayerChip(); refreshBinderOwnership(); paintEconomy(); paintDeckBadge(); paintForgeMark();
    tradingUI.refreshBadge();
    if (!identity.state() && tries < 4) setTimeout(() => bootState(tries + 1), 1500 * (tries + 1));
  });
  idStateP = bootState();
}
// a signed-in session that lost the server is back (identity.recover: state refreshed, outbox replayed): repaint
addEventListener('nomadmon-net', (e) => {
  if (e.detail?.down || identity.isGuest()) return;
  paintPlayerChip(); refreshBinderOwnership(); paintEconomy(); paintDeckBadge();
});
// SC18: the first-claim registry is small (mythic + alt only) and READ-ONLY, so
// guests fetch it too — the engraving is public prestige and a guest looking at
// a card back should still see who got there first. Only WRITING a claim needs
// a signed-in, server-backed pull.
identity.fetchFirstClaims();
paintPlayerChip();
paintEconomy();
paintDeckBadge();
setInterval(paintEconomy, 60e3);         // the countdown ticks each minute

// =================== SC46 — BACK NAVIGATION + THE EXIT CONFIRMATION ========
// See src/nav.js for the history mechanism and why the guard entry is a single
// entry rather than one per level. This block is the wiring: which layers exist
// and what "up one level" means for each of them.
//
// ORDERING IS BY z, NOT BY REGISTRATION ORDER, so a layer added later cannot
// silently end up underneath one it visually covers.
const exitConfirmEl = document.getElementById('exitConfirm');
function exitConfirmOpen() { return exitConfirmEl.classList.contains('open'); }
function showExitConfirm(on) {
  exitConfirmEl.classList.toggle('open', on);
  exitConfirmEl.setAttribute('aria-hidden', on ? 'false' : 'true');
  if (on) {
    // MULTIPLAYER IS THE CASE THAT MATTERS: leaving abandons a real person
    // mid-game, so the copy says so rather than reusing the solo line.
    const remote = !!(battle && battle.battleIsRemote && battle.battleIsRemote());
    document.getElementById('xcSub').textContent = remote
      ? 'Your opponent wins by forfeit.' : 'This counts as a forfeit.';
    // focus the SAFE choice — a stray Enter must not throw the battle away
    setTimeout(() => document.getElementById('xcStay').focus(), 40);
  }
}
document.getElementById('xcStay').addEventListener('click', () => showExitConfirm(false));
document.getElementById('xcGo').addEventListener('click', () => {
  showExitConfirm(false);
  // Exactly what the deleted FORFEIT button did: notify the opponent, then
  // resolve the battle through the ordinary defeat path so the record, the
  // streak and the interstitial all still happen.
  if (battle && battle.forfeitBattle) battle.forfeitBattle();
});

nav.registerLevel({ name: 'exitConfirm', z: 99,
  isOpen: exitConfirmOpen, close: () => showExitConfirm(false) });
nav.registerLevel({ name: 'tradeMoment', z: 98,
  isOpen: () => document.getElementById('tradeFx').classList.contains('on'),
  close: () => document.getElementById('tradeFx').dispatchEvent(new PointerEvent('pointerdown')) });
nav.registerLevel({ name: 'pwrPop', z: 90,
  isOpen: () => { const e = document.getElementById('pwrPop'); return !!e && e.classList.contains('open'); },
  close: () => guide.closePwrPop() });
nav.registerLevel({ name: 'tradeCompose', z: 89,
  isOpen: () => document.getElementById('tradeCompose').classList.contains('open'),
  close: () => document.getElementById('tradeComposeClose').click() });
nav.registerLevel({ name: 'tradeSocial', z: 88,
  isOpen: () => document.getElementById('tradeSocial').classList.contains('open'),
  close: () => document.getElementById('tradeSocialClose').click() });
nav.registerLevel({ name: 'spareSheet', z: 86,
  isOpen: () => spareSheet.classList.contains('open'),
  close: () => { spareSheet.classList.remove('open'); spareSheet.setAttribute('aria-hidden', 'true'); } });
nav.registerLevel({ name: 'binderSheet', z: 87,
  isOpen: () => binderSheetIndex >= 0, close: () => closeBinderSheet() });
nav.registerLevel({ name: 'inspect', z: 85,
  isOpen: () => inspecting, close: () => setInspect(false) });
nav.registerLevel({ name: 'guideSheet', z: 80,
  isOpen: () => { const e = document.getElementById('guideSheet'); return !!e && e.classList.contains('open'); },
  close: () => guide.closeGuide() });
nav.registerLevel({ name: 'labOverlay', z: 70,
  isOpen: () => labOverlay.classList.contains('show'), close: () => closeLab() });
nav.registerLevel({ name: 'settings', z: 50,
  isOpen: () => { const e = document.getElementById('settingsDrawer'); return !!e && e.classList.contains('open'); },
  close: () => document.getElementById('settingsDrawer').classList.remove('open') });
nav.registerLevel({ name: 'deckPanel', z: 45,
  isOpen: () => { const e = document.getElementById('deckPanel'); return !!e && e.classList.contains('open'); },
  close: () => document.getElementById('deckPanelClose')?.click() });
nav.registerLevel({ name: 'soloCard', z: 40,
  isOpen: () => !!soloCard, close: backFromSolo });
nav.registerLevel({ name: 'binderFilters', z: 35,
  isOpen: () => document.getElementById('binderFilterScrim').classList.contains('open'),
  close: () => setBinderFilters(false) });
nav.registerLevel({ name: 'binder', z: 30,
  isOpen: () => binderEl.classList.contains('open'), close: closeCollection });
// COLLECTION 2 layers: the focused card, the Duo/Fusion explainer, the forge sheet
nav.registerLevel({ name: 'collFocus', z: 37, isOpen: () => cf.open, close: closeFocus });
// THE CODEX: back steps a chapter back to the hall, then closes it
// THE ATLAS (campaign map v2): back skips the unlock cinematic, steps Scout -> card -> closed -> realm, then leaves
nav.registerLevel({ name: 'campaignMap', z: 20, isOpen: () => !!(campaignUI && campaignUI.isOpen()), close: () => campaignUI.back() });
nav.registerLevel({ name: 'codex', z: 50, isOpen: () => !!(codexMod && codexMod.isOpen()), close: () => codexMod.back() });
nav.registerLevel({ name: 'forgeExplain', z: 38, isOpen: () => forgeUI.explainOpen(), close: () => forgeUI.closeExplain() });
nav.registerLevel({ name: 'forgeSheet', z: 39,
  isOpen: () => !!document.getElementById('forgeSheet')?.classList.contains('on'), close: () => forgeUI.closeSheet() });
// THE NAME PICKER IS THE FRONT DOOR when there is no identity yet, so it is
// only a dismissable LEVEL once an identity exists. Otherwise back at the
// entry screen would close the only thing the player can act on.
// NAV AUDIT: a tap on a modal's dimmed backdrop closes it (the exit
// confirmation's backdrop is the SAFE answer: keep fighting).
document.querySelector('#exitConfirm .xc-scrim').addEventListener('click', () => showExitConfirm(false));
for (const [id, closeId] of [['tradeSocial', 'tradeSocialClose'], ['tradeCompose', 'tradeComposeClose'], ['oaConfirm', 'oaNo']]) {
  const el = document.getElementById(id);
  el.addEventListener('click', (e) => { if (e.target === el) document.getElementById(closeId).click(); });
}
// Panels with no backdrop of their own (the settings drawer, the guide sheet,
// the PWR popover): a tap outside closes the topmost one, and that tap is
// consumed, so it cannot also press whatever sat underneath.
{
  const outside = [
    ['pwrPop', () => guide.closePwrPop(), '[data-pwr-badge]'],
    ['guideSheet', () => guide.closeGuide(), '#binderGuide, #deckGuide, #settingsGuide'],
    ['settingsDrawer', () => document.getElementById('settingsDrawer').classList.remove('open'), '#titleSettings'],
  ];
  let swallowUntil = 0;
  document.addEventListener('pointerdown', (e) => {
    for (const [id, close, openers] of outside) {
      const el = document.getElementById(id);
      if (!el || !el.classList.contains('open')) continue;
      if (el.contains(e.target)) return;                 // a tap inside the topmost panel
      if (e.target.closest && e.target.closest(openers)) return;   // its own opener toggles it
      close(); swallowUntil = performance.now() + 450;
      return;
    }
  }, true);
  document.addEventListener('click', (e) => {
    if (performance.now() < swallowUntil) { swallowUntil = 0; e.preventDefault(); e.stopPropagation(); }
  }, true);
}

// NAV AUDIT: layers that had no Back at all.
nav.registerLevel({ name: 'openAllConfirm', z: 92,
  isOpen: () => document.getElementById('oaConfirm').classList.contains('show'),
  close: () => document.getElementById('oaNo').click() });
nav.registerLevel({ name: 'levelUpFx', z: 96,
  isOpen: () => levelFxOpen(),
  close: () => { if (levelFx) levelFx.then(mod => mod.skip()); } });
nav.registerLevel({ name: 'feedFx', z: 95,
  isOpen: () => { const e = document.getElementById('feedFx'); return !!e && e.classList.contains('on'); },
  close: () => finishFeedMoment(true) });
// Between gauntlet legs the battle is over, so Back abandons the run (the
// panel used to stay up over the title and "March on" relaunched a leg from
// there); on the end card it is the card's own "Back to the title".
nav.registerLevel({ name: 'gauntPanel', z: 65,
  isOpen: () => { const e = document.getElementById('gauntPanel'); return !!e && e.classList.contains('on'); },
  close: () => {
    const btn = document.getElementById('gpBtn');
    if (!gauntlet) { btn.click(); return; }
    gauntlet = null;
    const e = document.getElementById('gauntPanel');
    e.classList.remove('on'); e.setAttribute('aria-hidden', 'true');
    if (battleActive && battle && battle.exitBattle) battle.exitBattle();
    shellUI.go('title');
  } });
document.getElementById('pickerClose').addEventListener('click', () => pickerEl.classList.remove('open'));
nav.registerLevel({ name: 'playerPicker', z: 20,
  isOpen: () => pickerEl.classList.contains('open') && identity.hasIdentity(),
  close: () => pickerEl.classList.remove('open') });

nav.initNav({
  currentScreen: () => (battleActive ? 'battle'
    : packActive ? 'pack'
    : shellUI.currentScreen()),
  goTitle: () => {
    // NAV AUDIT: Back on a screen does what that screen's home medal does.
    // A pack ceremony is never torn in half: Back skips ahead to the summary
    // (nothing is lost — the cards are already yours), then Done closes it.
    if (packActive) {
      for (const id of ['montSkip', 'packSkipBtn', 'montDone', 'packDoneBtn']) {
        const b = document.getElementById(id);
        if (b && !b.disabled && b.offsetParent && getComputedStyle(b).visibility !== 'hidden') { b.click(); return; }
      }
      return;
    }
    const scr = shellUI.currentScreen();
    // the MP lobby: leaving must close the room, or the host's poll later
    // drags the player from the title into a deck pick
    if (scr === 'mp' && mpUI) { mpUI.cancel(); shellUI.go('title'); return; }
    // deck pick / lucky draw: the medal's route (an MP pick returns to the
    // cancellable lobby; a solo pick goes home)
    if (scr === 'deck' || scr === 'draw') {
      if (slotActive && slotmod) slotmod.endSlot();
      shellUI.deckHome();
      return;
    }
    if (slotActive && slotmod) slotmod.endSlot();
    // A FINISHED BATTLE STILL OWNS THE RENDER LOOP.
    // tick() short-circuits into battle.updateBattle() for as long as
    // battleActive is true, and that stays true until exitBattle() runs — which
    // is why the interstitial's CONTINUE calls it before go('title'). Without
    // the same call here, back from the post-battle interstitial paints the
    // title over a battle scene that is still updating behind it. Nothing
    // asserted this; it was found by reading what CONTINUE does and asking why
    // this path did less. A LIVE battle never reaches here — stepUp() sends it
    // to the confirmation instead — so this only ever tears down a finished one.
    if (battleActive && battle && battle.exitBattle) battle.exitBattle();
    shellUI.go('title');
  },
  // Only a LIVE, still-winnable battle is guarded. Once it is over the
  // interstitial is up and stepping back to the title costs nothing.
  battleActive: () => !!(battle && battle.battleInProgress && battle.battleInProgress()),
  confirmExit: () => showExitConfirm(true),
  // Tab close / app swipe-away mid-battle. No dialog is possible here; what
  // matters is that a multiplayer opponent is not left polling for 60 seconds.
  onUnloadRisk: () => { if (battle && battle.beaconForfeit) battle.beaconForfeit(); },
});
// Keep the history guard in step with the app's actual depth. Cheap, and it
// runs where the state actually changes rather than on a timer.
setInterval(() => nav.sync(), 400);

// v259 PERF BEACON (src/perfmon.js): each frame is labelled from the flags the app already keeps
// (allocation-free: interned strings only); a sub-phase is read only to label a hitch.
{
  const SCREEN = { title: 'title', deck: 'squad', draw: 'luckydraw', inter: 'result', mp: 'mp', lb: 'leaderboard', fan: 'collection' };
  const BATTLE = { volley: 'battle-volley', strike: 'battle-strike', enemy: 'battle-enemy', draw: 'battle-drawit',
    block: 'battle-block', lastbreath: 'battle-lastbreath', over: 'battle-over' };
  const PACK = { sealed: 'pack', materialize: 'pack', torn: 'pack', diaclench: 'pack' };
  const bodyCL = document.body.classList;
  perfmon.start({
    app: V,
    scene: () => {
      if (codexOpen) return 'codex';
      if (window.__nmCutscene && window.__nmCutscene.live()) return 'cutscene';
      if (levelFxCover) return 'levelup';
      if (battleActive) {
        if (shellUI.currentScreen() === 'inter') return 'result';
        const d = window.__nm && window.__nm.barrageDebug;
        return (d && BATTLE[d.phase]) || 'battle';
      }
      if (packActive) { const ph = pack && pack.phaseNow ? pack.phaseNow() : null; return PACK[ph] || 'reveal'; }
      if (bodyCL.contains('campaign-on')) return 'campaign';
      if (slotActive) return 'luckydraw';
      if (entryLayer.fx?.active || introLayer.fx?.active) return 'entry';
      const s = shellUI.currentScreen();
      if (s === 'fan' && binderEl.classList.contains('forge-on')) return 'forge';
      return SCREEN[s] || 'other';
    },
    sub: (label) => {
      if (label.startsWith('battle')) { const d = window.__nm && window.__nm.barrageDebug; return d ? `r${d.round}-${d.phase}` : null; }
      if (label === 'pack' || label === 'reveal') return pack && pack.phaseNow ? pack.phaseNow() : null;
      return null;
    },
    quality: () => ({ step: qualityStep, sheds: perfLog.sheds, restores: perfLog.restores }),
  });
  perfmon.setGpuFrom(renderer.getContext());
}

requestAnimationFrame(tick);

// diagnostics handle (debug builds and remote QA)
// ?debug=gfx: a tiny corner readout of the colour pipeline as THIS device runs
// it, so a screenshot from Jack's tablet settles "which build / tier / GPU".
function gfxDebug() {
  const gl = renderer.getContext();
  const info = gl.getExtension('WEBGL_debug_renderer_info');
  const hp = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
  const mp = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.MEDIUM_FLOAT);
  const tm = Object.keys(THREE).find((k) => k.endsWith('ToneMapping') && THREE[k] === renderer.toneMapping) || renderer.toneMapping;
  const css = document.getElementById('forgeCss');
  return {
    version: V, sw: navigator.serviceWorker?.controller ? 'controlled' : 'none',
    gpu: info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
    webgl: renderer.capabilities.isWebGL2 ? 2 : 1, floatLinear: renderer.extensions.has('OES_texture_float_linear'),
    fragHighp: hp ? hp.precision : 0, fragMediump: mp ? mp.precision : 0,
    outputColorSpace: renderer.outputColorSpace, toneMapping: tm, exposure: renderer.toneMappingExposure,
    envType: scene.environment ? scene.environment.type : null,
    passes: composer.passes.map((p) => `${p.name || p.constructor.name}${p.enabled ? '' : '(off)'}`).join(' > '),
    lut: lutController.debugState(), qualityStep, bloom: bloomPass.enabled,
    dpr: +renderer.getPixelRatio().toFixed(2), devDpr: devicePixelRatio, view: `${innerWidth}x${innerHeight}`,
    hud: document.body.dataset.hud, canvasFilter: getComputedStyle(renderer.domElement).filter,
    forgeCss: css ? css.getAttribute('href') : null,
  };
}
if (q.get('debug') === 'gfx') {
  const el = document.createElement('pre');
  el.id = 'gfxDebug';
  el.style.cssText = 'position:fixed;left:4px;bottom:4px;z-index:2147483647;margin:0;padding:6px 8px;max-width:min(96vw,520px);'
    + 'background:rgba(0,0,0,.78);color:#9f9;font:10px/1.3 ui-monospace,monospace;white-space:pre-wrap;pointer-events:none;border-radius:6px';
  document.body.appendChild(el);
  const paint = () => {
    const d = gfxDebug();
    el.textContent = Object.entries(d).map(([k, v]) => `${k}: ${typeof v === 'object' && v ? JSON.stringify(v) : v}`).join('\n');
  };
  paint(); setInterval(paint, 1000);
}

window.__nm = {
  // v11: `deck` is THE SET (forged Duos/Fusions are appended after it in the
  // live array, so indices below SET_SIZE are unchanged); forgedDeck lists them.
  cards, fan, aura, bloomPass, renderer, camera, composer, setFocus, deck: deck.slice(0, SET_SIZE), manip,
  forgedDeck: deck.filter(d => d.forged),
  // INK DUEL CHAMPIONS QA: grant(id) = a local win with that champion (first = roll, repeat = rank-up; never the
  // server, which grants only from a judged duel); stats(id) = its live line and the top three it averages
  champion: {
    data: () => CHAMPIONS.map(c => ({ ...c })),
    list: () => identity.championsOwned(),
    grand: () => championGrand(),
    grant(id) {
      if (!CHAMPIONS.some(c => c.id === id)) return { ok: false, error: 'unknown champion' };
      const r = identity.grantChampionLocal(id);
      if (BV) buildBinderModel();
      return { ok: true, first: r.first, entry: r.entry };
    },
    stats(id) {
      const d = cardsById[championCardId(id)];
      if (!d) return null;
      const L = d.live();
      return { id: d.id, hp: d.hp, spd: d.spd, basic: d.moves[0].dmg, special: d.moves[1].dmg, stars: d.stars, pwr: effectivePwr(d, 1),
        bonus: L.bonus, base: L.base, top: L.top, owned: identity.ownedCount(d.id) > 0 };
    },
  },
  // v257 PROGRESSION QA (read-only views + the same calls the UI makes)
  prog: { chest: () => identity.chestState(), claim: () => identity.claimChest(), trophies: () => identity.trophies(), doodleDay: () => identity.doodleDay(),
    paintTrophyTab: () => paintTrophyTab(),
    // a result as the barrage would hand it (QA: the trial / daily result screens without a 10-minute boss fight)
    finish: (res) => { if (!campaignUI) return false; campaignUI.onFinish(res, (then) => then && then()); return true; },
    // CAMPAIGN ASSIST: read (no argument) or set the account's setting, as the map's toggle does
    assist: (on) => (on === undefined ? identity.campaignAssist() : identity.setCampaignAssist(on)) },
  // questduo QA: finish(levelId) = a campaign win of that level through the real result screen + ledger;
  // state() = the running ceremony; drive.sway(p) / hold(ms) / climb(t) feed its gates like real input
  quest: {
    finish: async (levelId, res = {}) => {
      await openCampaign({ initOnly: true });
      campaignUI.onFinish({ id: levelId, won: true, lost: 0, rounds: 6, perfectDraw: 0, enemyHpPct: 0, ...res }, (then) => then && then());
      return true;
    },
    state: () => (questMod ? questMod.questState() : null),
    drive: { sway: (p) => questMod && questMod.questDrive.sway(p), hold: (ms) => questMod && questMod.questDrive.hold(ms), climb: (t) => questMod && questMod.questDrive.climb(t) },
    list: () => identity.questList(), granted: () => identity.questGranted(), pending: () => questPending(),
    // dupduo QA: play(key, { owned }) opens the quest ceremony directly ('duo' | 'fusion'), seen or not, granting
    // nothing; owned: true plays it as a grant of a card already owned (the ALREADY YOURS chip); owned: 'maxed' /
    // 'none' the other two chips; xp: the card's XP before (default 300)
    play: (key = 'duo', o = {}) => {
      const q = identity.questList().find(x => x.key === key), r = q && codexLib.recipe(q.card), d = q && cardsById[q.card];
      if (!q || !r || !d) return null;
      const x0 = Math.max(0, Number(o.xp) || 300), x1 = Math.min(codexLib.MAX_XP, x0 + codexLib.dupeXpFor(d.rarity));
      const dupe = o.owned === 'maxed' ? { maxed: true } : o.owned === 'none' ? null
        : { xpBefore: x0, xpAfter: x1, gained: x1 - x0, levelBefore: codexLib.levelFromXp(x0), levelAfter: codexLib.levelFromXp(x1) };
      return questReward([{ key, id: q.card, kind: r.kind, ...(o.owned ? { already: true, ...(dupe ? { dupe } : {}) } : {}) }], { force: true });
    },
  },
  forge: {
    tab: (k) => {
      const open = () => { setBinder(true); forgeUI.setTab(k); };
      if (shellUI.currentScreen() === 'fan') open(); else shellUI.go('fan', { after: open });
      return k;
    },
    open: (id) => forgeUI.openSheet(id),
    close: () => forgeUI.closeSheet(),
    fresh: () => forgeUI.freshReady(),
    owned: (id) => identity.ownedCount(id),
    level: (id) => identity.cardLevel(id),
    get ceremony() { return window.__nmForgeCer || null; },
  },
  packDupe: (id) => identity.packDupe(id),
  depthDebug: depthArt.debug,
  lutPass: lutController.pass,
  get lutDebug() { return lutController.debugState(); },
  get gfxDebug() { return gfxDebug(); },
  shell: shellUI, titleLayer,
  get entryDebug() { return entryLayer.fx?.debugState() || null; },
  get entryFx() { return entryLayer.fx || null; },
  get entryAmbient() { return { wanted: entryAmbientWanted, playing: !!entryAmbient }; },
  get entryAudioDebug() { return { ...entryAudioStatus }; },
  get entryDeckStackVisible() { return deckStack.group.visible; },
  get entryRosterExpected() { return entryRosterExpected; },
  entryQaMode(mode) { entryLayer.fx?.qaMode(mode); return entryLayer.fx?.debugState() || null; },
  entryQaTime(seconds = null) { entryLayer.fx?.setFixedTime(seconds); return entryLayer.fx?.debugState() || null; },
  entryMotionAt(seconds) { return entryLayer.fx?.motionAt(seconds) || null; },
  get introDebug() { return introLayer.fx?.debugState() || { playing: false, due: cinematicActive }; },
  introQaTime(seconds = null) { introLayer.fx?.setFixedTime(seconds); return introLayer.fx?.debugState() || null; },
  replayIntro: () => replayIntro(),
  academy: academy.api,   // v245 QA handle
  artTexStats: () => ({ size: artTexCache.size, cap: ART_TEX_CAP,
    live: artTexLive.size, pinned: artTexPinned.size, keys: [...artTexCache.keys()] }),
  inspectArtWidth: () => {
    const c = soloCard || cards[focusIndex];
    const map = c?.artMat?.uniforms?.map?.value || c?.artMat?.map;
    return Number(map?.image?.width) || 0;
  },
  openFromBinder, closeSolo, setBinder, livingVideo, livingVideo2, openLab, closeLab,
  poolDebug: () => audio.poolDebug(),      // SC6 QA: prove music PLAYBACK
  get focusIndex() { return focusIndex; },
  // SC34: the WebKit gate asserts a fast session is not left in the reduced tier
  get perfMode() { return perfMode; },
  get perfPixelsReduced() { return perfPixelsReduced; },
  get pixelCap() { return pixelCap; },
  get qualityStep() { return qualityStep; },
  perf: (why = 'qa') => perfmon.snapshot(why),   // v259 the perf beacon's current chunk
  perfFlush: (why = 'chunk') => perfmon.flush(why),
  get perfLog() { return { ...perfLog }; },
  // SC38c QA: drive one real shed-and-restore through the live chokepoint, so a
  // suite can assert the property that was actually broken — that the Graphics
  // row still reports the shed AFTER the tier has healed. There is no way to
  // provoke a genuine shed in a harness (it needs six consecutive slow 3s
  // windows), and forcing `?perf=1` pins the tier ON, which is the one state
  // where the bug cannot show. This calls the same setPerfMode both directions.
  // Normalise OFF first: headless renders on SwiftShader at ~8fps, so the real
  // boot-phase shed has usually already fired by the time a suite gets here and
  // a bare on->off would early-return and count nothing. off->on->off always
  // records exactly one transition, whatever state it starts in.
  slotQaHold(on) { return slotmod && slotmod.qaHoldResult ? slotmod.qaHoldResult(on) : false; },
  qaTexOrientation(url, minWidth) { return qaTexOrientation(url, minWidth); },
  qaArtUrl(i) { return artSrc(deck[i], V); },
  qaShedCycle() {
    setPerfMode(false);
    setPerfMode(true, { reducePixels: true });
    setPerfMode(false);
    return perfLog.sheds;
  },
  qaQualityVerdict(fps, elapsed) {
    return applyProbeVerdict(Number(fps), Number(elapsed));
  },
  // SC20 gesture QA: the live state machine + the classification log
  get gest() {
    return { phase: gest.phase, onCard: gest.onCard,
      scroll: { active: fanScroll.active, glide: fanScroll.glide,
        pos: +fanScroll.pos.toFixed(3), vel: +fanScroll.vel.toFixed(2) },
      focusPos: +focusPos.toFixed(3), inspecting,
      ry: +manip.ry.toFixed(3), ty: +manip.ty.toFixed(3), mode: manip.mode,
      log: (window.__gest || []).slice() };
  },
  gestClear() { window.__gest = []; },
  // SC26 QA: is the SANDBOX on? It must be OFF for the zero-credit suite
  // to mean anything. v241: it is ON by default for SUPREME only (GUEST is a
  // real guest) — see the note on identity.js guestSandboxOn().
  sandboxOn: () => identity.guestSandboxOn(),
  playerName: () => identity.displayName(),
  ownedCount: (id) => identity.ownedCount(id),
  creditsLeft: () => identity.creditsLeft(),
  // SC26 QA: how many cards the binder's SHELVES actually reach. The grid only
  // renders cards that land in a section, so this must equal deck.length —
  // when it didn't, 621 G2 cards were invisible while the readout lied.
  binderReach: () => {
    const seen = new Set();
    for (const s of binderSections(true)) for (const i of s.idxs) seen.add(i);
    return { reachable: seen.size, deck: SET_SIZE,
      sections: binderSections(true).map(s => `${s.label}:${s.idxs.length}`) };
  },
  get soloCard() { return soloCard; },
  get battleActive() { return battleActive; },
  get packActive() { return packActive; },
  get packDebug() { return pack && pack.debugState ? pack.debugState() : null; },
  get deckActive() { return deckActive; },
  get slotActive() { return slotActive; },
  get slotDebug() { return slotmod && slotmod.debugState ? slotmod.debugState() : null; },
  get studioPmremMs() { return studioPmremMs; },
  qaDraw(seed, picks = null) {
    const owned = deck.filter(card => card.role !== 'support' && identity.ownedCount(card.id) > 0);
    const chosen = Array.isArray(picks) ? picks.slice(0, 2) : owned.slice(0, 2).map(card => card.id);
    return resolveDraw({ cards: deck, owned: id => identity.ownedCount(id) > 0,
      picks: chosen, history: identity.fieldHistory(), seed });
  },
  get collectionPainted() {
    return !!(fan.visible && renderPass.scene === scene && !titleLayer.on);
  },
  get deckDebug() { return deckmod && deckmod.debugState ? deckmod.debugState() : null; },
  // SC32: the lockstep contract, exposed so the MP smoke can PROVE both clients
  // run the same engine rather than assume it.
  get engineVersion() { return ENGINE_VERSION; },
  get deckMod() { return deckmod; },
  // Levelling presentation QA: exercise the real queue/progress renderers with
  // deterministic rows without mutating a player's persisted XP.
  qaLevelUps: (rows) => showLevelUps(rows),
  // LEVEL-UP FX QA: plays a ceremony on real cards without touching anyone's
  // XP. kind: 'card' | 'holo' (crosses HOLO, star cost 2->3) | 'group' (five
  // at once) | 'player' | 'player3' (three levels in one go) | 'underdog' (UNDERDOG
  // LV 1 -> 4, one count-up) | 'underdog-group' (the same inside a group). opts override the
  // rows ({ id, from, to, starFrom, starTo } / { from, to, stars }). Returns the
  // ceremony's promise. levelFxSeek(t) freezes it at t seconds (null = live)
  // for frame capture; levelFxState() reads the running ceremony.
  levelFx: (kind = 'card', opts = {}) => qaLevelFx(kind, opts),
  // v12 PLAYER LEVEL QA: results XP strip (+ the level-up when it crosses one); writes nothing
  qaResults: (opts = {}) => qaResults(opts),
  levelFxSeek: (t) => loadLevelFx().then(m => m.qa.seek(t)),
  levelFxState: () => loadLevelFx().then(m => m.qa.state()),
  levelFxMod: () => loadLevelFx(),
  // SC31 QA: the flipped sheet's registration against its card, so "it maps to
  // the 3D card" is a measured claim rather than a look at a screenshot.
  sheetAlign: () => debugSheetAlign(),
  // SC31 QA: SFX start timestamps on both clocks (see audio.js sfxDebug)
  sfxDebug: () => audio.sfxDebug(),
  musicDebug: () => audio.musicDebug(),
  debugMusicFilter: () => audio.debugMusicFilter(),
  get voPlaying() { return audio.voActive(); },
  get battleDebug() { return battle && battle.debugState ? battle.debugState() : null; },
  // SC46 QA: the back-navigation state machine, and a way to drive a REAL back
  // press. The suite must go through history.back() rather than through a shim,
  // because the whole feature is about what the browser does with popstate.
  get nav() { return nav.navDebug(); },
  // NAV AUDIT / HOLO V2 QA: the hologram viewer's state and its first-load time
  get holo() { return { open: !!soloHolo, ready: !!soloHolo?.ready, version: soloHolo?.h?.version ?? 0,
    rise: soloHolo?.h?.figure?.rise ?? 0, loadMs: holoLoadMs, capable: holo2Ids ? holo2Ids.size : null }; },
  // HOLO QA: pin the viewer's rise (0..1, scrubbed) and card tilt for side/front
  // frames; holoQa(null) hands both back to the live pose
  holoQa: (o) => {
    holoQaPose = o ? { rx: o.rx ?? 0, ry: o.ry ?? 0 } : null;
    if (o && o.rise != null && soloHolo?.h) soloHolo.h.rise(o.rise);
    return !!soloHolo?.ready;
  },
  openHolo: (id) => { const di = deck.findIndex(d => d.id === id); if (di >= 0) openFromBinder(di, { holo: true }); return di; },
  navBack: () => nav.debugBack(),
  navDepth: () => nav.depth(),
  exitConfirmOpen: () => exitConfirmOpen(),
  // The forfeit ACTION, which outlived its button. Exposed so "does forfeiting
  // still resolve?" is answerable directly instead of through a control that
  // no longer exists.
  forfeitNow: () => (battle && battle.forfeitBattle ? battle.forfeitBattle() : false),
  // SC33 QA: the gauntlet's live state, so a stuck run can be diagnosed rather
  // than guessed at.
  get gauntletDebug() {
    return {
      active: !!gauntlet,
      leg: gauntlet ? gauntlet.leg : null,
      squad: gauntlet ? gauntlet.squad.slice() : null,
      battleActive, screen: shellUI.currentScreen(),
      lastError: gauntletLastError,
    };
  },
  get battleMod() { return battle; },
  // PERF/LIFECYCLE QA (scripts/battle-lifecycle-probe.mjs): GPU residency, and a direct enter/exit of a
  // seeded AI battle so repeated matches can be measured without replaying the menus each time.
  gpuMemory: () => ({ ...renderer.info.memory, programs: renderer.info.programs ? renderer.info.programs.length : null }),
  async qaBattle(seed = 7) {
    let s = seed >>> 0;
    const rng = () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
    const side = (d) => ({ chars: d.chars, supports: d.supports || [], mastery: null, holo: [],
      levels: d.levels || Object.fromEntries(d.chars.map((id) => [id, 1])) });
    const A = side(shellUI.aiDeck(rng, { difficulty: 'normal', streak: 0, seed }));
    const B = side(shellUI.aiDeck(rng, { difficulty: 'normal', streak: 0, seed: seed + 1 }));
    shellUI.go('battle', {});
    await launchBattle({ A, B }, { driver: { kind: 'ai', difficulty: 'normal' }, seed });
    return battleActive;
  },
  qaExitBattle() {
    if (battleActive && battle) battle.exitBattle();
    shellUI.go('title');
    return battleActive;
  },
  // THE CODEX QA (smoke `codex`): its state, exhibits and progress key once the chunk has loaded
  get codex() { return codexMod ? codexMod.qa : null; },
  openCodex: (opts) => openCodex(opts),
  captureQuality() {
    // CSS viewport and encoded surface are both 1280x720. DPR 1 selects the
    // real desktop-landscape rung instead of scaling up a phone composition.
    const expected = 1.0;
    const actual = renderer.getPixelRatio();
    const state = { offline: OFFLINE_RENDER, perfMode, sheds: perfLog.sheds,
      dpr: actual, expectedDpr: expected, bloom: bloomPass.enabled, pixelCap,
      width: innerWidth, height: innerHeight };
    if (!OFFLINE_RENDER || perfMode || perfLog.sheds !== 0 || Math.abs(actual - expected) > 0.001
      || pixelCap !== 2.0 || !bloomPass.enabled || innerWidth !== 1280 || innerHeight !== 720) {
      throw new Error(`OFFLINE_RENDER_QUALITY_ASSERTION:${JSON.stringify(state)}`);
    }
    return state;
  },
  async launchCanonicalReplay(replay, names = { A: 'CHALLENGER', B: 'RECIPIENT' }) {
    if (!OFFLINE_RENDER) throw new Error('OFFLINE_RENDER_FLAG_REQUIRED');
    if (!replay || replay.engineVersion !== ENGINE_VERSION) throw new Error('REPLAY_ENGINE_VERSION_MISMATCH');
    window.__nomadmonCapture = { done: false, qualityViolation: null, names,
      audioCaptureOriginMs: Date.now(), audioEvents: [], visualEvents: [],
      actionTimeScale: RENDER_ACTION_TIME_SCALE };
    let marker = document.getElementById('renderSyncMarker');
    if (!marker) {
      marker = document.createElement('div');
      marker.id = 'renderSyncMarker';
      Object.assign(marker.style, { position: 'fixed', right: '0', bottom: '0', width: '20px',
        height: '20px', background: '#000', zIndex: '2147483647', pointerEvents: 'none' });
      document.body.appendChild(marker);
    }
    // The offline renderer boots straight into the battle, so nothing has
    // visited the binder or deck builder and no card art has been ensured.
    // artTexById() would then hand buildCard() undefined and the render dies
    // on "Cannot read properties of undefined (reading 'image')". Load the art
    // for every card this replay touches before the battle builds anything.
    await Promise.all([...replay.decks.A.chars, ...replay.decks.A.supports,
      ...replay.decks.B.chars, ...replay.decks.B.supports]
      .map(id => { const i = deck.findIndex(d => d.id === id); return i >= 0 ? ensureArtTex(i) : null; }));
    const entry = document.getElementById('entry');
    if (entry) entry.classList.remove('on');
    shellUI.go('battle', {});
    await launchBattle(replay.decks, { driver: { kind: 'replay' }, seed: replay.seed,
      playerSide: 'A', replay, capture: true, replayTimeScale: 1 });
    return window.__nm.captureQuality();
  },
};
// ?lvlfx=card|holo|group|player|player3 plays that level-up ceremony once the
// app is up (QA / review links; nothing is granted).
if (q.get('lvlfx')) setTimeout(() => window.__nm.levelFx(q.get('lvlfx')), Number(q.get('lvlfxDelay') || 1500));
// v239 ?cutscene=<id>: the campaign cutscene player on its own (QA / review links; a Play gate
// supplies the gesture audio needs, then the story plays through, ignoring the seen state).
if (q.get('cutscene')) import('./cutscene-player.js').then((m) => m.demoRoute(q.get('cutscene')));
// ?spellit[=<word id>]: a test link straight to SPELL IT through the REAL ending: a quick match against a foe on its
// last card at 1 HP, the first volley won for you in the engine (no board is shown, nothing traced), so the match
// ends on a real K.O. -> FREEZE -> FINISH HIM -> SPELL IT (the given word, else a random one) on a clean frozen scene.
// window.__nm.qaSpellQuick(id) is the same from the console (or __nm.spellit.quick(id)). QA-tagged telemetry; nothing is granted.
window.__nm.qaSpellQuick = async (id = null, seed = null) => {
  window.__qaSpellIt = 'on';
  window.__qaBarrage = { ...(window.__qaBarrage || {}), foeLastLife: 1, winNow: true };
  window.__qaSpellWord = id ? String(id).toLowerCase() : null;   // (spellit.js takeForced; that chunk may not be loaded yet)
  if (battleActive) window.__nm.qaExitBattle();
  return window.__nm.qaBattle(Number(seed) || (Date.now() % 100000) + 1);
};
if (q.has('spellit')) {
  const word = (q.get('spellit') || '').trim().toLowerCase();
  window.__qaSpellLog = true;   // the test link's SPELL IT submits DO post their ink (the owner's real writing; spellit-ui.js logInk)
  let tries = 0;
  const go = () => {
    const menu = document.getElementById('menuSolo');   // the title menu is really up and on top (not under the first-run name forge)
    const r = menu ? menu.getBoundingClientRect() : null;
    const onTop = !!(r && r.width > 0 && menu.contains(document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2)));
    if (document.body.dataset.screen === 'title' && !battleActive && onTop) window.__nm.qaSpellQuick(word || null, q.get('seed'));
    else if (++tries < 1200) setTimeout(go, 500);
  };
  setTimeout(go, 800);
}
